export type Language = 'en' | 'fr' | 'es' | 'de' | 'it' | 'pt';

export interface LanguageOption {
  code: Language;
  label: string;
  flag: string;
}

export const languages: LanguageOption[] = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
];

const en: Record<string, string> = {
  // ── Landing ──
  'header.signIn': 'Sign In',
  'header.getStarted': 'Get Started',
  'hero.badge': 'Built for Multi-Store Retail Teams',
  'hero.title1': 'AI-Powered Staff Scheduling',
  'hero.title2': 'That Actually Balances Your Stores',
  'hero.subtitle': 'Built for growing retail and multi-location teams that need contract-compliant, AI-balanced schedules without the chaos.',
  'hero.cta': 'Start Free 30-Day Trial',
  'hero.signIn': 'Sign In',
  'hero.trust1': '30-day free trial',
  'hero.trust2': 'Set up in minutes',
  'hero.trust3': 'No commitment',
  'hero.euLine': 'EU-hosted. GDPR compliant. Built for European labour contracts.',

  // ── Trust Signal ──
  'trust.title': 'Built for Modern Retail Operations',
  'trust.eu': 'EU Hosting',
  'trust.gdpr': 'GDPR Compliant',
  'trust.secure': 'Secure Cloud Infrastructure',
  'trust.encrypted': 'Encrypted in Transit & at Rest',
  'trust.origin': 'Designed and operated from Italy.',

  // ── Screenshot ──
  'screenshot.caption': 'Auto-balanced schedule generated in one click.',
  'screenshot.sub': 'Respect contracts. Avoid overtime. Eliminate coverage gaps.',

  'features.title': 'Everything You Need to Run Smarter Schedules',
  'features.subtitle': 'Outcome-driven tools designed for busy retail managers.',
  'feature.scheduling.title': 'Smart Scheduling',
  'feature.scheduling.desc': 'AI-generated schedules that automatically balance hours and prevent coverage gaps.',
  'feature.employees.title': 'Employee Management',
  'feature.employees.desc': 'Track contracts, availability, and absences in one centralized dashboard.',
  'feature.stores.title': 'Multi-Store Support',
  'feature.stores.desc': 'Manage all locations from one intelligent control center.',
  'feature.absences.title': 'Absence Tracking',
  'feature.absences.desc': "Log sick days, personal leave, and time off. Always know who's available.",
  'feature.holidays.title': 'Holiday Management',
  'feature.holidays.desc': 'Handle public holidays and staff holiday requests. Track remaining allowances automatically.',
  'feature.reports.title': 'Reports & Exports',
  'feature.reports.desc': 'Generate coverage reports, hours summaries, and export everything to Excel with one click.',

  // ── Mid-page CTA ──
  'midCta.title': 'Stop Building Schedules Manually.',
  'midCta.subtitle': 'Join growing retail teams switching to AI-powered rota management.',
  'midCta.button': 'Start Free Trial',
  'stats.shifts': 'Shifts Scheduled',
  'stats.teams': 'Teams Using It',
  'stats.uptime': 'Uptime',
  'stats.rating': 'User Rating',
  'how.title': 'How It Works',
  'how.subtitle': 'Up and running in minutes, not days.',
  'how.step1.title': 'Add Your Stores',
  'how.step1.desc': 'Set up your locations and configure operating hours for each day of the week.',
  'how.step2.title': 'Add Employees',
  'how.step2.desc': 'Enter your team with their contracts, availability, and store assignments.',
  'how.step3.title': 'Build Schedules',
  'how.step3.desc': 'Create weekly schedules with smart suggestions that respect contracts and availability.',
  'pricing.title': 'Simple, Transparent Pricing',
  'pricing.subtitle': 'Start with a 30-day free trial. No credit card required.',
  'pricing.perMonth': '/month',
  'pricing.afterTrial': 'After your 30-day free trial',
  'pricing.cta': 'Start Free Trial',
  'pricing.included': 'Everything included:',
  'pricing.item1': 'Unlimited stores',
  'pricing.item2': 'Unlimited employees',
  'pricing.item3': 'AI auto-balancing included',
  'pricing.item4': 'Absence & holiday tracking',
  'pricing.item5': 'Contract-compliant scheduling',
  'pricing.item6': 'Priority support',
  'pricing.badge': 'Best for Growing Teams',
  'pricing.reassurance': 'Cancel anytime. No long-term contracts.',
  'pricing.target': 'For growing retail teams and multi-store businesses.',
  'pricing.monthly': 'Monthly',
  'pricing.annual': 'Annual',
  'pricing.comingSoon': 'Coming Soon',
  'cta.title': 'Ready to simplify your scheduling?',
  'cta.subtitle': "Join managers who've ditched spreadsheets for smarter rota planning.",
  'cta.button': 'Start Your Free Trial',
  'footer.features': 'Features',
  'footer.howItWorks': 'How It Works',
  'footer.signIn': 'Sign In',
  'footer.rights': '© {year} Operota. All rights reserved.',
  'footer.product': 'Product',
  'footer.legal': 'Legal',
  'footer.terms': 'Terms & Conditions',
  'footer.privacy': 'Privacy Policy',
  'footer.cookies': 'Cookie Policy',
  'footer.dpa': 'DPA',
  'footer.refunds': 'Refunds & Cancellation',

  // ── Pain Points ──
  'pain.title': 'Still Managing Rotas the Hard Way?',
  'pain.manual.title': 'Manual Scheduling',
  'pain.manual.desc': 'Writing rotas in Excel or paper wastes hours every week.',
  'pain.gaps.title': 'Coverage Gaps',
  'pain.gaps.desc': 'Last-minute sick days leave stores understaffed.',
  'pain.overtime.title': 'Overtime Mistakes',
  'pain.overtime.desc': 'Contract hours get exceeded without warning.',
  'pain.cta.text': 'Let Operota Handle It Instead',
  'pain.cta.button': 'Start Free Trial',

  // ── Comparison ──
  'compare.title': 'Why Operota Over Excel?',
  'compare.excel.1': 'Manual scheduling',
  'compare.excel.2': 'No contract tracking',
  'compare.excel.3': 'No multi-store view',
  'compare.excel.4': 'No holiday tracking',
  'compare.operota.1': 'AI auto-balance',
  'compare.operota.2': 'Contract-respecting schedules',
  'compare.operota.3': 'Multi-store dashboard',
  'compare.operota.4': 'Built-in absence management',

  // ── Testimonials ──
  'testimonials.title': 'Trusted by Growing Retail Teams',
  'testimonial.1.quote': 'Operota saved us 6 hours a week on rota planning. The AI balancing is a game-changer.',
  'testimonial.1.name': 'Marco Rossi',
  'testimonial.1.role': 'Retail Manager – Fashion Chain',
  'testimonial.2.quote': 'We stopped missing shifts after switching. Coverage gaps are a thing of the past.',
  'testimonial.2.name': 'Emma Clarke',
  'testimonial.2.role': 'Multi-Store Owner – Boutique Group',
  'testimonial.3.quote': 'Finally software that respects contract hours and keeps our stores compliant.',
  'testimonial.3.name': 'Daniel Weber',
  'testimonial.3.role': 'Operations Lead – Retail Chain',

  // ── FAQ ──
  'faq.title': 'Frequently Asked Questions',
  'faq.cancel.q': 'Can I cancel anytime?',
  'faq.cancel.a': 'Yes, you can cancel your subscription at any time with no penalties. There are no long-term contracts or hidden fees. Your data remains accessible until the end of your billing period.',
  'faq.trial.q': 'Is there a free trial?',
  'faq.trial.a': 'Yes. Every new account gets a full 30-day free trial with access to all features, including AI scheduling, multi-store management, and reporting. No credit card required.',
  'faq.stores.q': 'Does it support multiple stores?',
  'faq.stores.a': 'Absolutely. Operota supports unlimited store locations within a single organisation. You can manage schedules, staffing, and reports across all your stores from one dashboard.',
  'faq.contracts.q': 'Does it respect employee contracts?',
  'faq.contracts.a': 'Yes. Operota automatically respects contract hour limits when generating schedules. The AI prevents overtime and ensures each employee is scheduled within their agreed hours.',
  'faq.security.q': 'Is my data secure?',
  'faq.security.a': 'Your data is encrypted in transit and at rest, and hosted entirely within the EU. We use enterprise-grade cloud infrastructure with strict GDPR compliance to keep your business data safe.',

  // ── Final CTA ──
  'finalCta.title': 'Stop Managing Shifts in Spreadsheets.',
  'finalCta.subtitle': 'Start your 30-day free trial and take control of your workforce scheduling today.',
  'finalCta.button': 'Start Your Free Trial',
  'finalCta.signIn': 'Sign In',

  // ── Navigation ──
  'nav.dashboard': 'Dashboard',
  'nav.stores': 'Stores',
  'nav.employees': 'Employees',
  'nav.schedule': 'Schedule',
  'nav.holidays': 'Holidays',
  'nav.absences': 'Absences',
  'nav.reports': 'Reports',
  'nav.signOut': 'Sign out',
  'nav.settings': 'Settings',

  // ── Auth ──
  'auth.back': 'Back',
  'auth.title': 'Operota',
  'auth.subtitle': 'AI scheduling for shift-based teams — compliant, fast, and fair.',
  'auth.signIn': 'Sign In',
  'auth.signUp': 'Sign Up',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.signingIn': 'Signing in...',
  'auth.creatingAccount': 'Creating account...',
  'auth.createAccount': 'Create Account',
  'auth.loading': 'Loading...',
  'auth.checkEmail': 'Check your email to verify your account before signing in.',
  'auth.invalidCredentials': 'Invalid email or password. Please try again.',
  'auth.emailNotConfirmed': 'Please verify your email address before signing in.',
  'auth.alreadyRegistered': 'An account with this email already exists. Please sign in instead.',
  'auth.unexpectedError': 'An unexpected error occurred. Please try again.',
  'auth.invalidEmail': 'Please enter a valid email address',
  'auth.passwordMin': 'Password must be at least 6 characters',

  // ── Dashboard ──
  'dash.title': 'Dashboard',
  'dash.weekOf': 'Week of {start} – {end}',
  'dash.today': 'Today',
  'dash.employeesWorking': 'employees working',
  'dash.workingToday': 'working today',
  'dash.onShiftToday': 'on shift today',
  'dash.pendingRequests': 'pending requests',
  'dash.requestsToReview': 'requests to review',
  'dash.staffingGaps': 'staffing gaps',
  'dash.issuesToday': 'issues today',
  'dash.noIssuesToday': 'No issues today',
  'dash.noIssues': 'No issues',
  'dash.unconfirmedShifts': 'unconfirmed shifts',
  'dash.staffingAlerts': 'Staffing Issues',
  'dash.thisWeek': 'This Week',
  'dash.upcomingIssues': 'This Week',
  'dash.actionRequired': 'Action Required',
  'dash.insights': 'Insights',
  'dash.missingStaff': '{count} staff missing',
  'dash.moreIssues': '...and {count} more',
  'dash.viewSchedule': 'View Schedule',
  'dash.pendingHolidayRequests': '{count} pending holiday request(s)',
  'dash.pendingHolidayDesc': 'Review and approve or reject requests',
  'dash.reviewRequests': 'Review',
  'dash.nearOvertime': '{count} employee(s) nearing overtime',
  'dash.employeeOverContract': 'employee over contract',
  'dash.employeesOverContract': 'employees over contract',
  'dash.approachingLimit': 'approaching limit',
  'dash.more': 'more',
  'dash.allClear': 'All clear — no issues this week',
  'dash.weekGlance': 'This Week at a Glance',
  'dash.ofContractHours': 'of {hours}h contract hours',
  'dash.storesFullyCovered': 'Stores fully covered',
  'dash.staffingGapsWeek': 'Staffing gaps this week',
  'dash.coverageOverview': 'Coverage Overview',
  'dash.importData': 'Import Data',
  'dash.importTitle': 'Import Data',
  'dash.importDescription': 'Choose how to import your store and employee data.',
  'dash.aiImportTitle': 'AI Import',
  'dash.aiImportDesc': 'Paste text, photos, or documents and let AI extract data.',
  'dash.startAiImport': 'Start AI Import',
  'dash.excelImportTitle': 'Excel / CSV',
  'dash.excelImportDesc': 'Upload a spreadsheet with stores and employees.',
  'dash.uploadFile': 'Upload File',
  'dash.importHint': 'You can always import more data later from Settings.',

  // ── Stores ──
  'stores.title': 'Stores',
  'stores.subtitle': 'Manage your store locations, staffing and operating hours',
  'stores.add': 'Add Store',
  'stores.edit': 'Edit Store',
  'stores.addNew': 'Add New Store',
  'stores.editDesc': 'Update store details, hours and staffing',
  'stores.addDesc': 'Add a new store location',
  'stores.name': 'Store Name',
  'stores.address': 'Address',
  'stores.staffingReq': 'Daily Staffing Requirements',
  'stores.timeBlockStaffing': 'Staffing by Time Block',
  'stores.timeBlockDesc': 'Define how many staff are needed during specific time periods each day.',
  'stores.addBlock': 'Add block',
  'stores.noBlocks': 'No time blocks — uses daily requirement if set.',
  'stores.staff': 'staff',
  'stores.colTimeBlocks': 'Time Blocks',
  'stores.create': 'Create Store',
  'stores.update': 'Update Store',
  'stores.loading': 'Loading stores...',
  'stores.empty': 'No stores yet. Add your first store to get started.',
  'stores.colName': 'Name',
  'stores.colAddress': 'Address',
  'stores.colHours': 'Hours',
  'stores.colStaffing': 'Staffing',
  'stores.colActions': 'Actions',
  'stores.notSet': 'Not set',
  'stores.breakPolicy': 'Break Policy',
  'stores.breakPolicyDesc': 'Configure automatic break deductions for payroll. Breaks are deducted from paid hours, not scheduled at fixed times.',
  'stores.breakIfShiftExceeds': 'Apply break if shift exceeds',
  'stores.hours': 'hours',
  'stores.breakDuration': 'Break duration',
  'stores.addSecondBreak': 'Add second break for long shifts',
  'stores.storeClosesDuringBreak': 'Store closes during break',
  'stores.colBreaks': 'Break Policy',
  'stores.breakSummary': '{duration}min break ≥{threshold}h',
  'stores.breakSummaryPerDay': 'Per-day break durations configured',
  'stores.breakPresetHint': 'Click a preset to apply to all days, or customize per day below.',
  'stores.perDayBreakDuration': 'Break duration per day',
  'stores.breakExceedsLimit': 'Break exceeds 30% of a {hours}h shift (max {max}min)',
  'stores.perDayConfig': 'Per Day Staffing Configuration',
  'stores.block': 'block',
  'stores.blocks': 'blocks',
  'stores.custom': 'Custom',

  // ── Employees ──
  'emp.title': 'Employees',
  'emp.subtitle': 'Manage staff, availability, and store assignments',
  'emp.hPerWeek': 'h/week',
  'emp.add': 'Add Employee',
  'emp.edit': 'Edit Employee',
  'emp.addNew': 'Add New Employee',
  'emp.editDesc': 'Update employee details',
  'emp.addDesc': 'Add a new staff member',
  'emp.name': 'Name',
  'emp.email': 'Email',
  'emp.phone': 'Phone',
  'emp.contractHours': 'Contract Hours/Week',
  'emp.primaryStore': 'Primary Store',
  'emp.selectStore': 'Select primary store',
  'emp.dayAvailability': 'Day Availability',
  'emp.canWorkAt': 'Can Work At (Stores)',
  'emp.addBtn': 'Add Employee',
  'emp.updateBtn': 'Update Employee',
  'emp.search': 'Search by name, email, phone...',
  'emp.nameAZ': 'Name A–Z',
  'emp.nameZA': 'Name Z–A',
  'emp.hoursUp': 'Hours ↑',
  'emp.hoursDown': 'Hours ↓',
  'emp.byStore': 'By Store',
  'emp.allStores': 'All Stores',
  'emp.loading': 'Loading employees...',
  'emp.empty': 'No employees yet. Add your first team member to get started.',
  'emp.colName': 'Name',
  'emp.colContact': 'Contact',
  'emp.colHours': 'Hours',
  'emp.colStore': 'Primary Store',
  'emp.colAvailability': 'Availability',
  'emp.colActions': 'Actions',

  // ── Schedule ──
  'sched.title': 'Schedule',
  'sched.weekOf': 'Week of {start} - {end}',
  'sched.allStores': 'All Stores',
  'sched.selectStore': 'Select store',
  'sched.autoGenerate': 'Auto-Generate',
  'sched.export': 'Export',
  'sched.clearAll': 'Clear All',
  'sched.print': 'Print',
  'sched.printAll': 'Print All Stores',
  'sched.printStore': 'Print {name}',
  'sched.prevWeek': 'Previous Week',
  'sched.nextWeek': 'Next Week',
  'sched.today': 'Today',
  'sched.conflicts': 'Schedule Conflicts',
  'sched.andMore': '...and {count} more',
  'sched.addStoresFirst': 'Add stores first to start scheduling.',
  'sched.clear': 'Clear',
  'sched.holiday': 'Holiday',
  'sched.pendingHoliday': 'Pending Holiday',
  'sched.pendingTimeOff': 'Pending Time Off',
  'sched.pendingHolidays': '⚠ {count} Pending Requests',
  'sched.conflictsDetected': '{count} holiday-shift conflicts',
  'sched.includePending': 'Include pending',
  'sched.resolveConflicts': 'Auto-fix schedule',
  'sched.approvedConflict': 'Approved holiday',
  'sched.pendingConflict': 'Pending request',
  'sched.resolveTitle': 'Auto-fix Schedule Conflicts',
  'sched.resolveDesc': 'The following changes will be made to resolve holiday conflicts:',
  'sched.willResolve': '{count} conflicts will be fixed:',
  'sched.cannotResolve': '{count} require manual adjustment:',
  'sched.noReplacement': 'No replacement available',
  'sched.removeShift': 'Remove shift',
  'sched.applyFixes': 'Apply {count} fixes',
  'sched.resolveSuccess': '{count} conflicts resolved',
  'sched.resolvePartial': '{count} still require manual adjustment',
  'sched.resolveError': 'Error resolving conflicts',
  'sched.add': 'Add',
  'sched.editShift': 'Edit Shift',
  'sched.addShift': 'Add Shift',
  'sched.editShiftDesc': 'Update shift details',
  'sched.addShiftDesc': 'Schedule a new shift',
  'sched.employee': 'Employee',
  'sched.selectEmployee': 'Select employee',
  'sched.store': 'Store',
  'sched.date': 'Date',
  'sched.startTime': 'Start Time',
  'sched.endTime': 'End Time',
  'sched.updateShift': 'Update Shift',
  'sched.addShiftBtn': 'Add Shift',
  'sched.clearConfirm': 'Clear all shifts for this week?',
  'sched.clearStoreConfirm': 'Clear all shifts for {name} this week?',
  'sched.strategyStandard': 'Standard',
  'sched.strategyCoverage': 'Coverage Priority',
  'sched.strategyBalanced': 'Balanced',
  'sched.strategyFullCoverage': 'Full Coverage',
  'sched.balancedDesc': 'Optimises fairness and contract compliance while maintaining coverage.',
  'sched.coverageDesc': 'Prioritises full staffing. May allow limited overtime.',
  'sched.recommended': 'Recommended',
  'sched.coverageWarning': 'Prioritises full staffing coverage. May cause limited overtime for some employees.',
  'sched.strategyLabel': 'Scheduling Strategy',
  'sched.defaultStrategy': 'Default Scheduling Strategy',
  'sched.defaultStrategyDesc': 'Set the default scheduling strategy for your organisation.',
  'sched.strategySaved': 'Default strategy updated',
  'sched.shiftAdded': 'Shift added successfully',
  'sched.shiftUpdated': 'Shift updated successfully',
  'sched.shiftRemoved': 'Shift removed successfully',
  'sched.shiftsSwapped': 'Shifts swapped successfully',
  'sched.shiftMoved': 'Shift moved successfully',
  'sched.errorSwap': 'Error swapping shifts',
  'sched.errorMoveShift': 'Error moving shift',
  'sched.cannotSwapStores': 'Cannot swap shifts across different stores',
  'sched.swapHint': 'Drop to swap',
  'sched.alreadyScheduledSwap': 'Employee is already scheduled on that day',
  'sched.availabilityOverrideTitle': 'Availability Override',
  'sched.availabilityOverrideDesc': '{name} is only available on {days}. Approve this shift change?',
  'sched.approve': 'Approve',
  'sched.noDaysAvailable': 'no days',
  'sched.errorAddShift': 'Error adding shift',
  'sched.errorUpdateShift': 'Error updating shift',
  'sched.errorRemoveShift': 'Error removing shift',
  'sched.noShiftsToClear': 'No shifts to clear',
  'sched.clearedShifts': 'Cleared {count} shift(s)',
  'sched.clickUndo': 'Click undo to restore them.',
  'sched.undo': 'Undo',
  'sched.restoredShifts': 'Restored {count} shift(s)',
  'sched.errorRestore': 'Error restoring shifts',
  'sched.errorClear': 'Error clearing schedule',
  'sched.generatedShifts': 'Generated {count} shifts successfully',
  'sched.errorGenerate': 'Error generating schedule',
  'sched.validationError': 'Validation error',
  'sched.cannotAssign': 'Cannot assign shift',
  'sched.alreadyScheduled': '{name} already has a shift on this date.',
  'sched.onHoliday': '{name} is on approved holiday on this date.',
  'sched.onLeave': '{name} is on {type} leave on this date.',
  'sched.storeClosed': 'The store is closed on this day.',
  'sched.shiftOutsideHours': 'Shift is outside store hours',
  'sched.shiftOutsideHoursWarning': 'This shift extends outside store hours. The extra time will be tracked as overtime in reports.',
  'sched.significantOvertimeTitle': 'Shift significantly outside store hours',
  'sched.significantOvertimeDesc': 'This shift extends more than 60 minutes outside store operating hours. Are you sure you want to save it?',
  'sched.saveAnyway': 'Save Anyway',
  'sched.shiftPaidBreak': '{paid} paid ({break} break)',
  'sched.breakDeducted': '{duration} break deducted from paid hours',
  'sched.bannerGenerated': 'Schedule generated — {employees} employees scheduled, {hours}h contract hours respected, {gaps}, shifts distributed fairly.',
  'sched.bannerNoGaps': 'no coverage gaps',
  'sched.bannerGaps': '{count} coverage gap(s)',
  'sched.bannerNoIssues': 'No issues found',
  'sched.bannerReviewGaps': '{count} coverage gap(s) to review →',
  'sched.exportSuccess': 'Schedule exported successfully',
  'sched.coverageDiag': 'Coverage Diagnostics',
  'sched.tbCoverageDiag': 'Time Block Coverage Issues',
  'sched.moreGaps': '...and {count} more gaps',
  'sched.coverageMet': 'Coverage: {staffed} / {required} ✔',
  'sched.coverageShort': 'Coverage: {staffed} / {required} ✘',
  'sched.coverageOver': 'Coverage: {staffed} / {required} ⚠',
  'sched.missingStaff': 'Missing {count} staff for {time}',
  'sched.requirements': 'Requirements',
  'sched.shifts': 'Shifts',
  'sched.diagNoAvail': 'No availability ({count})',
  'sched.diagContractExh': 'Contract exhausted ({count})',
  'sched.diagMaxDays': 'Max days reached ({count})',
  'sched.diagNoAssigned': 'No employees assigned to this store',
  'sched.diagNoEligible': 'No eligible employees',
  'sched.diagNoReq': 'No staffing requirement configured',
  'sched.conflictOverHours': '{name} is scheduled for {hours}h but contract is {contract}h',
  'sched.conflictUnavailable': '{name} is not available on {day}',
  'sched.conflictWrongStore': '{name} is not assigned to work at {store}',
  'sched.exportEmployee': 'Employee',
  'sched.exportStaffing': 'Staffing',
  'sched.exportReqSched': 'Required/Scheduled',
  'abs.typeSick': 'Sick',
  'abs.typeEmergency': 'Emergency',
  'sched.copyWeek': 'Copy Week',
  'sched.copyWeekTitle': 'Copy Schedule Week',
  'sched.copyWeekDesc': 'Duplicate all shifts from one week to another.',
  'sched.sourceWeek': 'Source Week',
  'sched.targetWeek': 'Target Week',
  'sched.copyWeekConfirm': 'Copy Schedule',
  'sched.copyWeekSuccess': 'Schedule copied successfully.',
  'sched.copyWeekError': 'Error copying schedule',
  'sched.copyWeekNoShifts': 'No shifts found in the source week.',
  'sched.copyWeekReplace': 'This week already has {count} shift(s). Replace existing schedule?',
  'sched.copyWeekReplaceBtn': 'Replace & Copy',
  'sched.copyWeekCopying': 'Copying...',
  'sched.selectTargetWeek': 'Select target week',
  'sched.copyNextWeek': 'Next week',
  'sched.copyWeekAfterNext': 'Week after next',
  'sched.copySpecificWeek': 'Choose specific week',

  // ── Actual Hours Review ──
  'review.title': 'Review Shifts',
  'review.unconfirmedCount': '{count} unconfirmed',
  'review.confirmAll': 'Confirm All',
  'review.confirmed': 'Shifts confirmed',
  'review.errorConfirm': 'Error confirming shifts',
  'review.asScheduled': 'As scheduled',
  'review.setActual': 'Set actual',
  'review.editActual': 'Edit',
  'review.editActualTitle': 'Edit Actual Hours',
  'review.scheduled': 'Scheduled',
  'review.actualStart': 'Actual start',
  'review.actualEnd': 'Actual end',
  'review.saveConfirm': 'Save & Confirm',
  'review.actualSaved': 'Actual hours saved',
  'review.errorSave': 'Error saving actual hours',
  'review.late': 'Late: {min}m',
  'review.overtime': 'OT: +{min}m',
  'review.undertime': 'Short: -{min}m',
  'review.unconfirmedBanner': '{count} unconfirmed shift(s) today',
  'review.edited': 'edited',
  'review.save': 'Save',
  'review.saved': 'Hours saved',
  'review.confirmAsScheduled': 'Confirm as scheduled',
  'sched.overContract': 'over contract',
  'sched.unavailableShort': 'unavailable',
  'sched.wrongStoreShort': 'wrong store',
  'sched.fix': 'Fix',
  'sched.reduceHours': 'Reduce hours',
  'sched.reassignShift': 'Reassign shift',
  'sched.confirm': 'Confirm',
  'sched.edit': 'Edit',
  'sched.remove': 'Remove',
  'sched.understaffed': '⚠ Understaffed',
  'sched.missingCount': 'Missing {count} staff',
  'sched.staffed': 'staffed',
  'sched.staff': 'staff',
  'sched.statusReady': 'Schedule ready',
  'sched.statusIssue1': '1 issue needs attention',
  'sched.statusIssues': '{count} issues need attention',
  'sched.issueOverHours1': '{name} is over weekly hours',
  'sched.issueOverHoursN': '{count} employees are over weekly hours',
  'sched.issueShiftTooLong1': '{count} shift is too long',
  'sched.issueShiftTooLongN': '{count} shifts are too long',
  'sched.issueInvalidTimes1': '{count} shift has invalid times',
  'sched.issueInvalidTimesN': '{count} shifts have invalid times',
  'sched.issueOutsideHours1': '{count} shift is outside store hours',
  'sched.issueOutsideHoursN': '{count} shifts are outside store hours',
  'sched.issueClosedDay1': '{count} shift on a closed day',
  'sched.issueClosedDayN': '{count} shifts on closed days',
  'sched.issueUnavailable1': '{name} scheduled on unavailable day',
  'sched.issueUnavailableN': '{count} shifts on unavailable days',
  'sched.issueWrongStore1': '{name} assigned to wrong store',
  'sched.issueWrongStoreN': '{count} employees at wrong stores',
  'sched.issueOverlap1': '{count} overlapping shift',
  'sched.issueOverlapN': '{count} overlapping shifts',
  'sched.issueShiftTooShort1': '{count} shift is too short',
  'sched.issueShiftTooShortN': '{count} shifts are too short',
  'sched.issueUnderstaffed1': '{store} needs more staff on {day}',
  'sched.issueUnderstaffedN': '{count} days are understaffed',

  'hol.title': 'Holidays',
  'hol.subtitle': 'Track holiday requests and remaining balances',
  'hol.request': 'Request Holiday',
  'hol.balances': 'Holiday Balances',
  'hol.allRequests': 'All Requests',
  'hol.noRequests': 'No holiday requests yet',
  'hol.employee': 'Employee',
  'hol.selectEmployee': 'Select employee',
  'hol.dateRange': 'Date Range',
  'hol.pickDates': 'Pick dates',
  'hol.notes': 'Notes (optional)',
  'hol.notesPlaceholder': 'Reason for holiday...',
  'hol.submit': 'Submit Request',
  'hol.businessDays': '{count} business days',
  'hol.approved': 'Approved',
  'hol.approve': 'Approve',
  'hol.rejected': 'Rejected',
  'hol.reject': 'Reject',
  'hol.pending': 'Pending',
  'hol.delete': 'Delete',
  'hol.days': '{count} days',
  'hol.used': 'Used',
  'hol.pendingLabel': 'Pending',
  'hol.total': 'Total',
  'hol.dialogTitle': 'Request Holiday',
  'hol.dialogDesc': 'Submit a new holiday request',
  'hol.partialApprove': 'Approve partially',
  'hol.partialApproveTitle': 'Partial Approval',
  'hol.selectApprovedDates': 'Select dates to approve',
  'hol.partialSummary': '{approved} of {total} days approved',
  'hol.partialApproveAction': 'Approve {count} days',
  'hol.partialApproved': 'Partially approved',
  'hol.partialRejected': 'Not approved (partial)',
  'hol.partialRemainder': 'Remaining from partial approval',
  'hol.partialLabel': 'Partial',

  // ── Absences ──
  'abs.title': 'Absences',
  'abs.subtitle': 'Track sick days and emergency leave',
  'abs.record': 'Record Absence',
  'abs.summary': 'Employee Summary',
  'abs.allRecords': 'All Records',
  'abs.all': 'All',
  'abs.sickDays': 'Sick Days',
  'abs.emergencyLeave': 'Emergency Leave',
  'abs.sick': 'Sick',
  'abs.emergency': 'Emergency',
  'abs.noRecords': 'No records found',
  'abs.employee': 'Employee',
  'abs.selectEmployee': 'Select employee',
  'abs.type': 'Type',
  'abs.sickDay': 'Sick Day',
  'abs.dates': 'Dates',
  'abs.pickDates': 'Pick dates',
  'abs.reason': 'Reason',
  'abs.reasonPlaceholder': 'e.g. Flu, family emergency...',
  'abs.notes': 'Notes (optional)',
  'abs.notesPlaceholder': 'Additional details...',
  'abs.recordBtn': 'Record Absence',
  'abs.businessDays': '{count} business days',
  'abs.dialogTitle': 'Record Absence',
  'abs.dialogDesc': 'Log a sick day or emergency leave',
  'abs.totalLabel': 'Total',

  // ── Reports ──
  'rep.title': 'Reports',
  'rep.weekOf': 'Week of {label}',
  'rep.excel': 'Excel',
  'rep.pdfPrint': 'PDF / Print',
  'rep.thisWeek': 'This week',
  'rep.allStores': 'All Stores',
  'rep.warnings': 'Warnings',
  'rep.hoursVsContract': 'Hours vs Contract',
  'rep.storeCoverage': 'Store Coverage',
  'rep.holidayBalance': 'Holiday Balance',
  'rep.year': 'Year:',
  'rep.loading': 'Loading…',
  'rep.employeeOverContract': 'Employee over contract',
  'rep.employeesOverContract': 'Employees over contract',
  'rep.totalOvertimeRisk': 'Total overtime risk',
  'rep.employeeHours': 'Employee Hours',
  'rep.colEmployee': 'Employee',
  'rep.colStore': 'Store',
  'rep.colContract': 'Contract',
  'rep.colScheduled': 'Scheduled',
  'rep.colExpected': 'Expected',
  'rep.colDifference': 'Difference',
  'rep.noEmployees': 'No employees to show.',
  'rep.noStores': 'No stores to show.',
  'rep.understaffedDay': '{count} understaffed day this week across all stores.',
  'rep.understaffedDays': '{count} understaffed days this week across all stores.',
  'rep.needMore': 'Need {count} more',
  'rep.extra': '+{count} extra',
  'rep.ok': 'OK',
  'rep.holidayBalances': 'Holiday Balances',
  'rep.colAllowance': 'Allowance',
  'rep.colApproved': 'Approved',
  'rep.colPending': 'Pending',
  'rep.colRemaining': 'Remaining',
  'rep.colUsage': 'Usage',
  'rep.lowOrOverusedSingular': 'Employee with low or overused holiday balance',
  'rep.lowOrOverused': 'Employees with low or overused holiday balance',
  'rep.allClear': 'All clear',
  'rep.noIssues': 'No scheduling issues found this week.',
  'rep.issueSingular': 'Issue to review',
  'rep.issuesToReview': 'Issues to review',
  'rep.urgent': 'urgent',
  'rep.holidayConflict': 'Holiday Conflict',
  'rep.absenceConflict': 'Absence Conflict',
  'rep.overHours': 'Over Hours',
  'rep.unavailable': 'Unavailable',
  'rep.understaffed': 'Understaffed',
  'rep.fix': 'Fix',
  'rep.tabOverview': 'Overview',
  'rep.tabHoursPayroll': 'Hours & Payroll',
  'rep.tabCoverage': 'Coverage',
  'rep.tabLeave': 'Leave',
  'rep.tabCompliance': 'Compliance',
  'rep.overviewScheduledH': 'Scheduled Hours',
  'rep.overviewOvertimeH': 'Overtime Hours',
  'rep.overviewCoverageGaps': 'Coverage Gaps',
  'rep.overviewNearLimit': 'Near Limit',
  'rep.overviewExceeding': 'Exceeding Contract',
  'rep.overviewRiskLevel': 'Risk Level',
  'rep.overviewNoIssues': 'No active issues this week.',
  'rep.viewCompliance': 'View Compliance Details',
  'rep.viewCoverage': 'View Coverage Gaps',
  'rep.exportSummary': 'Export Summary',
  'rep.exportExcel': 'Export Excel',
  'rep.exportPDF': 'Export PDF',
  'rep.exportedExcel': 'Exported to Excel',
  'rep.exportedExcelDesc': 'Check your Downloads folder.',
  'rep.exportedCSV': 'Exported to CSV',
  'rep.weekly': 'Weekly',
  'rep.monthly': 'Monthly',
  'rep.totalScheduledH': 'Total Scheduled Hours',
  'rep.chartTitle': 'Scheduled vs Expected Hours by Employee',
  'rep.chartOnTrack': 'On Track',
  'rep.chartOverContract': 'Over Contract',
  'rep.chartUnderhours': 'Underhours',
  'rep.chartExpected': 'Expected',
  'rep.issuesToReviewTitle': 'Issues to Review',
  'rep.allClearPeriod': 'All Clear — no active issues this period.',
  'rep.employeesOverContractLabel': 'Employees Over Contract',
  'rep.totalOvertimeRiskLabel': 'Total Overtime Risk',
  'rep.employeeHoursTitle': 'Employee Hours',
  'rep.colOvertime': 'Overtime',
  'rep.colUnderhours': 'Underhours',
  'rep.colStatus': 'Status',
  'rep.payrollBreakdown': 'Payroll Breakdown — {period}',
  'rep.colScheduledH': 'Scheduled (h)',
  'rep.colPaidHolidayH': 'Paid Holiday (h)',
  'rep.colPaidSickH': 'Paid Sick (h)',
  'rep.colCountedHours': 'Counted Hours',
  'rep.colSickDays': 'Sick Days',
  'rep.totals': 'Totals',
  'rep.totalScheduled': 'Total Scheduled',
  'rep.totalActual': 'Total Actual',
  'rep.lowOrOverusedLabel': 'Employees with low or overused holiday balance',
  'rep.holidayAllowanceTitle': 'Holiday Allowance Usage by Employee',
  'rep.sickDaySummary': 'Sick Day Summary',
  'rep.colSickDaysUsed': 'Sick Days Used',
  'rep.colAnnualAllowance': 'Annual Allowance',
  'rep.colInstances': 'Instances',
  'rep.colPaid': 'Paid',
  'rep.colUnpaid': 'Unpaid',
  'rep.totalEmployees': 'Total Employees',
  'rep.compliant': 'Compliant',
  'rep.compWarnings': 'Warnings',
  'rep.actionRequired': 'Action Required',
  'rep.complianceDisclaimer': 'Contract hours may vary slightly due to store opening hours and minimum shift lengths. Small differences (±2h) are normal.',
  'rep.hExp': '{h}h exp',
  'rep.hCounted': '{h}h counted',
  'rep.hoursBreakdown': 'Hours Breakdown',
  'rep.scheduledWorked': 'Scheduled (worked)',
  'rep.paidHoliday': 'Paid Holiday',
  'rep.paidSick': 'Paid Sick',
  'rep.unpaidLeave': 'Unpaid Leave',
  'rep.complianceDetail': 'Compliance Detail',
  'rep.contract': 'Contract',
  'rep.expected': 'Expected',
  'rep.counted': 'Counted',
  'rep.variance': 'Variance',
  'rep.maxStreak': 'Max Streak',
  'rep.leaveBalanceYear': 'Leave Balance (Year)',
  'rep.holiday': 'Holiday',
  'rep.sick': 'Sick',
  'rep.remainingOf': '{remaining} remaining of {total}d',
  'rep.riskLog': 'Risk Log',
  'rep.highRisk': 'High Risk',
  'rep.mediumRisk': 'Medium Risk',
  'rep.lowRisk': 'Low Risk',
  'rep.colRiskType': 'Risk Type',
  'rep.colDateWeek': 'Date/Week',
  'rep.colSeverity': 'Severity',
  'rep.colDescription': 'Description',
  'rep.filterAll': 'All',
  'rep.filterHigh': 'High',
  'rep.filterMedium': 'Medium',
  'rep.filterLow': 'Low',
  'rep.statusCompliant': 'Compliant',
  'rep.statusWarning': 'Warning',
  'rep.statusActionRequired': 'Action Required',
  'rep.statusOnTrack': 'On Track',
  'rep.statusAtRisk': 'At Risk',
  'rep.riskHigh': 'High',
  'rep.riskMedium': 'Medium',
  'rep.riskLow': 'Low',
  'rep.gap': 'GAP',
  'rep.tooltipScheduled': 'Scheduled: {h}h',
  'rep.tooltipExpected': 'Expected: {h}h',
  'rep.tooltipVariance': 'Variance: {h}h',
  'rep.tooltipStatus': 'Status: {status}',

  // ── Planned vs Actual ──
  'pva.title': 'Scheduled vs Actual Hours',
  'pva.totalScheduled': 'Total Scheduled',
  'pva.totalPlanned': 'Total Planned',
  'pva.totalActual': 'Total Actual',
  'pva.totalVariance': 'Total Variance',
  'pva.confirmRate': '{confirmed}/{total} shifts confirmed',
  'pva.employeeBreakdown': 'Employee Breakdown',
  'pva.scheduled': 'Scheduled',
  'pva.planned': 'Planned',
  'pva.actual': 'Actual',
  'pva.variance': 'Variance',
  'pva.breakVar': 'Break ±',
  'pva.confirmed': 'Confirmed',
  'pva.lateArrivals': 'Late',
  'pva.earlyLeaves': 'Early Out',
  'pva.totals': 'Totals',
  'pva.noConfirmedShifts': 'No confirmed shifts yet — confirm shifts to see actual vs scheduled data',
  'pva.lateArrivalsCount': '{count} late arrival(s)',
  'pva.earlyLeavesCount': '{count} early leave(s)',
  'pva.totalOvertimeInsight': '{hours}h overtime worked',
  'pva.totalUnderhoursInsight': '{hours}h under scheduled',
  'pva.breakOvertime': '{mins}m extra break taken',
  'pva.breakScheduledVsActual': 'Break: Scheduled / Actual',
  'review.actualBreakStart': 'Break Start',
  'review.actualBreakEnd': 'Break End',

  // ── Not Found ──
  'notfound.title': '404',
  'notfound.message': 'Oops! Page not found',
  'notfound.link': 'Return to Home',

  // ── Warning Explanations ──
  'warn.hoursOverTitle': '{hours} hours over contract',
  'warn.hoursOverDesc': 'This employee is scheduled beyond their agreed weekly hours.',
  'warn.hoursOverOpt1': 'Remove a shift',
  'warn.hoursOverOpt2': 'Reassign to another employee',
  'warn.hoursOverOpt3': 'Increase contract hours',
  'warn.unfillableTitle': '{hours} hours cannot be scheduled this week',
  'warn.unfillableWhy': 'Why:',
  'warn.unfillableR1': 'All stores are fully staffed or closed',
  'warn.unfillableR2': "Remaining availability doesn't match open shifts",
  'warn.unfillableR3': 'Minimum shift length prevents partial assignments',
  'warn.unfillableOpt1': 'Reduce contract hours for this week',
  'warn.unfillableOpt2': 'Allow shorter shifts',
  'warn.unfillableOpt3': 'Increase store staffing needs',
  'warn.nearContractTitle': '{hours} hours below contract',
  'warn.nearContractDesc': 'This happens because all remaining shifts are full-length and the system avoids creating short or unrealistic shifts.',
  'warn.nearContractOpt1': 'Accept under-contract week',
  'warn.nearContractOpt2': 'Allow shorter shifts',
  'warn.nearContractOpt3': 'Adjust contract hours',
  'warn.underTitle': '{hours} hours below contract',
  'warn.underDesc': 'This employee has fewer hours scheduled than their contract requires. More shifts can still be added.',
  'warn.underOpt1': 'Add more shifts manually',
  'warn.underOpt2': 'Run auto-generate to optimise',
  'warn.underOpt3': 'Adjust contract hours',
  'warn.options': 'Options:',
  'warn.holidayDesc': 'This employee is on approved holiday for this date and cannot be scheduled.',
  'warn.holidayOpt1': 'Remove the shift',
  'warn.holidayOpt2': 'Assign another employee',
  'warn.absenceDesc': 'This employee is on approved leave for this date and should not be scheduled.',
  'warn.absenceOpt1': 'Remove the shift',
  'warn.absenceOpt2': 'Assign another employee',
  'warn.unavailableDesc': "This shift is outside the employee's available working days.",
  'warn.unavailableOpt1': 'Change shift date',
  'warn.unavailableOpt2': 'Assign another employee',
  'warn.unavailableOpt3': 'Update availability',
  'warn.understaffedDesc': 'This store does not have enough staff scheduled for this day.',
  'warn.understaffedOpt1': 'Add another employee',
  'warn.understaffedOpt2': 'Adjust staffing requirements',
  'warn.understaffedOpt3': 'Review employee availability',
  'warn.weeklyHours': 'Weekly Hours Summary',
  'warn.maxDaysTitle': 'Maximum working days reached',
  'warn.maxDaysDesc': 'This employee has reached their maximum of {days} working days this week. Additional shifts cannot be assigned even if contract hours remain.',
  'warn.maxDaysOpt1': 'Increase the max working days setting',
  'warn.maxDaysOpt2': 'Accept under-contract week',
  'warn.maxDaysOpt3': 'Adjust contract hours',
  'emp.maxWorkingDays': 'Max Working Days/Week',

  // ── Onboarding ──
  'onb.title': 'Create your organization',
  'onb.subtitle': 'Give your company or team a name to get started.',
  'onb.label': 'Organization name',
  'onb.placeholder': 'My Company',
  'onb.creating': 'Creating...',
  'onb.create': 'Create Organization',
  'onb.signOut': 'Sign out',
  'onb.invalidName': 'Invalid name',
  'onb.nameLength': 'Organization name must be 1-100 characters.',

  // ── Onboarding Setup ──
  'setup.title': "Let's Set Up Your Business",
  'setup.subtitle': 'Add your first store and employees to generate your first balanced schedule.',
  'setup.progress': 'Step {count} of {total} completed',
  'setup.step1': 'Add Your First Store',
  'setup.step2': 'Add Your First Employee',
  'setup.step3': 'Generate Your First Schedule',
  'setup.step3Hint': 'Requires at least 1 store and 1 employee',
  'setup.addAnother': 'Add Another',
  'setup.storeAdded': 'Store added!',
  'setup.employeeAdded': 'Employee added!',
  'setup.storeCount1': 'store added',
  'setup.storeCountN': 'stores added',
  'setup.empCount1': 'employee added',
  'setup.empCountN': 'employees added',
  'setup.generate': 'Generate First Schedule',
  'setup.generating': 'Generating...',
  'setup.success': 'Your first balanced schedule is ready.',
  'setup.genError': 'Schedule generation failed',
  'setup.noShiftsError': 'No shifts could be generated. Check staffing requirements and employee availability.',
  'setup.skip': 'Skip for now',
  'setup.emptySchedule': 'You need at least 1 store and 1 employee to build a schedule.',

  'settings.title': 'Settings',
  'settings.subtitle': 'Company preferences and configuration',
  'settings.balancedDesc': 'Maximises coverage while minimising overtime. Recommended for most teams.',

  // ── Operating Hours ──
  'hours.label': 'Operating Hours',
  'hours.quickSet': 'Quick Set',
  'hours.to': 'to',
  'hours.lunchBreak': 'Lunch break',
  'hours.applyWeekdays': 'Apply Mon–Fri',
  'hours.applyAll': 'Apply All Days',
  'hours.open': 'Open',
  'hours.closed': 'Closed',

  // ── AI ──
  'ai.getSuggestions': 'Get AI Suggestions',
  'ai.autoFix': 'Auto-fix',
  'ai.fixSchedule': 'Fix with AI',
  'ai.fixScheduleAI': 'Fix schedule with AI',
  'sched.issuesThisWeek': 'Issues this week',
  'ai.analyzing': 'Analyzing...',
  'ai.suggestions': 'AI Suggestions',
  'ai.fixesFound': '{count} fixes found',
  'ai.addresses': 'Addresses:',
  'ai.apply': 'Apply',
  'ai.preview': 'Preview',
  'ai.hidePreview': 'Hide',
  'ai.before': 'Before',
  'ai.after': 'After',
  'ai.keepsCoverage': 'Maintains coverage',
  'ai.withinContract': 'Within contract',
  'ai.shiftRemoved': 'Shift removed',
  'ai.noShift': 'No shift',
  'ai.refresh': 'Refresh',
  'ai.swap': 'Swap',
  'ai.reassign': 'Reassign',
  'ai.addShift': 'Add Shift',
  'ai.remove': 'Remove',
  'ai.adjust': 'Adjust',
  'ai.applied': 'Fix applied',
  'ai.changes': 'Changes',
  'ai.failedApply': 'Failed to apply',
  'ai.failedFetch': 'Failed to get suggestions',
  'ai.aiError': 'AI Error',
  'ai.importTitle': 'AI Data Import',
  'ai.stageInput': 'Paste a description of your business. Stores will be parsed first, then employees in batches.',
  'ai.stageParsingStores': 'Analyzing your text for store information...',
  'ai.stagePreviewStores': 'Review the stores below. Once confirmed, they will be saved and employee parsing will begin.',
  'ai.stageSavingStores': 'Saving stores to database...',
  'ai.stageCounting': 'Detecting employees in your text...',
  'ai.stageParsingEmps': 'Parsing employees in batches for reliability...',
  'ai.stageValidating': 'Validating parsed employee data...',
  'ai.stagePreviewEmps': 'Review employees below. Click "Confirm Import" to save.',
  'ai.stageImporting': 'Importing employees to database...',
  'ai.stageComplete': 'All data imported successfully!',
  'ai.parseStores': 'Parse Stores',
  'ai.startOver': 'Start Over',
  'ai.confirmStores': 'Confirm Stores & Parse Employees',
  'ai.importAnyway': 'Import Anyway',
  'ai.confirmImport': 'Confirm Import',
  'ai.done': 'Done',
  'ai.tip': 'Tip: Include store names, hours, staff per day, employee names, contract hours, store assignments, and availability days. Supports 5–500+ employees.',
  'ai.storesSaved': 'stores saved',
  'ai.employees': 'employees',
  'ai.totalWeekly': 'total weekly',
  'ai.importedSuccess': '{stores} stores and {employees} employees imported successfully.',

  // ── Employee Hours Summary ──
  'summary.ofContract': 'of contract',
  'summary.over': '+{hours}h over',
  'summary.cantFill': "{hours}h can't be filled",
  'summary.belowMin': '{hours}h below min shift',
  'summary.remaining': '{hours}h remaining',




  // ── Days ──
  'day.sun': 'Sunday', 'day.mon': 'Monday', 'day.tue': 'Tuesday', 'day.wed': 'Wednesday',
  'day.thu': 'Thursday', 'day.fri': 'Friday', 'day.sat': 'Saturday',
  'day.sunShort': 'Sun', 'day.monShort': 'Mon', 'day.tueShort': 'Tue', 'day.wedShort': 'Wed',
  'day.thuShort': 'Thu', 'day.friShort': 'Fri', 'day.satShort': 'Sat',

  // ── Settings Tabs ──
  'settings.tabOrg': 'Organization',
  'settings.tabTeam': 'Team',
  'settings.tabRules': 'Scheduling Rules',
  'settings.tabDanger': 'Danger Zone',
  'settings.orgName': 'Organization Name',
  'settings.orgNameDesc': 'The name of your organization as shown to team members.',
  'settings.save': 'Save',
  'settings.saved': 'Settings saved',
  'settings.teamTitle': 'Team Members',
  'settings.teamDesc': 'Manage your team and send invitations.',
  'settings.invite': 'Invite Member',
  'settings.colName': 'Name',
  'settings.colEmail': 'Email',
  'settings.colRole': 'Role',
  'settings.colStatus': 'Status',
  'settings.colActions': 'Actions',
  'settings.roleOwner': 'Owner',
  'settings.roleManager': 'Manager',
  'settings.active': 'Active',
  'settings.pending': 'Pending',
  'settings.remove': 'Remove',
  'settings.resend': 'Resend',
  'settings.removeConfirm': 'Remove this member from the organization?',
  'settings.removed': 'Member removed',
  'settings.inviteSent': 'Invitation sent',
  'settings.inviteEmail': 'Email address',
  'settings.inviteRole': 'Role',
  'settings.inviteBtn': 'Send Invitation',
  'settings.inviteTitle': 'Invite Team Member',
  'settings.inviteDesc': 'Send an invitation link to join your organization.',
  'settings.rulesTitle': 'Scheduling Rules',
  'settings.rulesDesc': 'Configure how schedules are generated.',
  'settings.dangerTitle': 'Danger Zone',
  'settings.dangerDesc': 'Irreversible actions. Proceed with caution.',
  'settings.deleteOrg': 'Delete Organization',
  'settings.deleteOrgDesc': 'Permanently delete this organization and all associated data.',
  'settings.deleteOrgConfirm': 'Are you sure? This action cannot be undone.',
  'settings.clearSchedules': 'Clear All Schedules',
  'settings.clearSchedulesDesc': 'Remove all shift data. Employee and store data will be kept.',
  'settings.clearConfirm': 'Are you sure? All schedule data will be deleted.',
  'settings.deleted': 'Organization deleted',
  'settings.cleared': 'All schedules cleared',
  'settings.cancel': 'Cancel',
  'settings.you': '(you)',
  'settings.cannotRemoveSelf': 'You cannot remove yourself',

  // ── Common ──
  'common.loading': 'Loading...',
  'common.cancel': 'Cancel',

  // ── Auth extras ──
  'auth.forgotPassword': 'Forgot password?',
  'auth.or': 'or',
  'auth.googleSignIn': 'Continue with Google',
  'auth.appleSignIn': 'Continue with Apple',

  // ── Forgot Password ──
  'forgot.title': 'Forgot Password',
  'forgot.subtitle': 'Enter your email to receive a reset link.',
  'forgot.button': 'Send Reset Link',
  'forgot.sent': 'If an account exists, a reset link has been sent. Check your inbox.',
  'forgot.backToLogin': 'Back to login',

  // ── Reset Password ──
  'reset.title': 'Reset Password',
  'reset.subtitle': 'Enter your new password below.',
  'reset.newPassword': 'New Password',
  'reset.confirmPassword': 'Confirm Password',
  'reset.button': 'Update Password',
  'reset.passwordMin': 'Password must be at least 8 characters',
  'reset.mismatch': 'Passwords do not match',
  'reset.failed': 'Failed to update password. Please try again.',
  'reset.success': 'Password updated successfully. Redirecting...',
  'reset.invalidLink': 'This reset link is invalid or has expired.',
  'reset.backToLogin': 'Back to login',

  // ── Account Settings ──
  'settings.tabAccount': 'Account',
  'settings.tabBilling': 'Billing',
  'billing.currentPlan': 'Current Plan',
  'billing.currentPlanDesc': 'Your subscription details',
  'billing.refresh': 'Refresh',
  'billing.noSubscription': 'No active subscription',
  'billing.paymentMethod': 'Payment Method',
  'billing.invoices': 'Invoices',
  'billing.billingDetails': 'Billing Details',
  'billing.editDetails': 'Edit Details',
  'billing.noDetails': 'No billing details available',
  'billing.activating': 'Activating your subscription…',
  'billing.activatingDesc': 'This usually takes a few seconds.',
  'billing.subEnded': 'Your subscription has ended',
  'billing.choosePlan': 'Choose your plan to get started',
  'billing.pastDue': 'Payment past due',
  'billing.canceled': 'Canceled',
  'billing.expired': 'Expired',
  'billing.monthly': 'Monthly',
  'billing.annual': 'Annual',
  'billing.save17': 'Save ~17%',
  'billing.trialDesc': '30-day free trial included. Cancel anytime.',
  'billing.redirecting': 'Redirecting…',
  'billing.reactivate': 'Reactivate subscription',
  'billing.startTrial': 'Start free trial',
  'billing.checkStatus': 'Already completed checkout? Check status',
  'billing.accountSettings': 'Account settings',
  'billing.signOut': 'Sign out',
  'billing.title': 'Billing & Payments',
  'billing.subtitle': 'Manage your subscription, payment method, and invoices',
  'billing.freeTrial': 'Free Trial',
  'billing.trialEnds': 'Trial ends',
  'billing.nextBilling': 'Next billing date',
  'billing.billingCycle': 'Billing cycle',
  'billing.cancelNotice': 'Your subscription will end on {date}. You can resume it from the billing portal.',
  'billing.manageBilling': 'Manage billing',
  'billing.noSub': 'No active subscription found.',
  'billing.update': 'Update',
  'billing.noPayment': 'No payment method on file',
  'billing.addCard': 'Add card',
  'billing.noInvoicesYet': 'No invoices yet',
  'billing.detailsDesc': 'Update your company name, billing email, VAT number, and address via the billing portal.',
  'billing.companyName': 'Company name',
  'billing.billingEmail': 'Billing email',
  'billing.vatNumber': 'VAT number',
  'billing.address': 'Address',
  'billing.editBilling': 'Edit billing details',
  'billing.noDetailsFound': 'No billing details found',
  'billing.setUp': 'Set up',
  'billing.expires': 'Expires',
  'billing.status_active': 'Active',
  'billing.status_trialing': 'Trial',
  'billing.status_past_due': 'Past due',
  'billing.status_canceled': 'Canceled',
  'billing.status_incomplete': 'Incomplete',
  'billing.status_unpaid': 'Unpaid',
  'billing.interval_month': 'month',
  'billing.interval_year': 'year',
  'billing.cancelsAtPeriodEnd': 'Cancels at period end',
  'billing.subActivated': 'Subscription activated!',
  'billing.syncSlow': 'Subscription sync taking longer than expected. Please refresh.',
  'billing.subFound': 'Subscription found!',
  'billing.noSubYet': 'No active subscription found yet.',
  'account.title': 'Account Information',
  'account.subtitle': 'Manage your email and password.',
  'account.changeEmail': 'Change Email',
  'account.changeEmailDesc': 'A verification email will be sent to your new address.',
  'account.newEmail': 'New Email',
  'account.confirmEmail': 'Confirm Email',
  'account.currentPassword': 'Current Password',
  'account.emailMismatch': 'Email addresses do not match',
  'account.emailVerificationSent': 'A verification link has been sent to your new email. Your email will update after confirmation.',
  'account.changePassword': 'Change Password',
  'account.changePasswordDesc': 'Update your password. You must verify your current password first.',
  'account.newPassword': 'New Password',
  'account.confirmNewPassword': 'Confirm New Password',
  'account.savePassword': 'Update Password',
  'account.passwordMin': 'Password must be at least 8 characters',
  'account.mismatch': 'Passwords do not match',
  'account.wrongPassword': 'Current password is incorrect.',
  'account.updateFailed': 'Failed to update. Please try again.',
  'account.passwordChanged': 'Password updated successfully',

  // ── Delete Account ──
  'account.deleteTitle': 'Delete Account',
  'account.deleteDesc': 'Permanently delete your account and all associated data.',
  'account.deleteButton': 'Delete My Account',
  'account.deleteConfirmTitle': 'Are you absolutely sure?',
  'account.deleteConfirmDesc': 'This will permanently delete your account, organization, and all data. This cannot be undone.',
  'account.deletePasswordLabel': 'Enter your password to confirm',
  'account.deleteConfirmButton': 'Delete Everything',
  'account.deleteCancelButton': 'Cancel',
  'account.deleteSuccess': 'Account deleted.',

  // ── Excel Import ──
  'import.title': 'Import Business Data',
  'import.subtitle': 'Upload an Excel or CSV file with your stores and employees.',
  'import.downloadTemplate': 'Download Excel Template',
  'import.dropzone': 'Drag & drop your file here, or click to browse',
  'import.dropzoneHint': 'Accepts .xlsx or .csv · Max 5MB',
  'import.parsing': 'Parsing your file...',
  'import.storesDetected': 'Stores detected',
  'import.employeesDetected': 'Employees detected',
  'import.hours': 'Hours',
  'import.staff': 'Staff (Wd/We)',
  'import.days': 'Availability',
  'import.totalWeekly': 'total weekly',
  'import.importing': 'Importing data...',
  'import.success': 'Import successful!',
  'import.successMessage': 'Import successful. Your data is ready.',
  'import.failed': 'Import failed',
  'import.confirmImport': 'Confirm Import',
  'import.cancel': 'Cancel',
  'import.tryAgain': 'Try Again',
  'import.done': 'Done',
  'import.uploadData': 'Upload Business Data',
  'import.uploadHint': 'Or import from an Excel/CSV file',

  // ── Compliance Reports ──
  'comp.complianceSummary': 'Compliance',
  'comp.payrollExport': 'Payroll Export',
  'comp.legalRisk': 'Legal Risk',
  'comp.totalEmployees': 'Total Employees',
  'comp.compliant': 'Compliant',
  'comp.warnings': 'Warnings',
  'comp.critical': 'Action Required',
  'comp.employee': 'Employee',
  'comp.store': 'Store',
  'comp.contractH': 'Contract (h/w)',
  'comp.scheduledH': 'Scheduled (h)',
  'comp.regularH': 'Regular Hours',
  'comp.overtimeH': 'Overtime',
  'comp.underhours': 'Underhours',
  'comp.holidays': 'Holidays',
  'comp.sickDays': 'Sick Days',
  'comp.maxStreak': 'Max Streak',
  'comp.status': 'Status',
  'comp.totalH': 'Total Hours',
  'comp.totals': 'Totals',
  'comp.allEmployees': 'All Employees',
  'comp.payrollDesc': 'Structured monthly hours breakdown for payroll processing. No wage calculations.',
  'comp.exportedCSV': 'Exported to CSV',
  'comp.exportedExcel': 'Exported to Excel',
  'comp.exportedExcelDesc': 'Check your Downloads folder to open the file',
  'comp.exportedCSVDesc': 'Check your Downloads folder to open the file',
  'excel.employee': 'Employee',
  'excel.store': 'Store',
  'excel.contractH': 'Contract (h)',
  'excel.scheduledH': 'Scheduled (h)',
  'excel.differenceH': 'Difference (h)',
  'excel.status': 'Status',
  'excel.hoursVsContract': 'Hours vs Contract',
  'excel.metric': 'Metric',
  'excel.required': 'Required',
  'excel.scheduled': 'Scheduled',
  'excel.difference': 'Difference',
  'excel.allowance': 'Allowance',
  'excel.approved': 'Approved',
  'excel.pending': 'Pending',
  'excel.remaining': 'Remaining',
  'excel.leaveBalance': 'Leave Balance',
  'comp.highRisk': 'High Risk',
  'comp.mediumRisk': 'Medium Risk',
  'comp.lowRisk': 'Low Risk',
  'comp.allSeverities': 'All Severities',
  'comp.exportRisk': 'Export Risk Report',
  'comp.riskType': 'Risk Type',
  'comp.dateWeek': 'Date / Week',
  'comp.severity': 'Severity',
  'comp.description': 'Description',
  'comp.noRisks': 'No risks detected',
  'comp.noRisksDesc': 'All scheduling complies with labour regulations this month.',
  'comp.riskType48h': 'Weekly hours > 48',
  'comp.riskTypeNoRest': 'No rest day in week',
  'comp.riskType6Consec': '6+ consecutive days',
  'comp.riskTypeContract': 'Contract hour violation',
  'comp.riskTypeMaxDays': 'Exceeds max days/week',
  'comp.riskTypeAvail': 'Scheduled outside availability',
  'comp.riskDesc48h': '{hours}h scheduled (max 48h)',
  'comp.riskDescNoRest': '{days} days worked with no rest day',
  'comp.riskDesc6Consec': '{days} days worked in week',
  'comp.riskDescContract': '{hours}h vs {contract}h contract (+{pct}%)',
  'comp.riskDescMaxDays': '{days} days worked (max {max})',
  'comp.riskDescAvail': 'Scheduled on unavailable day',
  'comp.severityHigh': 'High',
  'comp.severityMedium': 'Medium',
  'comp.severityLow': 'Low',

  // ── Landing Page (restructured) ──
  'lp.hero.title': 'AI Staff Scheduling for Teams That Work Shifts',
  'lp.hero.subtitle': 'Create rotas in seconds, track contract hours, manage leave, and stay compliant — all in one place. Built for any business with shift workers.',
  'lp.hero.demo': 'See How It Works',
  'lp.hero.trust': 'Trusted by retail stores, restaurants, clinics, gyms, franchises and service teams across Europe.',
  'lp.hero.langLine': 'Available in 6 languages — automatically adapts to your team',
  'lp.hero.noCard': 'No card required',
  'lp.hero.trustBadge1': 'GDPR Compliant',
  'lp.hero.trustBadge2': 'No per-employee fees',
  'lp.hero.trustBadge3': 'Setup in 15 minutes',

  'lp.prob.title': 'Shift Management Shouldn\'t Be This Complicated',
  'lp.prob1': 'Manual spreadsheets cause errors',
  'lp.prob1.desc': 'One wrong formula costs hours to fix and risks paying your team incorrectly.',
  'lp.prob2': 'Overtime goes unnoticed',
  'lp.prob2.desc': 'Contract breaches pile up silently until they become a legal and payroll problem.',
  'lp.prob3': 'Leave tracking becomes messy',
  'lp.prob3.desc': 'Holiday clashes and last-minute absences create cover chaos for any team.',
  'lp.prob4': 'Compliance risks increase',
  'lp.prob4.desc': 'Without automated checks you are one audit away from a costly fine.',
  'lp.prob5': 'Managers waste hours every week',
  'lp.prob5.desc': 'Building rotas manually is repetitive work that should take seconds not hours.',
  'lp.prob.closing': 'Small scheduling mistakes cost real money.',

  'lp.core.title': 'Everything You Need to Manage Shift Teams',
  'lp.core.subtitle': 'Operota\'s AI scheduling engine builds balanced rotas that respect contract hours, employee availability, and team coverage automatically. No spreadsheets. No guesswork.',
  'lp.core1.title': 'Smart Rota Builder',
  'lp.core1.desc': 'Auto-generate balanced schedules that respect contract hours, availability, and fair shift distribution across your entire team.',
  'lp.core2.title': 'Contract & Hours Control',
  'lp.core2.desc': 'Track expected vs worked hours weekly and monthly. Spot overtime before it becomes a payroll problem.',
  'lp.core3.title': 'Paid Leave & Sick Day Tracking',
  'lp.core3.desc': 'Manage holiday requests, sick days, and absence records with automatic paid leave calculations.',
  'lp.core4.title': 'Compliance Reporting Suite',
  'lp.core4.desc': 'Generate monthly compliance summaries showing hours worked vs contracted, leave balances, and scheduling violations — exportable to Excel in one click.',
  'lp.core5.title': 'Works in 6 Languages',
  'lp.core5.desc': 'Operota automatically detects your language and adapts the entire platform. English, Italian, Spanish, French, German and Portuguese — no setup needed.',
  'lp.core6.title': 'Multi-Store Support',
  'lp.core6.desc': 'Manage multiple locations from one account. Each store has its own schedule, staffing rules, and compliance reporting.',

  'lp.core.sectionTitle': 'Create Staff Rotas in Seconds',

  'lp.section5.title': 'Track Contract Hours and Compliance',
  'lp.section5.subtitle': 'Operota automatically monitors employee contract hours against scheduled shifts, flags overtime violations, and generates compliance reports — so you stay audit-ready without the manual work.',
  'lp.section5.card1.title': 'AI Shift Scheduling',
  'lp.section5.card1.desc': 'Our rota maker uses AI to generate balanced schedules that prevent overtime, respect availability, and ensure fair shift distribution across your team.',
  'lp.section5.card2.title': 'Compliance Reporting',
  'lp.section5.card2.desc': 'Generate monthly compliance summaries showing hours worked vs contracted, leave balances, and any scheduling violations — exportable to Excel in one click.',

  'lp.early.title': 'Be the first to share your story',
  'lp.early.subtitle': 'Join our early access programme.',

  'footer.solutions': 'Solutions',

  'lp.pricing.title': 'Simple, Professional Pricing',
  'lp.pricing.noFees': 'No hidden fees. No per-employee charges.',
  'lp.pricing.annualSave': 'Billed €990/year — 2 months free',
  'lp.pricing.annualBadge': 'Save €198/year',
  'lp.pricing.bestValue': 'Best Value',
  'lp.price1': 'Unlimited employees',
  'lp.price2': 'Multi-location support',
  'lp.price3': 'Full compliance reporting',
  'lp.price4': 'Leave & absence tracking',
  'lp.price5': '30-day free trial',
  'lp.price6': 'Works in 6 languages',
  'lp.price7': 'AI auto-scheduling',

  'lp.aud.title': 'Built for Any Business With Shift Workers',
  'lp.aud.subtitle': 'Whether you manage a single location or multiple sites, Operota keeps your team organised, compliant, and fairly scheduled.',
  'lp.aud1': 'Retail & Fashion',
  'lp.aud1.desc': 'Stores, boutiques and fashion chains',
  'lp.aud2': 'Cafes & Restaurants',
  'lp.aud2.desc': 'Front of house and kitchen teams',
  'lp.aud3': 'Health & Wellness',
  'lp.aud3.desc': 'Clinics, gyms and therapy practices',
  'lp.aud4': 'Gyms & Leisure',
  'lp.aud4.desc': 'Fitness centres and leisure facilities',
  'lp.aud5': 'Multi-Site Businesses',
  'lp.aud5.desc': 'Franchises and multi-location operators',
  'lp.aud6': 'Service & Trades',
  'lp.aud6.desc': 'Field teams and service businesses',

  'lp.testimonials.title': 'Teams That Run on Operota',
  'lp.testimonials.disclaimer': '* Testimonials are illustrative. Real customer stories coming soon.',
  'lp.testimonial1.quote': 'We used to spend 3 hours every Monday on the rota. Now it takes 30 seconds and the AI handles it.',
  'lp.testimonial1.name': 'Laura K.',
  'lp.testimonial1.role': 'Store Manager, Fashion Retail',
  'lp.testimonial2.quote': 'The compliance reports caught overtime issues we never would have spotted manually. Paid for itself in the first month.',
  'lp.testimonial2.name': 'Marco T.',
  'lp.testimonial2.role': 'Operations Manager, Restaurant Group',
  'lp.testimonial3.quote': 'Managing 4 locations and 40 staff used to be a nightmare. Operota made it simple.',
  'lp.testimonial3.name': 'Priya S.',
  'lp.testimonial3.role': 'Director, Gym & Wellness Chain',

  'lp.faq.payroll.q': 'Does it handle payroll?',
  'lp.faq.payroll.a': 'No. Operota provides structured monthly hour exports ready for your accountant or payroll system, but does not calculate wages, taxes, or deductions.',
  'lp.faq.contracts.q': 'Does it respect contract hours?',
  'lp.faq.contracts.a': 'Yes. The system automatically respects each employee\'s contracted weekly hours when generating rotas. It prevents overtime and flags violations.',
  'lp.faq.excel.q': 'Can I import from Excel?',
  'lp.faq.excel.a': 'Yes. You can import stores and employees from Excel or CSV files, or use our AI import feature to set up your business from a text description.',
  'lp.faq.security.q': 'Is my data secure?',
  'lp.faq.security.a': 'Yes. All data is encrypted, GDPR compliant, and never shared with third parties.',
  'lp.faq.stores.q': 'Can I manage multiple locations?',
  'lp.faq.stores.a': 'Absolutely. Operota supports unlimited locations within a single organisation. Manage schedules, staffing, and reports across all sites from one dashboard.',
  'lp.faq.businesses.q': 'What types of businesses use Operota?',
  'lp.faq.businesses.a': 'Any business with shift workers — retail stores, cafes and restaurants, gyms, clinics, cleaning companies, security firms, care homes, franchises, and more.',
  'lp.faq.setup.q': 'How long does setup take?',
  'lp.faq.setup.a': 'Most teams are up and running in under 15 minutes. Add your location, import your staff, set contract hours, and hit Auto-Generate.',
  'lp.faq.selfService.q': 'Can employees see their own schedules?',
  'lp.faq.selfService.a': 'Employee self-service is on our roadmap. Currently schedules can be exported to PDF or Excel and shared with your team directly.',
  'lp.faq.perEmployee.q': 'Is there a per-employee charge?',
  'lp.faq.perEmployee.a': 'No. Operota is a flat €99 per month regardless of how many employees or locations you have.',
  'lp.faq.afterTrial.q': 'What happens after the 30-day trial?',
  'lp.faq.afterTrial.a': 'You move to the monthly plan at €99/month. We remind you before your trial ends and you can cancel anytime.',
  'lp.faq.languages.q': 'What languages is it available in?',
  'lp.faq.languages.a': 'English, Italian, Spanish, French, German and Portuguese. It automatically detects your browser language — no setup needed.',
  'lp.faq.dataSecurity.q': 'Is my data secure?',
  'lp.faq.dataSecurity.a': 'Yes. All data is encrypted, GDPR compliant, and never shared with third parties.',

  // ── Leave Policy ──
  'settings.tabLeave': 'Leave Policy',
  'leave.globalPolicy': 'Global Leave Policy',
  'leave.globalPolicyDesc': 'Set default paid leave allowances for all employees.',
  'leave.paidHolidayDays': 'Annual Paid Holiday Days',
  'leave.paidSickDays': 'Annual Paid Sick Days',
  'leave.hoursPerDay': 'Working Hours Per Day',
  'leave.savePolicy': 'Save Policy',
  'leave.policySaved': 'Leave policy saved',
  'leave.bulkActions': 'Bulk Actions',
  'leave.bulkActionsDesc': 'Apply policy changes to employees or reset annual counters.',
  'leave.applyToAll': 'Apply To All Employees',
  'leave.confirmApply': 'Apply policy to all employees?',
  'leave.confirmApplyDesc': 'This will update holiday and sick day allowances for all employees who do not have a custom override. Employees with overrides will not be affected.',
  'leave.appliedToAll': 'Policy applied to all employees',
  'leave.resetCounters': 'Reset Annual Counters',
  'leave.confirmReset': 'Reset leave counters?',
  'leave.confirmResetDesc': 'Leave balances are calculated from recorded holiday requests and absence records. To start a new leave year, you can archive or delete previous year records.',
  'leave.resetInfo': 'Leave balances are computed from date-ranged records. Filter by year to see current balances.',
  'leave.overridePolicy': 'Override Default Leave Policy',
  'leave.paidHolH': 'Paid Hol. (h)',
  'leave.paidSickH': 'Paid Sick (h)',
  'leave.unpaidH': 'Unpaid Leave (h)',
  'leave.holAllowance': 'Holiday Allowance',
  'leave.sickAllowance': 'Sick Allowance',
  'leave.used': 'used',
  'leave.remainHol': 'Rem. Holiday',
  'leave.remainSick': 'Rem. Sick',
  'comp.contractCountedH': 'Counted Hours',
  'comp.contractCountedTooltip': 'Scheduled hours + paid holiday + paid sick. Used for contract compliance.',
  'comp.workingDays': 'working days',
  'comp.day': 'day',
  'comp.expectedH': 'Expected',
  'comp.expectedTooltip': 'Calculated from contract hours × working days in selected period. Uses exact day counting.',
  'comp.scheduledTooltip': 'Actual worked shift hours in the period.',
  'comp.underhoursVsContract': 'Underhours',
  'comp.underhoursTooltip': 'Counted hours − Expected hours. Negative means employee is short.',
  'comp.complianceWeekly': 'Weekly',
  'comp.complianceMonthly': 'Monthly',
  // Compliance print & detail labels
  'comp.complianceReport': 'Compliance Report',
  'comp.period': 'Period',
  'comp.print': 'Print',
  'comp.savePDF': 'Save as PDF',
  'comp.printOrSavePDF': 'Print / Save as PDF',
  'comp.printPDFHint': 'To save as PDF, select "Save as PDF" as the destination in the print dialog.',
  'comp.generatingPDF': 'Generating PDF...',
  'comp.backToReports': 'Back to Reports',
  'comp.totalOvertime': 'Total Overtime',
  'comp.totalUnderhours': 'Total Underhours',
  'comp.totalUnpaidLeave': 'Total Unpaid Leave',
  'comp.generatedOn': 'Generated on',
  'comp.generatedBy': 'Generated by',
  'comp.organization': 'Organization',
  'comp.reportPeriod': 'Report Period',
  'comp.hoursBreakdown': 'Hours Breakdown',
  'comp.compliance': 'Compliance',
  'comp.leaveBalanceYear': 'Leave Balance (Year)',
  'comp.leaveBalanceAnnual': 'Leave Balance (Annual)',
  'comp.scheduled': 'Scheduled (worked)',
  'comp.paidHoliday': 'Paid Holiday',
  'comp.paidSick': 'Paid Sick',
  'comp.unpaidLeave': 'Unpaid Leave',
  'comp.contract': 'Contract',
  'comp.expected': 'Expected',
  'comp.expectedPeriod': 'Expected for period',
  'comp.counted': 'Counted',
  'comp.variance': 'Variance',
  'comp.worked': 'Worked',
  'comp.holidayRemaining': 'Holiday Remaining',
  'comp.sickRemaining': 'Sick Remaining',
  'comp.days': 'days',
  'comp.notes': 'Notes',
  'comp.statusOk': 'On track',
  'comp.statusWarning': 'Minor variance',
  'comp.statusCritical': 'Action required',
  'comp.allClear': 'All employees on track',
  'comp.varianceExplainer': 'Contract hours may vary slightly due to store opening hours, employee availability, and minimum shift lengths. Small differences (±2h) are normal.',
  'comp.toleranceOk': 'Within tolerance',
  'comp.toleranceWarning': 'Review recommended',
  'comp.toleranceAction': 'Action required',
};

const fr: Record<string, string> = {
  // ── Landing ──
  'header.signIn': 'Se connecter',
  'header.getStarted': 'Commencer',
  'hero.badge': 'Conçu pour les équipes retail multi-sites',
  'hero.title1': 'Planification IA du personnel',
  'hero.title2': 'Qui équilibre vraiment vos magasins',
  'hero.subtitle': 'Conçu pour les équipes retail en croissance qui ont besoin de plannings conformes aux contrats, équilibrés par l\'IA, sans le chaos.',
  'hero.cta': 'Essai gratuit de 30 jours',
  'hero.signIn': 'Se connecter',
  'hero.trust1': 'Essai gratuit de 30 jours',
  'hero.trust2': 'Installation en quelques minutes',
  'hero.trust3': 'Sans engagement',
  'hero.euLine': 'Hébergé dans l\'UE. Conforme au RGPD. Conçu pour les contrats de travail européens.',
  'trust.title': 'Conçu pour les opérations retail modernes',
  'trust.eu': 'Hébergement UE',
  'trust.gdpr': 'Conforme RGPD',
  'trust.secure': 'Infrastructure cloud sécurisée',
  'trust.encrypted': 'Chiffré en transit et au repos',
  'trust.origin': 'Conçu et opéré depuis l\'Italie.',
  'screenshot.caption': 'Planning auto-équilibré généré en un clic.',
  'screenshot.sub': 'Respectez les contrats. Évitez les heures sup. Éliminez les trous de couverture.',
  'features.title': 'Tout ce qu\'il faut pour des plannings plus intelligents',
  'features.subtitle': 'Des outils axés sur les résultats pour les managers retail occupés.',
  'feature.scheduling.title': 'Planification intelligente',
  'feature.scheduling.desc': 'Des plannings générés par IA qui équilibrent automatiquement les heures et préviennent les lacunes.',
  'feature.employees.title': 'Gestion des employés',
  'feature.employees.desc': 'Suivez contrats, disponibilités et absences depuis un tableau de bord centralisé.',
  'feature.stores.title': 'Multi-établissements',
  'feature.stores.desc': 'Gérez tous vos sites depuis un centre de contrôle intelligent.',
  'feature.absences.title': 'Suivi des absences',
  'feature.absences.desc': 'Enregistrez les arrêts maladie et les congés. Sachez toujours qui est disponible.',
  'feature.holidays.title': 'Gestion des congés',
  'feature.holidays.desc': 'Gérez les jours fériés et les demandes de congés automatiquement.',
  'feature.reports.title': 'Rapports et exports',
  'feature.reports.desc': 'Générez des rapports et exportez tout en Excel en un clic.',
  'midCta.title': 'Arrêtez de créer des plannings manuellement.',
  'midCta.subtitle': 'Rejoignez les équipes retail qui passent à la gestion IA des plannings.',
  'midCta.button': 'Essai gratuit',
  'stats.shifts': 'Postes planifiés',
  'stats.teams': 'Équipes utilisatrices',
  'stats.uptime': 'Disponibilité',
  'stats.rating': 'Note utilisateurs',
  'how.title': 'Comment ça marche',
  'how.subtitle': 'Opérationnel en quelques minutes.',
  'how.step1.title': 'Ajoutez vos établissements',
  'how.step1.desc': "Configurez vos sites et les horaires d'ouverture.",
  'how.step2.title': 'Ajoutez vos employés',
  'how.step2.desc': 'Saisissez votre équipe avec leurs contrats et disponibilités.',
  'how.step3.title': 'Créez les plannings',
  'how.step3.desc': 'Construisez des plannings avec des suggestions intelligentes.',
  'pricing.title': 'Tarification simple et transparente',
  'pricing.subtitle': 'Essai gratuit de 30 jours. Sans carte bancaire.',
  'pricing.perMonth': '/mois',
  'pricing.afterTrial': 'Après votre essai gratuit de 30 jours',
  'pricing.cta': 'Essai gratuit',
  'pricing.included': 'Tout inclus :',
  'pricing.item1': 'Magasins illimités',
  'pricing.item2': 'Employés illimités',
  'pricing.item3': 'Équilibrage IA inclus',
  'pricing.item4': 'Suivi des absences et congés',
  'pricing.item5': 'Planification conforme aux contrats',
  'pricing.item6': 'Support prioritaire',
  'pricing.badge': 'Idéal pour les équipes en croissance',
  'pricing.reassurance': 'Annulez à tout moment. Pas de contrat longue durée.',
  'pricing.target': 'Pour les équipes retail en croissance et les entreprises multi-sites.',
  'pricing.monthly': 'Mensuel',
  'pricing.annual': 'Annuel',
  'pricing.comingSoon': 'Bientôt disponible',
  'cta.title': 'Prêt à simplifier votre planification ?',
  'cta.subtitle': 'Rejoignez les managers qui ont abandonné les tableurs.',
  'cta.button': "Commencer l'essai gratuit",
  'footer.features': 'Fonctionnalités',
  'footer.howItWorks': 'Comment ça marche',
  'footer.signIn': 'Se connecter',
  'footer.rights': '© {year} Operota. Tous droits réservés.',
  'footer.product': 'Produit',
  'footer.legal': 'Juridique',
  'footer.terms': 'Conditions Générales',
  'footer.privacy': 'Politique de Confidentialité',
  'footer.cookies': 'Politique de Cookies',
  'footer.dpa': 'DPA',
  'footer.refunds': 'Remboursements et Annulation',
  'pain.title': 'Vous gérez encore les plannings à l\'ancienne ?',
  'pain.manual.title': 'Planification manuelle',
  'pain.manual.desc': 'Rédiger des plannings sur Excel ou papier prend des heures chaque semaine.',
  'pain.gaps.title': 'Trous de couverture',
  'pain.gaps.desc': 'Les arrêts maladie de dernière minute laissent les magasins en sous-effectif.',
  'pain.overtime.title': 'Erreurs d\'heures sup',
  'pain.overtime.desc': 'Les heures contractuelles sont dépassées sans avertissement.',
  'pain.cta.text': 'Laissez Operota s\'en charger',
  'pain.cta.button': 'Commencer l\'essai gratuit',
  'compare.title': 'Pourquoi Operota plutôt qu\'Excel ?',
  'compare.excel.1': 'Planification manuelle',
  'compare.excel.2': 'Pas de suivi des contrats',
  'compare.excel.3': 'Pas de vue multi-magasins',
  'compare.excel.4': 'Pas de suivi des congés',
  'compare.operota.1': 'Équilibrage IA automatique',
  'compare.operota.2': 'Plannings respectant les contrats',
  'compare.operota.3': 'Tableau de bord multi-magasins',
  'compare.operota.4': 'Gestion des absences intégrée',
  'testimonials.title': 'Adopté par les équipes retail en croissance',
  'testimonial.1.quote': 'Operota nous fait gagner 6 heures par semaine. L\'équilibrage IA est révolutionnaire.',
  'testimonial.1.name': 'Marco Rossi',
  'testimonial.1.role': 'Responsable Retail – Chaîne de mode',
  'testimonial.2.quote': 'Plus aucun créneau manqué depuis le changement. Les trous de couverture appartiennent au passé.',
  'testimonial.2.name': 'Emma Clarke',
  'testimonial.2.role': 'Propriétaire multi-sites – Groupe boutique',
  'testimonial.3.quote': 'Enfin un logiciel qui respecte les heures contractuelles et garde nos magasins conformes.',
  'testimonial.3.name': 'Daniel Weber',
  'testimonial.3.role': 'Directeur des opérations – Chaîne retail',
  'faq.title': 'Questions Fréquentes',
  'faq.cancel.q': 'Puis-je annuler à tout moment ?',
  'faq.cancel.a': 'Oui, vous pouvez annuler votre abonnement à tout moment sans pénalité. Pas de contrats à long terme ni de frais cachés. Vos données restent accessibles jusqu\'à la fin de votre période de facturation.',
  'faq.trial.q': 'Y a-t-il un essai gratuit ?',
  'faq.trial.a': 'Oui. Chaque nouveau compte bénéficie d\'un essai gratuit de 30 jours avec accès à toutes les fonctionnalités, y compris la planification IA, la gestion multi-sites et les rapports. Sans carte bancaire.',
  'faq.stores.q': 'Supporte-t-il plusieurs magasins ?',
  'faq.stores.a': 'Absolument. Operota supporte un nombre illimité de sites au sein d\'une même organisation. Gérez plannings, effectifs et rapports pour tous vos magasins depuis un seul tableau de bord.',
  'faq.contracts.q': 'Respecte-t-il les contrats des employés ?',
  'faq.contracts.a': 'Oui. Operota respecte automatiquement les limites d\'heures contractuelles lors de la génération des plannings. L\'IA empêche les heures supplémentaires.',
  'faq.security.q': 'Mes données sont-elles sécurisées ?',
  'faq.security.a': 'Vos données sont chiffrées en transit et au repos, et hébergées entièrement dans l\'UE. Infrastructure cloud de niveau entreprise avec conformité stricte au RGPD.',
  'finalCta.title': 'Arrêtez de gérer les plannings dans des tableurs.',
  'finalCta.subtitle': 'Commencez votre essai gratuit de 30 jours et prenez le contrôle de votre planification.',
  'finalCta.button': 'Commencer l\'essai gratuit',
  'finalCta.signIn': 'Se connecter',

  // ── Navigation ──
  'nav.dashboard': 'Tableau de bord',
  'nav.stores': 'Établissements',
  'nav.employees': 'Employés',
  'nav.schedule': 'Planning',
  'nav.holidays': 'Congés',
  'nav.absences': 'Absences',
  'nav.reports': 'Rapports',
  'nav.signOut': 'Déconnexion',
  'nav.settings': 'Paramètres',

  // ── Auth ──
  'auth.back': 'Retour',
  'auth.title': 'Operota',
  'auth.subtitle': 'Gérez vos plannings facilement',
  'auth.signIn': 'Se connecter',
  'auth.signUp': "S'inscrire",
  'auth.email': 'E-mail',
  'auth.password': 'Mot de passe',
  'auth.signingIn': 'Connexion...',
  'auth.creatingAccount': 'Création du compte...',
  'auth.createAccount': 'Créer un compte',
  'auth.loading': 'Chargement...',
  'auth.checkEmail': 'Vérifiez votre e-mail pour confirmer votre compte.',
  'auth.invalidCredentials': 'E-mail ou mot de passe incorrect.',
  'auth.emailNotConfirmed': 'Veuillez vérifier votre adresse e-mail.',
  'auth.alreadyRegistered': 'Un compte avec cet e-mail existe déjà.',
  'auth.unexpectedError': 'Une erreur inattendue est survenue.',
  'auth.invalidEmail': 'Veuillez entrer une adresse e-mail valide',
  'auth.passwordMin': 'Le mot de passe doit contenir au moins 6 caractères',

  // ── Dashboard ──
  'dash.title': 'Tableau de bord',
  'dash.weekOf': 'Semaine du {start} - {end}',
  'dash.today': "Aujourd'hui",
  'dash.employeesWorking': 'employés en poste',
  'dash.workingToday': "travaillent aujourd'hui",
  'dash.onShiftToday': 'en poste aujourd\'hui',
  'dash.pendingRequests': 'demandes en attente',
  'dash.requestsToReview': 'demandes à traiter',
  'dash.staffingGaps': 'problèmes d\'effectifs',
  'dash.issuesToday': "problèmes aujourd'hui",
  'dash.noIssuesToday': "Aucun problème aujourd'hui",
  'dash.noIssues': 'Aucun problème',
  'dash.totalStores': 'Établissements',
  'dash.totalEmployees': 'Employés',
  'dash.shiftsThisWeek': 'Postes cette semaine',
  'dash.staffingAlerts': 'Alertes effectifs',
  'dash.thisWeek': 'Cette semaine',
  'dash.upcomingIssues': 'Cette semaine',
  'dash.actionRequired': 'Action requise',
  'dash.insights': 'Aperçus',
  'dash.understaffed': '{count} jour(s) en sous-effectif cette semaine',
  'dash.missingStaff': '{count} personnel manquant',
  'dash.moreIssues': '...et {count} de plus',
  'dash.viewSchedule': 'Voir le planning',
  'dash.pendingHolidayRequests': '{count} demande(s) de congé en attente',
  'dash.pendingHolidayDesc': 'Approuver ou refuser les demandes',
  'dash.reviewRequests': 'Examiner',
  'dash.nearOvertime': '{count} employé(s) proche(s) des heures sup.',
  'dash.employeeOverContract': 'employé au-delà du contrat',
  'dash.employeesOverContract': 'employés au-delà du contrat',
  'dash.approachingLimit': 'proche de la limite',
  'dash.more': 'de plus',
  'dash.allClear': 'Tout est en ordre — aucun problème cette semaine',
  'dash.weekGlance': 'Cette semaine en un coup d\'œil',
  'dash.ofContractHours': 'sur {hours}h contractuelles',
  'dash.storesFullyCovered': 'Établissements couverts',
  'dash.staffingGapsWeek': 'Manques d\'effectifs cette semaine',
  'dash.coverageOverview': 'Aperçu de la couverture',
  'dash.stores': 'Établissements',
  'dash.storesDesc': 'Gérer vos sites',
  'dash.manageStores': 'Gérer les établissements',
  'dash.employees': 'Employés',
  'dash.employeesDesc': 'Voir et gérer le personnel',
  'dash.manageEmployees': 'Gérer les employés',
  'dash.schedule': 'Planning',
  'dash.scheduleDesc': 'Créer et gérer les plannings',
  'dash.openSchedule': 'Ouvrir le planning',

  // ── Stores ──
  'stores.title': 'Établissements',
  'stores.subtitle': 'Gérez vos sites, effectifs et horaires',
  'stores.add': 'Ajouter un établissement',
  'stores.edit': 'Modifier l\'établissement',
  'stores.addNew': 'Nouvel établissement',
  'stores.editDesc': 'Modifier les détails, horaires et effectifs',
  'stores.addDesc': 'Ajouter un nouveau site',
  'stores.name': 'Nom du site',
  'stores.address': 'Adresse',
  'stores.staffingReq': 'Effectifs requis par jour',
  'stores.timeBlockStaffing': 'Effectifs par créneau',
  'stores.timeBlockDesc': 'Définissez le nombre d\'employés nécessaires par période.',
  'stores.addBlock': 'Ajouter',
  'stores.noBlocks': 'Aucun créneau — le besoin journalier s\'applique.',
  'stores.staff': 'pers.',
  'stores.colTimeBlocks': 'Créneaux',
  'stores.create': 'Créer l\'établissement',
  'stores.update': 'Mettre à jour',
  'stores.loading': 'Chargement des établissements...',
  'stores.empty': 'Aucun établissement. Ajoutez votre premier site.',
  'stores.colName': 'Nom',
  'stores.colAddress': 'Adresse',
  'stores.colHours': 'Horaires',
  'stores.colStaffing': 'Effectifs',
  'stores.colActions': 'Actions',
  'stores.notSet': 'Non défini',
  'stores.breakPolicy': 'Politique de pause',
  'stores.breakPolicyDesc': 'Configurez les déductions automatiques de pause pour la paie. Les pauses sont déduites des heures payées.',
  'stores.breakIfShiftExceeds': 'Appliquer la pause si le quart dépasse',
  'stores.hours': 'heures',
  'stores.breakDuration': 'Durée de la pause',
  'stores.addSecondBreak': 'Ajouter une deuxième pause pour les longs quarts',
  'stores.storeClosesDuringBreak': 'Le magasin ferme pendant la pause',
  'stores.colBreaks': 'Politique de pause',
  'stores.breakSummary': '{duration}min pause ≥{threshold}h',
  'stores.breakSummaryPerDay': 'Durées de pause par jour configurées',
  'stores.breakPresetHint': 'Cliquez sur un préréglage pour appliquer à tous les jours, ou personnalisez ci-dessous.',
  'stores.perDayBreakDuration': 'Durée de pause par jour',
  'stores.breakExceedsLimit': 'La pause dépasse 30% d\'un quart de {hours}h (max {max}min)',
  'stores.perDayConfig': 'Configuration par jour',
  'stores.block': 'bloc',
  'stores.blocks': 'blocs',

  // ── Employees ──
  'emp.title': 'Employés',
  'emp.subtitle': 'Gérer le personnel, les disponibilités et affectations',
  'emp.hPerWeek': 'h/semaine',
  'emp.add': 'Ajouter un employé',
  'emp.edit': 'Modifier l\'employé',
  'emp.addNew': 'Nouvel employé',
  'emp.editDesc': 'Modifier les détails de l\'employé',
  'emp.addDesc': 'Ajouter un nouveau membre',
  'emp.name': 'Nom',
  'emp.email': 'E-mail',
  'emp.phone': 'Téléphone',
  'emp.contractHours': 'Heures/semaine',
  'emp.primaryStore': 'Établissement principal',
  'emp.selectStore': 'Sélectionner un établissement',
  'emp.dayAvailability': 'Disponibilité',
  'emp.canWorkAt': 'Peut travailler à',
  'emp.addBtn': 'Ajouter l\'employé',
  'emp.updateBtn': 'Mettre à jour',
  'emp.search': 'Rechercher par nom, e-mail, téléphone...',
  'emp.nameAZ': 'Nom A–Z',
  'emp.nameZA': 'Nom Z–A',
  'emp.hoursUp': 'Heures ↑',
  'emp.hoursDown': 'Heures ↓',
  'emp.byStore': 'Par établissement',
  'emp.allStores': 'Tous les établissements',
  'emp.loading': 'Chargement des employés...',
  'emp.empty': 'Aucun employé. Ajoutez votre premier membre.',
  'emp.colName': 'Nom',
  'emp.colContact': 'Contact',
  'emp.colHours': 'Heures',
  'emp.colStore': 'Établissement',
  'emp.colAvailability': 'Disponibilité',
  'emp.colActions': 'Actions',

  // ── Schedule ──
  'sched.title': 'Planning',
  'sched.weekOf': 'Semaine du {start} - {end}',
  'sched.allStores': 'Tous les établissements',
  'sched.selectStore': 'Sélectionner un établissement',
  'sched.autoGenerate': 'Génération auto',
  'sched.export': 'Exporter',
  'sched.clearAll': 'Tout effacer',
  'sched.print': 'Imprimer',
  'sched.printAll': 'Imprimer tous les établissements',
  'sched.printStore': 'Imprimer {name}',
  'sched.prevWeek': 'Semaine précédente',
  'sched.nextWeek': 'Semaine suivante',
  'sched.today': "Aujourd'hui",
  'sched.conflicts': 'Conflits de planning',
  'sched.andMore': '...et {count} de plus',
  'sched.addStoresFirst': 'Ajoutez des établissements pour commencer.',
  'sched.clear': 'Effacer',
  'sched.holiday': 'Congé',
  'sched.pendingHoliday': 'Congé en attente',
  'sched.pendingTimeOff': 'Congé en attente',
  'sched.pendingHolidays': '⚠ {count} demandes en attente',
  'sched.conflictsDetected': '{count} conflits congé-planning',
  'sched.includePending': 'Inclure en attente',
  'sched.resolveConflicts': 'Corriger automatiquement',
  'sched.approvedConflict': 'Congé approuvé',
  'sched.pendingConflict': 'Demande en attente',
  'sched.resolveTitle': 'Corriger les conflits automatiquement',
  'sched.resolveDesc': 'Les modifications suivantes seront appliquées pour résoudre les conflits :',
  'sched.willResolve': '{count} conflits seront corrigés :',
  'sched.cannotResolve': '{count} nécessitent un ajustement manuel :',
  'sched.noReplacement': 'Aucun remplaçant disponible',
  'sched.removeShift': 'Supprimer le poste',
  'sched.applyFixes': 'Appliquer {count} corrections',
  'sched.resolveSuccess': '{count} conflits résolus',
  'sched.resolvePartial': '{count} nécessitent encore un ajustement manuel',
  'sched.resolveError': 'Erreur lors de la résolution',
  'sched.add': 'Ajouter',
  'sched.editShift': 'Modifier le poste',
  'sched.addShift': 'Ajouter un poste',
  'sched.editShiftDesc': 'Modifier les détails du poste',
  'sched.addShiftDesc': 'Planifier un nouveau poste',
  'sched.employee': 'Employé',
  'sched.selectEmployee': 'Sélectionner un employé',
  'sched.store': 'Établissement',
  'sched.date': 'Date',
  'sched.startTime': 'Heure de début',
  'sched.endTime': 'Heure de fin',
  'sched.updateShift': 'Mettre à jour le poste',
  'sched.addShiftBtn': 'Ajouter le poste',
  'sched.clearConfirm': 'Effacer tous les postes de cette semaine ?',
  'sched.clearStoreConfirm': 'Effacer tous les postes de {name} cette semaine ?',
  'sched.strategyStandard': 'Standard',
  'sched.strategyCoverage': 'Priorité couverture',
  'sched.strategyBalanced': 'Équilibré',
  'sched.strategyFullCoverage': 'Couverture totale',
  'sched.balancedDesc': 'Optimise l\'équité et la conformité contractuelle tout en maintenant la couverture.',
  'sched.coverageDesc': 'Priorise la couverture complète. Peut permettre des heures supplémentaires limitées.',
  'sched.recommended': 'Recommandé',
  'sched.coverageWarning': 'Priorise la couverture complète. Peut entraîner des heures supplémentaires limitées.',
  'sched.strategyLabel': 'Stratégie de planification',
  'sched.defaultStrategy': 'Stratégie par défaut',
  'sched.defaultStrategyDesc': 'Définir la stratégie par défaut pour votre organisation.',
  'sched.strategySaved': 'Stratégie par défaut mise à jour',
  'sched.shiftAdded': 'Poste ajouté avec succès',
  'sched.shiftUpdated': 'Poste mis à jour avec succès',
  'sched.shiftRemoved': 'Poste supprimé avec succès',
  'sched.shiftsSwapped': 'Postes échangés avec succès',
  'sched.shiftMoved': 'Poste déplacé avec succès',
  'sched.errorSwap': 'Erreur lors de l\'échange des postes',
  'sched.errorMoveShift': 'Erreur lors du déplacement du poste',
  'sched.cannotSwapStores': 'Impossible d\'échanger des postes entre différents magasins',
  'sched.swapHint': 'Déposer pour échanger',
  'sched.alreadyScheduledSwap': 'L\'employé est déjà programmé ce jour-là',
  'sched.availabilityOverrideTitle': 'Dérogation de disponibilité',
  'sched.availabilityOverrideDesc': '{name} n\'est disponible que les {days}. Approuver ce changement de poste ?',
  'sched.approve': 'Approuver',
  'sched.noDaysAvailable': 'aucun jour',
  'sched.errorAddShift': "Erreur lors de l'ajout du poste",
  'sched.errorUpdateShift': 'Erreur lors de la mise à jour du poste',
  'sched.errorRemoveShift': 'Erreur lors de la suppression du poste',
  'sched.noShiftsToClear': 'Aucun poste à effacer',
  'sched.clearedShifts': '{count} poste(s) effacé(s)',
  'sched.clickUndo': 'Cliquez sur annuler pour les restaurer.',
  'sched.undo': 'Annuler',
  'sched.restoredShifts': '{count} poste(s) restauré(s)',
  'sched.errorRestore': 'Erreur lors de la restauration des postes',
  'sched.errorClear': 'Erreur lors de la suppression du planning',
  'sched.generatedShifts': '{count} postes générés avec succès',
  'sched.errorGenerate': 'Erreur lors de la génération du planning',
  'sched.validationError': 'Erreur de validation',
  'sched.cannotAssign': 'Impossible d\'attribuer le poste',
  'sched.alreadyScheduled': '{name} a déjà un poste à cette date.',
  'sched.onHoliday': '{name} est en congé approuvé à cette date.',
  'sched.onLeave': '{name} est en congé {type} à cette date.',
  'sched.storeClosed': 'Le magasin est fermé ce jour.',
  'sched.shiftOutsideHours': 'Le poste est en dehors des horaires du magasin',
  'sched.shiftOutsideHoursWarning': 'Ce poste dépasse les horaires du magasin. Le temps supplémentaire sera suivi comme heures supplémentaires dans les rapports.',
  'sched.significantOvertimeTitle': 'Poste largement en dehors des horaires du magasin',
  'sched.significantOvertimeDesc': 'Ce poste dépasse de plus de 60 minutes les horaires d\'ouverture du magasin. Êtes-vous sûr de vouloir l\'enregistrer ?',
  'sched.saveAnyway': 'Enregistrer quand même',
  'sched.shiftPaidBreak': '{paid} payé ({break} pause)',
  'sched.breakDeducted': '{duration} pause déduite des heures payées',
  'sched.bannerGenerated': 'Planning généré — {employees} employés planifiés, {hours}h contractuelles respectées, {gaps}, postes distribués équitablement.',
  'sched.bannerNoGaps': 'aucun manque de couverture',
  'sched.bannerGaps': '{count} manque(s) de couverture',
  'sched.bannerNoIssues': 'Aucun problème trouvé',
  'sched.bannerReviewGaps': '{count} manque(s) de couverture à examiner →',
  'sched.exportSuccess': 'Planning exporté avec succès',
  'sched.coverageDiag': 'Diagnostique de couverture',
  'sched.tbCoverageDiag': 'Problèmes de couverture par créneau',
  'sched.moreGaps': '...et {count} lacunes supplémentaires',
  'sched.coverageMet': 'Couverture : {staffed} / {required} ✔',
  'sched.coverageShort': 'Couverture : {staffed} / {required} ✘',
  'sched.coverageOver': 'Couverture : {staffed} / {required} ⚠',
  'sched.missingStaff': '{count} manquant(s) pour {time}',
  'sched.requirements': 'Besoins',
  'sched.shifts': 'Postes',
  'sched.diagNoAvail': 'Pas de disponibilité ({count})',
  'sched.diagContractExh': 'Contrat épuisé ({count})',
  'sched.diagMaxDays': 'Jours max atteints ({count})',
  'sched.diagNoAssigned': 'Aucun employé affecté à cet établissement',
  'sched.diagNoEligible': 'Aucun employé éligible',
  'sched.diagNoReq': 'Aucun effectif requis configuré',
  'sched.conflictOverHours': '{name} est planifié pour {hours}h mais le contrat est de {contract}h',
  'sched.conflictUnavailable': '{name} n\'est pas disponible le {day}',
  'sched.conflictWrongStore': '{name} n\'est pas affecté à {store}',
  'sched.exportEmployee': 'Employé',
  'sched.exportStaffing': 'Effectifs',
  'sched.exportReqSched': 'Requis/Planifié',
  'abs.typeSick': 'Maladie',
  'abs.typeEmergency': 'Urgence',
  'sched.copyWeek': 'Copier la semaine',
  'sched.copyWeekTitle': 'Copier le planning',
  'sched.copyWeekDesc': 'Dupliquer tous les créneaux d\'une semaine à une autre.',
  'sched.sourceWeek': 'Semaine source',
  'sched.targetWeek': 'Semaine cible',
  'sched.copyWeekConfirm': 'Copier le planning',
  'sched.copyWeekSuccess': 'Planning copié avec succès.',
  'sched.copyWeekError': 'Erreur lors de la copie',
  'sched.copyWeekNoShifts': 'Aucun créneau trouvé dans la semaine source.',
  'sched.copyWeekReplace': 'Cette semaine contient déjà {count} créneau(x). Remplacer le planning existant ?',
  'sched.copyWeekReplaceBtn': 'Remplacer et copier',
  'sched.copyWeekCopying': 'Copie en cours...',
  'sched.selectTargetWeek': 'Sélectionner la semaine cible',
  'sched.copyNextWeek': 'Semaine prochaine',
  'sched.copyWeekAfterNext': 'Dans deux semaines',
  'sched.copySpecificWeek': 'Choisir une semaine',

  // ── Holidays ──
  'hol.title': 'Congés',
  'hol.subtitle': 'Suivre les demandes de congés et les soldes',
  'hol.request': 'Demander un congé',
  'hol.balances': 'Soldes de congés',
  'hol.allRequests': 'Toutes les demandes',
  'hol.noRequests': 'Aucune demande de congé',
  'hol.employee': 'Employé',
  'hol.selectEmployee': 'Sélectionner un employé',
  'hol.dateRange': 'Période',
  'hol.pickDates': 'Choisir les dates',
  'hol.notes': 'Notes (optionnel)',
  'hol.notesPlaceholder': 'Motif du congé...',
  'hol.submit': 'Soumettre la demande',
  'hol.businessDays': '{count} jours ouvrés',
  'hol.approved': 'Approuvé',
  'hol.approve': 'Approuver',
  'hol.rejected': 'Refusé',
  'hol.reject': 'Refuser',
  'hol.pending': 'En attente',
  'hol.delete': 'Supprimer',
  'hol.days': '{count} jours',
  'hol.used': 'Utilisés',
  'hol.pendingLabel': 'En attente',
  'hol.total': 'Total',
  'hol.dialogTitle': 'Demander un congé',
  'hol.dialogDesc': 'Soumettre une nouvelle demande',
  'hol.partialApprove': 'Approuver partiellement',
  'hol.partialApproveTitle': 'Approbation partielle',
  'hol.selectApprovedDates': 'Sélectionner les dates à approuver',
  'hol.partialSummary': '{approved} sur {total} jours approuvés',
  'hol.partialApproveAction': 'Approuver {count} jours',
  'hol.partialApproved': 'Partiellement approuvé',
  'hol.partialRejected': 'Non approuvé (partiel)',
  'hol.partialRemainder': 'Restant de l\'approbation partielle',
  'hol.partialLabel': 'Partiel',

  // ── Absences ──
  'abs.title': 'Absences',
  'abs.subtitle': 'Suivre les arrêts maladie et congés exceptionnels',
  'abs.record': 'Enregistrer une absence',
  'abs.summary': 'Résumé par employé',
  'abs.allRecords': 'Tous les enregistrements',
  'abs.all': 'Tous',
  'abs.sickDays': 'Arrêts maladie',
  'abs.emergencyLeave': 'Congés exceptionnels',
  'abs.sick': 'Maladie',
  'abs.emergency': 'Urgence',
  'abs.noRecords': 'Aucun enregistrement trouvé',
  'abs.employee': 'Employé',
  'abs.selectEmployee': 'Sélectionner un employé',
  'abs.type': 'Type',
  'abs.sickDay': 'Arrêt maladie',
  'abs.dates': 'Dates',
  'abs.pickDates': 'Choisir les dates',
  'abs.reason': 'Motif',
  'abs.reasonPlaceholder': 'ex. Grippe, urgence familiale...',
  'abs.notes': 'Notes (optionnel)',
  'abs.notesPlaceholder': 'Détails supplémentaires...',
  'abs.recordBtn': 'Enregistrer l\'absence',
  'abs.businessDays': '{count} jours ouvrés',
  'abs.dialogTitle': 'Enregistrer une absence',
  'abs.dialogDesc': 'Saisir un arrêt maladie ou congé exceptionnel',
  'abs.totalLabel': 'Total',

  // ── Reports ──
  'rep.title': 'Rapports',
  'rep.weekOf': 'Semaine du {label}',
  'rep.excel': 'Excel',
  'rep.pdfPrint': 'PDF / Imprimer',
  'rep.thisWeek': 'Cette semaine',
  'rep.allStores': 'Tous les établissements',
  'rep.warnings': 'Alertes',
  'rep.hoursVsContract': 'Heures vs Contrat',
  'rep.storeCoverage': 'Couverture',
  'rep.holidayBalance': 'Solde congés',
  'rep.year': 'Année :',
  'rep.loading': 'Chargement…',
  'rep.employeeOverContract': 'Employé au-dessus du contrat',
  'rep.employeesOverContract': 'Employés au-dessus du contrat',
  'rep.totalOvertimeRisk': 'Risque total d\'heures supplémentaires',
  'rep.employeeHours': 'Heures des employés',
  'rep.colEmployee': 'Employé',
  'rep.colStore': 'Établissement',
  'rep.colContract': 'Contrat',
  'rep.colScheduled': 'Planifié',
  'rep.colExpected': 'Prévu',
  'rep.colDifference': 'Différence',
  'rep.noEmployees': 'Aucun employé à afficher.',
  'rep.noStores': 'Aucun établissement à afficher.',
  'rep.understaffedDay': '{count} jour en sous-effectif cette semaine.',
  'rep.understaffedDays': '{count} jours en sous-effectif cette semaine.',
  'rep.needMore': '{count} de plus nécessaire(s)',
  'rep.extra': '+{count} en plus',
  'rep.ok': 'OK',
  'rep.holidayBalances': 'Soldes de congés',
  'rep.colAllowance': 'Allocation',
  'rep.colApproved': 'Approuvé',
  'rep.colPending': 'En attente',
  'rep.colRemaining': 'Restant',
  'rep.colUsage': 'Utilisation',
  'rep.lowOrOverusedSingular': 'Employé avec un solde de congés faible ou dépassé',
  'rep.lowOrOverused': 'Employés avec un solde de congés faible ou dépassé',
  'rep.allClear': 'Tout est en ordre',
  'rep.noIssues': 'Aucun problème de planification cette semaine.',
  'rep.issueSingular': 'Problème à examiner',
  'rep.issuesToReview': 'Problèmes à examiner',
  'rep.urgent': 'urgent(s)',
  'rep.holidayConflict': 'Conflit de congé',
  'rep.absenceConflict': 'Conflit d\'absence',
  'rep.overHours': 'Heures excédentaires',
  'rep.unavailable': 'Indisponible',
  'rep.understaffed': 'Sous-effectif',
  'rep.fix': 'Corriger',
  'rep.tabOverview': 'Aperçu',
  'rep.tabHoursPayroll': 'Heures & Paie',
  'rep.tabCoverage': 'Couverture',
  'rep.tabLeave': 'Congés',
  'rep.tabCompliance': 'Conformité',
  'rep.overviewScheduledH': 'Heures planifiées',
  'rep.overviewOvertimeH': 'Heures supplémentaires',
  'rep.overviewCoverageGaps': 'Écarts de couverture',
  'rep.overviewNearLimit': 'Proche de la limite',
  'rep.overviewExceeding': 'Dépassement du contrat',
  'rep.overviewRiskLevel': 'Niveau de risque',
  'rep.overviewNoIssues': 'Aucun problème actif cette semaine.',
  'rep.viewCompliance': 'Voir la conformité',
  'rep.viewCoverage': 'Voir la couverture',
  'rep.exportSummary': 'Exporter le résumé',
  'rep.exportExcel': 'Exporter Excel',
  'rep.exportPDF': 'Exporter PDF',
  'rep.exportedExcel': 'Exporté en Excel',
  'rep.exportedExcelDesc': 'Vérifiez votre dossier Téléchargements.',
  'rep.exportedCSV': 'Exporté en CSV',
  'rep.weekly': 'Hebdomadaire',
  'rep.monthly': 'Mensuel',
  'rep.totalScheduledH': 'Total heures planifiées',
  'rep.chartTitle': 'Heures planifiées vs prévues par employé',
  'rep.chartOnTrack': 'Conforme',
  'rep.chartOverContract': 'Au-dessus du contrat',
  'rep.chartUnderhours': 'Sous les heures',
  'rep.chartExpected': 'Prévu',
  'rep.issuesToReviewTitle': 'Problèmes à examiner',
  'rep.allClearPeriod': 'Tout est en ordre — aucun problème cette période.',
  'rep.employeesOverContractLabel': 'Employés au-dessus du contrat',
  'rep.totalOvertimeRiskLabel': 'Risque total d\'heures supp.',
  'rep.employeeHoursTitle': 'Heures des employés',
  'rep.colOvertime': 'Heures supp.',
  'rep.colUnderhours': 'Heures manquantes',
  'rep.colStatus': 'Statut',
  'rep.payrollBreakdown': 'Détail paie — {period}',
  'rep.colScheduledH': 'Planifié (h)',
  'rep.colPaidHolidayH': 'Congé payé (h)',
  'rep.colPaidSickH': 'Maladie payée (h)',
  'rep.colCountedHours': 'Heures comptées',
  'rep.colSickDays': 'Jours maladie',
  'rep.totals': 'Totaux',
  'rep.totalScheduled': 'Total planifié',
  'rep.totalActual': 'Total réel',
  'rep.lowOrOverusedLabel': 'Employés avec solde de congés faible ou dépassé',
  'rep.holidayAllowanceTitle': 'Utilisation des congés par employé',
  'rep.sickDaySummary': 'Résumé jours maladie',
  'rep.colSickDaysUsed': 'Jours maladie utilisés',
  'rep.colAnnualAllowance': 'Allocation annuelle',
  'rep.colInstances': 'Occurrences',
  'rep.colPaid': 'Payé',
  'rep.colUnpaid': 'Non payé',
  'rep.totalEmployees': 'Total employés',
  'rep.compliant': 'Conforme',
  'rep.compWarnings': 'Alertes',
  'rep.actionRequired': 'Action requise',
  'rep.complianceDisclaimer': 'Les heures contractuelles peuvent varier légèrement en raison des horaires d\'ouverture et des durées minimales de poste. De petites différences (±2h) sont normales.',
  'rep.hExp': '{h}h prévu',
  'rep.hCounted': '{h}h compté',
  'rep.hoursBreakdown': 'Détail des heures',
  'rep.scheduledWorked': 'Planifié (travaillé)',
  'rep.paidHoliday': 'Congé payé',
  'rep.paidSick': 'Maladie payée',
  'rep.unpaidLeave': 'Congé non payé',
  'rep.complianceDetail': 'Détail conformité',
  'rep.contract': 'Contrat',
  'rep.expected': 'Prévu',
  'rep.counted': 'Compté',
  'rep.variance': 'Écart',
  'rep.maxStreak': 'Jours consécutifs max',
  'rep.leaveBalanceYear': 'Solde congés (Année)',
  'rep.holiday': 'Congé',
  'rep.sick': 'Maladie',
  'rep.remainingOf': '{remaining} restant(s) sur {total}j',
  'rep.riskLog': 'Journal des risques',
  'rep.highRisk': 'Risque élevé',
  'rep.mediumRisk': 'Risque moyen',
  'rep.lowRisk': 'Risque faible',
  'rep.colRiskType': 'Type de risque',
  'rep.colDateWeek': 'Date/Semaine',
  'rep.colSeverity': 'Gravité',
  'rep.colDescription': 'Description',
  'rep.filterAll': 'Tous',
  'rep.filterHigh': 'Élevé',
  'rep.filterMedium': 'Moyen',
  'rep.filterLow': 'Faible',
  'rep.statusCompliant': 'Conforme',
  'rep.statusWarning': 'Alerte',
  'rep.statusActionRequired': 'Action requise',
  'rep.statusOnTrack': 'Conforme',
  'rep.statusAtRisk': 'À risque',
  'rep.riskHigh': 'Élevé',
  'rep.riskMedium': 'Moyen',
  'rep.riskLow': 'Faible',
  'rep.gap': 'MANQUE',
  'rep.tooltipScheduled': 'Planifié : {h}h',
  'rep.tooltipExpected': 'Prévu : {h}h',
  'rep.tooltipVariance': 'Écart : {h}h',
  'rep.tooltipStatus': 'Statut : {status}',

  // ── Planned vs Actual ──
  'pva.title': 'Heures planifiées vs réelles',
  'pva.totalScheduled': 'Total planifié',
  'pva.totalPlanned': 'Total prévu',
  'pva.totalActual': 'Total réel',
  'pva.totalVariance': 'Écart total',
  'pva.confirmRate': '{confirmed}/{total} quarts confirmés',
  'pva.employeeBreakdown': 'Détail par employé',
  'pva.scheduled': 'Planifié',
  'pva.planned': 'Prévu',
  'pva.actual': 'Réel',
  'pva.variance': 'Écart',
  'pva.breakVar': 'Pause ±',
  'pva.confirmed': 'Confirmé',
  'pva.lateArrivals': 'Retard',
  'pva.earlyLeaves': 'Départ tôt',
  'pva.totals': 'Totaux',
  'pva.noConfirmedShifts': 'Aucun quart confirmé — confirmez les quarts pour voir les données réelles',
  'pva.lateArrivalsCount': '{count} retard(s)',
  'pva.earlyLeavesCount': '{count} départ(s) anticipé(s)',
  'pva.totalOvertimeInsight': '{hours}h d\'heures supplémentaires',
  'pva.totalUnderhoursInsight': '{hours}h sous le planifié',
  'pva.breakOvertime': '{mins}m de pause en plus',
  'pva.breakScheduledVsActual': 'Pause: Planifié / Réel',
  'review.actualBreakStart': 'Début de pause',
  'review.actualBreakEnd': 'Fin de pause',

  // ── Not Found ──
  'notfound.title': '404',
  'notfound.message': 'Page introuvable',
  'notfound.link': "Retour à l'accueil",

  // ── Warning Explanations ──
  'warn.hoursOverTitle': '{hours} heures au-dessus du contrat',
  'warn.hoursOverDesc': 'Cet employé est planifié au-delà de ses heures hebdomadaires convenues.',
  'warn.hoursOverOpt1': 'Supprimer un poste',
  'warn.hoursOverOpt2': 'Réaffecter à un autre employé',
  'warn.hoursOverOpt3': 'Augmenter les heures de contrat',
  'warn.unfillableTitle': '{hours} heures ne peuvent pas être planifiées cette semaine',
  'warn.unfillableWhy': 'Pourquoi :',
  'warn.unfillableR1': 'Tous les établissements sont complets ou fermés',
  'warn.unfillableR2': 'La disponibilité restante ne correspond pas aux postes ouverts',
  'warn.unfillableR3': 'La durée minimale de poste empêche les affectations partielles',
  'warn.unfillableOpt1': 'Réduire les heures de contrat pour cette semaine',
  'warn.unfillableOpt2': 'Autoriser des postes plus courts',
  'warn.unfillableOpt3': 'Augmenter les besoins en personnel',
  'warn.nearContractTitle': '{hours} heures en dessous du contrat',
  'warn.nearContractDesc': 'Cela arrive car tous les postes restants sont complets et le système évite de créer des postes trop courts.',
  'warn.nearContractOpt1': 'Accepter une semaine sous contrat',
  'warn.nearContractOpt2': 'Autoriser des postes plus courts',
  'warn.nearContractOpt3': 'Ajuster les heures de contrat',
  'warn.underTitle': '{hours} heures en dessous du contrat',
  'warn.underDesc': 'Cet employé a moins d\'heures planifiées que son contrat ne l\'exige. Des postes peuvent encore être ajoutés.',
  'warn.underOpt1': 'Ajouter des postes manuellement',
  'warn.underOpt2': 'Lancer la génération automatique',
  'warn.underOpt3': 'Ajuster les heures de contrat',
  'warn.options': 'Options :',
  'warn.holidayDesc': 'Cet employé est en congé approuvé à cette date et ne peut pas être planifié.',
  'warn.holidayOpt1': 'Supprimer le poste',
  'warn.holidayOpt2': 'Affecter un autre employé',
  'warn.absenceDesc': 'Cet employé est en congé approuvé à cette date et ne devrait pas être planifié.',
  'warn.absenceOpt1': 'Supprimer le poste',
  'warn.absenceOpt2': 'Affecter un autre employé',
  'warn.unavailableDesc': 'Ce poste est en dehors des jours de disponibilité de l\'employé.',
  'warn.unavailableOpt1': 'Changer la date du poste',
  'warn.unavailableOpt2': 'Affecter un autre employé',
  'warn.unavailableOpt3': 'Mettre à jour la disponibilité',
  'warn.understaffedDesc': 'Cet établissement n\'a pas assez de personnel planifié pour ce jour.',
  'warn.understaffedOpt1': 'Ajouter un autre employé',
  'warn.understaffedOpt2': 'Ajuster les besoins en personnel',
  'warn.understaffedOpt3': 'Vérifier la disponibilité des employés',
  'warn.weeklyHours': 'Résumé hebdomadaire des heures',
  'warn.maxDaysTitle': 'Nombre maximum de jours de travail atteint',
  'warn.maxDaysDesc': 'Cet employé a atteint son maximum de {days} jours de travail cette semaine. Des postes supplémentaires ne peuvent pas être attribués même si des heures contractuelles restent.',
  'warn.maxDaysOpt1': 'Augmenter le nombre max de jours',
  'warn.maxDaysOpt2': 'Accepter la semaine sous contrat',
  'warn.maxDaysOpt3': 'Ajuster les heures contractuelles',
  'emp.maxWorkingDays': 'Jours de travail max/semaine',

  'onb.title': 'Créez votre organisation',
  'onb.subtitle': 'Donnez un nom à votre entreprise ou équipe pour commencer.',
  'onb.label': "Nom de l'organisation",
  'onb.placeholder': 'Mon Entreprise',
  'onb.creating': 'Création...',
  'onb.create': "Créer l'organisation",
  'onb.signOut': 'Déconnexion',
  'onb.invalidName': 'Nom invalide',
  'onb.nameLength': "Le nom de l'organisation doit contenir entre 1 et 100 caractères.",
  'setup.title': 'Configurons votre entreprise',
  'setup.subtitle': 'Ajoutez votre premier magasin et vos employés pour générer votre premier planning équilibré.',
  'setup.progress': 'Étape {count} sur {total} terminée',
  'setup.step1': 'Ajoutez votre premier magasin',
  'setup.step2': 'Ajoutez votre premier employé',
  'setup.step3': 'Générez votre premier planning',
  'setup.step3Hint': 'Nécessite au moins 1 magasin et 1 employé',
  'setup.addAnother': 'Ajouter un autre',
  'setup.storeAdded': 'Magasin ajouté !',
  'setup.employeeAdded': 'Employé ajouté !',
  'setup.storeCount1': 'magasin ajouté',
  'setup.storeCountN': 'magasins ajoutés',
  'setup.empCount1': 'employé ajouté',
  'setup.empCountN': 'employés ajoutés',
  'setup.generate': 'Générer le premier planning',
  'setup.generating': 'Génération...',
  'setup.success': 'Votre premier planning équilibré est prêt.',
  'setup.genError': 'Échec de la génération du planning',
  'setup.noShiftsError': 'Aucun créneau généré. Vérifiez les besoins en personnel et la disponibilité.',
  'setup.skip': 'Passer pour le moment',
  'setup.emptySchedule': 'Vous avez besoin d\'au moins 1 magasin et 1 employé pour créer un planning.',
  'settings.title': 'Paramètres',
  'settings.subtitle': "Préférences et configuration de l'entreprise",
  'settings.balancedDesc': "Maximise la couverture tout en minimisant les heures supplémentaires. Recommandé pour la plupart des équipes.",
  'hours.label': "Horaires d'ouverture",
  'hours.quickSet': 'Réglage rapide',
  'hours.to': 'à',
  'hours.lunchBreak': 'Pause déjeuner',
  'hours.applyWeekdays': 'Appliquer Lun–Ven',
  'hours.applyAll': 'Appliquer tous les jours',
  'hours.open': 'Ouvert',
  'hours.closed': 'Fermé',
  'ai.getSuggestions': 'Suggestions IA',
  'ai.autoFix': 'Corriger auto',
  'sched.fix': 'Corriger',
  'sched.reduceHours': 'Réduire les heures',
  'sched.reassignShift': 'Réassigner',
  'sched.confirm': 'Confirmer',
  'sched.edit': 'Modifier',
  'sched.remove': 'Supprimer',
  'sched.understaffed': '⚠ Sous-effectif',
  'sched.missingCount': '{count} employé(s) manquant(s)',
  'sched.staffed': 'couvert',
  'sched.staff': 'personnel',
  'sched.statusReady': 'Planning prêt',
  'sched.statusIssue1': '1 problème à vérifier',
  'sched.statusIssues': '{count} problèmes à vérifier',
  'sched.issueOverHours1': '{name} dépasse ses heures',
  'sched.issueOverHoursN': '{count} employés dépassent leurs heures',
  'sched.issueShiftTooLong1': '{count} shift trop long',
  'sched.issueShiftTooLongN': '{count} shifts trop longs',
  'sched.issueInvalidTimes1': '{count} shift a des horaires invalides',
  'sched.issueInvalidTimesN': '{count} shifts ont des horaires invalides',
  'sched.issueOutsideHours1': '{count} shift hors horaires du magasin',
  'sched.issueOutsideHoursN': '{count} shifts hors horaires du magasin',
  'sched.issueClosedDay1': '{count} shift un jour fermé',
  'sched.issueClosedDayN': '{count} shifts des jours fermés',
  'sched.issueUnavailable1': '{name} planifié un jour indisponible',
  'sched.issueUnavailableN': '{count} shifts sur des jours indisponibles',
  'sched.issueWrongStore1': '{name} assigné au mauvais magasin',
  'sched.issueWrongStoreN': '{count} employés au mauvais magasin',
  'sched.issueOverlap1': '{count} shift chevauchant',
  'sched.issueOverlapN': '{count} shifts chevauchants',
  'sched.issueShiftTooShort1': '{count} shift trop court',
  'sched.issueShiftTooShortN': '{count} shifts trop courts',
  'sched.issueUnderstaffed1': '{store} manque de personnel le {day}',
  'sched.issueUnderstaffedN': '{count} jours en sous-effectif',
  'ai.fixSchedule': 'Corriger avec IA',
  'ai.fixScheduleAI': 'Corriger le planning avec l\'IA',
  'sched.issuesThisWeek': 'Problèmes cette semaine',
  'ai.analyzing': 'Analyse...',
  'ai.suggestions': 'Suggestions IA',
  'ai.fixesFound': '{count} corrections trouvées',
  'ai.addresses': 'Concerne :',
  'ai.apply': 'Appliquer',
  'ai.preview': 'Aperçu',
  'ai.hidePreview': 'Masquer',
  'ai.before': 'Avant',
  'ai.after': 'Après',
  'ai.keepsCoverage': 'Couverture maintenue',
  'ai.withinContract': 'Contrat respecté',
  'ai.shiftRemoved': 'Poste supprimé',
  'ai.noShift': 'Aucun poste',
  'ai.refresh': 'Actualiser',
  'ai.swap': 'Échanger',
  'ai.reassign': 'Réaffecter',
  'ai.addShift': 'Ajouter un poste',
  'ai.remove': 'Supprimer',
  'ai.adjust': 'Ajuster',
  'ai.applied': 'Correction appliquée',
  'ai.changes': 'Modifications',
  'ai.failedApply': "Échec de l'application",
  'ai.failedFetch': 'Échec de la récupération',
  'ai.aiError': 'Erreur IA',
  'ai.importTitle': 'Import de données IA',
  'ai.stageInput': 'Collez une description de votre entreprise. Les établissements seront analysés en premier, puis les employés.',
  'ai.stageParsingStores': 'Analyse de votre texte pour les établissements...',
  'ai.stagePreviewStores': "Vérifiez les établissements ci-dessous. Une fois confirmés, l'analyse des employés commencera.",
  'ai.stageSavingStores': 'Enregistrement des établissements...',
  'ai.stageCounting': 'Détection des employés dans votre texte...',
  'ai.stageParsingEmps': 'Analyse des employés par lots...',
  'ai.stageValidating': 'Validation des données employés...',
  'ai.stagePreviewEmps': 'Vérifiez les employés ci-dessous. Cliquez sur « Confirmer » pour enregistrer.',
  'ai.stageImporting': 'Importation des employés...',
  'ai.stageComplete': 'Toutes les données importées avec succès !',
  'ai.parseStores': 'Analyser les établissements',
  'ai.startOver': 'Recommencer',
  'ai.confirmStores': 'Confirmer et analyser les employés',
  'ai.importAnyway': 'Importer quand même',
  'ai.confirmImport': "Confirmer l'importation",
  'ai.done': 'Terminé',
  'ai.tip': "Astuce : incluez noms de magasins, horaires, effectifs, noms d'employés, heures de contrat, affectations et disponibilités.",
  'ai.storesSaved': 'établissements enregistrés',
  'ai.employees': 'employés',
  'ai.totalWeekly': 'total hebdomadaire',
  'ai.importedSuccess': '{stores} établissements et {employees} employés importés avec succès.',
  'summary.ofContract': 'du contrat',
  'summary.over': '+{hours}h de plus',
  'summary.cantFill': '{hours}h non assignables',
  'summary.belowMin': '{hours}h sous le minimum',
  'summary.remaining': '{hours}h restantes',
  'dash.importData': 'Importer des données',
  'dash.importTitle': 'Importez vos données',
  'dash.importDescription': 'Choisissez comment configurer vos magasins et employés.',
  'dash.aiImportTitle': 'Import IA intelligent',
  'dash.aiImportDesc': 'Collez une description de votre entreprise et laissez l\'IA créer automatiquement magasins, employés et contrats.',
  'dash.startAiImport': 'Lancer l\'import IA',
  'dash.excelImportTitle': 'Télécharger un modèle Excel',
  'dash.excelImportDesc': 'Importez un fichier Excel structuré avec vos magasins et employés pour un import rapide et validé.',
  'dash.uploadFile': 'Télécharger un fichier',
  'dash.importHint': 'Pas sûr de votre choix ? L\'import IA est idéal pour une configuration rapide.',

  'day.sun': 'Dimanche', 'day.mon': 'Lundi', 'day.tue': 'Mardi', 'day.wed': 'Mercredi',
  'day.thu': 'Jeudi', 'day.fri': 'Vendredi', 'day.sat': 'Samedi',
  'day.sunShort': 'Dim', 'day.monShort': 'Lun', 'day.tueShort': 'Mar', 'day.wedShort': 'Mer',
  'day.thuShort': 'Jeu', 'day.friShort': 'Ven', 'day.satShort': 'Sam',

  'settings.tabOrg': 'Organisation', 'settings.tabTeam': 'Équipe', 'settings.tabRules': 'Règles de planification', 'settings.tabDanger': 'Zone de danger',
  'settings.orgName': "Nom de l'organisation", 'settings.orgNameDesc': "Le nom affiché aux membres de l'équipe.", 'settings.save': 'Enregistrer', 'settings.saved': 'Paramètres enregistrés',
  'settings.teamTitle': "Membres de l'équipe", 'settings.teamDesc': 'Gérez votre équipe et envoyez des invitations.', 'settings.invite': 'Inviter un membre',
  'settings.colName': 'Nom', 'settings.colEmail': 'E-mail', 'settings.colRole': 'Rôle', 'settings.colStatus': 'Statut', 'settings.colActions': 'Actions',
  'settings.roleOwner': 'Propriétaire', 'settings.roleManager': 'Gestionnaire', 'settings.active': 'Actif', 'settings.pending': 'En attente',
  'settings.remove': 'Supprimer', 'settings.resend': 'Renvoyer', 'settings.removeConfirm': 'Supprimer ce membre ?', 'settings.removed': 'Membre supprimé',
  'settings.inviteSent': 'Invitation envoyée', 'settings.inviteEmail': 'Adresse e-mail', 'settings.inviteRole': 'Rôle', 'settings.inviteBtn': "Envoyer l'invitation",
  'settings.inviteTitle': "Inviter un membre", 'settings.inviteDesc': "Envoyez un lien d'invitation pour rejoindre votre organisation.",
  'settings.rulesTitle': 'Règles de planification', 'settings.rulesDesc': 'Configurez la génération des plannings.',
  'settings.dangerTitle': 'Zone de danger', 'settings.dangerDesc': 'Actions irréversibles. Procédez avec prudence.',
  'settings.deleteOrg': "Supprimer l'organisation", 'settings.deleteOrgDesc': 'Supprimer définitivement cette organisation et toutes les données associées.',
  'settings.deleteOrgConfirm': 'Êtes-vous sûr ? Cette action est irréversible.', 'settings.clearSchedules': 'Effacer tous les plannings',
  'settings.clearSchedulesDesc': 'Supprimer toutes les données de planning. Les employés et magasins seront conservés.',
  'settings.clearConfirm': 'Êtes-vous sûr ? Toutes les données de planning seront supprimées.',
  'settings.deleted': 'Organisation supprimée', 'settings.cleared': 'Plannings effacés', 'settings.cancel': 'Annuler',
  'settings.you': '(vous)', 'settings.cannotRemoveSelf': 'Vous ne pouvez pas vous retirer',

  'common.loading': 'Chargement...',
  'common.cancel': 'Annuler',
  'auth.forgotPassword': 'Mot de passe oublié ?',
  'auth.or': 'ou',
  'auth.googleSignIn': 'Continuer avec Google',
  'auth.appleSignIn': 'Continuer avec Apple',
  'forgot.title': 'Mot de passe oublié',
  'forgot.subtitle': 'Entrez votre email pour recevoir un lien de réinitialisation.',
  'forgot.button': 'Envoyer le lien',
  'forgot.sent': 'Si un compte existe, un lien de réinitialisation a été envoyé.',
  'forgot.backToLogin': 'Retour à la connexion',
  'reset.title': 'Réinitialiser le mot de passe',
  'reset.subtitle': 'Entrez votre nouveau mot de passe.',
  'reset.newPassword': 'Nouveau mot de passe',
  'reset.confirmPassword': 'Confirmer le mot de passe',
  'reset.button': 'Mettre à jour',
  'reset.passwordMin': 'Le mot de passe doit contenir au moins 8 caractères',
  'reset.mismatch': 'Les mots de passe ne correspondent pas',
  'reset.failed': 'Échec de la mise à jour. Veuillez réessayer.',
  'reset.success': 'Mot de passe mis à jour. Redirection...',
  'reset.invalidLink': 'Ce lien est invalide ou a expiré.',
  'reset.backToLogin': 'Retour à la connexion',
  'settings.tabAccount': 'Compte',
  'settings.tabBilling': 'Facturation',
  'billing.currentPlan': 'Plan actuel',
  'billing.currentPlanDesc': 'Détails de votre abonnement',
  'billing.refresh': 'Actualiser',
  'billing.noSubscription': 'Aucun abonnement actif',
  'billing.paymentMethod': 'Moyen de paiement',
  'billing.invoices': 'Factures',
  'billing.billingDetails': 'Détails de facturation',
  'billing.editDetails': 'Modifier les détails',
  'billing.activating': 'Activation de votre abonnement…',
  'billing.activatingDesc': 'Cela prend généralement quelques secondes.',
  'billing.subEnded': 'Votre abonnement est terminé',
  'billing.choosePlan': 'Choisissez votre plan pour commencer',
  'billing.pastDue': 'Paiement en retard',
  'billing.canceled': 'Annulé',
  'billing.expired': 'Expiré',
  'billing.monthly': 'Mensuel',
  'billing.annual': 'Annuel',
  'billing.save17': 'Économisez ~17%',
  'billing.trialDesc': 'Essai gratuit de 30 jours inclus. Annulez à tout moment.',
  'billing.redirecting': 'Redirection…',
  'billing.reactivate': 'Réactiver l\'abonnement',
  'billing.startTrial': 'Démarrer l\'essai gratuit',
  'billing.checkStatus': 'Déjà effectué le paiement ? Vérifier le statut',
  'billing.accountSettings': 'Paramètres du compte',
  'billing.signOut': 'Déconnexion',
  'billing.title': 'Facturation & Paiements',
  'billing.subtitle': 'Gérez votre abonnement, mode de paiement et factures',
  'billing.freeTrial': 'Essai gratuit',
  'billing.trialEnds': 'Fin de l\'essai',
  'billing.billingCycle': 'Cycle de facturation',
  'billing.cancelNotice': 'Votre abonnement se terminera le {date}. Vous pouvez le reprendre depuis le portail de facturation.',
  'billing.noSub': 'Aucun abonnement actif trouvé.',
  'billing.update': 'Modifier',
  'billing.noPayment': 'Aucun moyen de paiement enregistré',
  'billing.addCard': 'Ajouter une carte',
  'billing.noInvoicesYet': 'Aucune facture pour le moment',
  'billing.detailsDesc': 'Mettez à jour le nom de votre entreprise, email de facturation, numéro de TVA et adresse via le portail.',
  'billing.companyName': 'Nom de l\'entreprise',
  'billing.billingEmail': 'Email de facturation',
  'billing.vatNumber': 'Numéro de TVA',
  'billing.address': 'Adresse',
  'billing.editBilling': 'Modifier les détails de facturation',
  'billing.noDetailsFound': 'Aucun détail de facturation trouvé',
  'billing.setUp': 'Configurer',
  'billing.expires': 'Expire',
  'billing.status_active': 'Actif',
  'billing.status_trialing': 'Essai',
  'billing.status_past_due': 'En retard',
  'billing.status_canceled': 'Annulé',
  'billing.status_incomplete': 'Incomplet',
  'billing.status_unpaid': 'Impayé',
  'billing.interval_month': 'mois',
  'billing.interval_year': 'an',
  'billing.cancelsAtPeriodEnd': 'Annulation en fin de période',
  'billing.subActivated': 'Abonnement activé !',
  'billing.syncSlow': 'La synchronisation prend plus de temps que prévu. Veuillez rafraîchir.',
  'billing.subFound': 'Abonnement trouvé !',
  'billing.noSubYet': 'Aucun abonnement actif trouvé pour le moment.',
  'account.title': 'Informations du compte',
  'account.subtitle': 'Gérez votre email et mot de passe.',
  'account.changeEmail': 'Changer l\'email',
  'account.changeEmailDesc': 'Un email de vérification sera envoyé à votre nouvelle adresse.',
  'account.newEmail': 'Nouvel email',
  'account.confirmEmail': 'Confirmer l\'email',
  'account.currentPassword': 'Mot de passe actuel',
  'account.emailMismatch': 'Les adresses email ne correspondent pas',
  'account.emailVerificationSent': 'Un lien de vérification a été envoyé à votre nouvel email.',
  'account.changePassword': 'Changer le mot de passe',
  'account.changePasswordDesc': 'Vous devez vérifier votre mot de passe actuel.',
  'account.newPassword': 'Nouveau mot de passe',
  'account.confirmNewPassword': 'Confirmer le nouveau mot de passe',
  'account.savePassword': 'Mettre à jour',
  'account.passwordMin': 'Le mot de passe doit contenir au moins 8 caractères',
  'account.mismatch': 'Les mots de passe ne correspondent pas',
  'account.wrongPassword': 'Mot de passe actuel incorrect.',
  'account.updateFailed': 'Échec de la mise à jour. Veuillez réessayer.',
  'account.passwordChanged': 'Mot de passe mis à jour',
  'account.deleteTitle': 'Supprimer le compte',
  'account.deleteDesc': 'Supprimer définitivement votre compte et toutes les données.',
  'account.deleteButton': 'Supprimer mon compte',
  'account.deleteConfirmTitle': 'Êtes-vous absolument sûr ?',
  'account.deleteConfirmDesc': 'Cette action supprimera définitivement votre compte, organisation et toutes les données.',
  'account.deletePasswordLabel': 'Entrez votre mot de passe pour confirmer',
  'account.deleteConfirmButton': 'Tout supprimer',
  'account.deleteCancelButton': 'Annuler',
  'account.deleteSuccess': 'Compte supprimé.',

  // ── Excel Import ──
  'import.title': 'Importer les données',
  'import.subtitle': 'Téléchargez un fichier Excel ou CSV avec vos magasins et employés.',
  'import.downloadTemplate': 'Télécharger le modèle Excel',
  'import.dropzone': 'Glissez-déposez votre fichier ici, ou cliquez pour parcourir',
  'import.dropzoneHint': 'Accepte .xlsx ou .csv · Max 5 Mo',
  'import.parsing': 'Analyse du fichier...',
  'import.storesDetected': 'Magasins détectés',
  'import.employeesDetected': 'Employés détectés',
  'import.hours': 'Horaires',
  'import.staff': 'Personnel (Sem/WE)',
  'import.days': 'Disponibilité',
  'import.totalWeekly': 'total hebdomadaire',
  'import.importing': 'Importation en cours...',
  'import.success': 'Importation réussie !',
  'import.successMessage': 'Importation réussie. Vos données sont prêtes.',
  'import.failed': 'Échec de l\'importation',
  'import.confirmImport': 'Confirmer l\'importation',
  'import.cancel': 'Annuler',
  'import.tryAgain': 'Réessayer',
  'import.done': 'Terminé',
  'import.uploadData': 'Importer des données',
  'import.uploadHint': 'Ou importez depuis un fichier Excel/CSV',

  // ── Compliance Reports ──
  'comp.complianceSummary': 'Conformité',
  'comp.payrollExport': 'Export paie',
  'comp.legalRisk': 'Risque juridique',
  'comp.totalEmployees': 'Total employés',
  'comp.compliant': 'Conformes',
  'comp.warnings': 'Avertissements',
  'comp.critical': 'Action requise',
  'comp.employee': 'Employé',
  'comp.store': 'Établissement',
  'comp.contractH': 'Contrat (h/s)',
  'comp.scheduledH': 'Planifié (h)',
  'comp.regularH': 'Heures normales',
  'comp.overtimeH': 'Heures sup.',
  'comp.underhours': 'Heures manquantes',
  'comp.holidays': 'Congés',
  'comp.sickDays': 'Jours maladie',
  'comp.maxStreak': 'Série max',
  'comp.status': 'Statut',
  'comp.totalH': 'Total heures',
  'comp.totals': 'Totaux',
  'comp.allEmployees': 'Tous les employés',
  'comp.payrollDesc': 'Récapitulatif mensuel structuré des heures pour le traitement de la paie. Aucun calcul de salaire.',
  'comp.exportedCSV': 'Exporté en CSV',
  'comp.exportedExcel': 'Exporté en Excel',
  'comp.exportedExcelDesc': 'Vérifiez votre dossier Téléchargements pour ouvrir le fichier',
  'comp.exportedCSVDesc': 'Vérifiez votre dossier Téléchargements pour ouvrir le fichier',
  'excel.employee': 'Employé',
  'excel.store': 'Magasin',
  'excel.contractH': 'Contrat (h)',
  'excel.scheduledH': 'Planifié (h)',
  'excel.differenceH': 'Différence (h)',
  'excel.status': 'Statut',
  'excel.hoursVsContract': 'Heures vs Contrat',
  'excel.metric': 'Métrique',
  'excel.required': 'Requis',
  'excel.scheduled': 'Planifié',
  'excel.difference': 'Différence',
  'excel.allowance': 'Droit',
  'excel.approved': 'Approuvé',
  'excel.pending': 'En attente',
  'excel.remaining': 'Restant',
  'excel.leaveBalance': 'Solde de congés',
  'comp.highRisk': 'Risque élevé',
  'comp.mediumRisk': 'Risque moyen',
  'comp.lowRisk': 'Risque faible',
  'comp.allSeverities': 'Toutes les sévérités',
  'comp.exportRisk': 'Exporter le rapport de risque',
  'comp.riskType': 'Type de risque',
  'comp.dateWeek': 'Date / Semaine',
  'comp.severity': 'Sévérité',
  'comp.description': 'Description',
  'comp.noRisks': 'Aucun risque détecté',
  'comp.noRisksDesc': 'Tous les plannings sont conformes à la réglementation du travail ce mois-ci.',
  'comp.riskType48h': 'Heures hebdo > 48',
  'comp.riskTypeNoRest': 'Pas de jour de repos',
  'comp.riskType6Consec': '6+ jours consécutifs',
  'comp.riskTypeContract': 'Violation heures contrat',
  'comp.riskTypeMaxDays': 'Dépasse max jours/semaine',
  'comp.riskTypeAvail': 'Planifié hors disponibilité',
  'comp.riskDesc48h': '{hours}h planifiées (max 48h)',
  'comp.riskDescNoRest': '{days} jours travaillés sans repos',
  'comp.riskDesc6Consec': '{days} jours travaillés dans la semaine',
  'comp.riskDescContract': '{hours}h vs {contract}h contrat (+{pct}%)',
  'comp.riskDescMaxDays': '{days} jours travaillés (max {max})',
  'comp.riskDescAvail': 'Planifié un jour indisponible',
  'comp.severityHigh': 'Élevé',
  'comp.severityMedium': 'Moyen',
  'comp.severityLow': 'Faible',

  // ── Landing Page (restructured) ──
  'lp.hero.title': 'Planification et conformité pour les équipes par quarts',
  'lp.hero.subtitle': 'Créez des plannings, suivez les heures contractuelles, gérez les congés payés et prévenez les problèmes de conformité — tout en un.',
  'lp.hero.demo': 'Réserver une démo',
  'lp.hero.trust': 'Pour le commerce, l\'hôtellerie, les cliniques, les salles de sport, les franchises et les entreprises de services.',

  'lp.prob.title': 'La gestion des quarts ne devrait pas être si compliquée',
  'lp.prob1': 'Les tableurs manuels causent des erreurs',
  'lp.prob2': 'Les heures supplémentaires passent inaperçues',
  'lp.prob3': 'Le suivi des congés devient chaotique',
  'lp.prob4': 'Les risques de conformité augmentent',
  'lp.prob5': 'Les managers perdent des heures chaque semaine',
  'lp.prob.closing': 'Les petites erreurs de planification coûtent cher.',

  'lp.core.title': 'Tout ce qu\'il faut pour gérer vos équipes par quarts',
  'lp.core1.title': 'Créateur de plannings intelligent',
  'lp.core1.desc': 'Générez automatiquement des plannings équilibrés en respectant les heures contractuelles et la disponibilité.',
  'lp.core2.title': 'Contrôle des contrats et heures',
  'lp.core2.desc': 'Suivez les heures prévues vs réelles mensuellement et hebdomadairement.',
  'lp.core3.title': 'Suivi des congés payés et maladie',
  'lp.core3.desc': 'Appliquez automatiquement les seuils payés et détectez les abus.',
  'lp.core4.title': 'Suite de rapports de conformité',
  'lp.core4.desc': 'Générez des résumés de conformité mensuels instantanément.',
  'lp.core5.title': 'Fonctionne en 6 langues',
  'lp.core5.desc': 'Operota détecte automatiquement votre langue et adapte toute la plateforme. Anglais, italien, espagnol, français, allemand et portugais — aucune configuration nécessaire.',
  'lp.core6.title': 'Support multi-sites',
  'lp.core6.desc': 'Gérez plusieurs sites depuis un seul compte. Chaque site a ses propres plannings, règles de personnel et rapports de conformité.',
  'lp.core.subtitle': 'Le moteur de planification IA d\'Operota crée des plannings équilibrés qui respectent les heures contractuelles, la disponibilité des employés et la couverture d\'équipe automatiquement. Sans tableurs. Sans devinettes.',
  'lp.core.sectionTitle': 'Créez des plannings en quelques secondes',

  'lp.hero.langLine': 'Disponible en 6 langues — s\'adapte automatiquement à votre équipe',
  'lp.hero.noCard': 'Aucune carte requise',
  'lp.hero.trustBadge1': 'Conforme RGPD',
  'lp.hero.trustBadge2': 'Pas de frais par employé',
  'lp.hero.trustBadge3': 'Configuration en 15 minutes',

  'lp.pricing.annualSave': 'Facturé 990€/an — 2 mois gratuits',
  'lp.pricing.annualBadge': 'Économisez 198€/an',
  'lp.pricing.bestValue': 'Meilleur rapport qualité-prix',
  'lp.price6': 'Fonctionne en 6 langues',
  'lp.price7': 'Planification IA automatique',

  'lp.prob1.desc': 'Une mauvaise formule coûte des heures à corriger et risque de mal payer votre équipe.',
  'lp.prob2.desc': 'Les violations de contrat s\'accumulent silencieusement jusqu\'à devenir un problème juridique.',
  'lp.prob3.desc': 'Les conflits de congés et absences de dernière minute créent le chaos pour toute équipe.',
  'lp.prob4.desc': 'Sans vérifications automatisées, vous êtes à un audit d\'une amende coûteuse.',
  'lp.prob5.desc': 'Construire les plannings manuellement est un travail répétitif qui devrait prendre des secondes.',

  'lp.aud1.desc': 'Magasins, boutiques et chaînes de mode',
  'lp.aud2.desc': 'Équipes de salle et de cuisine',
  'lp.aud3.desc': 'Cliniques, salles de sport et cabinets de thérapie',
  'lp.aud4.desc': 'Centres de fitness et établissements de loisirs',
  'lp.aud5.desc': 'Franchises et opérateurs multi-sites',
  'lp.aud6.desc': 'Équipes terrain et entreprises de services',

  'lp.section5.title': 'Suivez les heures contractuelles et la conformité',
  'lp.section5.subtitle': 'Operota surveille automatiquement les heures contractuelles par rapport aux quarts planifiés, signale les violations d\'heures supplémentaires et génère des rapports de conformité.',
  'lp.section5.card1.title': 'Planification IA des quarts',
  'lp.section5.card1.desc': 'Notre créateur de plannings utilise l\'IA pour générer des plannings équilibrés qui préviennent les heures supplémentaires et respectent la disponibilité.',
  'lp.section5.card2.title': 'Rapports de conformité',
  'lp.section5.card2.desc': 'Générez des résumés mensuels montrant les heures travaillées vs contractuelles, les soldes de congés et les violations — exportables en Excel.',

  'lp.early.title': 'Soyez les premiers à partager votre expérience',
  'lp.early.subtitle': 'Rejoignez notre programme d\'accès anticipé.',

  'footer.solutions': 'Solutions',

  'lp.faq.businesses.q': 'Quels types d\'entreprises utilisent Operota ?',
  'lp.faq.businesses.a': 'Toute entreprise avec des travailleurs par quarts — commerces, restaurants, salles de sport, cliniques, entreprises de nettoyage, agences de sécurité et plus.',
  'lp.faq.setup.q': 'Combien de temps prend la mise en place ?',
  'lp.faq.setup.a': 'La plupart des équipes sont opérationnelles en moins de 15 minutes. Ajoutez votre site, importez votre personnel et lancez la génération automatique.',
  'lp.faq.selfService.q': 'Les employés peuvent-ils voir leurs propres plannings ?',
  'lp.faq.selfService.a': 'Le libre-service employé est dans notre feuille de route. Actuellement, les plannings peuvent être exportés en PDF ou Excel.',
  'lp.faq.perEmployee.q': 'Y a-t-il un frais par employé ?',
  'lp.faq.perEmployee.a': 'Non. Operota est un forfait de 99€ par mois quel que soit le nombre d\'employés ou de sites.',
  'lp.faq.afterTrial.q': 'Que se passe-t-il après l\'essai de 30 jours ?',
  'lp.faq.afterTrial.a': 'Vous passez au forfait mensuel à 99€/mois. Nous vous prévenons avant la fin de votre essai et vous pouvez annuler à tout moment.',
  'lp.faq.languages.q': 'Dans quelles langues est-il disponible ?',
  'lp.faq.languages.a': 'Anglais, italien, espagnol, français, allemand et portugais. Il détecte automatiquement la langue de votre navigateur.',

  'lp.pricing.title': 'Tarification simple et professionnelle',
  'lp.pricing.noFees': 'Pas de frais cachés. Pas de facturation par employé.',
  'lp.price1': 'Employés illimités',
  'lp.price2': 'Support multi-sites',
  'lp.price3': 'Rapports de conformité complets',
  'lp.price4': 'Suivi des congés et absences',
  'lp.price5': 'Essai gratuit de 30 jours',

  'lp.aud.title': 'Conçu pour les entreprises par quarts en croissance',
  'lp.aud.subtitle': 'Si vous gérez des employés par quarts ou sur plusieurs sites, Operota garde votre équipe organisée et conforme.',
  'lp.aud1': 'Commerces',
  'lp.aud2': 'Groupes hôteliers',
  'lp.aud3': 'Cliniques',
  'lp.aud4': 'Salles de sport',
  'lp.aud5': 'Franchises',
  'lp.aud6': 'Entreprises de services',

  'lp.faq.payroll.q': 'Gère-t-il la paie ?',
  'lp.faq.payroll.a': 'Non. Operota fournit des exports mensuels structurés d\'heures prêts pour votre comptable ou système de paie, mais ne calcule ni salaires, ni taxes, ni déductions.',
  'lp.faq.contracts.q': 'Respecte-t-il les heures contractuelles ?',
  'lp.faq.contracts.a': 'Oui. Le système respecte automatiquement les heures contractuelles de chaque employé lors de la génération des plannings.',
  'lp.faq.excel.q': 'Puis-je importer depuis Excel ?',
  'lp.faq.excel.a': 'Oui. Importez sites et employés depuis Excel ou CSV, ou utilisez notre import IA pour configurer votre entreprise.',
  'lp.faq.security.q': 'Mes données sont-elles sécurisées ?',
  'lp.faq.security.a': 'Vos données sont chiffrées en transit et au repos, hébergées dans l\'UE, avec une infrastructure cloud de niveau entreprise et conformité RGPD stricte.',
  'lp.faq.stores.q': 'Puis-je gérer plusieurs sites ?',
  'lp.faq.stores.a': 'Absolument. Operota supporte un nombre illimité de sites. Gérez plannings, effectifs et rapports depuis un seul tableau de bord.',

  'settings.tabLeave': 'Politique de congés',
  'leave.globalPolicy': 'Politique de congés globale',
  'leave.globalPolicyDesc': 'Définir les allocations de congés payés par défaut pour tous les employés.',
  'leave.paidHolidayDays': 'Jours de congés payés annuels',
  'leave.paidSickDays': 'Jours de maladie payés annuels',
  'leave.hoursPerDay': 'Heures de travail par jour',
  'leave.savePolicy': 'Enregistrer la politique',
  'leave.policySaved': 'Politique de congés enregistrée',
  'leave.bulkActions': 'Actions en masse',
  'leave.bulkActionsDesc': 'Appliquer les changements de politique aux employés ou réinitialiser les compteurs annuels.',
  'leave.applyToAll': 'Appliquer à tous les employés',
  'leave.confirmApply': 'Appliquer la politique à tous ?',
  'leave.confirmApplyDesc': 'Cela mettra à jour les allocations de congés et maladie pour tous les employés sans remplacement personnalisé.',
  'leave.appliedToAll': 'Politique appliquée à tous les employés',
  'leave.resetCounters': 'Réinitialiser les compteurs',
  'leave.confirmReset': 'Réinitialiser les compteurs de congés ?',
  'leave.confirmResetDesc': 'Les soldes de congés sont calculés à partir des demandes enregistrées. Filtrez par année pour voir les soldes actuels.',
  'leave.resetInfo': 'Les soldes sont calculés à partir des enregistrements datés. Filtrez par année.',
  'leave.overridePolicy': 'Remplacer la politique par défaut',
  'leave.paidHolH': 'Congés payés (h)',
  'leave.paidSickH': 'Maladie payée (h)',
  'leave.unpaidH': 'Congé non payé (h)',
  'leave.holAllowance': 'Allocation congés',
  'leave.sickAllowance': 'Allocation maladie',
  'leave.used': 'utilisé',
  'leave.remainHol': 'Congés rest.',
  'leave.remainSick': 'Maladie rest.',
  'comp.contractCountedH': 'Heures comptées',
  'comp.contractCountedTooltip': 'Heures planifiées + congés payés. Pour la conformité contractuelle.',
  'comp.workingDays': 'jours ouvrables',
  'comp.day': 'jour',
  'comp.expectedH': 'Attendu',
  'comp.expectedTooltip': 'Calculé à partir des heures de contrat × jours ouvrables dans la période. Comptage exact.',
  'comp.scheduledTooltip': 'Heures de travail réelles dans la période.',
  'comp.underhoursVsContract': 'Sous-heures',
  'comp.underhoursTooltip': 'Heures comptées − Heures attendues. Négatif = insuffisant.',
  'comp.complianceWeekly': 'Hebdomadaire',
  'comp.complianceMonthly': 'Mensuel',
  'comp.complianceReport': 'Rapport de conformité',
  'comp.period': 'Période',
  'comp.print': 'Imprimer',
  'comp.savePDF': 'Enregistrer en PDF',
  'comp.printOrSavePDF': 'Imprimer / Enregistrer en PDF',
  'comp.printPDFHint': 'Pour enregistrer en PDF, sélectionnez « Enregistrer au format PDF » comme destination dans la boîte de dialogue d\'impression.',
  'comp.generatingPDF': 'Génération du PDF...',
  'comp.backToReports': 'Retour aux rapports',
  'comp.totalOvertime': 'Heures sup. totales',
  'comp.totalUnderhours': 'Sous-heures totales',
  'comp.totalUnpaidLeave': 'Congés non payés total',
  'comp.generatedOn': 'Généré le',
  'comp.generatedBy': 'Généré par',
  'comp.organization': 'Organisation',
  'comp.reportPeriod': 'Période du rapport',
  'comp.hoursBreakdown': 'Détail des heures',
  'comp.compliance': 'Conformité',
  'comp.leaveBalanceYear': 'Solde congés (Année)',
  'comp.leaveBalanceAnnual': 'Solde congés (Annuel)',
  'comp.scheduled': 'Planifié (travaillé)',
  'comp.paidHoliday': 'Congé payé',
  'comp.paidSick': 'Maladie payée',
  'comp.unpaidLeave': 'Congé non payé',
  'comp.contract': 'Contrat',
  'comp.expected': 'Attendu',
  'comp.expectedPeriod': 'Attendu pour la période',
  'comp.counted': 'Compté',
  'comp.variance': 'Écart',
  'comp.worked': 'Travaillé',
  'comp.holidayRemaining': 'Congés restants',
  'comp.sickRemaining': 'Maladie restante',
  'comp.days': 'jours',
  'comp.notes': 'Notes',
  'comp.statusOk': 'Conforme',
  'comp.statusWarning': 'Écart mineur',
  'comp.statusCritical': 'Action requise',
  'comp.allClear': 'Tous les employés conformes',
  'comp.varianceExplainer': 'Les heures contractuelles peuvent varier légèrement en raison des horaires d\'ouverture, de la disponibilité et de la durée minimale des shifts. Les petits écarts (±2h) sont normaux.',
  'comp.toleranceOk': 'Dans la tolérance',
  'comp.toleranceWarning': 'À vérifier',
  'comp.toleranceAction': 'Action requise',
};

const es: Record<string, string> = {
  'header.signIn': 'Iniciar sesión', 'header.getStarted': 'Empezar',
  'hero.badge': 'Para equipos retail multi-tienda',
  'hero.title1': 'Planificación IA de personal', 'hero.title2': 'Que realmente equilibra tus tiendas',
  'hero.subtitle': 'Diseñado para equipos retail en crecimiento que necesitan horarios conformes con contratos, equilibrados por IA, sin el caos.',
  'hero.cta': 'Prueba gratuita de 30 días', 'hero.signIn': 'Iniciar sesión',
  'hero.trust1': 'Prueba gratuita de 30 días', 'hero.trust2': 'Configuración en minutos', 'hero.trust3': 'Sin compromiso',
  'hero.euLine': 'Alojado en la UE. Cumple con el RGPD. Diseñado para contratos laborales europeos.',
  'trust.title': 'Diseñado para operaciones retail modernas',
  'trust.eu': 'Alojamiento UE', 'trust.gdpr': 'Cumple RGPD', 'trust.secure': 'Infraestructura cloud segura', 'trust.encrypted': 'Cifrado en tránsito y en reposo',
  'trust.origin': 'Diseñado y operado desde Italia.',
  'screenshot.caption': 'Horario auto-equilibrado generado en un clic.', 'screenshot.sub': 'Respeta contratos. Evita horas extra. Elimina huecos de cobertura.',
  'features.title': 'Todo lo que necesitas para horarios más inteligentes', 'features.subtitle': 'Herramientas orientadas a resultados para gerentes retail ocupados.',
  'feature.scheduling.title': 'Planificación inteligente', 'feature.scheduling.desc': 'Horarios generados por IA que equilibran automáticamente las horas y previenen huecos.',
  'feature.employees.title': 'Gestión de empleados', 'feature.employees.desc': 'Controla contratos, disponibilidad y ausencias en un panel centralizado.',
  'feature.stores.title': 'Soporte multi-tienda', 'feature.stores.desc': 'Gestiona todas las ubicaciones desde un centro de control inteligente.',
  'feature.absences.title': 'Control de ausencias', 'feature.absences.desc': 'Registra bajas y permisos. Siempre sabrás quién está disponible.',
  'feature.holidays.title': 'Gestión de vacaciones', 'feature.holidays.desc': 'Gestiona festivos y solicitudes automáticamente.',
  'feature.reports.title': 'Informes y exportaciones', 'feature.reports.desc': 'Genera informes y exporta a Excel.',
  'midCta.title': 'Deja de crear horarios manualmente.', 'midCta.subtitle': 'Únete a equipos retail que cambian a la gestión IA de turnos.', 'midCta.button': 'Prueba gratuita',
  'stats.shifts': 'Turnos planificados', 'stats.teams': 'Equipos activos', 'stats.uptime': 'Disponibilidad', 'stats.rating': 'Valoración',
  'how.title': 'Cómo funciona', 'how.subtitle': 'En funcionamiento en minutos.',
  'how.step1.title': 'Añade tus tiendas', 'how.step1.desc': 'Configura tus ubicaciones y horarios.',
  'how.step2.title': 'Añade empleados', 'how.step2.desc': 'Introduce tu equipo con contratos y disponibilidad.',
  'how.step3.title': 'Crea los horarios', 'how.step3.desc': 'Construye horarios con sugerencias inteligentes.',
  'pricing.title': 'Precios simples y transparentes', 'pricing.subtitle': 'Prueba gratuita de 30 días. Sin tarjeta.',
  'pricing.perMonth': '/mes', 'pricing.afterTrial': 'Después de tu prueba gratuita',
  'pricing.cta': 'Prueba gratuita', 'pricing.included': 'Todo incluido:',
  'pricing.item1': 'Tiendas ilimitadas', 'pricing.item2': 'Empleados ilimitados',
  'pricing.item3': 'Equilibrio IA incluido', 'pricing.item4': 'Control de ausencias y vacaciones',
  'pricing.item5': 'Planificación conforme a contratos', 'pricing.item6': 'Soporte prioritario',
  'pricing.badge': 'Ideal para equipos en crecimiento', 'pricing.reassurance': 'Cancela cuando quieras. Sin contratos a largo plazo.',
  'pricing.target': 'Para equipos retail en crecimiento y negocios multi-tienda.',
  'pricing.monthly': 'Mensual', 'pricing.annual': 'Anual', 'pricing.comingSoon': 'Próximamente',
  'cta.title': '¿Listo para simplificar tu planificación?', 'cta.subtitle': 'Únete a gerentes que dejaron las hojas de cálculo.',
  'cta.button': 'Comenzar prueba gratuita',
  'footer.features': 'Funciones', 'footer.howItWorks': 'Cómo funciona', 'footer.signIn': 'Iniciar sesión',
  'footer.rights': '© {year} Operota. Todos los derechos reservados.',
  'footer.product': 'Producto', 'footer.legal': 'Legal',
  'footer.terms': 'Términos y Condiciones', 'footer.privacy': 'Política de Privacidad',
  'footer.cookies': 'Política de Cookies', 'footer.dpa': 'DPA', 'footer.refunds': 'Reembolsos y Cancelación',
  'pain.title': '¿Aún gestionando turnos de forma difícil?',
  'pain.manual.title': 'Planificación manual', 'pain.manual.desc': 'Hacer turnos en Excel o papel consume horas cada semana.',
  'pain.gaps.title': 'Huecos de cobertura', 'pain.gaps.desc': 'Las bajas de última hora dejan tiendas sin personal.',
  'pain.overtime.title': 'Errores de horas extra', 'pain.overtime.desc': 'Las horas del contrato se exceden sin aviso.',
  'pain.cta.text': 'Deja que Operota se encargue', 'pain.cta.button': 'Comenzar prueba gratuita',
  'compare.title': '¿Por qué Operota en lugar de Excel?',
  'compare.excel.1': 'Planificación manual', 'compare.excel.2': 'Sin seguimiento de contratos',
  'compare.excel.3': 'Sin vista multi-tienda', 'compare.excel.4': 'Sin seguimiento de vacaciones',
  'compare.operota.1': 'Equilibrio automático con IA', 'compare.operota.2': 'Horarios que respetan contratos',
  'compare.operota.3': 'Panel multi-tienda', 'compare.operota.4': 'Gestión de ausencias integrada',
  'testimonials.title': 'La confianza de equipos retail en crecimiento',
  'testimonial.1.quote': 'Operota nos ahorra 6 horas semanales. El equilibrio IA es revolucionario.', 'testimonial.1.name': 'Marco Rossi', 'testimonial.1.role': 'Gerente Retail – Cadena de moda',
  'testimonial.2.quote': 'Dejamos de perder turnos al cambiar. Los huecos son cosa del pasado.', 'testimonial.2.name': 'Emma Clarke', 'testimonial.2.role': 'Propietaria multi-tienda – Grupo boutique',
  'testimonial.3.quote': 'Por fin un software que respeta las horas de contrato y mantiene las tiendas en conformidad.', 'testimonial.3.name': 'Daniel Weber', 'testimonial.3.role': 'Jefe de Operaciones – Cadena retail',
  'faq.title': 'Preguntas Frecuentes',
  'faq.cancel.q': '¿Puedo cancelar en cualquier momento?', 'faq.cancel.a': 'Sí, puedes cancelar tu suscripción en cualquier momento sin penalizaciones. Sin contratos a largo plazo ni tarifas ocultas. Tus datos permanecen accesibles hasta el final del período de facturación.',
  'faq.trial.q': '¿Hay una prueba gratuita?', 'faq.trial.a': 'Sí. Cada nueva cuenta obtiene una prueba gratuita completa de 30 días con acceso a todas las funciones, incluida la planificación IA. Sin tarjeta.',
  'faq.stores.q': '¿Soporta múltiples tiendas?', 'faq.stores.a': 'Absolutamente. Operota soporta tiendas ilimitadas dentro de una misma organización. Gestiona horarios, personal e informes desde un solo panel.',
  'faq.contracts.q': '¿Respeta los contratos de los empleados?', 'faq.contracts.a': 'Sí. Operota respeta automáticamente los límites de horas contractuales al generar horarios. La IA previene las horas extra.',
  'faq.security.q': '¿Están seguros mis datos?', 'faq.security.a': 'Tus datos están cifrados en tránsito y en reposo, y alojados íntegramente en la UE. Infraestructura cloud de nivel empresarial con estricto cumplimiento del RGPD.',
  'finalCta.title': 'Deja de gestionar turnos en hojas de cálculo.',
  'finalCta.subtitle': 'Comienza tu prueba gratuita de 30 días y toma el control de tu planificación.',
  'finalCta.button': 'Comenzar prueba gratuita', 'finalCta.signIn': 'Iniciar sesión',

  'nav.dashboard': 'Panel', 'nav.stores': 'Tiendas', 'nav.employees': 'Empleados',
  'nav.schedule': 'Horarios', 'nav.holidays': 'Vacaciones', 'nav.absences': 'Ausencias',
  'nav.reports': 'Informes', 'nav.signOut': 'Cerrar sesión', 'nav.settings': 'Configuración',

  'auth.back': 'Volver', 'auth.title': 'Operota', 'auth.subtitle': 'Gestiona tus horarios fácilmente',
  'auth.signIn': 'Iniciar sesión', 'auth.signUp': 'Registrarse', 'auth.email': 'Correo electrónico', 'auth.password': 'Contraseña',
  'auth.signingIn': 'Iniciando sesión...', 'auth.creatingAccount': 'Creando cuenta...',
  'auth.createAccount': 'Crear cuenta', 'auth.loading': 'Cargando...',
  'auth.checkEmail': 'Revisa tu correo para verificar tu cuenta.',
  'auth.invalidCredentials': 'Correo o contraseña incorrectos.',
  'auth.emailNotConfirmed': 'Verifica tu correo electrónico.',
  'auth.alreadyRegistered': 'Ya existe una cuenta con este correo.',
  'auth.unexpectedError': 'Ocurrió un error inesperado.',
  'auth.invalidEmail': 'Introduce un correo electrónico válido',
  'auth.passwordMin': 'La contraseña debe tener al menos 6 caracteres',

  'dash.title': 'Panel', 'dash.weekOf': 'Semana del {start} - {end}',
  'dash.today': 'Hoy',
  'dash.employeesWorking': 'empleados trabajando', 'dash.workingToday': 'trabajando hoy',
  'dash.onShiftToday': 'en turno hoy',
  'dash.pendingRequests': 'solicitudes pendientes', 'dash.requestsToReview': 'solicitudes por revisar',
  'dash.staffingGaps': 'problemas de personal', 'dash.issuesToday': 'problemas hoy',
  'dash.noIssuesToday': 'Sin problemas hoy', 'dash.noIssues': 'Sin problemas',
  'dash.totalStores': 'Tiendas', 'dash.totalEmployees': 'Empleados',
  'dash.shiftsThisWeek': 'Turnos esta semana', 'dash.staffingAlerts': 'Alertas de personal',
  'dash.thisWeek': 'Esta semana', 'dash.upcomingIssues': 'Esta semana',
  'dash.actionRequired': 'Acción requerida', 'dash.insights': 'Información',
  'dash.understaffed': '{count} día(s) con falta de personal',
  'dash.missingStaff': '{count} personal faltante', 'dash.moreIssues': '...y {count} más',
  'dash.viewSchedule': 'Ver horarios', 'dash.stores': 'Tiendas', 'dash.storesDesc': 'Gestionar ubicaciones',
  'dash.pendingHolidayRequests': '{count} solicitud(es) de vacaciones pendiente(s)',
  'dash.pendingHolidayDesc': 'Aprobar o rechazar solicitudes', 'dash.reviewRequests': 'Revisar',
  'dash.nearOvertime': '{count} empleado(s) cerca de horas extra',
  'dash.employeeOverContract': 'empleado por encima del contrato',
  'dash.employeesOverContract': 'empleados por encima del contrato',
  'dash.approachingLimit': 'acercándose al límite', 'dash.more': 'más',
  'dash.allClear': 'Todo en orden — sin problemas esta semana',
  'dash.weekGlance': 'Esta semana de un vistazo',
  'dash.ofContractHours': 'de {hours}h contratadas',
  'dash.storesFullyCovered': 'Tiendas cubiertas',
  'dash.staffingGapsWeek': 'Faltas de personal esta semana',
  'dash.coverageOverview': 'Resumen de cobertura',
  'dash.manageStores': 'Gestionar tiendas', 'dash.employees': 'Empleados', 'dash.employeesDesc': 'Ver y gestionar personal',
  'dash.manageEmployees': 'Gestionar empleados', 'dash.schedule': 'Horarios', 'dash.scheduleDesc': 'Crear y gestionar horarios',
  'dash.openSchedule': 'Abrir horarios',

  'stores.title': 'Tiendas', 'stores.subtitle': 'Gestiona tus ubicaciones, personal y horarios',
  'stores.add': 'Añadir tienda', 'stores.edit': 'Editar tienda', 'stores.addNew': 'Nueva tienda',
  'stores.editDesc': 'Actualizar detalles', 'stores.addDesc': 'Añadir nueva ubicación',
  'stores.name': 'Nombre', 'stores.address': 'Dirección', 'stores.staffingReq': 'Personal requerido por día',
  'stores.timeBlockStaffing': 'Personal por bloque horario', 'stores.timeBlockDesc': 'Define cuánto personal se necesita por período.', 'stores.addBlock': 'Añadir', 'stores.noBlocks': 'Sin bloques — se usa el requisito diario.', 'stores.staff': 'pers.', 'stores.colTimeBlocks': 'Bloques',
  'stores.create': 'Crear tienda', 'stores.update': 'Actualizar', 'stores.loading': 'Cargando tiendas...',
  'stores.empty': 'Sin tiendas. Añade tu primera ubicación.', 'stores.colName': 'Nombre',
  'stores.colAddress': 'Dirección', 'stores.colHours': 'Horarios', 'stores.colStaffing': 'Personal',
  'stores.colActions': 'Acciones', 'stores.notSet': 'No definido',
  'stores.breakPolicy': 'Política de descanso', 'stores.breakPolicyDesc': 'Configura las deducciones automáticas de descanso para la nómina.', 'stores.breakIfShiftExceeds': 'Aplicar descanso si el turno supera', 'stores.hours': 'horas', 'stores.breakDuration': 'Duración del descanso', 'stores.addSecondBreak': 'Añadir segundo descanso para turnos largos', 'stores.storeClosesDuringBreak': 'La tienda cierra durante el descanso', 'stores.colBreaks': 'Política de descanso', 'stores.breakSummary': '{duration}min descanso ≥{threshold}h',
  'stores.breakSummaryPerDay': 'Duraciones de descanso por día configuradas', 'stores.breakPresetHint': 'Haz clic en un preajuste para aplicar a todos los días, o personaliza por día abajo.', 'stores.perDayBreakDuration': 'Duración del descanso por día',
  'stores.breakExceedsLimit': 'El descanso supera el 30% de un turno de {hours}h (máx {max}min)',
  'stores.perDayConfig': 'Configuración por día',
  'stores.block': 'bloque',
  'stores.blocks': 'bloques',

  'emp.title': 'Empleados', 'emp.subtitle': 'Gestionar personal, disponibilidad y asignaciones',
  'emp.hPerWeek': 'h/semana',
  'emp.add': 'Añadir empleado', 'emp.edit': 'Editar empleado', 'emp.addNew': 'Nuevo empleado',
  'emp.editDesc': 'Actualizar detalles', 'emp.addDesc': 'Añadir nuevo miembro',
  'emp.name': 'Nombre', 'emp.email': 'Correo', 'emp.phone': 'Teléfono', 'emp.contractHours': 'Horas/semana',
  'emp.primaryStore': 'Tienda principal', 'emp.selectStore': 'Seleccionar tienda',
  'emp.dayAvailability': 'Disponibilidad', 'emp.canWorkAt': 'Puede trabajar en',
  'emp.addBtn': 'Añadir empleado', 'emp.updateBtn': 'Actualizar',
  'emp.search': 'Buscar por nombre, correo, teléfono...', 'emp.nameAZ': 'Nombre A–Z', 'emp.nameZA': 'Nombre Z–A',
  'emp.hoursUp': 'Horas ↑', 'emp.hoursDown': 'Horas ↓', 'emp.byStore': 'Por tienda', 'emp.allStores': 'Todas las tiendas',
  'emp.loading': 'Cargando empleados...', 'emp.empty': 'Sin empleados. Añade tu primer miembro.',
  'emp.colName': 'Nombre', 'emp.colContact': 'Contacto', 'emp.colHours': 'Horas',
  'emp.colStore': 'Tienda', 'emp.colAvailability': 'Disponibilidad', 'emp.colActions': 'Acciones',

  'sched.title': 'Horarios', 'sched.weekOf': 'Semana del {start} - {end}',
  'sched.allStores': 'Todas las tiendas', 'sched.selectStore': 'Seleccionar tienda',
  'sched.autoGenerate': 'Generar automáticamente', 'sched.export': 'Exportar', 'sched.clearAll': 'Borrar todo',
  'sched.print': 'Imprimir', 'sched.printAll': 'Imprimir todas', 'sched.printStore': 'Imprimir {name}',
  'sched.prevWeek': 'Semana anterior', 'sched.nextWeek': 'Semana siguiente', 'sched.today': 'Hoy',
  'sched.conflicts': 'Conflictos de horario', 'sched.andMore': '...y {count} más',
  'sched.addStoresFirst': 'Añade tiendas para empezar.', 'sched.clear': 'Borrar',
  'sched.holiday': 'Vacaciones', 'sched.pendingHoliday': 'Vacaciones pendientes', 'sched.pendingHolidays': '{count} solicitudes de vacaciones pendientes', 'sched.add': 'Añadir',
  'sched.pendingTimeOff': 'Ausencia pendiente',
  'sched.conflictsDetected': '{count} conflictos turno-vacaciones',
  'sched.includePending': 'Incluir pendientes',
  'sched.resolveConflicts': 'Corregir automáticamente',
  'sched.approvedConflict': 'Vacaciones aprobadas',
  'sched.pendingConflict': 'Solicitud pendiente',
  'sched.resolveTitle': 'Corregir conflictos automáticamente',
  'sched.resolveDesc': 'Se realizarán los siguientes cambios para resolver los conflictos:',
  'sched.willResolve': '{count} conflictos se corregirán:',
  'sched.cannotResolve': '{count} requieren ajuste manual:',
  'sched.noReplacement': 'Sin reemplazo disponible',
  'sched.removeShift': 'Eliminar turno',
  'sched.applyFixes': 'Aplicar {count} correcciones',
  'sched.resolveSuccess': '{count} conflictos resueltos',
  'sched.resolvePartial': '{count} aún requieren ajuste manual',
  'sched.editShift': 'Editar turno', 'sched.addShift': 'Añadir turno',
  'sched.editShiftDesc': 'Actualizar detalles', 'sched.addShiftDesc': 'Programar un turno',
  'sched.employee': 'Empleado', 'sched.selectEmployee': 'Seleccionar empleado',
  'sched.store': 'Tienda', 'sched.date': 'Fecha', 'sched.startTime': 'Hora inicio', 'sched.endTime': 'Hora fin',
  'sched.updateShift': 'Actualizar turno', 'sched.addShiftBtn': 'Añadir turno',
  'sched.clearConfirm': '¿Borrar todos los turnos de esta semana?',
  'sched.clearStoreConfirm': '¿Borrar todos los turnos de {name} esta semana?',
  'sched.strategyStandard': 'Estándar',
  'sched.strategyCoverage': 'Prioridad cobertura',
  'sched.strategyBalanced': 'Equilibrado',
  'sched.strategyFullCoverage': 'Cobertura total',
  'sched.balancedDesc': 'Optimiza la equidad y el cumplimiento contractual manteniendo la cobertura.',
  'sched.coverageDesc': 'Prioriza la dotación completa. Puede permitir horas extra limitadas.',
  'sched.recommended': 'Recomendado',
  'sched.coverageWarning': 'Prioriza la cobertura completa. Puede causar horas extra limitadas.',
  'sched.strategyLabel': 'Estrategia de planificación',
  'sched.defaultStrategy': 'Estrategia por defecto',
  'sched.defaultStrategyDesc': 'Establecer la estrategia predeterminada para su organización.',
  'sched.strategySaved': 'Estrategia por defecto actualizada',
  'sched.shiftAdded': 'Turno añadido correctamente',
  'sched.shiftUpdated': 'Turno actualizado correctamente',
  'sched.shiftRemoved': 'Turno eliminado correctamente',
  'sched.shiftsSwapped': 'Turnos intercambiados correctamente',
  'sched.shiftMoved': 'Turno movido correctamente',
  'sched.errorSwap': 'Error al intercambiar turnos',
  'sched.errorMoveShift': 'Error al mover turno',
  'sched.cannotSwapStores': 'No se pueden intercambiar turnos entre diferentes tiendas',
  'sched.swapHint': 'Soltar para intercambiar',
  'sched.alreadyScheduledSwap': 'El empleado ya está programado ese día',
  'sched.availabilityOverrideTitle': 'Anulación de disponibilidad',
  'sched.availabilityOverrideDesc': '{name} solo está disponible los {days}. ¿Aprobar este cambio de turno?',
  'sched.approve': 'Aprobar',
  'sched.noDaysAvailable': 'ningún día',
  'sched.errorAddShift': 'Error al añadir turno',
  'sched.errorUpdateShift': 'Error al actualizar turno',
  'sched.errorRemoveShift': 'Error al eliminar turno',
  'sched.noShiftsToClear': 'No hay turnos que borrar',
  'sched.clearedShifts': '{count} turno(s) borrado(s)',
  'sched.clickUndo': 'Haga clic en deshacer para restaurarlos.',
  'sched.undo': 'Deshacer',
  'sched.restoredShifts': '{count} turno(s) restaurado(s)',
  'sched.errorRestore': 'Error al restaurar turnos',
  'sched.errorClear': 'Error al borrar el horario',
  'sched.generatedShifts': '{count} turnos generados correctamente',
  'sched.errorGenerate': 'Error al generar horario',
  'sched.validationError': 'Error de validación',
  'sched.cannotAssign': 'No se puede asignar turno',
  'sched.alreadyScheduled': '{name} ya tiene un turno en esta fecha.',
  'sched.onHoliday': '{name} tiene vacaciones aprobadas en esta fecha.',
  'sched.onLeave': '{name} está de {type} en esta fecha.',
  'sched.storeClosed': 'La tienda está cerrada este día.',
  'sched.shiftOutsideHours': 'El turno está fuera del horario de la tienda',
  'sched.shiftOutsideHoursWarning': 'Este turno se extiende fuera del horario de la tienda. El tiempo extra se registrará como horas extras en los informes.',
  'sched.significantOvertimeTitle': 'Turno significativamente fuera del horario de la tienda',
  'sched.significantOvertimeDesc': 'Este turno se extiende más de 60 minutos fuera del horario de la tienda. ¿Estás seguro de que deseas guardarlo?',
  'sched.saveAnyway': 'Guardar de todos modos',
  'sched.shiftPaidBreak': '{paid} pagado ({break} descanso)',
  'sched.breakDeducted': '{duration} descanso deducido de las horas pagadas',
  'sched.bannerGenerated': 'Horario generado — {employees} empleados programados, {hours}h contractuales respetadas, {gaps}, turnos distribuidos equitativamente.',
  'sched.bannerNoGaps': 'sin faltas de cobertura',
  'sched.bannerGaps': '{count} falta(s) de cobertura',
  'sched.bannerNoIssues': 'Sin problemas encontrados',
  'sched.bannerReviewGaps': '{count} falta(s) de cobertura a revisar →',
  'sched.exportSuccess': 'Horario exportado correctamente',
  'sched.coverageDiag': 'Diagnóstico de cobertura',
  'sched.tbCoverageDiag': 'Problemas de cobertura por bloque',
  'sched.moreGaps': '...y {count} huecos más',
  'sched.coverageMet': 'Cobertura: {staffed} / {required} ✔',
  'sched.coverageShort': 'Cobertura: {staffed} / {required} ✘',
  'sched.coverageOver': 'Cobertura: {staffed} / {required} ⚠',
  'sched.missingStaff': 'Faltan {count} para {time}',
  'sched.requirements': 'Requisitos',
  'sched.shifts': 'Turnos',
  'sched.diagNoAvail': 'Sin disponibilidad ({count})',
  'sched.diagContractExh': 'Contrato agotado ({count})',
  'sched.diagMaxDays': 'Días máx. alcanzados ({count})',
  'sched.diagNoAssigned': 'Ningún empleado asignado a esta tienda',
  'sched.diagNoEligible': 'Ningún empleado elegible',
  'sched.diagNoReq': 'Sin requisito de personal configurado',
  'sched.conflictOverHours': '{name} está programado para {hours}h pero el contrato es de {contract}h',
  'sched.conflictUnavailable': '{name} no está disponible el {day}',
  'sched.conflictWrongStore': '{name} no está asignado a {store}',
  'sched.exportEmployee': 'Empleado',
  'sched.exportStaffing': 'Dotación',
  'sched.exportReqSched': 'Requerido/Programado',
  'abs.typeSick': 'Enfermo',
  'abs.typeEmergency': 'Emergencia',
  'sched.copyWeek': 'Copiar semana',
  'sched.copyWeekTitle': 'Copiar horario semanal',
  'sched.copyWeekDesc': 'Duplicar todos los turnos de una semana a otra.',
  'sched.sourceWeek': 'Semana origen',
  'sched.targetWeek': 'Semana destino',
  'sched.copyWeekConfirm': 'Copiar horario',
  'sched.copyWeekSuccess': 'Horario copiado correctamente.',
  'sched.copyWeekError': 'Error al copiar el horario',
  'sched.copyWeekNoShifts': 'No se encontraron turnos en la semana origen.',
  'sched.copyWeekReplace': 'Esta semana ya tiene {count} turno(s). ¿Reemplazar el horario existente?',
  'sched.copyWeekReplaceBtn': 'Reemplazar y copiar',
  'sched.copyWeekCopying': 'Copiando...',
  'sched.selectTargetWeek': 'Seleccionar semana destino',
  'sched.copyNextWeek': 'Semana siguiente',
  'sched.copyWeekAfterNext': 'Dentro de dos semanas',
  'sched.copySpecificWeek': 'Elegir semana',

  'hol.title': 'Vacaciones', 'hol.subtitle': 'Seguimiento de solicitudes y saldos',
  'hol.request': 'Solicitar vacaciones', 'hol.balances': 'Saldos de vacaciones',
  'hol.allRequests': 'Todas las solicitudes', 'hol.noRequests': 'Sin solicitudes',
  'hol.employee': 'Empleado', 'hol.selectEmployee': 'Seleccionar empleado', 'hol.dateRange': 'Período',
  'hol.pickDates': 'Seleccionar fechas', 'hol.notes': 'Notas (opcional)', 'hol.notesPlaceholder': 'Motivo...',
  'hol.submit': 'Enviar solicitud', 'hol.businessDays': '{count} días laborables',
  'hol.approved': 'Aprobado', 'hol.rejected': 'Rechazado', 'hol.pending': 'Pendiente', 'hol.delete': 'Eliminar',
  'hol.days': '{count} días', 'hol.used': 'Usados', 'hol.pendingLabel': 'Pendiente', 'hol.total': 'Total',
  'hol.dialogTitle': 'Solicitar vacaciones', 'hol.dialogDesc': 'Nueva solicitud de vacaciones',
  'hol.partialApprove': 'Aprobar parcialmente', 'hol.partialApproveTitle': 'Aprobación parcial',
  'hol.selectApprovedDates': 'Seleccionar fechas a aprobar', 'hol.partialSummary': '{approved} de {total} días aprobados',
  'hol.partialApproveAction': 'Aprobar {count} días', 'hol.partialApproved': 'Aprobado parcialmente',
  'hol.partialRejected': 'No aprobado (parcial)', 'hol.partialRemainder': 'Restante de aprobación parcial',
  'hol.partialLabel': 'Parcial',

  'abs.title': 'Ausencias', 'abs.subtitle': 'Control de bajas y permisos de emergencia',
  'abs.record': 'Registrar ausencia', 'abs.summary': 'Resumen por empleado',
  'abs.allRecords': 'Todos los registros', 'abs.all': 'Todos', 'abs.sickDays': 'Bajas',
  'abs.emergencyLeave': 'Permisos de emergencia', 'abs.sick': 'Baja', 'abs.emergency': 'Emergencia',
  'abs.noRecords': 'Sin registros', 'abs.employee': 'Empleado', 'abs.selectEmployee': 'Seleccionar empleado',
  'abs.type': 'Tipo', 'abs.sickDay': 'Baja por enfermedad', 'abs.dates': 'Fechas', 'abs.pickDates': 'Seleccionar fechas',
  'abs.reason': 'Motivo', 'abs.reasonPlaceholder': 'ej. Gripe, emergencia familiar...',
  'abs.notes': 'Notas (opcional)', 'abs.notesPlaceholder': 'Detalles adicionales...',
  'abs.recordBtn': 'Registrar ausencia', 'abs.businessDays': '{count} días laborables',
  'abs.dialogTitle': 'Registrar ausencia', 'abs.dialogDesc': 'Registrar baja o permiso',
  'abs.totalLabel': 'Total',

  'rep.title': 'Informes', 'rep.weekOf': 'Semana del {label}', 'rep.excel': 'Excel', 'rep.pdfPrint': 'PDF / Imprimir',
  'rep.thisWeek': 'Esta semana', 'rep.allStores': 'Todas las tiendas', 'rep.warnings': 'Alertas',
  'rep.hoursVsContract': 'Horas vs Contrato', 'rep.storeCoverage': 'Cobertura', 'rep.holidayBalance': 'Saldo vacaciones',
  'rep.year': 'Año:', 'rep.loading': 'Cargando…',
  'rep.employeeOverContract': 'Empleado por encima del contrato',
  'rep.employeesOverContract': 'Empleados por encima del contrato',
  'rep.totalOvertimeRisk': 'Riesgo total de horas extra',
  'rep.employeeHours': 'Horas de empleados',
  'rep.colEmployee': 'Empleado', 'rep.colStore': 'Tienda', 'rep.colContract': 'Contrato',
  'rep.colScheduled': 'Programado', 'rep.colExpected': 'Esperado', 'rep.colDifference': 'Diferencia',
  'rep.noEmployees': 'No hay empleados que mostrar.',
  'rep.noStores': 'No hay tiendas que mostrar.',
  'rep.understaffedDay': '{count} día con falta de personal esta semana.',
  'rep.understaffedDays': '{count} días con falta de personal esta semana.',
  'rep.needMore': 'Faltan {count}', 'rep.extra': '+{count} extra', 'rep.ok': 'OK',
  'rep.holidayBalances': 'Saldos de vacaciones',
  'rep.colAllowance': 'Asignación', 'rep.colApproved': 'Aprobado', 'rep.colPending': 'Pendiente',
  'rep.colRemaining': 'Restante', 'rep.colUsage': 'Uso',
  'rep.lowOrOverusedSingular': 'Empleado con saldo de vacaciones bajo o excedido',
  'rep.lowOrOverused': 'Empleados con saldo de vacaciones bajo o excedido',
  'rep.allClear': 'Todo en orden', 'rep.noIssues': 'No se encontraron problemas de planificación esta semana.',
  'rep.issueSingular': 'Problema por revisar',
  'rep.issuesToReview': 'Problemas por revisar',
  'rep.urgent': 'urgente(s)',
  'rep.holidayConflict': 'Conflicto de vacaciones', 'rep.absenceConflict': 'Conflicto de ausencia',
  'rep.overHours': 'Horas excedentes', 'rep.unavailable': 'No disponible', 'rep.understaffed': 'Falta de personal',
  'rep.fix': 'Corregir',
  'rep.tabOverview': 'Resumen',
  'rep.tabHoursPayroll': 'Horas y Nómina',
  'rep.tabCoverage': 'Cobertura',
  'rep.tabLeave': 'Permisos',
  'rep.tabCompliance': 'Cumplimiento',
  'rep.overviewScheduledH': 'Horas planificadas',
  'rep.overviewOvertimeH': 'Horas extra',
  'rep.overviewCoverageGaps': 'Brechas de cobertura',
  'rep.overviewNearLimit': 'Cerca del límite',
  'rep.overviewExceeding': 'Excedente del contrato',
  'rep.overviewRiskLevel': 'Nivel de riesgo',
  'rep.overviewNoIssues': 'Sin problemas activos esta semana.',
  'rep.viewCompliance': 'Ver cumplimiento',
  'rep.viewCoverage': 'Ver cobertura',
  'rep.exportSummary': 'Exportar resumen',
  'rep.exportExcel': 'Exportar Excel',
  'rep.exportPDF': 'Exportar PDF',
  'rep.exportedExcel': 'Exportado a Excel',
  'rep.exportedExcelDesc': 'Revisa tu carpeta de Descargas.',
  'rep.exportedCSV': 'Exportado a CSV',
  'rep.weekly': 'Semanal',
  'rep.monthly': 'Mensual',
  'rep.totalScheduledH': 'Total horas programadas',
  'rep.chartTitle': 'Horas programadas vs esperadas por empleado',
  'rep.chartOnTrack': 'Conforme',
  'rep.chartOverContract': 'Sobre contrato',
  'rep.chartUnderhours': 'Horas insuficientes',
  'rep.chartExpected': 'Esperado',
  'rep.issuesToReviewTitle': 'Problemas a revisar',
  'rep.allClearPeriod': 'Todo en orden — sin problemas este período.',
  'rep.employeesOverContractLabel': 'Empleados sobre contrato',
  'rep.totalOvertimeRiskLabel': 'Riesgo total de horas extra',
  'rep.employeeHoursTitle': 'Horas de empleados',
  'rep.colOvertime': 'Horas extra',
  'rep.colUnderhours': 'Horas faltantes',
  'rep.colStatus': 'Estado',
  'rep.payrollBreakdown': 'Desglose nómina — {period}',
  'rep.colScheduledH': 'Programado (h)',
  'rep.colPaidHolidayH': 'Vacaciones pagadas (h)',
  'rep.colPaidSickH': 'Enfermedad pagada (h)',
  'rep.colCountedHours': 'Horas contadas',
  'rep.colSickDays': 'Días de enfermedad',
  'rep.totals': 'Totales',
  'rep.totalScheduled': 'Total programado',
  'rep.totalActual': 'Total real',
  'rep.lowOrOverusedLabel': 'Empleados con saldo de vacaciones bajo o excedido',
  'rep.holidayAllowanceTitle': 'Uso de vacaciones por empleado',
  'rep.sickDaySummary': 'Resumen días de enfermedad',
  'rep.colSickDaysUsed': 'Días de enfermedad usados',
  'rep.colAnnualAllowance': 'Asignación anual',
  'rep.colInstances': 'Instancias',
  'rep.colPaid': 'Pagado',
  'rep.colUnpaid': 'No pagado',
  'rep.totalEmployees': 'Total empleados',
  'rep.compliant': 'Conforme',
  'rep.compWarnings': 'Alertas',
  'rep.actionRequired': 'Acción requerida',
  'rep.complianceDisclaimer': 'Las horas contractuales pueden variar ligeramente debido a los horarios de apertura y las duraciones mínimas de turno. Pequeñas diferencias (±2h) son normales.',
  'rep.hExp': '{h}h esp',
  'rep.hCounted': '{h}h contado',
  'rep.hoursBreakdown': 'Desglose de horas',
  'rep.scheduledWorked': 'Programado (trabajado)',
  'rep.paidHoliday': 'Vacaciones pagadas',
  'rep.paidSick': 'Enfermedad pagada',
  'rep.unpaidLeave': 'Licencia no pagada',
  'rep.complianceDetail': 'Detalle de cumplimiento',
  'rep.contract': 'Contrato',
  'rep.expected': 'Esperado',
  'rep.counted': 'Contado',
  'rep.variance': 'Variación',
  'rep.maxStreak': 'Días consecutivos máx',
  'rep.leaveBalanceYear': 'Saldo de licencias (Año)',
  'rep.holiday': 'Vacaciones',
  'rep.sick': 'Enfermedad',
  'rep.remainingOf': '{remaining} restantes de {total}d',
  'rep.riskLog': 'Registro de riesgos',
  'rep.highRisk': 'Riesgo alto',
  'rep.mediumRisk': 'Riesgo medio',
  'rep.lowRisk': 'Riesgo bajo',
  'rep.colRiskType': 'Tipo de riesgo',
  'rep.colDateWeek': 'Fecha/Semana',
  'rep.colSeverity': 'Gravedad',
  'rep.colDescription': 'Descripción',
  'rep.filterAll': 'Todos',
  'rep.filterHigh': 'Alto',
  'rep.filterMedium': 'Medio',
  'rep.filterLow': 'Bajo',
  'rep.statusCompliant': 'Conforme',
  'rep.statusWarning': 'Alerta',
  'rep.statusActionRequired': 'Acción requerida',
  'rep.statusOnTrack': 'Conforme',
  'rep.statusAtRisk': 'En riesgo',
  'rep.riskHigh': 'Alto',
  'rep.riskMedium': 'Medio',
  'rep.riskLow': 'Bajo',
  'rep.gap': 'FALTA',
  'rep.tooltipScheduled': 'Programado: {h}h',
  'rep.tooltipExpected': 'Esperado: {h}h',
  'rep.tooltipVariance': 'Variación: {h}h',
  'rep.tooltipStatus': 'Estado: {status}',

  'pva.title': 'Horas programadas vs reales',
  'pva.totalScheduled': 'Total programado',
  'pva.totalPlanned': 'Total planificado',
  'pva.totalActual': 'Total real',
  'pva.totalVariance': 'Variación total',
  'pva.confirmRate': '{confirmed}/{total} turnos confirmados',
  'pva.employeeBreakdown': 'Desglose por empleado',
  'pva.scheduled': 'Programado',
  'pva.planned': 'Planificado',
  'pva.actual': 'Real',
  'pva.variance': 'Variación',
  'pva.breakVar': 'Pausa ±',
  'pva.confirmed': 'Confirmado',
  'pva.lateArrivals': 'Tarde',
  'pva.earlyLeaves': 'Salida temprana',
  'pva.totals': 'Totales',
  'pva.noConfirmedShifts': 'Sin turnos confirmados — confirma turnos para ver datos reales',
  'pva.lateArrivalsCount': '{count} llegada(s) tarde',
  'pva.earlyLeavesCount': '{count} salida(s) temprana(s)',
  'pva.totalOvertimeInsight': '{hours}h de horas extras',
  'pva.totalUnderhoursInsight': '{hours}h por debajo de lo programado',
  'pva.breakOvertime': '{mins}m de pausa extra',
  'pva.breakScheduledVsActual': 'Pausa: Programado / Real',
  'review.actualBreakStart': 'Inicio de pausa',
  'review.actualBreakEnd': 'Fin de pausa',

  // ── Warning Explanations ──
  'warn.hoursOverTitle': '{hours} horas por encima del contrato',
  'warn.hoursOverDesc': 'Este empleado está programado más allá de sus horas semanales acordadas.',
  'warn.hoursOverOpt1': 'Eliminar un turno',
  'warn.hoursOverOpt2': 'Reasignar a otro empleado',
  'warn.hoursOverOpt3': 'Aumentar horas de contrato',
  'warn.unfillableTitle': '{hours} horas no se pueden programar esta semana',
  'warn.unfillableWhy': 'Por qué:',
  'warn.unfillableR1': 'Todas las tiendas están completas o cerradas',
  'warn.unfillableR2': 'La disponibilidad restante no coincide con los turnos abiertos',
  'warn.unfillableR3': 'La duración mínima de turno impide asignaciones parciales',
  'warn.unfillableOpt1': 'Reducir horas de contrato esta semana',
  'warn.unfillableOpt2': 'Permitir turnos más cortos',
  'warn.unfillableOpt3': 'Aumentar necesidades de personal',
  'warn.nearContractTitle': '{hours} horas por debajo del contrato',
  'warn.nearContractDesc': 'Esto ocurre porque todos los turnos restantes son completos y el sistema evita crear turnos cortos.',
  'warn.nearContractOpt1': 'Aceptar semana bajo contrato',
  'warn.nearContractOpt2': 'Permitir turnos más cortos',
  'warn.nearContractOpt3': 'Ajustar horas de contrato',
  'warn.underTitle': '{hours} horas por debajo del contrato',
  'warn.underDesc': 'Este empleado tiene menos horas programadas de las que requiere su contrato. Se pueden añadir más turnos.',
  'warn.underOpt1': 'Añadir turnos manualmente',
  'warn.underOpt2': 'Ejecutar generación automática',
  'warn.underOpt3': 'Ajustar horas de contrato',
  'warn.options': 'Opciones:',
  'warn.holidayDesc': 'Este empleado está de vacaciones aprobadas y no puede ser programado.',
  'warn.holidayOpt1': 'Eliminar el turno',
  'warn.holidayOpt2': 'Asignar otro empleado',
  'warn.absenceDesc': 'Este empleado está de baja aprobada y no debería ser programado.',
  'warn.absenceOpt1': 'Eliminar el turno',
  'warn.absenceOpt2': 'Asignar otro empleado',
  'warn.unavailableDesc': 'Este turno está fuera de los días de disponibilidad del empleado.',
  'warn.unavailableOpt1': 'Cambiar fecha del turno',
  'warn.unavailableOpt2': 'Asignar otro empleado',
  'warn.unavailableOpt3': 'Actualizar disponibilidad',
  'warn.understaffedDesc': 'Esta tienda no tiene suficiente personal programado para este día.',
  'warn.understaffedOpt1': 'Añadir otro empleado',
  'warn.understaffedOpt2': 'Ajustar requisitos de personal',
  'warn.understaffedOpt3': 'Revisar disponibilidad de empleados',
  'warn.weeklyHours': 'Resumen semanal de horas',
  'warn.maxDaysTitle': 'Máximo de días laborales alcanzado',
  'warn.maxDaysDesc': 'Este empleado ha alcanzado su máximo de {days} días laborales esta semana. No se pueden asignar turnos adicionales aunque queden horas de contrato.',
  'warn.maxDaysOpt1': 'Aumentar el máximo de días laborales',
  'warn.maxDaysOpt2': 'Aceptar semana bajo contrato',
  'warn.maxDaysOpt3': 'Ajustar horas de contrato',
  'emp.maxWorkingDays': 'Días laborales máx./semana',

  'notfound.title': '404', 'notfound.message': 'Página no encontrada', 'notfound.link': 'Volver al inicio',
  'onb.title': 'Crea tu organización',
  'onb.subtitle': 'Dale un nombre a tu empresa o equipo para empezar.',
  'onb.label': 'Nombre de la organización',
  'onb.placeholder': 'Mi Empresa',
  'onb.creating': 'Creando...',
  'onb.create': 'Crear organización',
  'onb.signOut': 'Cerrar sesión',
  'onb.invalidName': 'Nombre inválido',
  'onb.nameLength': 'El nombre debe tener entre 1 y 100 caracteres.',
  'setup.title': 'Configuremos tu negocio',
  'setup.subtitle': 'Añade tu primera tienda y empleados para generar tu primer horario equilibrado.',
  'setup.progress': 'Paso {count} de {total} completado',
  'setup.step1': 'Añade tu primera tienda',
  'setup.step2': 'Añade tu primer empleado',
  'setup.step3': 'Genera tu primer horario',
  'setup.step3Hint': 'Requiere al menos 1 tienda y 1 empleado',
  'setup.addAnother': 'Añadir otro',
  'setup.storeAdded': '¡Tienda añadida!',
  'setup.employeeAdded': '¡Empleado añadido!',
  'setup.storeCount1': 'tienda añadida',
  'setup.storeCountN': 'tiendas añadidas',
  'setup.empCount1': 'empleado añadido',
  'setup.empCountN': 'empleados añadidos',
  'setup.generate': 'Generar primer horario',
  'setup.generating': 'Generando...',
  'setup.success': 'Tu primer horario equilibrado está listo.',
  'setup.genError': 'Error al generar el horario',
  'setup.noShiftsError': 'No se pudieron generar turnos. Verifica requisitos de personal y disponibilidad.',
  'setup.skip': 'Omitir por ahora',
  'setup.emptySchedule': 'Necesitas al menos 1 tienda y 1 empleado para crear un horario.',
  'settings.title': 'Configuración',
  'settings.subtitle': 'Preferencias y configuración de la empresa',
  'settings.balancedDesc': 'Maximiza la cobertura minimizando horas extra. Recomendado para la mayoría de equipos.',
  'hours.label': 'Horario de apertura',
  'hours.quickSet': 'Ajuste rápido',
  'hours.to': 'a',
  'hours.lunchBreak': 'Pausa para almuerzo',
  'hours.applyWeekdays': 'Aplicar Lun–Vie',
  'hours.applyAll': 'Aplicar todos los días',
  'hours.open': 'Abierto',
  'hours.closed': 'Cerrado',
  'ai.getSuggestions': 'Sugerencias IA',
  'ai.autoFix': 'Corrección auto',
  'sched.fix': 'Corregir',
  'sched.reduceHours': 'Reducir horas',
  'sched.reassignShift': 'Reasignar turno',
  'sched.confirm': 'Confirmar',
  'sched.edit': 'Editar',
  'sched.remove': 'Eliminar',
  'sched.understaffed': '⚠ Falta personal',
  'sched.missingCount': 'Faltan {count} empleados',
  'sched.staffed': 'cubierto',
  'sched.staff': 'personal',
  'sched.statusReady': 'Horario listo',
  'sched.statusIssue1': '1 problema por revisar',
  'sched.statusIssues': '{count} problemas por revisar',
  'sched.issueOverHours1': '{name} supera sus horas semanales',
  'sched.issueOverHoursN': '{count} empleados superan sus horas',
  'sched.issueShiftTooLong1': '{count} turno es demasiado largo',
  'sched.issueShiftTooLongN': '{count} turnos son demasiado largos',
  'sched.issueInvalidTimes1': '{count} turno con horarios inválidos',
  'sched.issueInvalidTimesN': '{count} turnos con horarios inválidos',
  'sched.issueOutsideHours1': '{count} turno fuera del horario de tienda',
  'sched.issueOutsideHoursN': '{count} turnos fuera del horario de tienda',
  'sched.issueClosedDay1': '{count} turno en día cerrado',
  'sched.issueClosedDayN': '{count} turnos en días cerrados',
  'sched.issueUnavailable1': '{name} asignado en día no disponible',
  'sched.issueUnavailableN': '{count} turnos en días no disponibles',
  'sched.issueWrongStore1': '{name} en tienda incorrecta',
  'sched.issueWrongStoreN': '{count} empleados en tienda incorrecta',
  'sched.issueOverlap1': '{count} turno superpuesto',
  'sched.issueOverlapN': '{count} turnos superpuestos',
  'sched.issueShiftTooShort1': '{count} turno demasiado corto',
  'sched.issueShiftTooShortN': '{count} turnos demasiado cortos',
  'sched.issueUnderstaffed1': '{store} necesita más personal el {day}',
  'sched.issueUnderstaffedN': '{count} días con falta de personal',
  'ai.fixSchedule': 'Corregir con IA',
  'ai.fixScheduleAI': 'Corregir horario con IA',
  'sched.issuesThisWeek': 'Problemas esta semana',
  'ai.analyzing': 'Analizando...',
  'ai.suggestions': 'Sugerencias IA',
  'ai.fixesFound': '{count} correcciones encontradas',
  'ai.addresses': 'Resuelve:',
  'ai.apply': 'Aplicar',
  'ai.preview': 'Vista previa',
  'ai.hidePreview': 'Ocultar',
  'ai.before': 'Antes',
  'ai.after': 'Después',
  'ai.keepsCoverage': 'Cobertura mantenida',
  'ai.withinContract': 'Dentro del contrato',
  'ai.shiftRemoved': 'Turno eliminado',
  'ai.noShift': 'Sin turno',
  'ai.refresh': 'Actualizar',
  'ai.swap': 'Intercambiar',
  'ai.reassign': 'Reasignar',
  'ai.addShift': 'Añadir turno',
  'ai.remove': 'Eliminar',
  'ai.adjust': 'Ajustar',
  'ai.applied': 'Corrección aplicada',
  'ai.changes': 'Cambios',
  'ai.failedApply': 'Error al aplicar',
  'ai.failedFetch': 'Error al obtener sugerencias',
  'ai.aiError': 'Error IA',
  'ai.importTitle': 'Importación de datos IA',
  'ai.stageInput': 'Pega una descripción de tu negocio. Primero se analizarán las tiendas, luego los empleados.',
  'ai.stageParsingStores': 'Analizando tu texto para información de tiendas...',
  'ai.stagePreviewStores': 'Revisa las tiendas abajo. Una vez confirmadas, se analizarán los empleados.',
  'ai.stageSavingStores': 'Guardando tiendas...',
  'ai.stageCounting': 'Detectando empleados en tu texto...',
  'ai.stageParsingEmps': 'Analizando empleados por lotes...',
  'ai.stageValidating': 'Validando datos de empleados...',
  'ai.stagePreviewEmps': 'Revisa los empleados abajo. Haz clic en "Confirmar" para guardar.',
  'ai.stageImporting': 'Importando empleados...',
  'ai.stageComplete': '¡Todos los datos importados con éxito!',
  'ai.parseStores': 'Analizar tiendas',
  'ai.startOver': 'Empezar de nuevo',
  'ai.confirmStores': 'Confirmar tiendas y analizar empleados',
  'ai.importAnyway': 'Importar de todos modos',
  'ai.confirmImport': 'Confirmar importación',
  'ai.done': 'Hecho',
  'ai.tip': 'Consejo: incluye nombres de tiendas, horarios, personal, nombres de empleados, horas de contrato, asignaciones y disponibilidad.',
  'ai.storesSaved': 'tiendas guardadas',
  'ai.employees': 'empleados',
  'ai.totalWeekly': 'total semanal',
  'ai.importedSuccess': '{stores} tiendas y {employees} empleados importados con éxito.',
  'summary.ofContract': 'del contrato',
  'summary.over': '+{hours}h de más',
  'summary.cantFill': '{hours}h sin cubrir',
  'summary.belowMin': '{hours}h bajo el mínimo',
  'summary.remaining': '{hours}h restantes',
  'dash.importData': 'Importar datos',
  'dash.importTitle': 'Importa tus datos de negocio',
  'dash.importDescription': 'Elige cómo quieres configurar tus tiendas y empleados.',
  'dash.aiImportTitle': 'Importación IA inteligente',
  'dash.aiImportDesc': 'Pega una descripción de tu negocio y deja que la IA cree automáticamente tiendas, empleados y contratos.',
  'dash.startAiImport': 'Iniciar importación IA',
  'dash.excelImportTitle': 'Subir plantilla Excel',
  'dash.excelImportDesc': 'Sube un archivo Excel estructurado con tus tiendas y empleados para una importación rápida y validada.',
  'dash.uploadFile': 'Subir archivo',
  'dash.importHint': '¿No sabes cuál elegir? La importación IA es ideal para una configuración rápida.',

  'day.sun': 'Domingo', 'day.mon': 'Lunes', 'day.tue': 'Martes', 'day.wed': 'Miércoles',
  'day.thu': 'Jueves', 'day.fri': 'Viernes', 'day.sat': 'Sábado',
  'day.sunShort': 'Dom', 'day.monShort': 'Lun', 'day.tueShort': 'Mar', 'day.wedShort': 'Mié',
  'day.thuShort': 'Jue', 'day.friShort': 'Vie', 'day.satShort': 'Sáb',

  'settings.tabOrg': 'Organización', 'settings.tabTeam': 'Equipo', 'settings.tabRules': 'Reglas de planificación', 'settings.tabDanger': 'Zona de peligro',
  'settings.orgName': 'Nombre de la organización', 'settings.orgNameDesc': 'El nombre que ven los miembros del equipo.', 'settings.save': 'Guardar', 'settings.saved': 'Configuración guardada',
  'settings.teamTitle': 'Miembros del equipo', 'settings.teamDesc': 'Gestiona tu equipo y envía invitaciones.', 'settings.invite': 'Invitar miembro',
  'settings.colName': 'Nombre', 'settings.colEmail': 'Correo', 'settings.colRole': 'Rol', 'settings.colStatus': 'Estado', 'settings.colActions': 'Acciones',
  'settings.roleOwner': 'Propietario', 'settings.roleManager': 'Gerente', 'settings.active': 'Activo', 'settings.pending': 'Pendiente',
  'settings.remove': 'Eliminar', 'settings.resend': 'Reenviar', 'settings.removeConfirm': '¿Eliminar este miembro?', 'settings.removed': 'Miembro eliminado',
  'settings.inviteSent': 'Invitación enviada', 'settings.inviteEmail': 'Correo electrónico', 'settings.inviteRole': 'Rol', 'settings.inviteBtn': 'Enviar invitación',
  'settings.inviteTitle': 'Invitar miembro', 'settings.inviteDesc': 'Envía un enlace de invitación para unirse a tu organización.',
  'settings.rulesTitle': 'Reglas de planificación', 'settings.rulesDesc': 'Configura cómo se generan los horarios.',
  'settings.dangerTitle': 'Zona de peligro', 'settings.dangerDesc': 'Acciones irreversibles. Procede con precaución.',
  'settings.deleteOrg': 'Eliminar organización', 'settings.deleteOrgDesc': 'Eliminar permanentemente esta organización y todos los datos asociados.',
  'settings.deleteOrgConfirm': '¿Estás seguro? Esta acción no se puede deshacer.', 'settings.clearSchedules': 'Borrar todos los horarios',
  'settings.clearSchedulesDesc': 'Eliminar todos los datos de turnos. Los empleados y tiendas se conservarán.',
  'settings.clearConfirm': '¿Estás seguro? Todos los datos de horarios se eliminarán.',
  'settings.deleted': 'Organización eliminada', 'settings.cleared': 'Horarios eliminados', 'settings.cancel': 'Cancelar',
  'settings.you': '(tú)', 'settings.cannotRemoveSelf': 'No puedes eliminarte a ti mismo',

  'common.loading': 'Cargando...',
  'common.cancel': 'Cancelar',
  'auth.forgotPassword': '¿Olvidaste tu contraseña?',
  'auth.or': 'o',
  'auth.googleSignIn': 'Continuar con Google',
  'auth.appleSignIn': 'Continuar con Apple',
  'forgot.title': 'Contraseña olvidada',
  'forgot.subtitle': 'Introduce tu email para recibir un enlace de restablecimiento.',
  'forgot.button': 'Enviar enlace',
  'forgot.sent': 'Si existe una cuenta, se ha enviado un enlace de restablecimiento.',
  'forgot.backToLogin': 'Volver al inicio de sesión',
  'reset.title': 'Restablecer contraseña',
  'reset.subtitle': 'Introduce tu nueva contraseña.',
  'reset.newPassword': 'Nueva contraseña',
  'reset.confirmPassword': 'Confirmar contraseña',
  'reset.button': 'Actualizar',
  'reset.passwordMin': 'La contraseña debe tener al menos 8 caracteres',
  'reset.mismatch': 'Las contraseñas no coinciden',
  'reset.failed': 'Error al actualizar. Inténtalo de nuevo.',
  'reset.success': 'Contraseña actualizada. Redirigiendo...',
  'reset.invalidLink': 'Este enlace es inválido o ha expirado.',
  'reset.backToLogin': 'Volver al inicio de sesión',
  'settings.tabAccount': 'Cuenta',
  'settings.tabBilling': 'Facturación',
  'billing.currentPlan': 'Plan actual',
  'billing.currentPlanDesc': 'Detalles de tu suscripción',
  'billing.refresh': 'Actualizar',
  'billing.noSubscription': 'Sin suscripción activa',
  'billing.paymentMethod': 'Método de pago',
  'billing.invoices': 'Facturas',
  'billing.billingDetails': 'Datos de facturación',
  'billing.editDetails': 'Editar datos',
  'billing.activating': 'Activando tu suscripción…',
  'billing.activatingDesc': 'Esto suele tardar unos segundos.',
  'billing.subEnded': 'Tu suscripción ha finalizado',
  'billing.choosePlan': 'Elige tu plan para empezar',
  'billing.pastDue': 'Pago vencido',
  'billing.canceled': 'Cancelado',
  'billing.expired': 'Expirado',
  'billing.monthly': 'Mensual',
  'billing.annual': 'Anual',
  'billing.save17': 'Ahorra ~17%',
  'billing.trialDesc': 'Prueba gratuita de 30 días incluida. Cancela en cualquier momento.',
  'billing.redirecting': 'Redirigiendo…',
  'billing.reactivate': 'Reactivar suscripción',
  'billing.startTrial': 'Iniciar prueba gratuita',
  'billing.checkStatus': '¿Ya completaste el pago? Verificar estado',
  'billing.accountSettings': 'Configuración de cuenta',
  'billing.signOut': 'Cerrar sesión',
  'billing.title': 'Facturación y Pagos',
  'billing.subtitle': 'Gestiona tu suscripción, método de pago y facturas',
  'billing.freeTrial': 'Prueba gratuita',
  'billing.trialEnds': 'Fin de la prueba',
  'billing.billingCycle': 'Ciclo de facturación',
  'billing.cancelNotice': 'Tu suscripción terminará el {date}. Puedes reanudarla desde el portal de facturación.',
  'billing.noSub': 'No se encontró suscripción activa.',
  'billing.update': 'Actualizar',
  'billing.noPayment': 'Sin método de pago registrado',
  'billing.addCard': 'Añadir tarjeta',
  'billing.noInvoicesYet': 'Sin facturas aún',
  'billing.detailsDesc': 'Actualiza el nombre de tu empresa, email de facturación, número de IVA y dirección desde el portal.',
  'billing.companyName': 'Nombre de empresa',
  'billing.billingEmail': 'Email de facturación',
  'billing.vatNumber': 'Número de IVA',
  'billing.address': 'Dirección',
  'billing.editBilling': 'Editar datos de facturación',
  'billing.noDetailsFound': 'No se encontraron datos de facturación',
  'billing.setUp': 'Configurar',
  'billing.expires': 'Vence',
  'billing.status_active': 'Activo',
  'billing.status_trialing': 'Prueba',
  'billing.status_past_due': 'Vencido',
  'billing.status_canceled': 'Cancelado',
  'billing.status_incomplete': 'Incompleto',
  'billing.status_unpaid': 'Impago',
  'billing.interval_month': 'mes',
  'billing.interval_year': 'año',
  'billing.cancelsAtPeriodEnd': 'Se cancela al final del período',
  'billing.subActivated': '¡Suscripción activada!',
  'billing.syncSlow': 'La sincronización está tardando más de lo esperado. Por favor, actualiza.',
  'billing.subFound': '¡Suscripción encontrada!',
  'billing.noSubYet': 'Aún no se encontró suscripción activa.',
  'account.title': 'Información de la cuenta',
  'account.subtitle': 'Gestiona tu email y contraseña.',
  'account.changeEmail': 'Cambiar email',
  'account.changeEmailDesc': 'Se enviará un email de verificación a tu nueva dirección.',
  'account.newEmail': 'Nuevo email',
  'account.confirmEmail': 'Confirmar email',
  'account.currentPassword': 'Contraseña actual',
  'account.emailMismatch': 'Las direcciones email no coinciden',
  'account.emailVerificationSent': 'Se ha enviado un enlace de verificación a tu nuevo email.',
  'account.changePassword': 'Cambiar contraseña',
  'account.changePasswordDesc': 'Debes verificar tu contraseña actual.',
  'account.newPassword': 'Nueva contraseña',
  'account.confirmNewPassword': 'Confirmar nueva contraseña',
  'account.savePassword': 'Actualizar',
  'account.passwordMin': 'La contraseña debe tener al menos 8 caracteres',
  'account.mismatch': 'Las contraseñas no coinciden',
  'account.wrongPassword': 'Contraseña actual incorrecta.',
  'account.updateFailed': 'Error al actualizar. Inténtalo de nuevo.',
  'account.passwordChanged': 'Contraseña actualizada',
  'account.deleteTitle': 'Eliminar cuenta',
  'account.deleteDesc': 'Eliminar permanentemente tu cuenta y todos los datos.',
  'account.deleteButton': 'Eliminar mi cuenta',
  'account.deleteConfirmTitle': '¿Estás absolutamente seguro?',
  'account.deleteConfirmDesc': 'Esta acción eliminará permanentemente tu cuenta, organización y todos los datos.',
  'account.deletePasswordLabel': 'Introduce tu contraseña para confirmar',
  'account.deleteConfirmButton': 'Eliminar todo',
  'account.deleteCancelButton': 'Cancelar',
  'account.deleteSuccess': 'Cuenta eliminada.',

  // ── Excel Import ──
  'import.title': 'Importar datos',
  'import.subtitle': 'Suba un archivo Excel o CSV con sus tiendas y empleados.',
  'import.downloadTemplate': 'Descargar plantilla Excel',
  'import.dropzone': 'Arrastre y suelte su archivo aquí, o haga clic para buscar',
  'import.dropzoneHint': 'Acepta .xlsx o .csv · Máx 5MB',
  'import.parsing': 'Analizando archivo...',
  'import.storesDetected': 'Tiendas detectadas',
  'import.employeesDetected': 'Empleados detectados',
  'import.hours': 'Horario',
  'import.staff': 'Personal (S/FS)',
  'import.days': 'Disponibilidad',
  'import.totalWeekly': 'total semanal',
  'import.importing': 'Importando datos...',
  'import.success': '¡Importación exitosa!',
  'import.successMessage': 'Importación exitosa. Sus datos están listos.',
  'import.failed': 'Error en la importación',
  'import.confirmImport': 'Confirmar importación',
  'import.cancel': 'Cancelar',
  'import.tryAgain': 'Intentar de nuevo',
  'import.done': 'Listo',
  'import.uploadData': 'Importar datos',
  'import.uploadHint': 'O importe desde un archivo Excel/CSV',

  // ── Compliance Reports ──
  'comp.complianceSummary': 'Cumplimiento',
  'comp.payrollExport': 'Exportación nómina',
  'comp.legalRisk': 'Riesgo legal',
  'comp.totalEmployees': 'Total empleados',
  'comp.compliant': 'Conformes',
  'comp.warnings': 'Advertencias',
  'comp.critical': 'Acción requerida',
  'comp.employee': 'Empleado',
  'comp.store': 'Tienda',
  'comp.contractH': 'Contrato (h/s)',
  'comp.scheduledH': 'Programado (h)',
  'comp.regularH': 'Horas regulares',
  'comp.overtimeH': 'Horas extra',
  'comp.underhours': 'Horas faltantes',
  'comp.holidays': 'Vacaciones',
  'comp.sickDays': 'Días de baja',
  'comp.maxStreak': 'Racha máx.',
  'comp.status': 'Estado',
  'comp.totalH': 'Total horas',
  'comp.totals': 'Totales',
  'comp.allEmployees': 'Todos los empleados',
  'comp.payrollDesc': 'Desglose mensual estructurado de horas para procesamiento de nómina. Sin cálculos salariales.',
  'comp.exportedCSV': 'Exportado a CSV',
  'comp.exportedExcel': 'Exportado a Excel',
  'comp.exportedExcelDesc': 'Revisa tu carpeta de Descargas para abrir el archivo',
  'comp.exportedCSVDesc': 'Revisa tu carpeta de Descargas para abrir el archivo',
  'excel.employee': 'Empleado',
  'excel.store': 'Tienda',
  'excel.contractH': 'Contrato (h)',
  'excel.scheduledH': 'Programado (h)',
  'excel.differenceH': 'Diferencia (h)',
  'excel.status': 'Estado',
  'excel.hoursVsContract': 'Horas vs Contrato',
  'excel.metric': 'Métrica',
  'excel.required': 'Requerido',
  'excel.scheduled': 'Programado',
  'excel.difference': 'Diferencia',
  'excel.allowance': 'Asignación',
  'excel.approved': 'Aprobado',
  'excel.pending': 'Pendiente',
  'excel.remaining': 'Restante',
  'excel.leaveBalance': 'Balance de vacaciones',
  'comp.highRisk': 'Riesgo alto',
  'comp.mediumRisk': 'Riesgo medio',
  'comp.lowRisk': 'Riesgo bajo',
  'comp.allSeverities': 'Todas las severidades',
  'comp.exportRisk': 'Exportar informe de riesgo',
  'comp.riskType': 'Tipo de riesgo',
  'comp.dateWeek': 'Fecha / Semana',
  'comp.severity': 'Severidad',
  'comp.description': 'Descripción',
  'comp.noRisks': 'No se detectaron riesgos',
  'comp.noRisksDesc': 'Toda la programación cumple con la normativa laboral este mes.',
  'comp.riskType48h': 'Horas semanales > 48',
  'comp.riskTypeNoRest': 'Sin día de descanso',
  'comp.riskType6Consec': '6+ días consecutivos',
  'comp.riskTypeContract': 'Violación horas contrato',
  'comp.riskTypeMaxDays': 'Excede máx días/semana',
  'comp.riskTypeAvail': 'Programado fuera de disponibilidad',
  'comp.riskDesc48h': '{hours}h programadas (máx 48h)',
  'comp.riskDescNoRest': '{days} días trabajados sin descanso',
  'comp.riskDesc6Consec': '{days} días trabajados en la semana',
  'comp.riskDescContract': '{hours}h vs {contract}h contrato (+{pct}%)',
  'comp.riskDescMaxDays': '{days} días trabajados (máx {max})',
  'comp.riskDescAvail': 'Programado en día no disponible',
  'comp.severityHigh': 'Alto',
  'comp.severityMedium': 'Medio',
  'comp.severityLow': 'Bajo',

  // ── Landing Page (restructured) ──
  'lp.hero.title': 'Planificación y cumplimiento para equipos por turnos',
  'lp.hero.subtitle': 'Crea turnos, controla horas contractuales, gestiona permisos pagados y previene problemas de cumplimiento — todo en un sistema.',
  'lp.hero.demo': 'Reservar una demo',
  'lp.hero.trust': 'Para comercios, hostelería, clínicas, gimnasios, franquicias y empresas de servicios.',

  'lp.prob.title': 'La gestión de turnos no debería ser tan complicada',
  'lp.prob1': 'Las hojas de cálculo manuales causan errores',
  'lp.prob2': 'Las horas extra pasan desapercibidas',
  'lp.prob3': 'El seguimiento de permisos se vuelve caótico',
  'lp.prob4': 'Los riesgos de cumplimiento aumentan',
  'lp.prob5': 'Los managers pierden horas cada semana',
  'lp.prob.closing': 'Los pequeños errores de planificación cuestan dinero real.',

  'lp.core.title': 'Todo lo que necesitas para gestionar equipos por turnos',
  'lp.core1.title': 'Constructor de turnos inteligente',
  'lp.core1.desc': 'Genera automáticamente turnos equilibrados respetando las horas contractuales y la disponibilidad.',
  'lp.core2.title': 'Control de contratos y horas',
  'lp.core2.desc': 'Controla horas previstas vs reales mensual y semanalmente.',
  'lp.core3.title': 'Seguimiento de permisos pagados y bajas',
  'lp.core3.desc': 'Aplica automáticamente umbrales pagados y detecta usos excesivos.',
  'lp.core4.title': 'Suite de informes de cumplimiento',
  'lp.core4.desc': 'Genera resúmenes de cumplimiento mensuales al instante.',
  'lp.core5.title': 'Funciona en 6 idiomas',
  'lp.core5.desc': 'Operota detecta automáticamente tu idioma y adapta toda la plataforma. Inglés, italiano, español, francés, alemán y portugués — sin configuración.',
  'lp.core6.title': 'Soporte multi-ubicación',
  'lp.core6.desc': 'Gestiona múltiples ubicaciones desde una sola cuenta. Cada ubicación tiene sus propios horarios, reglas de personal e informes de cumplimiento.',
  'lp.core.subtitle': 'El motor de planificación IA de Operota crea turnos equilibrados que respetan las horas contractuales, la disponibilidad de los empleados y la cobertura del equipo automáticamente. Sin hojas de cálculo. Sin conjeturas.',
  'lp.core.sectionTitle': 'Crea turnos de personal en segundos',

  'lp.hero.langLine': 'Disponible en 6 idiomas — se adapta automáticamente a tu equipo',
  'lp.hero.noCard': 'Sin tarjeta requerida',
  'lp.hero.trustBadge1': 'Cumple con RGPD',
  'lp.hero.trustBadge2': 'Sin cargos por empleado',
  'lp.hero.trustBadge3': 'Configuración en 15 minutos',

  'lp.pricing.annualSave': 'Facturado 990€/año — 2 meses gratis',
  'lp.pricing.annualBadge': 'Ahorra 198€/año',
  'lp.pricing.bestValue': 'Mejor valor',
  'lp.price6': 'Funciona en 6 idiomas',
  'lp.price7': 'Programación automática con IA',

  'lp.prob1.desc': 'Una fórmula incorrecta cuesta horas corregir y arriesga pagar mal a tu equipo.',
  'lp.prob2.desc': 'Las violaciones de contrato se acumulan silenciosamente hasta convertirse en un problema legal.',
  'lp.prob3.desc': 'Los conflictos de permisos y ausencias de último minuto crean caos de cobertura.',
  'lp.prob4.desc': 'Sin verificaciones automáticas, estás a una auditoría de una multa costosa.',
  'lp.prob5.desc': 'Crear turnos manualmente es trabajo repetitivo que debería tomar segundos.',

  'lp.aud1.desc': 'Tiendas, boutiques y cadenas de moda',
  'lp.aud2.desc': 'Equipos de sala y cocina',
  'lp.aud3.desc': 'Clínicas, gimnasios y consultorios de terapia',
  'lp.aud4.desc': 'Centros de fitness e instalaciones de ocio',
  'lp.aud5.desc': 'Franquicias y operadores multi-ubicación',
  'lp.aud6.desc': 'Equipos de campo y empresas de servicios',

  'lp.section5.title': 'Controla horas contractuales y cumplimiento',
  'lp.section5.subtitle': 'Operota monitorea automáticamente las horas contractuales contra los turnos programados, señala violaciones de horas extra y genera informes de cumplimiento.',
  'lp.section5.card1.title': 'Programación de turnos con IA',
  'lp.section5.card1.desc': 'Nuestro creador de turnos usa IA para generar horarios equilibrados que previenen horas extra y respetan la disponibilidad.',
  'lp.section5.card2.title': 'Informes de cumplimiento',
  'lp.section5.card2.desc': 'Genera resúmenes mensuales mostrando horas trabajadas vs contractuales, balances de permisos y violaciones — exportables a Excel.',

  'lp.early.title': 'Sé el primero en compartir tu experiencia',
  'lp.early.subtitle': 'Únete a nuestro programa de acceso anticipado.',

  'footer.solutions': 'Soluciones',

  'lp.faq.businesses.q': '¿Qué tipos de negocios usan Operota?',
  'lp.faq.businesses.a': 'Cualquier negocio con trabajadores por turnos — tiendas, restaurantes, gimnasios, clínicas, empresas de limpieza, seguridad y más.',
  'lp.faq.setup.q': '¿Cuánto tiempo toma la configuración?',
  'lp.faq.setup.a': 'La mayoría de los equipos están operativos en menos de 15 minutos. Agrega tu ubicación, importa tu personal y genera automáticamente.',
  'lp.faq.selfService.q': '¿Los empleados pueden ver sus propios horarios?',
  'lp.faq.selfService.a': 'El autoservicio para empleados está en nuestra hoja de ruta. Actualmente los horarios se pueden exportar a PDF o Excel.',
  'lp.faq.perEmployee.q': '¿Hay un cargo por empleado?',
  'lp.faq.perEmployee.a': 'No. Operota es una tarifa fija de 99€ al mes sin importar cuántos empleados o ubicaciones tengas.',
  'lp.faq.afterTrial.q': '¿Qué pasa después de la prueba de 30 días?',
  'lp.faq.afterTrial.a': 'Pasas al plan mensual a 99€/mes. Te avisamos antes de que termine tu prueba y puedes cancelar en cualquier momento.',
  'lp.faq.languages.q': '¿En qué idiomas está disponible?',
  'lp.faq.languages.a': 'Inglés, italiano, español, francés, alemán y portugués. Detecta automáticamente el idioma de tu navegador.',

  'lp.pricing.title': 'Precios simples y profesionales',
  'lp.pricing.noFees': 'Sin costes ocultos. Sin cargos por empleado.',
  'lp.price1': 'Empleados ilimitados',
  'lp.price2': 'Soporte multi-ubicación',
  'lp.price3': 'Informes de cumplimiento completos',
  'lp.price4': 'Seguimiento de permisos y ausencias',
  'lp.price5': 'Prueba gratuita de 30 días',

  'lp.aud.title': 'Creado para negocios por turnos en crecimiento',
  'lp.aud.subtitle': 'Si gestionas empleados por turnos o en varias ubicaciones, Operota mantiene tu equipo organizado y conforme.',
  'lp.aud1': 'Comercios',
  'lp.aud2': 'Grupos de hostelería',
  'lp.aud3': 'Clínicas',
  'lp.aud4': 'Gimnasios',
  'lp.aud5': 'Franquicias',
  'lp.aud6': 'Empresas de servicios',

  'lp.faq.payroll.q': '¿Gestiona la nómina?',
  'lp.faq.payroll.a': 'No. Operota proporciona exportaciones mensuales estructuradas de horas listas para tu contador o sistema de nómina, pero no calcula salarios, impuestos ni deducciones.',
  'lp.faq.contracts.q': '¿Respeta las horas contractuales?',
  'lp.faq.contracts.a': 'Sí. El sistema respeta automáticamente las horas contractuales de cada empleado al generar los turnos.',
  'lp.faq.excel.q': '¿Puedo importar desde Excel?',
  'lp.faq.excel.a': 'Sí. Importa ubicaciones y empleados desde archivos Excel o CSV, o usa nuestra función de importación IA.',
  'lp.faq.security.q': '¿Están mis datos seguros?',
  'lp.faq.security.a': 'Tus datos están cifrados en tránsito y en reposo, alojados en la UE, con infraestructura cloud empresarial y cumplimiento estricto del RGPD.',
  'lp.faq.stores.q': '¿Puedo gestionar múltiples ubicaciones?',
  'lp.faq.stores.a': 'Por supuesto. Operota soporta ubicaciones ilimitadas. Gestiona turnos, personal e informes desde un solo panel.',

  'settings.tabLeave': 'Política de permisos',
  'leave.globalPolicy': 'Política global de permisos',
  'leave.globalPolicyDesc': 'Establecer las asignaciones predeterminadas de permisos pagados para todos los empleados.',
  'leave.paidHolidayDays': 'Días de vacaciones pagadas anuales',
  'leave.paidSickDays': 'Días de enfermedad pagados anuales',
  'leave.hoursPerDay': 'Horas de trabajo por día',
  'leave.savePolicy': 'Guardar política',
  'leave.policySaved': 'Política de permisos guardada',
  'leave.bulkActions': 'Acciones masivas',
  'leave.bulkActionsDesc': 'Aplicar cambios de política a empleados o reiniciar contadores anuales.',
  'leave.applyToAll': 'Aplicar a todos los empleados',
  'leave.confirmApply': '¿Aplicar política a todos?',
  'leave.confirmApplyDesc': 'Esto actualizará las asignaciones de vacaciones y enfermedad para todos los empleados sin anulación personalizada.',
  'leave.appliedToAll': 'Política aplicada a todos los empleados',
  'leave.resetCounters': 'Reiniciar contadores',
  'leave.confirmReset': '¿Reiniciar contadores de permisos?',
  'leave.confirmResetDesc': 'Los saldos de permisos se calculan a partir de los registros. Filtre por año para ver los saldos actuales.',
  'leave.resetInfo': 'Los saldos se calculan a partir de registros con fecha. Filtre por año.',
  'leave.overridePolicy': 'Anular política predeterminada',
  'leave.paidHolH': 'Vac. pagadas (h)',
  'leave.paidSickH': 'Enf. pagada (h)',
  'leave.unpaidH': 'Permiso no pagado (h)',
  'leave.holAllowance': 'Asignación vacaciones',
  'leave.sickAllowance': 'Asignación enfermedad',
  'leave.used': 'usado',
  'leave.remainHol': 'Vac. rest.',
  'leave.remainSick': 'Enf. rest.',
  'comp.contractCountedH': 'Horas contadas',
  'comp.contractCountedTooltip': 'Horas programadas + permisos pagados. Para cumplimiento contractual.',
  'comp.workingDays': 'días laborables',
  'comp.day': 'día',
  'comp.expectedH': 'Esperado',
  'comp.expectedTooltip': 'Calculado a partir de horas de contrato × días laborables en el período. Conteo exacto.',
  'comp.scheduledTooltip': 'Horas de turno reales en el período.',
  'comp.underhoursVsContract': 'Horas faltantes',
  'comp.underhoursTooltip': 'Horas contadas − Horas esperadas. Negativo = déficit.',
  'comp.complianceWeekly': 'Semanal',
  'comp.complianceMonthly': 'Mensual',
  'comp.complianceReport': 'Informe de cumplimiento',
  'comp.period': 'Período',
  'comp.print': 'Imprimir',
  'comp.savePDF': 'Guardar como PDF',
  'comp.printOrSavePDF': 'Imprimir / Guardar como PDF',
  'comp.printPDFHint': 'Para guardar como PDF, selecciona "Guardar como PDF" como destino en el diálogo de impresión.',
  'comp.generatingPDF': 'Generando PDF...',
  'comp.backToReports': 'Volver a informes',
  'comp.totalOvertime': 'Horas extra totales',
  'comp.totalUnderhours': 'Horas faltantes totales',
  'comp.totalUnpaidLeave': 'Licencia no remunerada total',
  'comp.generatedOn': 'Generado el',
  'comp.generatedBy': 'Generado por',
  'comp.organization': 'Organización',
  'comp.reportPeriod': 'Período del informe',
  'comp.hoursBreakdown': 'Desglose de horas',
  'comp.compliance': 'Cumplimiento',
  'comp.leaveBalanceYear': 'Saldo licencias (Año)',
  'comp.leaveBalanceAnnual': 'Saldo licencias (Anual)',
  'comp.scheduled': 'Programado (trabajado)',
  'comp.paidHoliday': 'Vacaciones pagadas',
  'comp.paidSick': 'Enfermedad pagada',
  'comp.unpaidLeave': 'Licencia no remunerada',
  'comp.contract': 'Contrato',
  'comp.expected': 'Esperado',
  'comp.expectedPeriod': 'Esperado para el período',
  'comp.counted': 'Contado',
  'comp.variance': 'Variación',
  'comp.worked': 'Trabajado',
  'comp.holidayRemaining': 'Vacaciones restantes',
  'comp.sickRemaining': 'Enfermedad restante',
  'comp.days': 'días',
  'comp.notes': 'Notas',
  'comp.statusOk': 'Conforme',
  'comp.statusWarning': 'Variación menor',
  'comp.statusCritical': 'Acción requerida',
  'comp.allClear': 'Todos los empleados conformes',
  'comp.varianceExplainer': 'Las horas contractuales pueden variar ligeramente debido a los horarios de apertura, la disponibilidad y la duración mínima de los turnos. Las pequeñas diferencias (±2h) son normales.',
  'comp.toleranceOk': 'Dentro de tolerancia',
  'comp.toleranceWarning': 'Revisión recomendada',
  'comp.toleranceAction': 'Acción requerida',
};

const de: Record<string, string> = {
  'header.signIn': 'Anmelden', 'header.getStarted': 'Loslegen',
  'hero.badge': 'Für Multi-Standort Retail-Teams',
  'hero.title1': 'KI-gestützte Personalplanung', 'hero.title2': 'Die Ihre Filialen wirklich ausbalanciert',
  'hero.subtitle': 'Entwickelt für wachsende Retail-Teams, die vertragskonforme, KI-ausbalancierte Dienstpläne ohne Chaos benötigen.',
  'hero.cta': '30 Tage kostenlos testen', 'hero.signIn': 'Anmelden',
  'hero.trust1': '30 Tage kostenlos', 'hero.trust2': 'Einrichtung in Minuten', 'hero.trust3': 'Keine Verpflichtung',
  'hero.euLine': 'EU-gehostet. DSGVO-konform. Für europäische Arbeitsverträge entwickelt.',
  'trust.title': 'Entwickelt für moderne Retail-Operationen',
  'trust.eu': 'EU-Hosting', 'trust.gdpr': 'DSGVO-konform', 'trust.secure': 'Sichere Cloud-Infrastruktur', 'trust.encrypted': 'Verschlüsselt im Transit und im Ruhezustand',
  'trust.origin': 'Entworfen und betrieben aus Italien.',
  'screenshot.caption': 'Automatisch ausbalancierter Dienstplan in einem Klick.', 'screenshot.sub': 'Verträge einhalten. Überstunden vermeiden. Besetzungslücken eliminieren.',
  'features.title': 'Alles für intelligentere Dienstpläne', 'features.subtitle': 'Ergebnisorientierte Tools für beschäftigte Retail-Manager.',
  'feature.scheduling.title': 'Intelligente Planung', 'feature.scheduling.desc': 'KI-generierte Dienstpläne, die automatisch Stunden ausbalancieren und Lücken verhindern.',
  'feature.employees.title': 'Mitarbeiterverwaltung', 'feature.employees.desc': 'Verträge, Verfügbarkeit und Abwesenheiten in einem zentralen Dashboard verfolgen.',
  'feature.stores.title': 'Multi-Standort', 'feature.stores.desc': 'Alle Standorte von einem intelligenten Kontrollzentrum verwalten.',
  'feature.absences.title': 'Abwesenheitsverfolgung', 'feature.absences.desc': 'Krankheitstage und Urlaub erfassen. Immer wissen, wer verfügbar ist.',
  'feature.holidays.title': 'Urlaubsverwaltung', 'feature.holidays.desc': 'Feiertage und Urlaubsanträge automatisch verwalten.',
  'feature.reports.title': 'Berichte & Exporte', 'feature.reports.desc': 'Berichte erstellen und nach Excel exportieren.',
  'midCta.title': 'Hören Sie auf, Dienstpläne manuell zu erstellen.', 'midCta.subtitle': 'Schließen Sie sich wachsenden Retail-Teams an, die auf KI-Dienstplanverwaltung umsteigen.', 'midCta.button': 'Kostenlos testen',
  'stats.shifts': 'Geplante Schichten', 'stats.teams': 'Teams nutzen es', 'stats.uptime': 'Verfügbarkeit', 'stats.rating': 'Bewertung',
  'how.title': 'So funktioniert es', 'how.subtitle': 'In Minuten einsatzbereit.',
  'how.step1.title': 'Standorte hinzufügen', 'how.step1.desc': 'Richten Sie Ihre Standorte und Öffnungszeiten ein.',
  'how.step2.title': 'Mitarbeiter hinzufügen', 'how.step2.desc': 'Geben Sie Ihr Team mit Verträgen ein.',
  'how.step3.title': 'Dienstpläne erstellen', 'how.step3.desc': 'Erstellen Sie Pläne mit intelligenten Vorschlägen.',
  'pricing.title': 'Einfache, transparente Preise', 'pricing.subtitle': '30 Tage kostenlos. Keine Kreditkarte.',
  'pricing.perMonth': '/Monat', 'pricing.afterTrial': 'Nach der kostenlosen Testphase',
  'pricing.cta': 'Kostenlos testen', 'pricing.included': 'Alles inklusive:',
  'pricing.item1': 'Unbegrenzte Standorte', 'pricing.item2': 'Unbegrenzte Mitarbeiter',
  'pricing.item3': 'KI-Ausbalancierung inklusive', 'pricing.item4': 'Abwesenheits- & Urlaubsverfolgung',
  'pricing.item5': 'Vertragskonforme Planung', 'pricing.item6': 'Prioritäts-Support',
  'pricing.badge': 'Ideal für wachsende Teams', 'pricing.reassurance': 'Jederzeit kündbar. Keine langfristigen Verträge.',
  'pricing.target': 'Für wachsende Retail-Teams und Multi-Standort-Unternehmen.',
  'pricing.monthly': 'Monatlich', 'pricing.annual': 'Jährlich', 'pricing.comingSoon': 'Demnächst',
  'cta.title': 'Bereit, Ihre Planung zu vereinfachen?', 'cta.subtitle': 'Schließen Sie sich Managern an, die intelligenter planen.',
  'cta.button': 'Kostenlose Testphase starten',
  'footer.features': 'Funktionen', 'footer.howItWorks': 'So funktioniert es', 'footer.signIn': 'Anmelden',
  'footer.rights': '© {year} Operota. Alle Rechte vorbehalten.',
  'footer.product': 'Produkt', 'footer.legal': 'Rechtliches',
  'footer.terms': 'AGB', 'footer.privacy': 'Datenschutzrichtlinie',
  'footer.cookies': 'Cookie-Richtlinie', 'footer.dpa': 'AVV', 'footer.refunds': 'Erstattung & Kündigung',
  'pain.title': 'Planen Sie Dienstpläne noch auf die harte Tour?',
  'pain.manual.title': 'Manuelle Planung', 'pain.manual.desc': 'Dienstpläne in Excel oder auf Papier kosten jede Woche Stunden.',
  'pain.gaps.title': 'Besetzungslücken', 'pain.gaps.desc': 'Kurzfristige Krankmeldungen lassen Filialen unterbesetzt.',
  'pain.overtime.title': 'Überstundenfehler', 'pain.overtime.desc': 'Vertragsstunden werden ohne Warnung überschritten.',
  'pain.cta.text': 'Lassen Sie Operota das übernehmen', 'pain.cta.button': 'Kostenlos testen',
  'compare.title': 'Warum Operota statt Excel?',
  'compare.excel.1': 'Manuelle Planung', 'compare.excel.2': 'Kein Vertrags-Tracking',
  'compare.excel.3': 'Keine Multi-Filial-Ansicht', 'compare.excel.4': 'Kein Urlaubs-Tracking',
  'compare.operota.1': 'KI-Autobalance', 'compare.operota.2': 'Vertragskonforme Dienstpläne',
  'compare.operota.3': 'Multi-Filial-Dashboard', 'compare.operota.4': 'Integrierte Abwesenheitsverwaltung',
  'testimonials.title': 'Vertraut von wachsenden Retail-Teams',
  'testimonial.1.quote': 'Operota spart uns 6 Stunden pro Woche. Die KI-Ausbalancierung ist bahnbrechend.', 'testimonial.1.name': 'Marco Rossi', 'testimonial.1.role': 'Filialleiter – Modekette',
  'testimonial.2.quote': 'Seit dem Wechsel verpassen wir keine Schichten mehr. Besetzungslücken gehören der Vergangenheit an.', 'testimonial.2.name': 'Emma Clarke', 'testimonial.2.role': 'Multi-Standort-Inhaberin – Boutique-Gruppe',
  'testimonial.3.quote': 'Endlich Software, die Vertragsstunden respektiert und unsere Filialen konform hält.', 'testimonial.3.name': 'Daniel Weber', 'testimonial.3.role': 'Betriebsleiter – Retail-Kette',
  'faq.title': 'Häufig gestellte Fragen',
  'faq.cancel.q': 'Kann ich jederzeit kündigen?', 'faq.cancel.a': 'Ja, Sie können Ihr Abonnement jederzeit ohne Strafen kündigen. Keine langfristigen Verträge oder versteckte Gebühren. Ihre Daten bleiben bis zum Ende Ihres Abrechnungszeitraums zugänglich.',
  'faq.trial.q': 'Gibt es eine kostenlose Testphase?', 'faq.trial.a': 'Ja. Jedes neue Konto erhält eine vollständige 30-tägige Testphase mit Zugang zu allen Funktionen, einschließlich KI-Planung. Keine Kreditkarte erforderlich.',
  'faq.stores.q': 'Unterstützt es mehrere Filialen?', 'faq.stores.a': 'Absolut. Operota unterstützt unbegrenzte Standorte innerhalb einer Organisation. Verwalten Sie Dienstpläne, Personal und Berichte für alle Filialen von einem Dashboard.',
  'faq.contracts.q': 'Respektiert es Arbeitsverträge?', 'faq.contracts.a': 'Ja. Operota respektiert automatisch die vertraglich vereinbarten Stundenlimits bei der Dienstplanerstellung. Die KI verhindert Überstunden.',
  'faq.security.q': 'Sind meine Daten sicher?', 'faq.security.a': 'Ihre Daten werden im Transit und im Ruhezustand verschlüsselt und vollständig in der EU gehostet. Enterprise-Cloud-Infrastruktur mit strikter DSGVO-Konformität.',
  'finalCta.title': 'Hören Sie auf, Schichten in Tabellen zu verwalten.',
  'finalCta.subtitle': 'Starten Sie Ihre 30-tägige Testphase und übernehmen Sie die Kontrolle über Ihre Personalplanung.',
  'finalCta.button': 'Kostenlos testen', 'finalCta.signIn': 'Anmelden',

  'nav.dashboard': 'Dashboard', 'nav.stores': 'Standorte', 'nav.employees': 'Mitarbeiter',
  'nav.schedule': 'Dienstplan', 'nav.holidays': 'Urlaub', 'nav.absences': 'Abwesenheiten',
  'nav.reports': 'Berichte', 'nav.signOut': 'Abmelden', 'nav.settings': 'Einstellungen',

  'auth.back': 'Zurück', 'auth.title': 'Operota', 'auth.subtitle': 'Verwalten Sie Ihre Dienstpläne einfach',
  'auth.signIn': 'Anmelden', 'auth.signUp': 'Registrieren', 'auth.email': 'E-Mail', 'auth.password': 'Passwort',
  'auth.signingIn': 'Anmelden...', 'auth.creatingAccount': 'Konto erstellen...',
  'auth.createAccount': 'Konto erstellen', 'auth.loading': 'Laden...',
  'auth.checkEmail': 'Überprüfen Sie Ihre E-Mail zur Bestätigung.',
  'auth.invalidCredentials': 'Ungültige E-Mail oder Passwort.',
  'auth.emailNotConfirmed': 'Bitte bestätigen Sie Ihre E-Mail-Adresse.',
  'auth.alreadyRegistered': 'Ein Konto mit dieser E-Mail existiert bereits.',
  'auth.unexpectedError': 'Ein unerwarteter Fehler ist aufgetreten.',
  'auth.invalidEmail': 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
  'auth.passwordMin': 'Passwort muss mindestens 6 Zeichen lang sein',

  'dash.title': 'Dashboard', 'dash.weekOf': 'Woche vom {start} - {end}',
  'dash.today': 'Heute',
  'dash.employeesWorking': 'Mitarbeiter im Dienst', 'dash.workingToday': 'heute im Dienst',
  'dash.onShiftToday': 'heute in Schicht',
  'dash.pendingRequests': 'offene Anträge', 'dash.requestsToReview': 'Anträge zu prüfen',
  'dash.staffingGaps': 'Personalprobleme', 'dash.issuesToday': 'Probleme heute',
  'dash.noIssuesToday': 'Keine Probleme heute', 'dash.noIssues': 'Keine Probleme',
  'dash.totalStores': 'Standorte', 'dash.totalEmployees': 'Mitarbeiter',
  'dash.shiftsThisWeek': 'Schichten diese Woche', 'dash.staffingAlerts': 'Personalalarm',
  'dash.thisWeek': 'Diese Woche', 'dash.upcomingIssues': 'Diese Woche',
  'dash.actionRequired': 'Handlung erforderlich', 'dash.insights': 'Einblicke',
  'dash.understaffed': '{count} Tag(e) unterbesetzt',
  'dash.missingStaff': '{count} Personal fehlt', 'dash.moreIssues': '...und {count} weitere',
  'dash.viewSchedule': 'Dienstplan ansehen', 'dash.stores': 'Standorte', 'dash.storesDesc': 'Standorte verwalten',
  'dash.pendingHolidayRequests': '{count} offene(r) Urlaubsantrag/-anträge',
  'dash.pendingHolidayDesc': 'Anträge genehmigen oder ablehnen', 'dash.reviewRequests': 'Prüfen',
  'dash.nearOvertime': '{count} Mitarbeiter nahe Überstunden',
  'dash.employeeOverContract': 'Mitarbeiter über Vertrag',
  'dash.employeesOverContract': 'Mitarbeiter über Vertrag',
  'dash.approachingLimit': 'nahe am Limit', 'dash.more': 'weitere',
  'dash.allClear': 'Alles in Ordnung — keine Probleme diese Woche',
  'dash.weekGlance': 'Diese Woche auf einen Blick',
  'dash.ofContractHours': 'von {hours}h Vertragsstunden',
  'dash.storesFullyCovered': 'Standorte vollständig besetzt',
  'dash.staffingGapsWeek': 'Personallücken diese Woche',
  'dash.coverageOverview': 'Abdeckungsübersicht',
  'dash.manageStores': 'Standorte verwalten', 'dash.employees': 'Mitarbeiter', 'dash.employeesDesc': 'Personal verwalten',
  'dash.manageEmployees': 'Mitarbeiter verwalten', 'dash.schedule': 'Dienstplan', 'dash.scheduleDesc': 'Dienstpläne erstellen',
  'dash.openSchedule': 'Dienstplan öffnen',

  'stores.title': 'Standorte', 'stores.subtitle': 'Standorte, Personal und Öffnungszeiten verwalten',
  'stores.add': 'Standort hinzufügen', 'stores.edit': 'Standort bearbeiten', 'stores.addNew': 'Neuer Standort',
  'stores.editDesc': 'Details aktualisieren', 'stores.addDesc': 'Neuen Standort hinzufügen',
  'stores.name': 'Name', 'stores.address': 'Adresse', 'stores.staffingReq': 'Täglicher Personalbedarf',
  'stores.timeBlockStaffing': 'Personal nach Zeitblock', 'stores.timeBlockDesc': 'Definieren Sie den Bedarf pro Zeitraum.', 'stores.addBlock': 'Hinzufügen', 'stores.noBlocks': 'Keine Zeitblöcke — Tagesbedarf gilt.', 'stores.staff': 'Pers.', 'stores.colTimeBlocks': 'Zeitblöcke',
  'stores.create': 'Standort erstellen', 'stores.update': 'Aktualisieren', 'stores.loading': 'Standorte laden...',
  'stores.empty': 'Keine Standorte. Fügen Sie Ihren ersten hinzu.', 'stores.colName': 'Name',
  'stores.colAddress': 'Adresse', 'stores.colHours': 'Öffnungszeiten', 'stores.colStaffing': 'Personal',
  'stores.colActions': 'Aktionen', 'stores.notSet': 'Nicht festgelegt',
  'stores.breakPolicy': 'Pausenregelung', 'stores.breakPolicyDesc': 'Automatische Pausenabzüge für die Lohnabrechnung konfigurieren.', 'stores.breakIfShiftExceeds': 'Pause anwenden wenn Schicht überschreitet', 'stores.hours': 'Stunden', 'stores.breakDuration': 'Pausendauer', 'stores.addSecondBreak': 'Zweite Pause für lange Schichten', 'stores.storeClosesDuringBreak': 'Geschäft schließt während der Pause', 'stores.colBreaks': 'Pausenregelung', 'stores.breakSummary': '{duration}min Pause ≥{threshold}h',
  'stores.breakSummaryPerDay': 'Pausendauer pro Tag konfiguriert', 'stores.breakPresetHint': 'Klicken Sie auf eine Vorlage, um sie auf alle Tage anzuwenden, oder passen Sie unten pro Tag an.', 'stores.perDayBreakDuration': 'Pausendauer pro Tag',
  'stores.breakExceedsLimit': 'Pause überschreitet 30% einer {hours}h-Schicht (max {max}min)',
  'stores.perDayConfig': 'Tagesweise Konfiguration',
  'stores.block': 'Block',
  'stores.blocks': 'Blöcke',

  'emp.title': 'Mitarbeiter', 'emp.subtitle': 'Personal, Verfügbarkeit und Zuweisungen verwalten',
  'emp.hPerWeek': 'h/Woche',
  'emp.add': 'Mitarbeiter hinzufügen', 'emp.edit': 'Mitarbeiter bearbeiten', 'emp.addNew': 'Neuer Mitarbeiter',
  'emp.editDesc': 'Details aktualisieren', 'emp.addDesc': 'Neues Teammitglied hinzufügen',
  'emp.name': 'Name', 'emp.email': 'E-Mail', 'emp.phone': 'Telefon', 'emp.contractHours': 'Stunden/Woche',
  'emp.primaryStore': 'Hauptstandort', 'emp.selectStore': 'Standort auswählen',
  'emp.dayAvailability': 'Verfügbarkeit', 'emp.canWorkAt': 'Kann arbeiten bei',
  'emp.addBtn': 'Mitarbeiter hinzufügen', 'emp.updateBtn': 'Aktualisieren',
  'emp.search': 'Nach Name, E-Mail, Telefon suchen...', 'emp.nameAZ': 'Name A–Z', 'emp.nameZA': 'Name Z–A',
  'emp.hoursUp': 'Stunden ↑', 'emp.hoursDown': 'Stunden ↓', 'emp.byStore': 'Nach Standort', 'emp.allStores': 'Alle Standorte',
  'emp.loading': 'Mitarbeiter laden...', 'emp.empty': 'Keine Mitarbeiter. Fügen Sie Ihr erstes Teammitglied hinzu.',
  'emp.colName': 'Name', 'emp.colContact': 'Kontakt', 'emp.colHours': 'Stunden',
  'emp.colStore': 'Standort', 'emp.colAvailability': 'Verfügbarkeit', 'emp.colActions': 'Aktionen',

  'sched.title': 'Dienstplan', 'sched.weekOf': 'Woche vom {start} - {end}',
  'sched.allStores': 'Alle Standorte', 'sched.selectStore': 'Standort auswählen',
  'sched.autoGenerate': 'Auto-Generieren', 'sched.export': 'Exportieren', 'sched.clearAll': 'Alles löschen',
  'sched.print': 'Drucken', 'sched.printAll': 'Alle drucken', 'sched.printStore': '{name} drucken',
  'sched.prevWeek': 'Vorherige Woche', 'sched.nextWeek': 'Nächste Woche', 'sched.today': 'Heute',
  'sched.conflicts': 'Dienstplankonflikte', 'sched.andMore': '...und {count} weitere',
  'sched.addStoresFirst': 'Fügen Sie zuerst Standorte hinzu.', 'sched.clear': 'Löschen',
  'sched.holiday': 'Urlaub', 'sched.pendingHoliday': 'Urlaub ausstehend', 'sched.pendingHolidays': '{count} ausstehende Urlaubsanträge', 'sched.add': 'Hinzufügen',
  'sched.pendingTimeOff': 'Ausstehende Abwesenheit',
  'sched.conflictsDetected': '{count} Urlaub-Schicht-Konflikte',
  'sched.includePending': 'Ausstehende einbeziehen',
  'sched.resolveConflicts': 'Automatisch korrigieren',
  'sched.approvedConflict': 'Genehmigter Urlaub',
  'sched.pendingConflict': 'Ausstehende Anfrage',
  'sched.resolveTitle': 'Konflikte automatisch korrigieren',
  'sched.resolveDesc': 'Folgende Änderungen werden vorgenommen, um Urlaubskonflikte zu lösen:',
  'sched.willResolve': '{count} Konflikte werden behoben:',
  'sched.cannotResolve': '{count} erfordern manuelle Anpassung:',
  'sched.noReplacement': 'Kein Ersatz verfügbar',
  'sched.removeShift': 'Schicht entfernen',
  'sched.applyFixes': '{count} Korrekturen anwenden',
  'sched.resolveSuccess': '{count} Konflikte gelöst',
  'sched.resolvePartial': '{count} erfordern noch manuelle Anpassung',
  'sched.editShift': 'Schicht bearbeiten', 'sched.addShift': 'Schicht hinzufügen',
  'sched.editShiftDesc': 'Schichtdetails aktualisieren', 'sched.addShiftDesc': 'Neue Schicht planen',
  'sched.employee': 'Mitarbeiter', 'sched.selectEmployee': 'Mitarbeiter auswählen',
  'sched.store': 'Standort', 'sched.date': 'Datum', 'sched.startTime': 'Beginn', 'sched.endTime': 'Ende',
  'sched.updateShift': 'Schicht aktualisieren', 'sched.addShiftBtn': 'Schicht hinzufügen',
  'sched.clearConfirm': 'Alle Schichten dieser Woche löschen?',
  'sched.clearStoreConfirm': 'Alle Schichten von {name} diese Woche löschen?',
  'sched.strategyStandard': 'Standard',
  'sched.strategyCoverage': 'Abdeckungspriorität',
  'sched.strategyBalanced': 'Ausgewogen',
  'sched.strategyFullCoverage': 'Volle Abdeckung',
  'sched.balancedDesc': 'Optimiert Fairness und Vertragstreue bei gleichzeitiger Abdeckung.',
  'sched.coverageDesc': 'Priorisiert volle Besetzung. Kann begrenzte Überstunden erlauben.',
  'sched.recommended': 'Empfohlen',
  'sched.coverageWarning': 'Priorisiert volle Abdeckung. Kann begrenzte Überstunden verursachen.',
  'sched.strategyLabel': 'Planungsstrategie',
  'sched.defaultStrategy': 'Standardstrategie',
  'sched.defaultStrategyDesc': 'Standardstrategie für Ihre Organisation festlegen.',
  'sched.strategySaved': 'Standardstrategie aktualisiert',
  'sched.shiftAdded': 'Schicht erfolgreich hinzugefügt',
  'sched.shiftUpdated': 'Schicht erfolgreich aktualisiert',
  'sched.shiftRemoved': 'Schicht erfolgreich entfernt',
  'sched.shiftsSwapped': 'Schichten erfolgreich getauscht',
  'sched.shiftMoved': 'Schicht erfolgreich verschoben',
  'sched.errorSwap': 'Fehler beim Tauschen der Schichten',
  'sched.errorMoveShift': 'Fehler beim Verschieben der Schicht',
  'sched.cannotSwapStores': 'Schichten können nicht zwischen verschiedenen Filialen getauscht werden',
  'sched.swapHint': 'Ablegen zum Tauschen',
  'sched.alreadyScheduledSwap': 'Der Mitarbeiter ist an diesem Tag bereits eingeplant',
  'sched.availabilityOverrideTitle': 'Verfügbarkeitsüberschreibung',
  'sched.availabilityOverrideDesc': '{name} ist nur an {days} verfügbar. Diese Schichtänderung genehmigen?',
  'sched.approve': 'Genehmigen',
  'sched.noDaysAvailable': 'keinen Tagen',
  'sched.errorAddShift': 'Fehler beim Hinzufügen der Schicht',
  'sched.errorUpdateShift': 'Fehler beim Aktualisieren der Schicht',
  'sched.errorRemoveShift': 'Fehler beim Entfernen der Schicht',
  'sched.noShiftsToClear': 'Keine Schichten zum Löschen',
  'sched.clearedShifts': '{count} Schicht(en) gelöscht',
  'sched.clickUndo': 'Klicken Sie auf Rückgängig, um sie wiederherzustellen.',
  'sched.undo': 'Rückgängig',
  'sched.restoredShifts': '{count} Schicht(en) wiederhergestellt',
  'sched.errorRestore': 'Fehler beim Wiederherstellen der Schichten',
  'sched.errorClear': 'Fehler beim Löschen des Dienstplans',
  'sched.generatedShifts': '{count} Schichten erfolgreich generiert',
  'sched.errorGenerate': 'Fehler beim Generieren des Dienstplans',
  'sched.validationError': 'Validierungsfehler',
  'sched.cannotAssign': 'Schicht kann nicht zugewiesen werden',
  'sched.alreadyScheduled': '{name} hat bereits eine Schicht an diesem Tag.',
  'sched.onHoliday': '{name} hat genehmigten Urlaub an diesem Tag.',
  'sched.onLeave': '{name} ist an diesem Tag im {type}.',
  'sched.storeClosed': 'Das Geschäft ist an diesem Tag geschlossen.',
  'sched.shiftOutsideHours': 'Schicht liegt außerhalb der Öffnungszeiten',
  'sched.shiftOutsideHoursWarning': 'Diese Schicht liegt außerhalb der Öffnungszeiten. Die Mehrzeit wird in den Berichten als Überstunden erfasst.',
  'sched.significantOvertimeTitle': 'Schicht deutlich außerhalb der Öffnungszeiten',
  'sched.significantOvertimeDesc': 'Diese Schicht liegt mehr als 60 Minuten außerhalb der Öffnungszeiten. Möchten Sie sie trotzdem speichern?',
  'sched.saveAnyway': 'Trotzdem speichern',
  'sched.shiftPaidBreak': '{paid} bezahlt ({break} Pause)',
  'sched.breakDeducted': '{duration} Pause von den bezahlten Stunden abgezogen',
  'sched.bannerGenerated': 'Dienstplan erstellt — {employees} Mitarbeiter eingeteilt, {hours}h Vertragsstunden eingehalten, {gaps}, Schichten fair verteilt.',
  'sched.bannerNoGaps': 'keine Abdeckungslücken',
  'sched.bannerGaps': '{count} Abdeckungslücke(n)',
  'sched.bannerNoIssues': 'Keine Probleme gefunden',
  'sched.bannerReviewGaps': '{count} Abdeckungslücke(n) zu prüfen →',
  'sched.exportSuccess': 'Dienstplan erfolgreich exportiert',
  'sched.coverageDiag': 'Abdeckungsdiagnose',
  'sched.tbCoverageDiag': 'Zeitblock-Abdeckungsprobleme',
  'sched.moreGaps': '...und {count} weitere Lücken',
  'sched.coverageMet': 'Besetzung: {staffed} / {required} ✔',
  'sched.coverageShort': 'Besetzung: {staffed} / {required} ✘',
  'sched.coverageOver': 'Besetzung: {staffed} / {required} ⚠',
  'sched.missingStaff': '{count} fehlend für {time}',
  'sched.requirements': 'Anforderungen',
  'sched.shifts': 'Schichten',
  'sched.diagNoAvail': 'Keine Verfügbarkeit ({count})',
  'sched.diagContractExh': 'Vertrag erschöpft ({count})',
  'sched.diagMaxDays': 'Max. Tage erreicht ({count})',
  'sched.diagNoAssigned': 'Keine Mitarbeiter diesem Standort zugewiesen',
  'sched.diagNoEligible': 'Keine berechtigten Mitarbeiter',
  'sched.diagNoReq': 'Kein Personalbedarf konfiguriert',
  'sched.conflictOverHours': '{name} ist für {hours}h eingeteilt, aber der Vertrag beträgt {contract}h',
  'sched.conflictUnavailable': '{name} ist am {day} nicht verfügbar',
  'sched.conflictWrongStore': '{name} ist nicht {store} zugewiesen',
  'sched.exportEmployee': 'Mitarbeiter',
  'sched.exportStaffing': 'Besetzung',
  'sched.exportReqSched': 'Erforderlich/Geplant',
  'abs.typeSick': 'Krank',
  'abs.typeEmergency': 'Notfall',
  'sched.copyWeek': 'Woche kopieren',
  'sched.copyWeekTitle': 'Wochenplan kopieren',
  'sched.copyWeekDesc': 'Alle Schichten einer Woche in eine andere duplizieren.',
  'sched.sourceWeek': 'Quellwoche',
  'sched.targetWeek': 'Zielwoche',
  'sched.copyWeekConfirm': 'Plan kopieren',
  'sched.copyWeekSuccess': 'Plan erfolgreich kopiert.',
  'sched.copyWeekError': 'Fehler beim Kopieren',
  'sched.copyWeekNoShifts': 'Keine Schichten in der Quellwoche gefunden.',
  'sched.copyWeekReplace': 'Diese Woche hat bereits {count} Schicht(en). Bestehenden Plan ersetzen?',
  'sched.copyWeekReplaceBtn': 'Ersetzen & kopieren',
  'sched.copyWeekCopying': 'Wird kopiert...',
  'sched.selectTargetWeek': 'Zielwoche auswählen',
  'sched.copyNextWeek': 'Nächste Woche',
  'sched.copyWeekAfterNext': 'Übernächste Woche',
  'sched.copySpecificWeek': 'Woche auswählen',

  'hol.title': 'Urlaub', 'hol.subtitle': 'Urlaubsanträge und Kontingente verfolgen',
  'hol.request': 'Urlaub beantragen', 'hol.balances': 'Urlaubskontingente',
  'hol.allRequests': 'Alle Anträge', 'hol.noRequests': 'Keine Urlaubsanträge',
  'hol.employee': 'Mitarbeiter', 'hol.selectEmployee': 'Mitarbeiter auswählen', 'hol.dateRange': 'Zeitraum',
  'hol.pickDates': 'Daten auswählen', 'hol.notes': 'Notizen (optional)', 'hol.notesPlaceholder': 'Grund...',
  'hol.submit': 'Antrag einreichen', 'hol.businessDays': '{count} Arbeitstage',
  'hol.approved': 'Genehmigt', 'hol.rejected': 'Abgelehnt', 'hol.pending': 'Ausstehend', 'hol.delete': 'Löschen',
  'hol.days': '{count} Tage', 'hol.used': 'Genommen', 'hol.pendingLabel': 'Ausstehend', 'hol.total': 'Gesamt',
  'hol.dialogTitle': 'Urlaub beantragen', 'hol.dialogDesc': 'Neuen Urlaubsantrag einreichen',
  'hol.partialApprove': 'Teilweise genehmigen', 'hol.partialApproveTitle': 'Teilgenehmigung',
  'hol.selectApprovedDates': 'Genehmigte Daten auswählen', 'hol.partialSummary': '{approved} von {total} Tagen genehmigt',
  'hol.partialApproveAction': '{count} Tage genehmigen', 'hol.partialApproved': 'Teilweise genehmigt',
  'hol.partialRejected': 'Nicht genehmigt (teilweise)', 'hol.partialRemainder': 'Verbleibend aus Teilgenehmigung',
  'hol.partialLabel': 'Teilweise',

  'abs.title': 'Abwesenheiten', 'abs.subtitle': 'Krankheitstage und Notfallurlaub verfolgen',
  'abs.record': 'Abwesenheit erfassen', 'abs.summary': 'Mitarbeiterübersicht',
  'abs.allRecords': 'Alle Einträge', 'abs.all': 'Alle', 'abs.sickDays': 'Krankheitstage',
  'abs.emergencyLeave': 'Notfallurlaub', 'abs.sick': 'Krank', 'abs.emergency': 'Notfall',
  'abs.noRecords': 'Keine Einträge', 'abs.employee': 'Mitarbeiter', 'abs.selectEmployee': 'Mitarbeiter auswählen',
  'abs.type': 'Typ', 'abs.sickDay': 'Krankheitstag', 'abs.dates': 'Daten', 'abs.pickDates': 'Daten auswählen',
  'abs.reason': 'Grund', 'abs.reasonPlaceholder': 'z.B. Grippe, Familiennotfall...',
  'abs.notes': 'Notizen (optional)', 'abs.notesPlaceholder': 'Weitere Details...',
  'abs.recordBtn': 'Abwesenheit erfassen', 'abs.businessDays': '{count} Arbeitstage',
  'abs.dialogTitle': 'Abwesenheit erfassen', 'abs.dialogDesc': 'Krankheitstag oder Notfall erfassen',
  'abs.totalLabel': 'Gesamt',

  'rep.title': 'Berichte', 'rep.weekOf': 'Woche vom {label}', 'rep.excel': 'Excel', 'rep.pdfPrint': 'PDF / Drucken',
  'rep.thisWeek': 'Diese Woche', 'rep.allStores': 'Alle Standorte', 'rep.warnings': 'Warnungen',
  'rep.hoursVsContract': 'Stunden vs Vertrag', 'rep.storeCoverage': 'Abdeckung', 'rep.holidayBalance': 'Urlaubskonto',
  'rep.year': 'Jahr:', 'rep.loading': 'Laden…',
  'rep.employeeOverContract': 'Mitarbeiter über Vertrag',
  'rep.employeesOverContract': 'Mitarbeiter über Vertrag',
  'rep.totalOvertimeRisk': 'Gesamtrisiko Überstunden',
  'rep.employeeHours': 'Mitarbeiterstunden',
  'rep.colEmployee': 'Mitarbeiter', 'rep.colStore': 'Standort', 'rep.colContract': 'Vertrag',
  'rep.colScheduled': 'Geplant', 'rep.colExpected': 'Erwartet', 'rep.colDifference': 'Differenz',
  'rep.noEmployees': 'Keine Mitarbeiter vorhanden.',
  'rep.noStores': 'Keine Standorte vorhanden.',
  'rep.understaffedDay': '{count} unterbesetzter Tag diese Woche.',
  'rep.understaffedDays': '{count} unterbesetzte Tage diese Woche.',
  'rep.needMore': '{count} mehr benötigt', 'rep.extra': '+{count} extra', 'rep.ok': 'OK',
  'rep.holidayBalances': 'Urlaubssalden',
  'rep.colAllowance': 'Anspruch', 'rep.colApproved': 'Genehmigt', 'rep.colPending': 'Ausstehend',
  'rep.colRemaining': 'Verbleibend', 'rep.colUsage': 'Nutzung',
  'rep.lowOrOverusedSingular': 'Mitarbeiter mit niedrigem oder überschrittenem Urlaubssaldo',
  'rep.lowOrOverused': 'Mitarbeiter mit niedrigem oder überschrittenem Urlaubssaldo',
  'rep.allClear': 'Alles in Ordnung', 'rep.noIssues': 'Keine Planungsprobleme diese Woche.',
  'rep.issueSingular': 'Problem zu prüfen',
  'rep.issuesToReview': 'Probleme zu prüfen',
  'rep.urgent': 'dringend',
  'rep.holidayConflict': 'Urlaubskonflikt', 'rep.absenceConflict': 'Abwesenheitskonflikt',
  'rep.overHours': 'Überstunden', 'rep.unavailable': 'Nicht verfügbar', 'rep.understaffed': 'Unterbesetzt',
  'rep.fix': 'Beheben',
  'rep.tabOverview': 'Übersicht',
  'rep.tabHoursPayroll': 'Stunden & Abrechnung',
  'rep.tabCoverage': 'Abdeckung',
  'rep.tabLeave': 'Urlaub',
  'rep.tabCompliance': 'Compliance',
  'rep.overviewScheduledH': 'Geplante Stunden',
  'rep.overviewOvertimeH': 'Überstunden',
  'rep.overviewCoverageGaps': 'Abdeckungslücken',
  'rep.overviewNearLimit': 'Nahe am Limit',
  'rep.overviewExceeding': 'Vertragsüberschreitung',
  'rep.overviewRiskLevel': 'Risikostufe',
  'rep.overviewNoIssues': 'Keine aktiven Probleme diese Woche.',
  'rep.viewCompliance': 'Compliance anzeigen',
  'rep.viewCoverage': 'Abdeckung anzeigen',
  'rep.exportSummary': 'Zusammenfassung exportieren',
  'rep.exportExcel': 'Excel exportieren',
  'rep.exportPDF': 'PDF exportieren',
  'rep.exportedExcel': 'Als Excel exportiert',
  'rep.exportedExcelDesc': 'Prüfen Sie Ihren Downloads-Ordner.',
  'rep.exportedCSV': 'Als CSV exportiert',
  'rep.weekly': 'Wöchentlich',
  'rep.monthly': 'Monatlich',
  'rep.totalScheduledH': 'Geplante Stunden gesamt',
  'rep.chartTitle': 'Geplante vs erwartete Stunden pro Mitarbeiter',
  'rep.chartOnTrack': 'Konform',
  'rep.chartOverContract': 'Über Vertrag',
  'rep.chartUnderhours': 'Unterstunden',
  'rep.chartExpected': 'Erwartet',
  'rep.issuesToReviewTitle': 'Probleme zu prüfen',
  'rep.allClearPeriod': 'Alles in Ordnung — keine Probleme in diesem Zeitraum.',
  'rep.employeesOverContractLabel': 'Mitarbeiter über Vertrag',
  'rep.totalOvertimeRiskLabel': 'Überstundenrisiko gesamt',
  'rep.employeeHoursTitle': 'Mitarbeiterstunden',
  'rep.colOvertime': 'Überstunden',
  'rep.colUnderhours': 'Unterstunden',
  'rep.colStatus': 'Status',
  'rep.payrollBreakdown': 'Lohnabrechnung — {period}',
  'rep.colScheduledH': 'Geplant (h)',
  'rep.colPaidHolidayH': 'Bezahlter Urlaub (h)',
  'rep.colPaidSickH': 'Bezahlte Krankheit (h)',
  'rep.colCountedHours': 'Gezählte Stunden',
  'rep.colSickDays': 'Krankheitstage',
  'rep.totals': 'Gesamt',
  'rep.totalScheduled': 'Gesamt geplant',
  'rep.totalActual': 'Gesamt tatsächlich',
  'rep.lowOrOverusedLabel': 'Mitarbeiter mit niedrigem oder überschrittenem Urlaubssaldo',
  'rep.holidayAllowanceTitle': 'Urlaubsverbrauch pro Mitarbeiter',
  'rep.sickDaySummary': 'Krankheitstage-Übersicht',
  'rep.colSickDaysUsed': 'Krankheitstage genutzt',
  'rep.colAnnualAllowance': 'Jahresanspruch',
  'rep.colInstances': 'Fälle',
  'rep.colPaid': 'Bezahlt',
  'rep.colUnpaid': 'Unbezahlt',
  'rep.totalEmployees': 'Mitarbeiter gesamt',
  'rep.compliant': 'Konform',
  'rep.compWarnings': 'Warnungen',
  'rep.actionRequired': 'Handlungsbedarf',
  'rep.complianceDisclaimer': 'Vertragsstunden können aufgrund von Öffnungszeiten und Mindestschichtdauer leicht variieren. Kleine Abweichungen (±2h) sind normal.',
  'rep.hExp': '{h}h erw.',
  'rep.hCounted': '{h}h gezählt',
  'rep.hoursBreakdown': 'Stundenaufschlüsselung',
  'rep.scheduledWorked': 'Geplant (gearbeitet)',
  'rep.paidHoliday': 'Bezahlter Urlaub',
  'rep.paidSick': 'Bezahlte Krankheit',
  'rep.unpaidLeave': 'Unbezahlter Urlaub',
  'rep.complianceDetail': 'Compliance-Details',
  'rep.contract': 'Vertrag',
  'rep.expected': 'Erwartet',
  'rep.counted': 'Gezählt',
  'rep.variance': 'Abweichung',
  'rep.maxStreak': 'Max aufeinanderfolgend',
  'rep.leaveBalanceYear': 'Urlaubssaldo (Jahr)',
  'rep.holiday': 'Urlaub',
  'rep.sick': 'Krankheit',
  'rep.remainingOf': '{remaining} verbleibend von {total}T',
  'rep.riskLog': 'Risikoprotokoll',
  'rep.highRisk': 'Hohes Risiko',
  'rep.mediumRisk': 'Mittleres Risiko',
  'rep.lowRisk': 'Niedriges Risiko',
  'rep.colRiskType': 'Risikotyp',
  'rep.colDateWeek': 'Datum/Woche',
  'rep.colSeverity': 'Schwere',
  'rep.colDescription': 'Beschreibung',
  'rep.filterAll': 'Alle',
  'rep.filterHigh': 'Hoch',
  'rep.filterMedium': 'Mittel',
  'rep.filterLow': 'Niedrig',
  'rep.statusCompliant': 'Konform',
  'rep.statusWarning': 'Warnung',
  'rep.statusActionRequired': 'Handlungsbedarf',
  'rep.statusOnTrack': 'Konform',
  'rep.statusAtRisk': 'Gefährdet',
  'rep.riskHigh': 'Hoch',
  'rep.riskMedium': 'Mittel',
  'rep.riskLow': 'Niedrig',
  'rep.gap': 'LÜCKE',
  'rep.tooltipScheduled': 'Geplant: {h}h',
  'rep.tooltipExpected': 'Erwartet: {h}h',
  'rep.tooltipVariance': 'Abweichung: {h}h',
  'rep.tooltipStatus': 'Status: {status}',

  'pva.title': 'Geplante vs tatsächliche Stunden',
  'pva.totalScheduled': 'Gesamt geplant',
  'pva.totalPlanned': 'Gesamt geplant',
  'pva.totalActual': 'Gesamt tatsächlich',
  'pva.totalVariance': 'Gesamtabweichung',
  'pva.confirmRate': '{confirmed}/{total} Schichten bestätigt',
  'pva.employeeBreakdown': 'Aufschlüsselung nach Mitarbeiter',
  'pva.scheduled': 'Geplant',
  'pva.planned': 'Geplant',
  'pva.actual': 'Tatsächlich',
  'pva.variance': 'Abweichung',
  'pva.breakVar': 'Pause ±',
  'pva.confirmed': 'Bestätigt',
  'pva.lateArrivals': 'Verspätet',
  'pva.earlyLeaves': 'Früher weg',
  'pva.totals': 'Gesamt',
  'pva.noConfirmedShifts': 'Keine bestätigten Schichten — bestätigen Sie Schichten für Ist-Daten',
  'pva.lateArrivalsCount': '{count} Verspätung(en)',
  'pva.earlyLeavesCount': '{count} frühe(r) Abgang/Abgänge',
  'pva.totalOvertimeInsight': '{hours}h Überstunden',
  'pva.totalUnderhoursInsight': '{hours}h unter Plan',
  'pva.breakOvertime': '{mins}m extra Pause',
  'pva.breakScheduledVsActual': 'Pause: Geplant / Tatsächlich',
  'review.actualBreakStart': 'Pausenbeginn',
  'review.actualBreakEnd': 'Pausenende',

  // ── Warning Explanations ──
  'warn.hoursOverTitle': '{hours} Stunden über Vertrag',
  'warn.hoursOverDesc': 'Dieser Mitarbeiter ist über seine vereinbarten Wochenstunden eingeplant.',
  'warn.hoursOverOpt1': 'Schicht entfernen',
  'warn.hoursOverOpt2': 'Einem anderen Mitarbeiter zuweisen',
  'warn.hoursOverOpt3': 'Vertragsstunden erhöhen',
  'warn.unfillableTitle': '{hours} Stunden können diese Woche nicht eingeplant werden',
  'warn.unfillableWhy': 'Warum:',
  'warn.unfillableR1': 'Alle Standorte sind voll besetzt oder geschlossen',
  'warn.unfillableR2': 'Die verbleibende Verfügbarkeit passt nicht zu offenen Schichten',
  'warn.unfillableR3': 'Die Mindestschichtdauer verhindert Teilzuweisungen',
  'warn.unfillableOpt1': 'Vertragsstunden für diese Woche reduzieren',
  'warn.unfillableOpt2': 'Kürzere Schichten erlauben',
  'warn.unfillableOpt3': 'Personalbedarf erhöhen',
  'warn.nearContractTitle': '{hours} Stunden unter Vertrag',
  'warn.nearContractDesc': 'Dies liegt daran, dass alle verbleibenden Schichten volle Länge haben und das System keine zu kurzen Schichten erstellt.',
  'warn.nearContractOpt1': 'Untervertragswochen akzeptieren',
  'warn.nearContractOpt2': 'Kürzere Schichten erlauben',
  'warn.nearContractOpt3': 'Vertragsstunden anpassen',
  'warn.underTitle': '{hours} Stunden unter Vertrag',
  'warn.underDesc': 'Dieser Mitarbeiter hat weniger Stunden eingeplant als der Vertrag erfordert. Weitere Schichten können hinzugefügt werden.',
  'warn.underOpt1': 'Schichten manuell hinzufügen',
  'warn.underOpt2': 'Auto-Generierung ausführen',
  'warn.underOpt3': 'Vertragsstunden anpassen',
  'warn.options': 'Optionen:',
  'warn.holidayDesc': 'Dieser Mitarbeiter ist an diesem Tag im genehmigten Urlaub und kann nicht eingeplant werden.',
  'warn.holidayOpt1': 'Schicht entfernen',
  'warn.holidayOpt2': 'Anderen Mitarbeiter zuweisen',
  'warn.absenceDesc': 'Dieser Mitarbeiter ist an diesem Tag im genehmigten Urlaub und sollte nicht eingeplant werden.',
  'warn.absenceOpt1': 'Schicht entfernen',
  'warn.absenceOpt2': 'Anderen Mitarbeiter zuweisen',
  'warn.unavailableDesc': 'Diese Schicht liegt außerhalb der verfügbaren Arbeitstage des Mitarbeiters.',
  'warn.unavailableOpt1': 'Schichtdatum ändern',
  'warn.unavailableOpt2': 'Anderen Mitarbeiter zuweisen',
  'warn.unavailableOpt3': 'Verfügbarkeit aktualisieren',
  'warn.understaffedDesc': 'Dieser Standort hat an diesem Tag nicht genügend Personal eingeplant.',
  'warn.understaffedOpt1': 'Weiteren Mitarbeiter hinzufügen',
  'warn.understaffedOpt2': 'Personalanforderungen anpassen',
  'warn.understaffedOpt3': 'Verfügbarkeit der Mitarbeiter prüfen',
  'warn.weeklyHours': 'Wöchentliche Stundenübersicht',
  'warn.maxDaysTitle': 'Maximale Arbeitstage erreicht',
  'warn.maxDaysDesc': 'Dieser Mitarbeiter hat sein Maximum von {days} Arbeitstagen diese Woche erreicht. Weitere Schichten können nicht zugewiesen werden, auch wenn Vertragsstunden übrig sind.',
  'warn.maxDaysOpt1': 'Maximale Arbeitstage erhöhen',
  'warn.maxDaysOpt2': 'Unterstunden-Woche akzeptieren',
  'warn.maxDaysOpt3': 'Vertragsstunden anpassen',
  'emp.maxWorkingDays': 'Max. Arbeitstage/Woche',

  'notfound.title': '404', 'notfound.message': 'Seite nicht gefunden', 'notfound.link': 'Zurück zur Startseite',
  'onb.title': 'Erstellen Sie Ihre Organisation',
  'onb.subtitle': 'Geben Sie Ihrem Unternehmen oder Team einen Namen.',
  'onb.label': 'Organisationsname',
  'onb.placeholder': 'Mein Unternehmen',
  'onb.creating': 'Wird erstellt...',
  'onb.create': 'Organisation erstellen',
  'onb.signOut': 'Abmelden',
  'onb.invalidName': 'Ungültiger Name',
  'onb.nameLength': 'Der Name muss 1-100 Zeichen lang sein.',
  'setup.title': 'Richten wir Ihr Unternehmen ein',
  'setup.subtitle': 'Fügen Sie Ihre erste Filiale und Mitarbeiter hinzu, um Ihren ersten ausgewogenen Dienstplan zu erstellen.',
  'setup.progress': 'Schritt {count} von {total} abgeschlossen',
  'setup.step1': 'Erste Filiale hinzufügen',
  'setup.step2': 'Ersten Mitarbeiter hinzufügen',
  'setup.step3': 'Ersten Dienstplan erstellen',
  'setup.step3Hint': 'Benötigt mindestens 1 Filiale und 1 Mitarbeiter',
  'setup.addAnother': 'Weiteren hinzufügen',
  'setup.storeAdded': 'Filiale hinzugefügt!',
  'setup.employeeAdded': 'Mitarbeiter hinzugefügt!',
  'setup.storeCount1': 'Filiale hinzugefügt',
  'setup.storeCountN': 'Filialen hinzugefügt',
  'setup.empCount1': 'Mitarbeiter hinzugefügt',
  'setup.empCountN': 'Mitarbeiter hinzugefügt',
  'setup.generate': 'Ersten Dienstplan erstellen',
  'setup.generating': 'Wird erstellt...',
  'setup.success': 'Ihr erster ausgewogener Dienstplan ist fertig.',
  'setup.genError': 'Fehler bei der Dienstplanerstellung',
  'setup.noShiftsError': 'Keine Schichten generiert. Prüfen Sie Personalanforderungen und Verfügbarkeit.',
  'setup.skip': 'Vorerst überspringen',
  'setup.emptySchedule': 'Sie benötigen mindestens 1 Filiale und 1 Mitarbeiter, um einen Dienstplan zu erstellen.',
  'settings.title': 'Einstellungen',
  'settings.subtitle': 'Firmenpräferenzen und Konfiguration',
  'settings.balancedDesc': 'Maximiert die Abdeckung bei minimaler Überstunden. Empfohlen für die meisten Teams.',
  'hours.label': 'Öffnungszeiten',
  'hours.quickSet': 'Schnelleinstellung',
  'hours.to': 'bis',
  'hours.lunchBreak': 'Mittagspause',
  'hours.applyWeekdays': 'Mo–Fr anwenden',
  'hours.applyAll': 'Alle Tage anwenden',
  'hours.open': 'Geöffnet',
  'hours.closed': 'Geschlossen',
  'ai.getSuggestions': 'KI-Vorschläge',
  'ai.autoFix': 'Auto-Korrektur',
  'sched.fix': 'Beheben',
  'sched.reduceHours': 'Stunden reduzieren',
  'sched.reassignShift': 'Schicht umverteilen',
  'sched.confirm': 'Bestätigen',
  'sched.edit': 'Bearbeiten',
  'sched.remove': 'Entfernen',
  'sched.understaffed': '⚠ Unterbesetzt',
  'sched.missingCount': '{count} Mitarbeiter fehlen',
  'sched.staffed': 'besetzt',
  'sched.staff': 'Personal',
  'sched.statusReady': 'Dienstplan bereit',
  'sched.statusIssue1': '1 Problem zu prüfen',
  'sched.statusIssues': '{count} Probleme zu prüfen',
  'sched.issueOverHours1': '{name} überschreitet Wochenstunden',
  'sched.issueOverHoursN': '{count} Mitarbeiter überschreiten ihre Stunden',
  'sched.issueShiftTooLong1': '{count} Schicht zu lang',
  'sched.issueShiftTooLongN': '{count} Schichten zu lang',
  'sched.issueInvalidTimes1': '{count} Schicht mit ungültigen Zeiten',
  'sched.issueInvalidTimesN': '{count} Schichten mit ungültigen Zeiten',
  'sched.issueOutsideHours1': '{count} Schicht außerhalb der Öffnungszeiten',
  'sched.issueOutsideHoursN': '{count} Schichten außerhalb der Öffnungszeiten',
  'sched.issueClosedDay1': '{count} Schicht an geschlossenem Tag',
  'sched.issueClosedDayN': '{count} Schichten an geschlossenen Tagen',
  'sched.issueUnavailable1': '{name} an nicht verfügbarem Tag eingeteilt',
  'sched.issueUnavailableN': '{count} Schichten an nicht verfügbaren Tagen',
  'sched.issueWrongStore1': '{name} falschem Standort zugewiesen',
  'sched.issueWrongStoreN': '{count} Mitarbeiter an falschen Standorten',
  'sched.issueOverlap1': '{count} überlappende Schicht',
  'sched.issueOverlapN': '{count} überlappende Schichten',
  'sched.issueShiftTooShort1': '{count} Schicht zu kurz',
  'sched.issueShiftTooShortN': '{count} Schichten zu kurz',
  'sched.issueUnderstaffed1': '{store} braucht mehr Personal am {day}',
  'sched.issueUnderstaffedN': '{count} Tage unterbesetzt',
  'ai.fixSchedule': 'Mit KI beheben',
  'ai.fixScheduleAI': 'Dienstplan mit KI korrigieren',
  'sched.issuesThisWeek': 'Probleme diese Woche',
  'ai.analyzing': 'Analyse...',
  'ai.suggestions': 'KI-Vorschläge',
  'ai.fixesFound': '{count} Korrekturen gefunden',
  'ai.addresses': 'Betrifft:',
  'ai.apply': 'Anwenden',
  'ai.preview': 'Vorschau',
  'ai.hidePreview': 'Ausblenden',
  'ai.before': 'Vorher',
  'ai.after': 'Nachher',
  'ai.keepsCoverage': 'Abdeckung gesichert',
  'ai.withinContract': 'Im Vertrag',
  'ai.shiftRemoved': 'Schicht entfernt',
  'ai.noShift': 'Keine Schicht',
  'ai.refresh': 'Aktualisieren',
  'ai.swap': 'Tauschen',
  'ai.reassign': 'Neu zuweisen',
  'ai.addShift': 'Schicht hinzufügen',
  'ai.remove': 'Entfernen',
  'ai.adjust': 'Anpassen',
  'ai.applied': 'Korrektur angewendet',
  'ai.changes': 'Änderungen',
  'ai.failedApply': 'Anwendung fehlgeschlagen',
  'ai.failedFetch': 'Abruf fehlgeschlagen',
  'ai.aiError': 'KI-Fehler',
  'ai.importTitle': 'KI-Datenimport',
  'ai.stageInput': 'Beschreiben Sie Ihr Unternehmen. Filialen werden zuerst analysiert, dann Mitarbeiter.',
  'ai.stageParsingStores': 'Ihr Text wird auf Filialinformationen analysiert...',
  'ai.stagePreviewStores': 'Überprüfen Sie die Filialen. Nach Bestätigung werden Mitarbeiter analysiert.',
  'ai.stageSavingStores': 'Filialen werden gespeichert...',
  'ai.stageCounting': 'Mitarbeiter in Ihrem Text werden erkannt...',
  'ai.stageParsingEmps': 'Mitarbeiter werden in Stapeln analysiert...',
  'ai.stageValidating': 'Mitarbeiterdaten werden validiert...',
  'ai.stagePreviewEmps': 'Überprüfen Sie die Mitarbeiter. Klicken Sie auf „Bestätigen" zum Speichern.',
  'ai.stageImporting': 'Mitarbeiter werden importiert...',
  'ai.stageComplete': 'Alle Daten erfolgreich importiert!',
  'ai.parseStores': 'Filialen analysieren',
  'ai.startOver': 'Neu beginnen',
  'ai.confirmStores': 'Filialen bestätigen & Mitarbeiter analysieren',
  'ai.importAnyway': 'Trotzdem importieren',
  'ai.confirmImport': 'Import bestätigen',
  'ai.done': 'Fertig',
  'ai.tip': 'Tipp: Geben Sie Filialnamen, Öffnungszeiten, Personalbedarf, Mitarbeiternamen, Vertragsstunden, Zuweisungen und Verfügbarkeit an.',
  'ai.storesSaved': 'Filialen gespeichert',
  'ai.employees': 'Mitarbeiter',
  'ai.totalWeekly': 'wöchentlich gesamt',
  'ai.importedSuccess': '{stores} Filialen und {employees} Mitarbeiter erfolgreich importiert.',
  'summary.ofContract': 'des Vertrags',
  'summary.over': '+{hours}h über',
  'summary.cantFill': '{hours}h nicht besetzbar',
  'summary.belowMin': '{hours}h unter Mindestschicht',
  'summary.remaining': '{hours}h verbleibend',
  'dash.importData': 'Daten importieren',
  'dash.importTitle': 'Geschäftsdaten importieren',
  'dash.importDescription': 'Wählen Sie, wie Sie Ihre Filialen und Mitarbeiter einrichten möchten.',
  'dash.aiImportTitle': 'KI-Smart-Import',
  'dash.aiImportDesc': 'Fügen Sie eine Beschreibung Ihres Unternehmens ein und lassen Sie die KI automatisch Filialen, Mitarbeiter und Verträge erstellen.',
  'dash.startAiImport': 'KI-Import starten',
  'dash.excelImportTitle': 'Excel-Vorlage hochladen',
  'dash.excelImportDesc': 'Laden Sie eine strukturierte Excel-Datei mit Ihren Filialen und Mitarbeitern für einen schnellen, validierten Import hoch.',
  'dash.uploadFile': 'Datei hochladen',
  'dash.importHint': 'Unsicher? Der KI-Import eignet sich am besten für eine schnelle Einrichtung.',

  'day.sun': 'Sonntag', 'day.mon': 'Montag', 'day.tue': 'Dienstag', 'day.wed': 'Mittwoch',
  'day.thu': 'Donnerstag', 'day.fri': 'Freitag', 'day.sat': 'Samstag',
  'day.sunShort': 'So', 'day.monShort': 'Mo', 'day.tueShort': 'Di', 'day.wedShort': 'Mi',
  'day.thuShort': 'Do', 'day.friShort': 'Fr', 'day.satShort': 'Sa',

  'settings.tabOrg': 'Organisation', 'settings.tabTeam': 'Team', 'settings.tabRules': 'Planungsregeln', 'settings.tabDanger': 'Gefahrenzone',
  'settings.orgName': 'Organisationsname', 'settings.orgNameDesc': 'Der Name, der den Teammitgliedern angezeigt wird.', 'settings.save': 'Speichern', 'settings.saved': 'Einstellungen gespeichert',
  'settings.teamTitle': 'Teammitglieder', 'settings.teamDesc': 'Verwalten Sie Ihr Team und senden Sie Einladungen.', 'settings.invite': 'Mitglied einladen',
  'settings.colName': 'Name', 'settings.colEmail': 'E-Mail', 'settings.colRole': 'Rolle', 'settings.colStatus': 'Status', 'settings.colActions': 'Aktionen',
  'settings.roleOwner': 'Eigentümer', 'settings.roleManager': 'Manager', 'settings.active': 'Aktiv', 'settings.pending': 'Ausstehend',
  'settings.remove': 'Entfernen', 'settings.resend': 'Erneut senden', 'settings.removeConfirm': 'Dieses Mitglied entfernen?', 'settings.removed': 'Mitglied entfernt',
  'settings.inviteSent': 'Einladung gesendet', 'settings.inviteEmail': 'E-Mail-Adresse', 'settings.inviteRole': 'Rolle', 'settings.inviteBtn': 'Einladung senden',
  'settings.inviteTitle': 'Teammitglied einladen', 'settings.inviteDesc': 'Senden Sie einen Einladungslink zum Beitritt Ihrer Organisation.',
  'settings.rulesTitle': 'Planungsregeln', 'settings.rulesDesc': 'Konfigurieren Sie die Dienstplangenerierung.',
  'settings.dangerTitle': 'Gefahrenzone', 'settings.dangerDesc': 'Unumkehrbare Aktionen. Mit Vorsicht vorgehen.',
  'settings.deleteOrg': 'Organisation löschen', 'settings.deleteOrgDesc': 'Diese Organisation und alle zugehörigen Daten dauerhaft löschen.',
  'settings.deleteOrgConfirm': 'Sind Sie sicher? Diese Aktion kann nicht rückgängig gemacht werden.', 'settings.clearSchedules': 'Alle Dienstpläne löschen',
  'settings.clearSchedulesDesc': 'Alle Schichtdaten entfernen. Mitarbeiter- und Filialdaten bleiben erhalten.',
  'settings.clearConfirm': 'Sind Sie sicher? Alle Dienstplandaten werden gelöscht.',
  'settings.deleted': 'Organisation gelöscht', 'settings.cleared': 'Dienstpläne gelöscht', 'settings.cancel': 'Abbrechen',
  'settings.you': '(Sie)', 'settings.cannotRemoveSelf': 'Sie können sich nicht selbst entfernen',

  'common.loading': 'Laden...',
  'common.cancel': 'Abbrechen',
  'auth.forgotPassword': 'Passwort vergessen?',
  'auth.or': 'oder',
  'auth.googleSignIn': 'Weiter mit Google',
  'auth.appleSignIn': 'Weiter mit Apple',
  'forgot.title': 'Passwort vergessen',
  'forgot.subtitle': 'Geben Sie Ihre E-Mail ein, um einen Link zum Zurücksetzen zu erhalten.',
  'forgot.button': 'Link senden',
  'forgot.sent': 'Falls ein Konto existiert, wurde ein Link zum Zurücksetzen gesendet.',
  'forgot.backToLogin': 'Zurück zur Anmeldung',
  'reset.title': 'Passwort zurücksetzen',
  'reset.subtitle': 'Geben Sie Ihr neues Passwort ein.',
  'reset.newPassword': 'Neues Passwort',
  'reset.confirmPassword': 'Passwort bestätigen',
  'reset.button': 'Aktualisieren',
  'reset.passwordMin': 'Passwort muss mindestens 8 Zeichen lang sein',
  'reset.mismatch': 'Passwörter stimmen nicht überein',
  'reset.failed': 'Aktualisierung fehlgeschlagen. Bitte erneut versuchen.',
  'reset.success': 'Passwort aktualisiert. Weiterleitung...',
  'reset.invalidLink': 'Dieser Link ist ungültig oder abgelaufen.',
  'reset.backToLogin': 'Zurück zur Anmeldung',
  'settings.tabAccount': 'Konto',
  'settings.tabBilling': 'Abrechnung',
  'billing.currentPlan': 'Aktueller Plan',
  'billing.currentPlanDesc': 'Ihre Abonnementdetails',
  'billing.refresh': 'Aktualisieren',
  'billing.noSubscription': 'Kein aktives Abonnement',
  'billing.paymentMethod': 'Zahlungsmethode',
  'billing.invoices': 'Rechnungen',
  'billing.billingDetails': 'Rechnungsdetails',
  'billing.editDetails': 'Details bearbeiten',
  'billing.activating': 'Abonnement wird aktiviert…',
  'billing.activatingDesc': 'Dies dauert normalerweise nur wenige Sekunden.',
  'billing.subEnded': 'Ihr Abonnement ist abgelaufen',
  'billing.choosePlan': 'Wählen Sie Ihren Plan, um loszulegen',
  'billing.pastDue': 'Zahlung überfällig',
  'billing.canceled': 'Storniert',
  'billing.expired': 'Abgelaufen',
  'billing.monthly': 'Monatlich',
  'billing.annual': 'Jährlich',
  'billing.save17': '~17% sparen',
  'billing.trialDesc': '30-tägige kostenlose Testphase inklusive. Jederzeit kündbar.',
  'billing.redirecting': 'Weiterleitung…',
  'billing.reactivate': 'Abonnement reaktivieren',
  'billing.startTrial': 'Testphase starten',
  'billing.checkStatus': 'Bereits bezahlt? Status prüfen',
  'billing.accountSettings': 'Kontoeinstellungen',
  'billing.signOut': 'Abmelden',
  'billing.title': 'Abrechnung & Zahlungen',
  'billing.subtitle': 'Verwalten Sie Ihr Abonnement, Zahlungsmethode und Rechnungen',
  'billing.freeTrial': 'Kostenlose Testphase',
  'billing.trialEnds': 'Testphase endet',
  'billing.billingCycle': 'Abrechnungszyklus',
  'billing.cancelNotice': 'Ihr Abonnement endet am {date}. Sie können es über das Abrechnungsportal fortsetzen.',
  'billing.noSub': 'Kein aktives Abonnement gefunden.',
  'billing.update': 'Ändern',
  'billing.noPayment': 'Keine Zahlungsmethode hinterlegt',
  'billing.addCard': 'Karte hinzufügen',
  'billing.noInvoicesYet': 'Noch keine Rechnungen',
  'billing.detailsDesc': 'Aktualisieren Sie Firmenname, Rechnungs-E-Mail, USt-IdNr. und Adresse über das Portal.',
  'billing.companyName': 'Firmenname',
  'billing.billingEmail': 'Rechnungs-E-Mail',
  'billing.vatNumber': 'USt-IdNr.',
  'billing.address': 'Adresse',
  'billing.editBilling': 'Rechnungsdetails bearbeiten',
  'billing.noDetailsFound': 'Keine Rechnungsdetails gefunden',
  'billing.setUp': 'Einrichten',
  'billing.expires': 'Läuft ab',
  'billing.status_active': 'Aktiv',
  'billing.status_trialing': 'Testphase',
  'billing.status_past_due': 'Überfällig',
  'billing.status_canceled': 'Gekündigt',
  'billing.status_incomplete': 'Unvollständig',
  'billing.status_unpaid': 'Unbezahlt',
  'billing.interval_month': 'Monat',
  'billing.interval_year': 'Jahr',
  'billing.cancelsAtPeriodEnd': 'Kündigung zum Periodenende',
  'billing.subActivated': 'Abonnement aktiviert!',
  'billing.syncSlow': 'Die Synchronisierung dauert länger als erwartet. Bitte aktualisieren.',
  'billing.subFound': 'Abonnement gefunden!',
  'billing.noSubYet': 'Noch kein aktives Abonnement gefunden.',
  'account.title': 'Kontoinformationen',
  'account.subtitle': 'Verwalten Sie Ihre E-Mail und Ihr Passwort.',
  'account.changeEmail': 'E-Mail ändern',
  'account.changeEmailDesc': 'Eine Bestätigungsmail wird an Ihre neue Adresse gesendet.',
  'account.newEmail': 'Neue E-Mail',
  'account.confirmEmail': 'E-Mail bestätigen',
  'account.currentPassword': 'Aktuelles Passwort',
  'account.emailMismatch': 'E-Mail-Adressen stimmen nicht überein',
  'account.emailVerificationSent': 'Ein Bestätigungslink wurde an Ihre neue E-Mail gesendet.',
  'account.changePassword': 'Passwort ändern',
  'account.changePasswordDesc': 'Sie müssen Ihr aktuelles Passwort bestätigen.',
  'account.newPassword': 'Neues Passwort',
  'account.confirmNewPassword': 'Neues Passwort bestätigen',
  'account.savePassword': 'Aktualisieren',
  'account.passwordMin': 'Passwort muss mindestens 8 Zeichen lang sein',
  'account.mismatch': 'Passwörter stimmen nicht überein',
  'account.wrongPassword': 'Aktuelles Passwort ist falsch.',
  'account.updateFailed': 'Aktualisierung fehlgeschlagen. Bitte erneut versuchen.',
  'account.passwordChanged': 'Passwort aktualisiert',
  'account.deleteTitle': 'Konto löschen',
  'account.deleteDesc': 'Ihr Konto und alle Daten dauerhaft löschen.',
  'account.deleteButton': 'Mein Konto löschen',
  'account.deleteConfirmTitle': 'Sind Sie absolut sicher?',
  'account.deleteConfirmDesc': 'Diese Aktion löscht dauerhaft Ihr Konto, Ihre Organisation und alle Daten.',
  'account.deletePasswordLabel': 'Passwort zur Bestätigung eingeben',
  'account.deleteConfirmButton': 'Alles löschen',
  'account.deleteCancelButton': 'Abbrechen',
  'account.deleteSuccess': 'Konto gelöscht.',

  // ── Excel Import ──
  'import.title': 'Daten importieren',
  'import.subtitle': 'Laden Sie eine Excel- oder CSV-Datei mit Ihren Filialen und Mitarbeitern hoch.',
  'import.downloadTemplate': 'Excel-Vorlage herunterladen',
  'import.dropzone': 'Datei hierher ziehen oder klicken zum Durchsuchen',
  'import.dropzoneHint': 'Akzeptiert .xlsx oder .csv · Max 5MB',
  'import.parsing': 'Datei wird analysiert...',
  'import.storesDetected': 'Filialen erkannt',
  'import.employeesDetected': 'Mitarbeiter erkannt',
  'import.hours': 'Zeiten',
  'import.staff': 'Personal (WT/WE)',
  'import.days': 'Verfügbarkeit',
  'import.totalWeekly': 'gesamt wöchentlich',
  'import.importing': 'Daten werden importiert...',
  'import.success': 'Import erfolgreich!',
  'import.successMessage': 'Import erfolgreich. Ihre Daten sind bereit.',
  'import.failed': 'Import fehlgeschlagen',
  'import.confirmImport': 'Import bestätigen',
  'import.cancel': 'Abbrechen',
  'import.tryAgain': 'Erneut versuchen',
  'import.done': 'Fertig',
  'import.uploadData': 'Daten importieren',
  'import.uploadHint': 'Oder importieren Sie aus einer Excel/CSV-Datei',

  // ── Compliance Reports ──
  'comp.complianceSummary': 'Compliance',
  'comp.payrollExport': 'Lohnexport',
  'comp.legalRisk': 'Rechtsrisiko',
  'comp.totalEmployees': 'Mitarbeiter gesamt',
  'comp.compliant': 'Konform',
  'comp.warnings': 'Warnungen',
  'comp.critical': 'Handlung erforderlich',
  'comp.employee': 'Mitarbeiter',
  'comp.store': 'Filiale',
  'comp.contractH': 'Vertrag (h/W)',
  'comp.scheduledH': 'Geplant (h)',
  'comp.regularH': 'Regelstunden',
  'comp.overtimeH': 'Überstunden',
  'comp.underhours': 'Minderstunden',
  'comp.holidays': 'Urlaub',
  'comp.sickDays': 'Krankheitstage',
  'comp.maxStreak': 'Max. Serie',
  'comp.status': 'Status',
  'comp.totalH': 'Gesamtstunden',
  'comp.totals': 'Summen',
  'comp.allEmployees': 'Alle Mitarbeiter',
  'comp.payrollDesc': 'Strukturierte monatliche Stundenübersicht für die Lohnabrechnung. Keine Gehaltsberechnungen.',
  'comp.exportedCSV': 'Als CSV exportiert',
  'comp.exportedExcel': 'Als Excel exportiert',
  'comp.exportedExcelDesc': 'Überprüfen Sie Ihren Downloads-Ordner, um die Datei zu öffnen',
  'comp.exportedCSVDesc': 'Überprüfen Sie Ihren Downloads-Ordner, um die Datei zu öffnen',
  'excel.employee': 'Mitarbeiter',
  'excel.store': 'Filiale',
  'excel.contractH': 'Vertrag (h)',
  'excel.scheduledH': 'Geplant (h)',
  'excel.differenceH': 'Differenz (h)',
  'excel.status': 'Status',
  'excel.hoursVsContract': 'Stunden vs Vertrag',
  'excel.metric': 'Metrik',
  'excel.required': 'Erforderlich',
  'excel.scheduled': 'Geplant',
  'excel.difference': 'Differenz',
  'excel.allowance': 'Anspruch',
  'excel.approved': 'Genehmigt',
  'excel.pending': 'Ausstehend',
  'excel.remaining': 'Verbleibend',
  'excel.leaveBalance': 'Urlaubssaldo',
  'comp.highRisk': 'Hohes Risiko',
  'comp.mediumRisk': 'Mittleres Risiko',
  'comp.lowRisk': 'Niedriges Risiko',
  'comp.allSeverities': 'Alle Schweregrade',
  'comp.exportRisk': 'Risikobericht exportieren',
  'comp.riskType': 'Risikotyp',
  'comp.dateWeek': 'Datum / Woche',
  'comp.severity': 'Schweregrad',
  'comp.description': 'Beschreibung',
  'comp.noRisks': 'Keine Risiken erkannt',
  'comp.noRisksDesc': 'Alle Dienstpläne entsprechen diesen Monat den Arbeitsvorschriften.',
  'comp.riskType48h': 'Wochenstunden > 48',
  'comp.riskTypeNoRest': 'Kein Ruhetag in der Woche',
  'comp.riskType6Consec': '6+ aufeinanderfolgende Tage',
  'comp.riskTypeContract': 'Vertragsstundenverstoß',
  'comp.riskTypeMaxDays': 'Überschreitet max Tage/Woche',
  'comp.riskTypeAvail': 'Außerhalb der Verfügbarkeit geplant',
  'comp.riskDesc48h': '{hours}h geplant (max 48h)',
  'comp.riskDescNoRest': '{days} Tage ohne Ruhetag gearbeitet',
  'comp.riskDesc6Consec': '{days} Tage in der Woche gearbeitet',
  'comp.riskDescContract': '{hours}h vs {contract}h Vertrag (+{pct}%)',
  'comp.riskDescMaxDays': '{days} Tage gearbeitet (max {max})',
  'comp.riskDescAvail': 'An nicht verfügbarem Tag geplant',
  'comp.severityHigh': 'Hoch',
  'comp.severityMedium': 'Mittel',
  'comp.severityLow': 'Niedrig',

  // ── Landing Page (restructured) ──
  'lp.hero.title': 'Personalplanung & Compliance für schichtbasierte Teams',
  'lp.hero.subtitle': 'Erstellen Sie Dienstpläne, verfolgen Sie Vertragsstunden, verwalten Sie bezahlten Urlaub und verhindern Sie Compliance-Probleme — alles in einem System.',
  'lp.hero.demo': 'Demo buchen',
  'lp.hero.trust': 'Für Einzelhandel, Gastronomie, Kliniken, Fitnessstudios, Franchises und Dienstleistungsunternehmen.',

  'lp.prob.title': 'Schichtplanung sollte nicht so kompliziert sein',
  'lp.prob1': 'Manuelle Tabellen verursachen Fehler',
  'lp.prob2': 'Überstunden bleiben unbemerkt',
  'lp.prob3': 'Urlaubsverfolgung wird chaotisch',
  'lp.prob4': 'Compliance-Risiken steigen',
  'lp.prob5': 'Manager verschwenden jede Woche Stunden',
  'lp.prob.closing': 'Kleine Planungsfehler kosten echtes Geld.',

  'lp.core.title': 'Alles, was Sie für die Schichtplanung brauchen',
  'lp.core1.title': 'Intelligenter Dienstplan-Ersteller',
  'lp.core1.desc': 'Generiert automatisch ausgewogene Pläne unter Beachtung von Vertragsstunden und Verfügbarkeit.',
  'lp.core2.title': 'Vertrags- & Stundenkontrolle',
  'lp.core2.desc': 'Verfolgen Sie Soll- vs. Ist-Stunden monatlich und wöchentlich.',
  'lp.core3.title': 'Urlaubs- & Krankheitsverfolgung',
  'lp.core3.desc': 'Wenden Sie automatisch bezahlte Schwellenwerte an und erkennen Sie Übernutzung.',
  'lp.core4.title': 'Compliance-Berichtssuite',
  'lp.core4.desc': 'Generieren Sie monatliche Compliance-Zusammenfassungen sofort.',
  'lp.core5.title': 'Funktioniert in 6 Sprachen',
  'lp.core5.desc': 'Operota erkennt automatisch Ihre Sprache und passt die gesamte Plattform an. Englisch, Italienisch, Spanisch, Französisch, Deutsch und Portugiesisch — keine Einrichtung nötig.',
  'lp.core6.title': 'Multi-Standort-Unterstützung',
  'lp.core6.desc': 'Verwalten Sie mehrere Standorte von einem Konto aus. Jeder Standort hat eigene Dienstpläne, Personalregeln und Compliance-Berichte.',
  'lp.core.subtitle': 'Operotas KI-Planungsengine erstellt ausgewogene Dienstpläne, die Vertragsstunden, Mitarbeiterverfügbarkeit und Teamabdeckung automatisch berücksichtigen. Keine Tabellen. Kein Rätselraten.',
  'lp.core.sectionTitle': 'Erstellen Sie Dienstpläne in Sekunden',

  'lp.hero.langLine': 'Verfügbar in 6 Sprachen — passt sich automatisch Ihrem Team an',
  'lp.hero.noCard': 'Keine Karte erforderlich',
  'lp.hero.trustBadge1': 'DSGVO-konform',
  'lp.hero.trustBadge2': 'Keine Kosten pro Mitarbeiter',
  'lp.hero.trustBadge3': 'Einrichtung in 15 Minuten',

  'lp.pricing.annualSave': '990€/Jahr — 2 Monate kostenlos',
  'lp.pricing.annualBadge': '198€/Jahr sparen',
  'lp.pricing.bestValue': 'Bestes Angebot',
  'lp.price6': 'Funktioniert in 6 Sprachen',
  'lp.price7': 'KI-Automatikplanung',

  'lp.prob1.desc': 'Eine falsche Formel kostet Stunden und riskiert fehlerhafte Gehaltsabrechnungen.',
  'lp.prob2.desc': 'Vertragsverletzungen häufen sich still, bis sie zum rechtlichen Problem werden.',
  'lp.prob3.desc': 'Urlaubskonflikte und kurzfristige Abwesenheiten erzeugen Planungschaos.',
  'lp.prob4.desc': 'Ohne automatisierte Prüfungen sind Sie nur eine Prüfung von einem teuren Bußgeld entfernt.',
  'lp.prob5.desc': 'Manuelle Dienstplanerstellung ist repetitive Arbeit, die Sekunden statt Stunden dauern sollte.',

  'lp.aud1.desc': 'Geschäfte, Boutiquen und Modeketten',
  'lp.aud2.desc': 'Service- und Küchenteams',
  'lp.aud3.desc': 'Kliniken, Fitnessstudios und Therapiepraxen',
  'lp.aud4.desc': 'Fitnesscenter und Freizeiteinrichtungen',
  'lp.aud5.desc': 'Franchises und Multi-Standort-Betreiber',
  'lp.aud6.desc': 'Außendienstteams und Dienstleistungsunternehmen',

  'lp.section5.title': 'Verfolgen Sie Vertragsstunden und Compliance',
  'lp.section5.subtitle': 'Operota überwacht automatisch Vertragsstunden gegenüber geplanten Schichten, meldet Überstundenverletzungen und erstellt Compliance-Berichte.',
  'lp.section5.card1.title': 'KI-Schichtplanung',
  'lp.section5.card1.desc': 'Unser Dienstplanersteller nutzt KI für ausgewogene Pläne, die Überstunden verhindern und Verfügbarkeit berücksichtigen.',
  'lp.section5.card2.title': 'Compliance-Berichte',
  'lp.section5.card2.desc': 'Erstellen Sie monatliche Zusammenfassungen mit Arbeits- vs. Vertragsstunden, Urlaubssalden und Verstößen — exportierbar nach Excel.',

  'lp.early.title': 'Seien Sie die Ersten, die ihre Geschichte teilen',
  'lp.early.subtitle': 'Treten Sie unserem Early-Access-Programm bei.',

  'footer.solutions': 'Lösungen',

  'lp.faq.businesses.q': 'Welche Arten von Unternehmen nutzen Operota?',
  'lp.faq.businesses.a': 'Jedes Unternehmen mit Schichtarbeitern — Einzelhandel, Restaurants, Fitnessstudios, Kliniken, Reinigungsfirmen, Sicherheitsdienste und mehr.',
  'lp.faq.setup.q': 'Wie lange dauert die Einrichtung?',
  'lp.faq.setup.a': 'Die meisten Teams sind in unter 15 Minuten einsatzbereit. Fügen Sie Ihren Standort hinzu, importieren Sie Ihr Personal und starten Sie die automatische Generierung.',
  'lp.faq.selfService.q': 'Können Mitarbeiter ihre eigenen Dienstpläne sehen?',
  'lp.faq.selfService.a': 'Der Mitarbeiter-Selbstservice steht auf unserer Roadmap. Aktuell können Dienstpläne als PDF oder Excel exportiert werden.',
  'lp.faq.perEmployee.q': 'Gibt es eine Gebühr pro Mitarbeiter?',
  'lp.faq.perEmployee.a': 'Nein. Operota kostet pauschal 99€ pro Monat, unabhängig von der Anzahl der Mitarbeiter oder Standorte.',
  'lp.faq.afterTrial.q': 'Was passiert nach der 30-tägigen Testphase?',
  'lp.faq.afterTrial.a': 'Sie wechseln zum Monatsplan für 99€/Monat. Wir erinnern Sie vor Ablauf und Sie können jederzeit kündigen.',
  'lp.faq.languages.q': 'In welchen Sprachen ist es verfügbar?',
  'lp.faq.languages.a': 'Englisch, Italienisch, Spanisch, Französisch, Deutsch und Portugiesisch. Es erkennt automatisch Ihre Browsersprache.',

  'lp.pricing.title': 'Einfache, professionelle Preise',
  'lp.pricing.noFees': 'Keine versteckten Gebühren. Keine Kosten pro Mitarbeiter.',
  'lp.price1': 'Unbegrenzte Mitarbeiter',
  'lp.price2': 'Multi-Standort-Unterstützung',
  'lp.price3': 'Vollständige Compliance-Berichte',
  'lp.price4': 'Urlaubs- & Abwesenheitsverfolgung',
  'lp.price5': '30-tägige kostenlose Testphase',

  'lp.aud.title': 'Für wachsende schichtbasierte Unternehmen',
  'lp.aud.subtitle': 'Wenn Sie Mitarbeiter über Schichten oder Standorte verwalten, hält Operota Ihr Team organisiert und konform.',
  'lp.aud1': 'Einzelhandel',
  'lp.aud2': 'Gastronomie-Gruppen',
  'lp.aud3': 'Kliniken',
  'lp.aud4': 'Fitnessstudios',
  'lp.aud5': 'Franchises',
  'lp.aud6': 'Dienstleistungsunternehmen',

  'lp.faq.payroll.q': 'Berechnet es die Lohnabrechnung?',
  'lp.faq.payroll.a': 'Nein. Operota liefert strukturierte monatliche Stundenexporte für Ihren Buchhalter oder Lohnsystem, berechnet aber keine Gehälter, Steuern oder Abzüge.',
  'lp.faq.contracts.q': 'Respektiert es Vertragsstunden?',
  'lp.faq.contracts.a': 'Ja. Das System respektiert automatisch die vereinbarten Wochenstunden jedes Mitarbeiters bei der Dienstplanerstellung.',
  'lp.faq.excel.q': 'Kann ich aus Excel importieren?',
  'lp.faq.excel.a': 'Ja. Importieren Sie Standorte und Mitarbeiter aus Excel- oder CSV-Dateien, oder nutzen Sie unseren KI-Import.',
  'lp.faq.security.q': 'Sind meine Daten sicher?',
  'lp.faq.security.a': 'Ihre Daten sind im Transit und im Ruhezustand verschlüsselt, vollständig in der EU gehostet, mit Enterprise-Cloud-Infrastruktur und strikter DSGVO-Konformität.',
  'lp.faq.stores.q': 'Kann ich mehrere Standorte verwalten?',
  'lp.faq.stores.a': 'Absolut. Operota unterstützt unbegrenzte Standorte. Verwalten Sie Dienstpläne, Personal und Berichte von einem Dashboard aus.',

  'settings.tabLeave': 'Urlaubsrichtlinie',
  'leave.globalPolicy': 'Globale Urlaubsrichtlinie',
  'leave.globalPolicyDesc': 'Standardmäßige bezahlte Urlaubsansprüche für alle Mitarbeiter festlegen.',
  'leave.paidHolidayDays': 'Bezahlte Urlaubstage pro Jahr',
  'leave.paidSickDays': 'Bezahlte Krankheitstage pro Jahr',
  'leave.hoursPerDay': 'Arbeitsstunden pro Tag',
  'leave.savePolicy': 'Richtlinie speichern',
  'leave.policySaved': 'Urlaubsrichtlinie gespeichert',
  'leave.bulkActions': 'Massenaktionen',
  'leave.bulkActionsDesc': 'Richtlinienänderungen auf Mitarbeiter anwenden oder Jahreszähler zurücksetzen.',
  'leave.applyToAll': 'Auf alle Mitarbeiter anwenden',
  'leave.confirmApply': 'Richtlinie auf alle anwenden?',
  'leave.confirmApplyDesc': 'Dies aktualisiert die Urlaubs- und Krankheitstage für alle Mitarbeiter ohne individuelle Überschreibung.',
  'leave.appliedToAll': 'Richtlinie auf alle Mitarbeiter angewendet',
  'leave.resetCounters': 'Zähler zurücksetzen',
  'leave.confirmReset': 'Urlaubszähler zurücksetzen?',
  'leave.confirmResetDesc': 'Urlaubssalden werden aus den Aufzeichnungen berechnet. Filtern Sie nach Jahr.',
  'leave.resetInfo': 'Salden werden aus datumsbasierten Aufzeichnungen berechnet.',
  'leave.overridePolicy': 'Standard-Richtlinie überschreiben',
  'leave.paidHolH': 'Bez. Urlaub (h)',
  'leave.paidSickH': 'Bez. Krank (h)',
  'leave.unpaidH': 'Unbez. Urlaub (h)',
  'leave.holAllowance': 'Urlaubsanspruch',
  'leave.sickAllowance': 'Krankheitsanspruch',
  'leave.used': 'genutzt',
  'leave.remainHol': 'Urlaub rest.',
  'leave.remainSick': 'Krank rest.',
  'comp.contractCountedH': 'Gezählte Std.',
  'comp.contractCountedTooltip': 'Geplante Stunden + bezahlter Urlaub. Für Vertragskonformität.',
  'comp.workingDays': 'Arbeitstage',
  'comp.day': 'Tag',
  'comp.expectedH': 'Erwartet',
  'comp.expectedTooltip': 'Berechnet aus Vertragsstunden × Arbeitstage im Zeitraum. Exakte Tageszählung.',
  'comp.scheduledTooltip': 'Tatsächliche Schichtstunden im Zeitraum.',
  'comp.underhoursVsContract': 'Unterstunden',
  'comp.underhoursTooltip': 'Gezählte Stunden − Erwartete Stunden. Negativ = Defizit.',
  'comp.complianceWeekly': 'Wöchentlich',
  'comp.complianceMonthly': 'Monatlich',
  'comp.complianceReport': 'Compliance-Bericht',
  'comp.period': 'Zeitraum',
  'comp.print': 'Drucken',
  'comp.savePDF': 'Als PDF speichern',
  'comp.printOrSavePDF': 'Drucken / Als PDF speichern',
  'comp.printPDFHint': 'Um als PDF zu speichern, wählen Sie im Druckdialog „Als PDF speichern" als Ziel.',
  'comp.generatingPDF': 'PDF wird erstellt...',
  'comp.backToReports': 'Zurück zu Berichten',
  'comp.totalOvertime': 'Überstunden gesamt',
  'comp.totalUnderhours': 'Fehlstunden gesamt',
  'comp.totalUnpaidLeave': 'Unbezahlter Urlaub gesamt',
  'comp.generatedOn': 'Erstellt am',
  'comp.generatedBy': 'Erstellt von',
  'comp.organization': 'Organisation',
  'comp.reportPeriod': 'Berichtszeitraum',
  'comp.hoursBreakdown': 'Stundenaufschlüsselung',
  'comp.compliance': 'Compliance',
  'comp.leaveBalanceYear': 'Urlaubssaldo (Jahr)',
  'comp.leaveBalanceAnnual': 'Urlaubssaldo (Jährlich)',
  'comp.scheduled': 'Geplant (gearbeitet)',
  'comp.paidHoliday': 'Bezahlter Urlaub',
  'comp.paidSick': 'Bezahlte Krankheit',
  'comp.unpaidLeave': 'Unbezahlter Urlaub',
  'comp.contract': 'Vertrag',
  'comp.expected': 'Erwartet',
  'comp.expectedPeriod': 'Erwartet für Zeitraum',
  'comp.counted': 'Gezählt',
  'comp.variance': 'Abweichung',
  'comp.worked': 'Gearbeitet',
  'comp.holidayRemaining': 'Urlaub verbleibend',
  'comp.sickRemaining': 'Krankheit verbleibend',
  'comp.days': 'Tage',
  'comp.notes': 'Hinweise',
  'comp.statusOk': 'Im Plan',
  'comp.statusWarning': 'Geringe Abweichung',
  'comp.statusCritical': 'Handlung erforderlich',
  'comp.allClear': 'Alle Mitarbeiter im Plan',
  'comp.varianceExplainer': 'Vertragsstunden können aufgrund von Öffnungszeiten, Verfügbarkeit und Mindestschichtdauer leicht abweichen. Kleine Unterschiede (±2h) sind normal.',
  'comp.toleranceOk': 'Innerhalb der Toleranz',
  'comp.toleranceWarning': 'Überprüfung empfohlen',
  'comp.toleranceAction': 'Handlung erforderlich',
};

const it: Record<string, string> = {
  'header.signIn': 'Accedi', 'header.getStarted': 'Inizia',
  'hero.badge': 'Per team retail multi-sede',
  'hero.title1': 'Pianificazione IA del personale', 'hero.title2': 'Che bilancia davvero i tuoi negozi',
  'hero.subtitle': 'Progettato per team retail in crescita che necessitano di turni conformi ai contratti, bilanciati dall\'IA, senza il caos.',
  'hero.cta': 'Prova gratuita di 30 giorni', 'hero.signIn': 'Accedi',
  'hero.trust1': 'Prova gratuita di 30 giorni', 'hero.trust2': 'Configurazione in minuti', 'hero.trust3': 'Senza impegno',
  'hero.euLine': 'Ospitato nell\'UE. Conforme al GDPR. Progettato per i contratti di lavoro europei.',
  'trust.title': 'Progettato per le operazioni retail moderne',
  'trust.eu': 'Hosting UE', 'trust.gdpr': 'Conforme GDPR', 'trust.secure': 'Infrastruttura cloud sicura', 'trust.encrypted': 'Crittografato in transito e a riposo',
  'trust.origin': 'Progettato e operato dall\'Italia.',
  'screenshot.caption': 'Turno auto-bilanciato generato in un clic.', 'screenshot.sub': 'Rispetta i contratti. Evita gli straordinari. Elimina le lacune di copertura.',
  'features.title': 'Tutto per turni più intelligenti', 'features.subtitle': 'Strumenti orientati ai risultati per manager retail impegnati.',
  'feature.scheduling.title': 'Pianificazione intelligente', 'feature.scheduling.desc': 'Turni generati dall\'IA che bilanciano automaticamente le ore e prevengono le lacune.',
  'feature.employees.title': 'Gestione dipendenti', 'feature.employees.desc': 'Monitora contratti, disponibilità e assenze in un pannello centralizzato.',
  'feature.stores.title': 'Supporto multi-sede', 'feature.stores.desc': 'Gestisci tutte le sedi da un centro di controllo intelligente.',
  'feature.absences.title': 'Monitoraggio assenze', 'feature.absences.desc': 'Registra malattie e permessi. Saprai sempre chi è disponibile.',
  'feature.holidays.title': 'Gestione ferie', 'feature.holidays.desc': 'Gestisci festività e richieste di ferie automaticamente.',
  'feature.reports.title': 'Report ed esportazioni', 'feature.reports.desc': 'Genera report ed esporta in Excel.',
  'midCta.title': 'Smetti di creare turni manualmente.', 'midCta.subtitle': 'Unisciti ai team retail che passano alla gestione IA dei turni.', 'midCta.button': 'Prova gratuita',
  'stats.shifts': 'Turni pianificati', 'stats.teams': 'Team attivi', 'stats.uptime': 'Disponibilità', 'stats.rating': 'Valutazione',
  'how.title': 'Come funziona', 'how.subtitle': 'Operativo in pochi minuti.',
  'how.step1.title': 'Aggiungi le sedi', 'how.step1.desc': 'Configura le sedi e gli orari.',
  'how.step2.title': 'Aggiungi dipendenti', 'how.step2.desc': 'Inserisci il team con contratti e disponibilità.',
  'how.step3.title': 'Crea i turni', 'how.step3.desc': 'Costruisci programmi con suggerimenti intelligenti.',
  'pricing.title': 'Prezzi semplici e trasparenti', 'pricing.subtitle': 'Prova gratuita di 30 giorni. Nessuna carta.',
  'pricing.perMonth': '/mese', 'pricing.afterTrial': 'Dopo la prova gratuita',
  'pricing.cta': 'Prova gratuita', 'pricing.included': 'Tutto incluso:',
  'pricing.item1': 'Sedi illimitate', 'pricing.item2': 'Dipendenti illimitati',
  'pricing.item3': 'Bilanciamento IA incluso', 'pricing.item4': 'Monitoraggio assenze e ferie',
  'pricing.item5': 'Pianificazione conforme ai contratti', 'pricing.item6': 'Supporto prioritario',
  'pricing.badge': 'Ideale per team in crescita', 'pricing.reassurance': 'Cancella in qualsiasi momento. Nessun contratto a lungo termine.',
  'pricing.target': 'Per team retail in crescita e aziende multi-sede.',
  'pricing.monthly': 'Mensile', 'pricing.annual': 'Annuale', 'pricing.comingSoon': 'In arrivo',
  'cta.title': 'Pronto a semplificare la pianificazione?', 'cta.subtitle': 'Unisciti ai manager che pianificano in modo più intelligente.',
  'cta.button': 'Inizia la prova gratuita',
  'footer.features': 'Funzionalità', 'footer.howItWorks': 'Come funziona', 'footer.signIn': 'Accedi',
  'footer.rights': '© {year} Operota. Tutti i diritti riservati.',
  'footer.product': 'Prodotto', 'footer.legal': 'Legale',
  'footer.terms': 'Termini e Condizioni', 'footer.privacy': 'Informativa sulla Privacy',
  'footer.cookies': 'Politica dei Cookie', 'footer.dpa': 'DPA', 'footer.refunds': 'Rimborsi e Cancellazione',
  'pain.title': 'Gestisci ancora i turni nel modo difficile?',
  'pain.manual.title': 'Pianificazione manuale', 'pain.manual.desc': 'Scrivere turni su Excel o carta spreca ore ogni settimana.',
  'pain.gaps.title': 'Lacune di copertura', 'pain.gaps.desc': 'Le assenze dell\'ultimo minuto lasciano i negozi sotto organico.',
  'pain.overtime.title': 'Errori di straordinario', 'pain.overtime.desc': 'Le ore contrattuali vengono superate senza preavviso.',
  'pain.cta.text': 'Lascia che sia Operota a gestirlo', 'pain.cta.button': 'Inizia la prova gratuita',
  'compare.title': 'Perché Operota invece di Excel?',
  'compare.excel.1': 'Pianificazione manuale', 'compare.excel.2': 'Nessun tracciamento contratti',
  'compare.excel.3': 'Nessuna vista multi-negozio', 'compare.excel.4': 'Nessun tracciamento ferie',
  'compare.operota.1': 'Bilanciamento automatico IA', 'compare.operota.2': 'Turni rispettosi dei contratti',
  'compare.operota.3': 'Dashboard multi-negozio', 'compare.operota.4': 'Gestione assenze integrata',
  'testimonials.title': 'Scelto dai team retail in crescita',
  'testimonial.1.quote': 'Operota ci fa risparmiare 6 ore a settimana. Il bilanciamento IA è rivoluzionario.', 'testimonial.1.name': 'Marco Rossi', 'testimonial.1.role': 'Responsabile Retail – Catena di moda',
  'testimonial.2.quote': 'Non perdiamo più turni da quando abbiamo cambiato. Le lacune di copertura appartengono al passato.', 'testimonial.2.name': 'Emma Clarke', 'testimonial.2.role': 'Proprietaria multi-sede – Gruppo boutique',
  'testimonial.3.quote': 'Finalmente un software che rispetta le ore contrattuali e tiene i nostri negozi conformi.', 'testimonial.3.name': 'Daniel Weber', 'testimonial.3.role': 'Responsabile Operazioni – Catena retail',
  'faq.title': 'Domande Frequenti',
  'faq.cancel.q': 'Posso cancellare in qualsiasi momento?', 'faq.cancel.a': 'Sì, puoi cancellare il tuo abbonamento in qualsiasi momento senza penalità. Nessun contratto a lungo termine né costi nascosti. I tuoi dati restano accessibili fino alla fine del periodo di fatturazione.',
  'faq.trial.q': 'C\'è una prova gratuita?', 'faq.trial.a': 'Sì. Ogni nuovo account ottiene una prova gratuita completa di 30 giorni con accesso a tutte le funzionalità, inclusa la pianificazione IA. Nessuna carta richiesta.',
  'faq.stores.q': 'Supporta più negozi?', 'faq.stores.a': 'Assolutamente. Operota supporta sedi illimitate all\'interno di un\'unica organizzazione. Gestisci turni, personale e report per tutti i negozi da un unico pannello.',
  'faq.contracts.q': 'Rispetta i contratti dei dipendenti?', 'faq.contracts.a': 'Sì. Operota rispetta automaticamente i limiti di ore contrattuali nella generazione dei turni. L\'IA previene gli straordinari.',
  'faq.security.q': 'I miei dati sono al sicuro?', 'faq.security.a': 'I tuoi dati sono crittografati in transito e a riposo, e ospitati interamente nell\'UE. Infrastruttura cloud di livello enterprise con stretta conformità GDPR.',
  'finalCta.title': 'Smetti di gestire i turni nei fogli di calcolo.',
  'finalCta.subtitle': 'Inizia la tua prova gratuita di 30 giorni e prendi il controllo della pianificazione del personale.',
  'finalCta.button': 'Inizia la prova gratuita', 'finalCta.signIn': 'Accedi',

  'nav.dashboard': 'Dashboard', 'nav.stores': 'Sedi', 'nav.employees': 'Dipendenti',
  'nav.schedule': 'Turni', 'nav.holidays': 'Ferie', 'nav.absences': 'Assenze',
  'nav.reports': 'Report', 'nav.signOut': 'Esci', 'nav.settings': 'Impostazioni',

  'auth.back': 'Indietro', 'auth.title': 'Operota', 'auth.subtitle': 'Gestisci i turni facilmente',
  'auth.signIn': 'Accedi', 'auth.signUp': 'Registrati', 'auth.email': 'E-mail', 'auth.password': 'Password',
  'auth.signingIn': 'Accesso...', 'auth.creatingAccount': 'Creazione account...',
  'auth.createAccount': 'Crea account', 'auth.loading': 'Caricamento...',
  'auth.checkEmail': 'Controlla la tua e-mail per verificare l\'account.',
  'auth.invalidCredentials': 'E-mail o password non validi.',
  'auth.emailNotConfirmed': 'Verifica il tuo indirizzo e-mail.',
  'auth.alreadyRegistered': 'Un account con questa e-mail esiste già.',
  'auth.unexpectedError': 'Si è verificato un errore imprevisto.',
  'auth.invalidEmail': 'Inserisci un indirizzo e-mail valido',
  'auth.passwordMin': 'La password deve avere almeno 6 caratteri',

  'dash.title': 'Dashboard', 'dash.weekOf': 'Settimana del {start} - {end}',
  'dash.today': 'Oggi',
  'dash.employeesWorking': 'dipendenti in servizio', 'dash.workingToday': 'in servizio oggi',
  'dash.onShiftToday': 'in turno oggi',
  'dash.pendingRequests': 'richieste in sospeso', 'dash.requestsToReview': 'richieste da esaminare',
  'dash.staffingGaps': 'problemi di personale', 'dash.issuesToday': 'problemi oggi',
  'dash.noIssuesToday': 'Nessun problema oggi', 'dash.noIssues': 'Nessun problema',
  'dash.totalStores': 'Sedi', 'dash.totalEmployees': 'Dipendenti',
  'dash.shiftsThisWeek': 'Turni questa settimana', 'dash.staffingAlerts': 'Avvisi personale',
  'dash.thisWeek': 'Questa settimana', 'dash.upcomingIssues': 'Questa settimana',
  'dash.actionRequired': 'Azione richiesta', 'dash.insights': 'Informazioni',
  'dash.understaffed': '{count} giorno/i sotto organico',
  'dash.missingStaff': '{count} personale mancante', 'dash.moreIssues': '...e altri {count}',
  'dash.viewSchedule': 'Vedi turni', 'dash.stores': 'Sedi', 'dash.storesDesc': 'Gestisci le sedi',
  'dash.pendingHolidayRequests': '{count} richiesta/e di ferie in sospeso',
  'dash.pendingHolidayDesc': 'Approva o rifiuta le richieste', 'dash.reviewRequests': 'Esamina',
  'dash.nearOvertime': '{count} dipendente/i vicino/i agli straordinari',
  'dash.employeeOverContract': 'dipendente oltre il contratto',
  'dash.employeesOverContract': 'dipendenti oltre il contratto',
  'dash.approachingLimit': 'vicino al limite', 'dash.more': 'altri',
  'dash.allClear': 'Tutto in ordine — nessun problema questa settimana',
  'dash.weekGlance': 'Questa settimana in sintesi',
  'dash.ofContractHours': 'di {hours}h contrattuali',
  'dash.storesFullyCovered': 'Sedi completamente coperte',
  'dash.staffingGapsWeek': 'Carenze di personale questa settimana',
  'dash.coverageOverview': 'Panoramica copertura',
  'dash.manageStores': 'Gestisci sedi', 'dash.employees': 'Dipendenti', 'dash.employeesDesc': 'Visualizza e gestisci il personale',
  'dash.manageEmployees': 'Gestisci dipendenti', 'dash.schedule': 'Turni', 'dash.scheduleDesc': 'Crea e gestisci i turni',
  'dash.openSchedule': 'Apri turni',

  'stores.title': 'Sedi', 'stores.subtitle': 'Gestisci sedi, personale e orari',
  'stores.add': 'Aggiungi sede', 'stores.edit': 'Modifica sede', 'stores.addNew': 'Nuova sede',
  'stores.editDesc': 'Aggiorna i dettagli', 'stores.addDesc': 'Aggiungi nuova sede',
  'stores.name': 'Nome', 'stores.address': 'Indirizzo', 'stores.staffingReq': 'Personale richiesto per giorno',
  'stores.timeBlockStaffing': 'Personale per fascia oraria', 'stores.timeBlockDesc': 'Definisci il personale necessario per periodo.', 'stores.addBlock': 'Aggiungi', 'stores.noBlocks': 'Nessun blocco — si usa il requisito giornaliero.', 'stores.staff': 'pers.', 'stores.colTimeBlocks': 'Fasce',
  'stores.create': 'Crea sede', 'stores.update': 'Aggiorna', 'stores.loading': 'Caricamento sedi...',
  'stores.empty': 'Nessuna sede. Aggiungi la prima.', 'stores.colName': 'Nome',
  'stores.colAddress': 'Indirizzo', 'stores.colHours': 'Orari', 'stores.colStaffing': 'Personale',
  'stores.colActions': 'Azioni', 'stores.notSet': 'Non impostato',
  'stores.breakPolicy': 'Politica pause', 'stores.breakPolicyDesc': 'Configura le deduzioni automatiche delle pause per il libro paga.', 'stores.breakIfShiftExceeds': 'Applica pausa se il turno supera', 'stores.hours': 'ore', 'stores.breakDuration': 'Durata pausa', 'stores.addSecondBreak': 'Aggiungi seconda pausa per turni lunghi', 'stores.storeClosesDuringBreak': 'Il negozio chiude durante la pausa', 'stores.colBreaks': 'Politica pause', 'stores.breakSummary': '{duration}min pausa ≥{threshold}h',
  'stores.breakSummaryPerDay': 'Durate pausa per giorno configurate', 'stores.breakPresetHint': 'Clicca un preset per applicare a tutti i giorni, oppure personalizza per giorno qui sotto.', 'stores.perDayBreakDuration': 'Durata pausa per giorno',
  'stores.breakExceedsLimit': 'La pausa supera il 30% di un turno di {hours}h (max {max}min)',
  'stores.perDayConfig': 'Configurazione per giorno',
  'stores.block': 'blocco',
  'stores.blocks': 'blocchi',

  'emp.title': 'Dipendenti', 'emp.subtitle': 'Gestisci personale, disponibilità e assegnazioni',
  'emp.hPerWeek': 'h/settimana',
  'emp.add': 'Aggiungi dipendente', 'emp.edit': 'Modifica dipendente', 'emp.addNew': 'Nuovo dipendente',
  'emp.editDesc': 'Aggiorna i dettagli', 'emp.addDesc': 'Aggiungi nuovo membro',
  'emp.name': 'Nome', 'emp.email': 'E-mail', 'emp.phone': 'Telefono', 'emp.contractHours': 'Ore/settimana',
  'emp.primaryStore': 'Sede principale', 'emp.selectStore': 'Seleziona sede',
  'emp.dayAvailability': 'Disponibilità', 'emp.canWorkAt': 'Può lavorare presso',
  'emp.addBtn': 'Aggiungi dipendente', 'emp.updateBtn': 'Aggiorna',
  'emp.search': 'Cerca per nome, e-mail, telefono...', 'emp.nameAZ': 'Nome A–Z', 'emp.nameZA': 'Nome Z–A',
  'emp.hoursUp': 'Ore ↑', 'emp.hoursDown': 'Ore ↓', 'emp.byStore': 'Per sede', 'emp.allStores': 'Tutte le sedi',
  'emp.loading': 'Caricamento dipendenti...', 'emp.empty': 'Nessun dipendente. Aggiungi il primo membro.',
  'emp.colName': 'Nome', 'emp.colContact': 'Contatto', 'emp.colHours': 'Ore',
  'emp.colStore': 'Sede', 'emp.colAvailability': 'Disponibilità', 'emp.colActions': 'Azioni',

  'sched.title': 'Turni', 'sched.weekOf': 'Settimana del {start} - {end}',
  'sched.allStores': 'Tutte le sedi', 'sched.selectStore': 'Seleziona sede',
  'sched.autoGenerate': 'Auto-Generazione', 'sched.export': 'Esporta', 'sched.clearAll': 'Cancella tutto',
  'sched.print': 'Stampa', 'sched.printAll': 'Stampa tutte', 'sched.printStore': 'Stampa {name}',
  'sched.prevWeek': 'Settimana precedente', 'sched.nextWeek': 'Settimana successiva', 'sched.today': 'Oggi',
  'sched.conflicts': 'Conflitti di turno', 'sched.andMore': '...e {count} altri',
  'sched.addStoresFirst': 'Aggiungi prima le sedi.', 'sched.clear': 'Cancella',
  'sched.holiday': 'Ferie', 'sched.pendingHoliday': 'Ferie in attesa', 'sched.pendingHolidays': '{count} richieste ferie in attesa', 'sched.add': 'Aggiungi',
  'sched.pendingTimeOff': 'Assenza in attesa',
  'sched.conflictsDetected': '{count} conflitti ferie-turno',
  'sched.includePending': 'Includi in attesa',
  'sched.resolveConflicts': 'Correggi automaticamente',
  'sched.approvedConflict': 'Ferie approvate',
  'sched.pendingConflict': 'Richiesta in attesa',
  'sched.resolveTitle': 'Correggi conflitti automaticamente',
  'sched.resolveDesc': 'Le seguenti modifiche verranno apportate per risolvere i conflitti:',
  'sched.willResolve': '{count} conflitti verranno risolti:',
  'sched.cannotResolve': '{count} richiedono intervento manuale:',
  'sched.noReplacement': 'Nessun sostituto disponibile',
  'sched.removeShift': 'Rimuovi turno',
  'sched.applyFixes': 'Applica {count} correzioni',
  'sched.resolveSuccess': '{count} conflitti risolti',
  'sched.resolvePartial': '{count} richiedono ancora intervento manuale',
  'sched.editShift': 'Modifica turno', 'sched.addShift': 'Aggiungi turno',
  'sched.editShiftDesc': 'Aggiorna i dettagli', 'sched.addShiftDesc': 'Pianifica un nuovo turno',
  'sched.employee': 'Dipendente', 'sched.selectEmployee': 'Seleziona dipendente',
  'sched.store': 'Sede', 'sched.date': 'Data', 'sched.startTime': 'Inizio', 'sched.endTime': 'Fine',
  'sched.updateShift': 'Aggiorna turno', 'sched.addShiftBtn': 'Aggiungi turno',
  'sched.clearConfirm': 'Cancellare tutti i turni di questa settimana?',
  'sched.clearStoreConfirm': 'Cancellare tutti i turni di {name} questa settimana?',
  'sched.strategyStandard': 'Standard',
  'sched.strategyCoverage': 'Priorità copertura',
  'sched.strategyBalanced': 'Bilanciato',
  'sched.strategyFullCoverage': 'Copertura totale',
  'sched.balancedDesc': 'Ottimizza equità e conformità contrattuale mantenendo la copertura.',
  'sched.coverageDesc': 'Dà priorità alla copertura completa. Può consentire straordinari limitati.',
  'sched.recommended': 'Consigliato',
  'sched.coverageWarning': 'Dà priorità alla copertura completa. Può causare straordinari limitati.',
  'sched.strategyLabel': 'Strategia di pianificazione',
  'sched.defaultStrategy': 'Strategia predefinita',
  'sched.defaultStrategyDesc': 'Imposta la strategia predefinita per la tua organizzazione.',
  'sched.strategySaved': 'Strategia predefinita aggiornata',
  'sched.shiftAdded': 'Turno aggiunto con successo',
  'sched.shiftUpdated': 'Turno aggiornato con successo',
  'sched.shiftRemoved': 'Turno rimosso con successo',
  'sched.shiftsSwapped': 'Turni scambiati con successo',
  'sched.shiftMoved': 'Turno spostato con successo',
  'sched.errorSwap': 'Errore nello scambio dei turni',
  'sched.errorMoveShift': 'Errore nello spostamento del turno',
  'sched.cannotSwapStores': 'Non è possibile scambiare turni tra negozi diversi',
  'sched.swapHint': 'Rilascia per scambiare',
  'sched.alreadyScheduledSwap': 'Il dipendente è già programmato per quel giorno',
  'sched.availabilityOverrideTitle': 'Sostituzione disponibilità',
  'sched.availabilityOverrideDesc': '{name} è disponibile solo il {days}. Approvare questo cambio di turno?',
  'sched.approve': 'Approva',
  'sched.noDaysAvailable': 'nessun giorno',
  'sched.errorAddShift': "Errore nell'aggiunta del turno",
  'sched.errorUpdateShift': "Errore nell'aggiornamento del turno",
  'sched.errorRemoveShift': 'Errore nella rimozione del turno',
  'sched.noShiftsToClear': 'Nessun turno da cancellare',
  'sched.clearedShifts': '{count} turno/i cancellato/i',
  'sched.clickUndo': 'Clicca su annulla per ripristinarli.',
  'sched.undo': 'Annulla',
  'sched.restoredShifts': '{count} turno/i ripristinato/i',
  'sched.errorRestore': 'Errore nel ripristino dei turni',
  'sched.errorClear': 'Errore nella cancellazione del planning',
  'sched.generatedShifts': '{count} turni generati con successo',
  'sched.errorGenerate': 'Errore nella generazione del planning',
  'sched.validationError': 'Errore di validazione',
  'sched.cannotAssign': 'Impossibile assegnare il turno',
  'sched.alreadyScheduled': '{name} ha già un turno in questa data.',
  'sched.onHoliday': '{name} è in ferie approvate in questa data.',
  'sched.onLeave': '{name} è in {type} in questa data.',
  'sched.storeClosed': 'Il negozio è chiuso questo giorno.',
  'sched.shiftOutsideHours': 'Il turno è fuori dall\'orario del negozio',
  'sched.shiftOutsideHoursWarning': 'Questo turno si estende oltre l\'orario del negozio. Il tempo extra verrà registrato come straordinario nei report.',
  'sched.significantOvertimeTitle': 'Turno significativamente fuori dall\'orario del negozio',
  'sched.significantOvertimeDesc': 'Questo turno si estende di oltre 60 minuti oltre l\'orario di apertura del negozio. Sei sicuro di volerlo salvare?',
  'sched.saveAnyway': 'Salva comunque',
  'sched.shiftPaidBreak': '{paid} pagato ({break} pausa)',
  'sched.breakDeducted': '{duration} pausa dedotta dalle ore pagate',
  'sched.bannerGenerated': 'Turni generati — {employees} dipendenti programmati, {hours}h contrattuali rispettate, {gaps}, turni distribuiti equamente.',
  'sched.bannerNoGaps': 'nessuna lacuna di copertura',
  'sched.bannerGaps': '{count} lacuna/e di copertura',
  'sched.bannerNoIssues': 'Nessun problema trovato',
  'sched.bannerReviewGaps': '{count} lacuna/e di copertura da verificare →',
  'sched.exportSuccess': 'Planning esportato con successo',
  'sched.coverageDiag': 'Diagnostica copertura',
  'sched.tbCoverageDiag': 'Problemi copertura per fascia',
  'sched.moreGaps': '...e altri {count} gap',
  'sched.coverageMet': 'Copertura: {staffed} / {required} ✔',
  'sched.coverageShort': 'Copertura: {staffed} / {required} ✘',
  'sched.coverageOver': 'Copertura: {staffed} / {required} ⚠',
  'sched.missingStaff': '{count} mancanti per {time}',
  'sched.requirements': 'Requisiti',
  'sched.shifts': 'Turni',
  'sched.diagNoAvail': 'Nessuna disponibilità ({count})',
  'sched.diagContractExh': 'Contratto esaurito ({count})',
  'sched.diagMaxDays': 'Giorni max raggiunti ({count})',
  'sched.diagNoAssigned': 'Nessun dipendente assegnato a questa sede',
  'sched.diagNoEligible': 'Nessun dipendente idoneo',
  'sched.diagNoReq': 'Nessun requisito di personale configurato',
  'sched.conflictOverHours': '{name} è pianificato per {hours}h ma il contratto è di {contract}h',
  'sched.conflictUnavailable': '{name} non è disponibile il {day}',
  'sched.conflictWrongStore': '{name} non è assegnato a {store}',
  'sched.exportEmployee': 'Dipendente',
  'sched.exportStaffing': 'Organico',
  'sched.exportReqSched': 'Richiesto/Pianificato',
  'abs.typeSick': 'Malattia',
  'abs.typeEmergency': 'Emergenza',
  'sched.copyWeek': 'Copia settimana',
  'sched.copyWeekTitle': 'Copia pianificazione settimanale',
  'sched.copyWeekDesc': 'Duplica tutti i turni da una settimana all\'altra.',
  'sched.sourceWeek': 'Settimana di origine',
  'sched.targetWeek': 'Settimana di destinazione',
  'sched.copyWeekConfirm': 'Copia pianificazione',
  'sched.copyWeekSuccess': 'Pianificazione copiata con successo.',
  'sched.copyWeekError': 'Errore durante la copia',
  'sched.copyWeekNoShifts': 'Nessun turno trovato nella settimana di origine.',
  'sched.copyWeekReplace': 'Questa settimana ha già {count} turno/i. Sostituire la pianificazione esistente?',
  'sched.copyWeekReplaceBtn': 'Sostituisci e copia',
  'sched.copyWeekCopying': 'Copia in corso...',
  'sched.selectTargetWeek': 'Seleziona settimana di destinazione',
  'sched.copyNextWeek': 'Settimana prossima',
  'sched.copyWeekAfterNext': 'Fra due settimane',
  'sched.copySpecificWeek': 'Scegli settimana',

  'hol.title': 'Ferie', 'hol.subtitle': 'Monitoraggio richieste e saldi ferie',
  'hol.request': 'Richiedi ferie', 'hol.balances': 'Saldi ferie',
  'hol.allRequests': 'Tutte le richieste', 'hol.noRequests': 'Nessuna richiesta',
  'hol.employee': 'Dipendente', 'hol.selectEmployee': 'Seleziona dipendente', 'hol.dateRange': 'Periodo',
  'hol.pickDates': 'Seleziona date', 'hol.notes': 'Note (opzionale)', 'hol.notesPlaceholder': 'Motivo...',
  'hol.submit': 'Invia richiesta', 'hol.businessDays': '{count} giorni lavorativi',
  'hol.approved': 'Approvato', 'hol.rejected': 'Rifiutato', 'hol.pending': 'In attesa', 'hol.delete': 'Elimina',
  'hol.days': '{count} giorni', 'hol.used': 'Usati', 'hol.pendingLabel': 'In attesa', 'hol.total': 'Totale',
  'hol.dialogTitle': 'Richiedi ferie', 'hol.dialogDesc': 'Invia nuova richiesta',
  'hol.partialApprove': 'Approva parzialmente', 'hol.partialApproveTitle': 'Approvazione parziale',
  'hol.selectApprovedDates': 'Seleziona le date da approvare', 'hol.partialSummary': '{approved} di {total} giorni approvati',
  'hol.partialApproveAction': 'Approva {count} giorni', 'hol.partialApproved': 'Approvato parzialmente',
  'hol.partialRejected': 'Non approvato (parziale)', 'hol.partialRemainder': 'Rimanente da approvazione parziale',
  'hol.partialLabel': 'Parziale',

  'abs.title': 'Assenze', 'abs.subtitle': 'Monitoraggio malattie e permessi di emergenza',
  'abs.record': 'Registra assenza', 'abs.summary': 'Riepilogo dipendenti',
  'abs.allRecords': 'Tutti i record', 'abs.all': 'Tutti', 'abs.sickDays': 'Malattie',
  'abs.emergencyLeave': 'Permessi di emergenza', 'abs.sick': 'Malattia', 'abs.emergency': 'Emergenza',
  'abs.noRecords': 'Nessun record', 'abs.employee': 'Dipendente', 'abs.selectEmployee': 'Seleziona dipendente',
  'abs.type': 'Tipo', 'abs.sickDay': 'Giorno di malattia', 'abs.dates': 'Date', 'abs.pickDates': 'Seleziona date',
  'abs.reason': 'Motivo', 'abs.reasonPlaceholder': 'es. Influenza, emergenza familiare...',
  'abs.notes': 'Note (opzionale)', 'abs.notesPlaceholder': 'Dettagli aggiuntivi...',
  'abs.recordBtn': 'Registra assenza', 'abs.businessDays': '{count} giorni lavorativi',
  'abs.dialogTitle': 'Registra assenza', 'abs.dialogDesc': 'Registrare malattia o emergenza',
  'abs.totalLabel': 'Totale',

  'rep.title': 'Report', 'rep.weekOf': 'Settimana del {label}', 'rep.excel': 'Excel', 'rep.pdfPrint': 'PDF / Stampa',
  'rep.thisWeek': 'Questa settimana', 'rep.allStores': 'Tutte le sedi', 'rep.warnings': 'Avvisi',
  'rep.hoursVsContract': 'Ore vs Contratto', 'rep.storeCoverage': 'Copertura', 'rep.holidayBalance': 'Saldo ferie',
  'rep.year': 'Anno:', 'rep.loading': 'Caricamento…',
  'rep.employeeOverContract': 'Dipendente oltre il contratto',
  'rep.employeesOverContract': 'Dipendenti oltre il contratto',
  'rep.totalOvertimeRisk': 'Rischio totale straordinario',
  'rep.employeeHours': 'Ore dipendenti',
  'rep.colEmployee': 'Dipendente', 'rep.colStore': 'Sede', 'rep.colContract': 'Contratto',
  'rep.colScheduled': 'Programmato', 'rep.colExpected': 'Previsto', 'rep.colDifference': 'Differenza',
  'rep.noEmployees': 'Nessun dipendente da mostrare.',
  'rep.noStores': 'Nessuna sede da mostrare.',
  'rep.understaffedDay': '{count} giorno sotto organico questa settimana.',
  'rep.understaffedDays': '{count} giorni sotto organico questa settimana.',
  'rep.needMore': 'Servono {count}', 'rep.extra': '+{count} extra', 'rep.ok': 'OK',
  'rep.holidayBalances': 'Saldi ferie',
  'rep.colAllowance': 'Spettanza', 'rep.colApproved': 'Approvato', 'rep.colPending': 'In attesa',
  'rep.colRemaining': 'Rimanente', 'rep.colUsage': 'Utilizzo',
  'rep.lowOrOverusedSingular': 'Dipendente con saldo ferie basso o esaurito',
  'rep.lowOrOverused': 'Dipendenti con saldo ferie basso o esaurito',
  'rep.allClear': 'Tutto in ordine', 'rep.noIssues': 'Nessun problema di pianificazione questa settimana.',
  'rep.issueSingular': 'Problema da esaminare',
  'rep.issuesToReview': 'Problemi da esaminare',
  'rep.urgent': 'urgente/i',
  'rep.holidayConflict': 'Conflitto ferie', 'rep.absenceConflict': 'Conflitto assenza',
  'rep.overHours': 'Ore in eccesso', 'rep.unavailable': 'Non disponibile', 'rep.understaffed': 'Sotto organico',
  'rep.fix': 'Correggi',
  'rep.tabOverview': 'Panoramica',
  'rep.tabHoursPayroll': 'Ore e Buste paga',
  'rep.tabCoverage': 'Copertura',
  'rep.tabLeave': 'Ferie',
  'rep.tabCompliance': 'Conformità',
  'rep.overviewScheduledH': 'Ore pianificate',
  'rep.overviewOvertimeH': 'Ore di straordinario',
  'rep.overviewCoverageGaps': 'Lacune nella copertura',
  'rep.overviewNearLimit': 'Vicino al limite',
  'rep.overviewExceeding': 'Superamento contratto',
  'rep.overviewRiskLevel': 'Livello di rischio',
  'rep.overviewNoIssues': 'Nessun problema attivo questa settimana.',
  'rep.viewCompliance': 'Vedi conformità',
  'rep.viewCoverage': 'Vedi copertura',
  'rep.exportSummary': 'Esporta riepilogo',
  'rep.exportExcel': 'Esporta Excel',
  'rep.exportPDF': 'Esporta PDF',
  'rep.exportedExcel': 'Esportato in Excel',
  'rep.exportedExcelDesc': 'Controlla la cartella Download.',
  'rep.exportedCSV': 'Esportato in CSV',
  'rep.weekly': 'Settimanale',
  'rep.monthly': 'Mensile',
  'rep.totalScheduledH': 'Ore pianificate totali',
  'rep.chartTitle': 'Ore pianificate vs previste per dipendente',
  'rep.chartOnTrack': 'Conforme',
  'rep.chartOverContract': 'Oltre il contratto',
  'rep.chartUnderhours': 'Ore insufficienti',
  'rep.chartExpected': 'Previsto',
  'rep.issuesToReviewTitle': 'Problemi da verificare',
  'rep.allClearPeriod': 'Tutto in ordine — nessun problema in questo periodo.',
  'rep.employeesOverContractLabel': 'Dipendenti oltre il contratto',
  'rep.totalOvertimeRiskLabel': 'Rischio straordinari totale',
  'rep.employeeHoursTitle': 'Ore dipendenti',
  'rep.colOvertime': 'Straordinari',
  'rep.colUnderhours': 'Ore mancanti',
  'rep.colStatus': 'Stato',
  'rep.payrollBreakdown': 'Dettaglio buste paga — {period}',
  'rep.colScheduledH': 'Pianificato (h)',
  'rep.colPaidHolidayH': 'Ferie pagate (h)',
  'rep.colPaidSickH': 'Malattia pagata (h)',
  'rep.colCountedHours': 'Ore contate',
  'rep.colSickDays': 'Giorni malattia',
  'rep.totals': 'Totali',
  'rep.totalScheduled': 'Totale pianificato',
  'rep.totalActual': 'Totale effettivo',
  'rep.lowOrOverusedLabel': 'Dipendenti con saldo ferie basso o superato',
  'rep.holidayAllowanceTitle': 'Utilizzo ferie per dipendente',
  'rep.sickDaySummary': 'Riepilogo giorni malattia',
  'rep.colSickDaysUsed': 'Giorni malattia usati',
  'rep.colAnnualAllowance': 'Quota annuale',
  'rep.colInstances': 'Occorrenze',
  'rep.colPaid': 'Pagato',
  'rep.colUnpaid': 'Non pagato',
  'rep.totalEmployees': 'Totale dipendenti',
  'rep.compliant': 'Conforme',
  'rep.compWarnings': 'Avvisi',
  'rep.actionRequired': 'Azione richiesta',
  'rep.complianceDisclaimer': 'Le ore contrattuali possono variare leggermente a causa degli orari di apertura e delle durate minime dei turni. Piccole differenze (±2h) sono normali.',
  'rep.hExp': '{h}h prev',
  'rep.hCounted': '{h}h contato',
  'rep.hoursBreakdown': 'Dettaglio ore',
  'rep.scheduledWorked': 'Pianificato (lavorato)',
  'rep.paidHoliday': 'Ferie pagate',
  'rep.paidSick': 'Malattia pagata',
  'rep.unpaidLeave': 'Congedo non pagato',
  'rep.complianceDetail': 'Dettaglio conformità',
  'rep.contract': 'Contratto',
  'rep.expected': 'Previsto',
  'rep.counted': 'Contato',
  'rep.variance': 'Scostamento',
  'rep.maxStreak': 'Giorni consecutivi max',
  'rep.leaveBalanceYear': 'Saldo congedi (Anno)',
  'rep.holiday': 'Ferie',
  'rep.sick': 'Malattia',
  'rep.remainingOf': '{remaining} rimanenti su {total}g',
  'rep.riskLog': 'Registro rischi',
  'rep.highRisk': 'Rischio alto',
  'rep.mediumRisk': 'Rischio medio',
  'rep.lowRisk': 'Rischio basso',
  'rep.colRiskType': 'Tipo di rischio',
  'rep.colDateWeek': 'Data/Settimana',
  'rep.colSeverity': 'Gravità',
  'rep.colDescription': 'Descrizione',
  'rep.filterAll': 'Tutti',
  'rep.filterHigh': 'Alto',
  'rep.filterMedium': 'Medio',
  'rep.filterLow': 'Basso',
  'rep.statusCompliant': 'Conforme',
  'rep.statusWarning': 'Avviso',
  'rep.statusActionRequired': 'Azione richiesta',
  'rep.statusOnTrack': 'Conforme',
  'rep.statusAtRisk': 'A rischio',
  'rep.riskHigh': 'Alto',
  'rep.riskMedium': 'Medio',
  'rep.riskLow': 'Basso',
  'rep.gap': 'LACUNA',
  'rep.tooltipScheduled': 'Pianificato: {h}h',
  'rep.tooltipExpected': 'Previsto: {h}h',
  'rep.tooltipVariance': 'Scostamento: {h}h',
  'rep.tooltipStatus': 'Stato: {status}',

  'pva.title': 'Ore pianificate vs effettive',
  'pva.totalScheduled': 'Totale pianificato',
  'pva.totalPlanned': 'Totale pianificato',
  'pva.totalActual': 'Totale effettivo',
  'pva.totalVariance': 'Variazione totale',
  'pva.confirmRate': '{confirmed}/{total} turni confermati',
  'pva.employeeBreakdown': 'Dettaglio per dipendente',
  'pva.scheduled': 'Pianificato',
  'pva.planned': 'Pianificato',
  'pva.actual': 'Effettivo',
  'pva.variance': 'Variazione',
  'pva.breakVar': 'Pausa ±',
  'pva.confirmed': 'Confermato',
  'pva.lateArrivals': 'Ritardo',
  'pva.earlyLeaves': 'Uscita anticipata',
  'pva.totals': 'Totali',
  'pva.noConfirmedShifts': 'Nessun turno confermato — conferma i turni per vedere i dati effettivi',
  'pva.lateArrivalsCount': '{count} ritardo/i',
  'pva.earlyLeavesCount': '{count} uscita/e anticipata/e',
  'pva.totalOvertimeInsight': '{hours}h di straordinario',
  'pva.totalUnderhoursInsight': '{hours}h sotto il pianificato',
  'pva.breakOvertime': '{mins}m di pausa in più',
  'pva.breakScheduledVsActual': 'Pausa: Pianificato / Effettivo',
  'review.actualBreakStart': 'Inizio pausa',
  'review.actualBreakEnd': 'Fine pausa',

  // ── Warning Explanations ──
  'warn.hoursOverTitle': '{hours} ore oltre il contratto',
  'warn.hoursOverDesc': 'Questo dipendente è programmato oltre le ore settimanali concordate.',
  'warn.hoursOverOpt1': 'Rimuovere un turno',
  'warn.hoursOverOpt2': 'Riassegnare a un altro dipendente',
  'warn.hoursOverOpt3': 'Aumentare le ore di contratto',
  'warn.unfillableTitle': '{hours} ore non possono essere programmate questa settimana',
  'warn.unfillableWhy': 'Perché:',
  'warn.unfillableR1': 'Tutte le sedi sono al completo o chiuse',
  'warn.unfillableR2': 'La disponibilità rimanente non corrisponde ai turni aperti',
  'warn.unfillableR3': 'La durata minima del turno impedisce assegnazioni parziali',
  'warn.unfillableOpt1': 'Ridurre le ore di contratto per questa settimana',
  'warn.unfillableOpt2': 'Consentire turni più brevi',
  'warn.unfillableOpt3': 'Aumentare le esigenze di personale',
  'warn.nearContractTitle': '{hours} ore sotto il contratto',
  'warn.nearContractDesc': 'Questo accade perché tutti i turni rimanenti sono a lunghezza intera e il sistema evita di creare turni troppo corti.',
  'warn.nearContractOpt1': 'Accettare settimana sotto contratto',
  'warn.nearContractOpt2': 'Consentire turni più brevi',
  'warn.nearContractOpt3': 'Modificare le ore di contratto',
  'warn.underTitle': '{hours} ore sotto il contratto',
  'warn.underDesc': 'Questo dipendente ha meno ore programmate di quelle richieste dal contratto. Si possono ancora aggiungere turni.',
  'warn.underOpt1': 'Aggiungere turni manualmente',
  'warn.underOpt2': 'Eseguire la generazione automatica',
  'warn.underOpt3': 'Modificare le ore di contratto',
  'warn.options': 'Opzioni:',
  'warn.holidayDesc': 'Questo dipendente è in ferie approvate per questa data e non può essere programmato.',
  'warn.holidayOpt1': 'Rimuovere il turno',
  'warn.holidayOpt2': 'Assegnare un altro dipendente',
  'warn.absenceDesc': 'Questo dipendente è in congedo approvato per questa data e non dovrebbe essere programmato.',
  'warn.absenceOpt1': 'Rimuovere il turno',
  'warn.absenceOpt2': 'Assegnare un altro dipendente',
  'warn.unavailableDesc': 'Questo turno è al di fuori dei giorni di disponibilità del dipendente.',
  'warn.unavailableOpt1': 'Cambiare la data del turno',
  'warn.unavailableOpt2': 'Assegnare un altro dipendente',
  'warn.unavailableOpt3': 'Aggiornare la disponibilità',
  'warn.understaffedDesc': 'Questa sede non ha abbastanza personale programmato per questo giorno.',
  'warn.understaffedOpt1': 'Aggiungere un altro dipendente',
  'warn.understaffedOpt2': 'Modificare i requisiti di personale',
  'warn.understaffedOpt3': 'Verificare la disponibilità dei dipendenti',
  'warn.weeklyHours': 'Riepilogo ore settimanali',
  'warn.maxDaysTitle': 'Giorni lavorativi massimi raggiunti',
  'warn.maxDaysDesc': 'Questo dipendente ha raggiunto il massimo di {days} giorni lavorativi questa settimana. Non è possibile assegnare turni aggiuntivi anche se restano ore contrattuali.',
  'warn.maxDaysOpt1': 'Aumentare il massimo di giorni lavorativi',
  'warn.maxDaysOpt2': 'Accettare settimana sotto contratto',
  'warn.maxDaysOpt3': 'Modificare le ore contrattuali',
  'emp.maxWorkingDays': 'Giorni lavorativi max/settimana',

  'notfound.title': '404', 'notfound.message': 'Pagina non trovata', 'notfound.link': 'Torna alla home',
  'onb.title': 'Crea la tua organizzazione',
  'onb.subtitle': 'Dai un nome alla tua azienda o team per iniziare.',
  'onb.label': "Nome dell'organizzazione",
  'onb.placeholder': 'La Mia Azienda',
  'onb.creating': 'Creazione...',
  'onb.create': 'Crea organizzazione',
  'onb.signOut': 'Esci',
  'onb.invalidName': 'Nome non valido',
  'onb.nameLength': "Il nome dell'organizzazione deve avere tra 1 e 100 caratteri.",
  'setup.title': 'Configuriamo la tua attività',
  'setup.subtitle': 'Aggiungi il tuo primo negozio e i dipendenti per generare il tuo primo turno bilanciato.',
  'setup.progress': 'Passo {count} di {total} completato',
  'setup.step1': 'Aggiungi il tuo primo negozio',
  'setup.step2': 'Aggiungi il tuo primo dipendente',
  'setup.step3': 'Genera il tuo primo turno',
  'setup.step3Hint': 'Richiede almeno 1 negozio e 1 dipendente',
  'setup.addAnother': 'Aggiungi un altro',
  'setup.storeAdded': 'Negozio aggiunto!',
  'setup.employeeAdded': 'Dipendente aggiunto!',
  'setup.storeCount1': 'negozio aggiunto',
  'setup.storeCountN': 'negozi aggiunti',
  'setup.empCount1': 'dipendente aggiunto',
  'setup.empCountN': 'dipendenti aggiunti',
  'setup.generate': 'Genera primo turno',
  'setup.generating': 'Generazione...',
  'setup.success': 'Il tuo primo turno bilanciato è pronto.',
  'setup.genError': 'Errore nella generazione del turno',
  'setup.noShiftsError': 'Nessun turno generato. Controlla i requisiti di personale e la disponibilità.',
  'setup.skip': 'Salta per ora',
  'setup.emptySchedule': 'Hai bisogno di almeno 1 negozio e 1 dipendente per creare un turno.',
  'settings.title': 'Impostazioni',
  'settings.subtitle': "Preferenze e configurazione dell'azienda",
  'settings.balancedDesc': 'Massimizza la copertura minimizzando gli straordinari. Consigliato per la maggior parte dei team.',
  'hours.label': 'Orari di apertura',
  'hours.quickSet': 'Impostazione rapida',
  'hours.to': 'a',
  'hours.lunchBreak': 'Pausa pranzo',
  'hours.applyWeekdays': 'Applica Lun–Ven',
  'hours.applyAll': 'Applica tutti i giorni',
  'hours.open': 'Aperto',
  'hours.closed': 'Chiuso',
  'ai.getSuggestions': 'Suggerimenti IA',
  'ai.autoFix': 'Correzione auto',
  'sched.fix': 'Correggi',
  'sched.reduceHours': 'Riduci ore',
  'sched.reassignShift': 'Riassegna turno',
  'sched.confirm': 'Conferma',
  'sched.edit': 'Modifica',
  'sched.remove': 'Rimuovi',
  'sched.understaffed': '⚠ Sottorganico',
  'sched.missingCount': '{count} dipendenti mancanti',
  'sched.staffed': 'coperto',
  'sched.staff': 'personale',
  'sched.statusReady': 'Turni pronti',
  'sched.statusIssue1': '1 problema da verificare',
  'sched.statusIssues': '{count} problemi da verificare',
  'sched.issueOverHours1': '{name} supera le ore settimanali',
  'sched.issueOverHoursN': '{count} dipendenti superano le ore',
  'sched.issueShiftTooLong1': '{count} turno troppo lungo',
  'sched.issueShiftTooLongN': '{count} turni troppo lunghi',
  'sched.issueInvalidTimes1': '{count} turno con orari non validi',
  'sched.issueInvalidTimesN': '{count} turni con orari non validi',
  'sched.issueOutsideHours1': '{count} turno fuori orario negozio',
  'sched.issueOutsideHoursN': '{count} turni fuori orario negozio',
  'sched.issueClosedDay1': '{count} turno in giorno di chiusura',
  'sched.issueClosedDayN': '{count} turni in giorni di chiusura',
  'sched.issueUnavailable1': '{name} assegnato in giorno non disponibile',
  'sched.issueUnavailableN': '{count} turni in giorni non disponibili',
  'sched.issueWrongStore1': '{name} nel negozio sbagliato',
  'sched.issueWrongStoreN': '{count} dipendenti nel negozio sbagliato',
  'sched.issueOverlap1': '{count} turno sovrapposto',
  'sched.issueOverlapN': '{count} turni sovrapposti',
  'sched.issueShiftTooShort1': '{count} turno troppo corto',
  'sched.issueShiftTooShortN': '{count} turni troppo corti',
  'sched.issueUnderstaffed1': '{store} ha bisogno di più personale il {day}',
  'sched.issueUnderstaffedN': '{count} giorni sotto organico',
  'ai.fixSchedule': 'Correggi con IA',
  'ai.fixScheduleAI': 'Correggi turni con IA',
  'sched.issuesThisWeek': 'Problemi questa settimana',
  'ai.analyzing': 'Analisi...',
  'ai.suggestions': 'Suggerimenti IA',
  'ai.fixesFound': '{count} correzioni trovate',
  'ai.addresses': 'Risolve:',
  'ai.apply': 'Applica',
  'ai.preview': 'Anteprima',
  'ai.hidePreview': 'Nascondi',
  'ai.before': 'Prima',
  'ai.after': 'Dopo',
  'ai.keepsCoverage': 'Copertura mantenuta',
  'ai.withinContract': 'Entro contratto',
  'ai.shiftRemoved': 'Turno rimosso',
  'ai.noShift': 'Nessun turno',
  'ai.refresh': 'Aggiorna',
  'ai.swap': 'Scambia',
  'ai.reassign': 'Riassegna',
  'ai.addShift': 'Aggiungi turno',
  'ai.remove': 'Rimuovi',
  'ai.adjust': 'Modifica',
  'ai.applied': 'Correzione applicata',
  'ai.changes': 'Modifiche',
  'ai.failedApply': 'Applicazione fallita',
  'ai.failedFetch': 'Recupero fallito',
  'ai.aiError': 'Errore IA',
  'ai.importTitle': 'Importazione dati IA',
  'ai.stageInput': "Incolla una descrizione della tua attività. Prima i negozi, poi i dipendenti.",
  'ai.stageParsingStores': 'Analisi del testo per informazioni sui negozi...',
  'ai.stagePreviewStores': "Controlla i negozi qui sotto. Una volta confermati, verranno analizzati i dipendenti.",
  'ai.stageSavingStores': 'Salvataggio negozi...',
  'ai.stageCounting': 'Rilevamento dipendenti nel testo...',
  'ai.stageParsingEmps': 'Analisi dipendenti a lotti...',
  'ai.stageValidating': 'Validazione dati dipendenti...',
  'ai.stagePreviewEmps': 'Controlla i dipendenti qui sotto. Clicca "Conferma" per salvare.',
  'ai.stageImporting': 'Importazione dipendenti...',
  'ai.stageComplete': 'Tutti i dati importati con successo!',
  'ai.parseStores': 'Analizza negozi',
  'ai.startOver': 'Ricomincia',
  'ai.confirmStores': 'Conferma negozi e analizza dipendenti',
  'ai.importAnyway': 'Importa comunque',
  'ai.confirmImport': "Conferma importazione",
  'ai.done': 'Fatto',
  'ai.tip': 'Suggerimento: includi nomi dei negozi, orari, personale, nomi dei dipendenti, ore contrattuali, assegnazioni e disponibilità.',
  'ai.storesSaved': 'negozi salvati',
  'ai.employees': 'dipendenti',
  'ai.totalWeekly': 'totale settimanale',
  'ai.importedSuccess': '{stores} negozi e {employees} dipendenti importati con successo.',
  'summary.ofContract': 'del contratto',
  'summary.over': '+{hours}h in più',
  'summary.cantFill': '{hours}h non assegnabili',
  'summary.belowMin': '{hours}h sotto il minimo',
  'summary.remaining': '{hours}h rimanenti',
  'dash.importData': 'Importa dati',
  'dash.importTitle': 'Importa i dati aziendali',
  'dash.importDescription': 'Scegli come configurare i tuoi negozi e dipendenti.',
  'dash.aiImportTitle': 'Importazione IA intelligente',
  'dash.aiImportDesc': 'Incolla una descrizione della tua attività e lascia che l\'IA crei automaticamente negozi, dipendenti e contratti.',
  'dash.startAiImport': 'Avvia importazione IA',
  'dash.excelImportTitle': 'Carica modello Excel',
  'dash.excelImportDesc': 'Carica un file Excel strutturato con i tuoi negozi e dipendenti per un\'importazione rapida e validata.',
  'dash.uploadFile': 'Carica file',
  'dash.importHint': 'Non sai quale scegliere? L\'importazione IA è ideale per una configurazione rapida.',

  'day.sun': 'Domenica', 'day.mon': 'Lunedì', 'day.tue': 'Martedì', 'day.wed': 'Mercoledì',
  'day.thu': 'Giovedì', 'day.fri': 'Venerdì', 'day.sat': 'Sabato',
  'day.sunShort': 'Dom', 'day.monShort': 'Lun', 'day.tueShort': 'Mar', 'day.wedShort': 'Mer',
  'day.thuShort': 'Gio', 'day.friShort': 'Ven', 'day.satShort': 'Sab',

  'settings.tabOrg': 'Organizzazione', 'settings.tabTeam': 'Team', 'settings.tabRules': 'Regole di pianificazione', 'settings.tabDanger': 'Zona di pericolo',
  'settings.orgName': "Nome dell'organizzazione", 'settings.orgNameDesc': 'Il nome visualizzato dai membri del team.', 'settings.save': 'Salva', 'settings.saved': 'Impostazioni salvate',
  'settings.teamTitle': 'Membri del team', 'settings.teamDesc': 'Gestisci il tuo team e invia inviti.', 'settings.invite': 'Invita membro',
  'settings.colName': 'Nome', 'settings.colEmail': 'Email', 'settings.colRole': 'Ruolo', 'settings.colStatus': 'Stato', 'settings.colActions': 'Azioni',
  'settings.roleOwner': 'Proprietario', 'settings.roleManager': 'Gestore', 'settings.active': 'Attivo', 'settings.pending': 'In attesa',
  'settings.remove': 'Rimuovi', 'settings.resend': 'Reinvia', 'settings.removeConfirm': 'Rimuovere questo membro?', 'settings.removed': 'Membro rimosso',
  'settings.inviteSent': 'Invito inviato', 'settings.inviteEmail': 'Indirizzo email', 'settings.inviteRole': 'Ruolo', 'settings.inviteBtn': 'Invia invito',
  'settings.inviteTitle': 'Invita membro del team', 'settings.inviteDesc': "Invia un link d'invito per unirsi alla tua organizzazione.",
  'settings.rulesTitle': 'Regole di pianificazione', 'settings.rulesDesc': 'Configura come vengono generati i turni.',
  'settings.dangerTitle': 'Zona di pericolo', 'settings.dangerDesc': 'Azioni irreversibili. Procedi con cautela.',
  'settings.deleteOrg': "Elimina organizzazione", 'settings.deleteOrgDesc': 'Elimina definitivamente questa organizzazione e tutti i dati associati.',
  'settings.deleteOrgConfirm': "Sei sicuro? Questa azione non può essere annullata.", 'settings.clearSchedules': 'Cancella tutti i turni',
  'settings.clearSchedulesDesc': 'Rimuovi tutti i dati dei turni. I dati di dipendenti e negozi saranno mantenuti.',
  'settings.clearConfirm': 'Sei sicuro? Tutti i dati dei turni verranno eliminati.',
  'settings.deleted': 'Organizzazione eliminata', 'settings.cleared': 'Turni cancellati', 'settings.cancel': 'Annulla',
  'settings.you': '(tu)', 'settings.cannotRemoveSelf': 'Non puoi rimuovere te stesso',

  'common.loading': 'Caricamento...',
  'common.cancel': 'Annulla',
  'auth.forgotPassword': 'Password dimenticata?',
  'auth.or': 'oppure',
  'auth.googleSignIn': 'Continua con Google',
  'auth.appleSignIn': 'Continua con Apple',
  'forgot.title': 'Password dimenticata',
  'forgot.subtitle': 'Inserisci la tua email per ricevere un link di ripristino.',
  'forgot.button': 'Invia link',
  'forgot.sent': 'Se esiste un account, è stato inviato un link di ripristino.',
  'forgot.backToLogin': 'Torna al login',
  'reset.title': 'Reimposta password',
  'reset.subtitle': 'Inserisci la tua nuova password.',
  'reset.newPassword': 'Nuova password',
  'reset.confirmPassword': 'Conferma password',
  'reset.button': 'Aggiorna',
  'reset.passwordMin': 'La password deve avere almeno 8 caratteri',
  'reset.mismatch': 'Le password non corrispondono',
  'reset.failed': 'Aggiornamento fallito. Riprova.',
  'reset.success': 'Password aggiornata. Reindirizzamento...',
  'reset.invalidLink': 'Questo link è invalido o è scaduto.',
  'reset.backToLogin': 'Torna al login',
  'settings.tabAccount': 'Account',
  'settings.tabBilling': 'Fatturazione',
  'billing.currentPlan': 'Piano attuale',
  'billing.currentPlanDesc': 'Dettagli del tuo abbonamento',
  'billing.refresh': 'Aggiorna',
  'billing.noSubscription': 'Nessun abbonamento attivo',
  'billing.paymentMethod': 'Metodo di pagamento',
  'billing.invoices': 'Fatture',
  'billing.billingDetails': 'Dettagli di fatturazione',
  'billing.editDetails': 'Modifica dettagli',
  'billing.activating': 'Attivazione dell\'abbonamento…',
  'billing.activatingDesc': 'Di solito ci vogliono pochi secondi.',
  'billing.subEnded': 'Il tuo abbonamento è scaduto',
  'billing.choosePlan': 'Scegli il tuo piano per iniziare',
  'billing.pastDue': 'Pagamento scaduto',
  'billing.canceled': 'Annullato',
  'billing.expired': 'Scaduto',
  'billing.monthly': 'Mensile',
  'billing.annual': 'Annuale',
  'billing.save17': 'Risparmia ~17%',
  'billing.trialDesc': 'Prova gratuita di 30 giorni inclusa. Annulla in qualsiasi momento.',
  'billing.redirecting': 'Reindirizzamento…',
  'billing.reactivate': 'Riattiva abbonamento',
  'billing.startTrial': 'Inizia la prova gratuita',
  'billing.checkStatus': 'Hai già completato il pagamento? Verifica lo stato',
  'billing.accountSettings': 'Impostazioni account',
  'billing.signOut': 'Esci',
  'billing.title': 'Fatturazione e Pagamenti',
  'billing.subtitle': 'Gestisci il tuo abbonamento, metodo di pagamento e fatture',
  'billing.freeTrial': 'Prova gratuita',
  'billing.trialEnds': 'Fine della prova',
  'billing.billingCycle': 'Ciclo di fatturazione',
  'billing.cancelNotice': 'Il tuo abbonamento terminerà il {date}. Puoi riprenderlo dal portale di fatturazione.',
  'billing.noSub': 'Nessun abbonamento attivo trovato.',
  'billing.update': 'Aggiorna',
  'billing.noPayment': 'Nessun metodo di pagamento registrato',
  'billing.addCard': 'Aggiungi carta',
  'billing.noInvoicesYet': 'Nessuna fattura ancora',
  'billing.detailsDesc': 'Aggiorna il nome della tua azienda, email di fatturazione, partita IVA e indirizzo tramite il portale.',
  'billing.companyName': 'Nome azienda',
  'billing.billingEmail': 'Email di fatturazione',
  'billing.vatNumber': 'Partita IVA',
  'billing.address': 'Indirizzo',
  'billing.editBilling': 'Modifica dettagli di fatturazione',
  'billing.noDetailsFound': 'Nessun dettaglio di fatturazione trovato',
  'billing.setUp': 'Configura',
  'billing.expires': 'Scade',
  'billing.status_active': 'Attivo',
  'billing.status_trialing': 'Prova',
  'billing.status_past_due': 'Scaduto',
  'billing.status_canceled': 'Annullato',
  'billing.status_incomplete': 'Incompleto',
  'billing.status_unpaid': 'Non pagato',
  'billing.interval_month': 'mese',
  'billing.interval_year': 'anno',
  'billing.cancelsAtPeriodEnd': 'Si annulla alla fine del periodo',
  'billing.subActivated': 'Abbonamento attivato!',
  'billing.syncSlow': 'La sincronizzazione sta richiedendo più tempo del previsto. Aggiorna la pagina.',
  'billing.subFound': 'Abbonamento trovato!',
  'billing.noSubYet': 'Nessun abbonamento attivo trovato per ora.',
  'account.title': 'Informazioni account',
  'account.subtitle': 'Gestisci la tua email e password.',
  'account.changeEmail': 'Cambia email',
  'account.changeEmailDesc': 'Verrà inviata un\'email di verifica al nuovo indirizzo.',
  'account.newEmail': 'Nuova email',
  'account.confirmEmail': 'Conferma email',
  'account.currentPassword': 'Password attuale',
  'account.emailMismatch': 'Gli indirizzi email non corrispondono',
  'account.emailVerificationSent': 'Un link di verifica è stato inviato alla nuova email.',
  'account.changePassword': 'Cambia password',
  'account.changePasswordDesc': 'Devi verificare la tua password attuale.',
  'account.newPassword': 'Nuova password',
  'account.confirmNewPassword': 'Conferma nuova password',
  'account.savePassword': 'Aggiorna',
  'account.passwordMin': 'La password deve avere almeno 8 caratteri',
  'account.mismatch': 'Le password non corrispondono',
  'account.wrongPassword': 'Password attuale non corretta.',
  'account.updateFailed': 'Aggiornamento fallito. Riprova.',
  'account.passwordChanged': 'Password aggiornata',
  'account.deleteTitle': 'Elimina account',
  'account.deleteDesc': 'Elimina permanentemente il tuo account e tutti i dati.',
  'account.deleteButton': 'Elimina il mio account',
  'account.deleteConfirmTitle': 'Sei assolutamente sicuro?',
  'account.deleteConfirmDesc': 'Questa azione eliminerà permanentemente il tuo account, organizzazione e tutti i dati.',
  'account.deletePasswordLabel': 'Inserisci la password per confermare',
  'account.deleteConfirmButton': 'Elimina tutto',
  'account.deleteCancelButton': 'Annulla',
  'account.deleteSuccess': 'Account eliminato.',

  // ── Excel Import ──
  'import.title': 'Importa dati',
  'import.subtitle': 'Carica un file Excel o CSV con i tuoi negozi e dipendenti.',
  'import.downloadTemplate': 'Scarica modello Excel',
  'import.dropzone': 'Trascina e rilascia il file qui, o clicca per sfogliare',
  'import.dropzoneHint': 'Accetta .xlsx o .csv · Max 5MB',
  'import.parsing': 'Analisi del file...',
  'import.storesDetected': 'Negozi rilevati',
  'import.employeesDetected': 'Dipendenti rilevati',
  'import.hours': 'Orari',
  'import.staff': 'Personale (Fer/WE)',
  'import.days': 'Disponibilità',
  'import.totalWeekly': 'totale settimanale',
  'import.importing': 'Importazione in corso...',
  'import.success': 'Importazione riuscita!',
  'import.successMessage': 'Importazione riuscita. I tuoi dati sono pronti.',
  'import.failed': 'Importazione fallita',
  'import.confirmImport': 'Conferma importazione',
  'import.cancel': 'Annulla',
  'import.tryAgain': 'Riprova',
  'import.done': 'Fatto',
  'import.uploadData': 'Importa dati',
  'import.uploadHint': 'Oppure importa da un file Excel/CSV',

  // ── Compliance Reports ──
  'comp.complianceSummary': 'Conformità',
  'comp.payrollExport': 'Export buste paga',
  'comp.legalRisk': 'Rischio legale',
  'comp.totalEmployees': 'Totale dipendenti',
  'comp.compliant': 'Conformi',
  'comp.warnings': 'Avvisi',
  'comp.critical': 'Azione richiesta',
  'comp.employee': 'Dipendente',
  'comp.store': 'Negozio',
  'comp.contractH': 'Contratto (h/s)',
  'comp.scheduledH': 'Pianificato (h)',
  'comp.regularH': 'Ore regolari',
  'comp.overtimeH': 'Straordinari',
  'comp.underhours': 'Ore mancanti',
  'comp.holidays': 'Ferie',
  'comp.sickDays': 'Giorni malattia',
  'comp.maxStreak': 'Serie max',
  'comp.status': 'Stato',
  'comp.totalH': 'Ore totali',
  'comp.totals': 'Totali',
  'comp.allEmployees': 'Tutti i dipendenti',
  'comp.payrollDesc': 'Riepilogo mensile strutturato delle ore per l\'elaborazione delle buste paga. Nessun calcolo salariale.',
  'comp.exportedCSV': 'Esportato in CSV',
  'comp.exportedExcel': 'Esportato in Excel',
  'comp.exportedExcelDesc': 'Controlla la cartella Download per aprire il file',
  'comp.exportedCSVDesc': 'Controlla la cartella Download per aprire il file',
  'excel.employee': 'Dipendente',
  'excel.store': 'Negozio',
  'excel.contractH': 'Contratto (h)',
  'excel.scheduledH': 'Pianificato (h)',
  'excel.differenceH': 'Differenza (h)',
  'excel.status': 'Stato',
  'excel.hoursVsContract': 'Ore vs Contratto',
  'excel.metric': 'Metrica',
  'excel.required': 'Richiesto',
  'excel.scheduled': 'Pianificato',
  'excel.difference': 'Differenza',
  'excel.allowance': 'Spettanza',
  'excel.approved': 'Approvato',
  'excel.pending': 'In attesa',
  'excel.remaining': 'Rimanente',
  'excel.leaveBalance': 'Saldo ferie',
  'comp.highRisk': 'Rischio alto',
  'comp.mediumRisk': 'Rischio medio',
  'comp.lowRisk': 'Rischio basso',
  'comp.allSeverities': 'Tutte le severità',
  'comp.exportRisk': 'Esporta rapporto rischi',
  'comp.riskType': 'Tipo di rischio',
  'comp.dateWeek': 'Data / Settimana',
  'comp.severity': 'Severità',
  'comp.description': 'Descrizione',
  'comp.noRisks': 'Nessun rischio rilevato',
  'comp.noRisksDesc': 'Tutti i turni sono conformi alla normativa sul lavoro questo mese.',
  'comp.riskType48h': 'Ore settimanali > 48',
  'comp.riskTypeNoRest': 'Nessun giorno di riposo',
  'comp.riskType6Consec': '6+ giorni consecutivi',
  'comp.riskTypeContract': 'Violazione ore contratto',
  'comp.riskTypeMaxDays': 'Supera max giorni/settimana',
  'comp.riskTypeAvail': 'Programmato fuori disponibilità',
  'comp.riskDesc48h': '{hours}h programmate (max 48h)',
  'comp.riskDescNoRest': '{days} giorni lavorati senza riposo',
  'comp.riskDesc6Consec': '{days} giorni lavorati nella settimana',
  'comp.riskDescContract': '{hours}h vs {contract}h contratto (+{pct}%)',
  'comp.riskDescMaxDays': '{days} giorni lavorati (max {max})',
  'comp.riskDescAvail': 'Programmato in giorno non disponibile',
  'comp.severityHigh': 'Alto',
  'comp.severityMedium': 'Medio',
  'comp.severityLow': 'Basso',

  // ── Landing Page (restructured) ──
  'lp.hero.title': 'Pianificazione e conformità per team a turni',
  'lp.hero.subtitle': 'Crea turni, monitora le ore contrattuali, gestisci i permessi pagati e previeni problemi di conformità — tutto in un sistema.',
  'lp.hero.demo': 'Prenota una demo',
  'lp.hero.trust': 'Per negozi, ospitalità, cliniche, palestre, franchising e aziende di servizi.',

  'lp.prob.title': 'La gestione dei turni non dovrebbe essere così complicata',
  'lp.prob1': 'I fogli di calcolo manuali causano errori',
  'lp.prob2': 'Gli straordinari passano inosservati',
  'lp.prob3': 'Il monitoraggio dei permessi diventa caotico',
  'lp.prob4': 'I rischi di conformità aumentano',
  'lp.prob5': 'I manager sprecano ore ogni settimana',
  'lp.prob.closing': 'I piccoli errori di pianificazione costano denaro reale.',

  'lp.core.title': 'Tutto ciò che serve per gestire team a turni',
  'lp.core1.title': 'Creatore di turni intelligente',
  'lp.core1.desc': 'Genera automaticamente turni equilibrati rispettando le ore contrattuali e la disponibilità.',
  'lp.core2.title': 'Controllo contratti e ore',
  'lp.core2.desc': 'Monitora ore previste vs effettive mensilmente e settimanalmente.',
  'lp.core3.title': 'Monitoraggio permessi e malattia',
  'lp.core3.desc': 'Applica automaticamente le soglie pagate e rileva gli abusi.',
  'lp.core4.title': 'Suite di report di conformità',
  'lp.core4.desc': 'Genera riepiloghi di conformità mensili istantaneamente.',
  'lp.core5.title': 'Funziona in 6 lingue',
  'lp.core5.desc': 'Operota rileva automaticamente la tua lingua e adatta l\'intera piattaforma. Inglese, italiano, spagnolo, francese, tedesco e portoghese — nessuna configurazione necessaria.',
  'lp.core6.title': 'Supporto multi-sede',
  'lp.core6.desc': 'Gestisci più sedi da un unico account. Ogni sede ha i propri turni, regole di personale e report di conformità.',
  'lp.core.subtitle': 'Il motore di pianificazione IA di Operota crea turni equilibrati che rispettano le ore contrattuali, la disponibilità dei dipendenti e la copertura del team automaticamente. Senza fogli di calcolo. Senza congetture.',
  'lp.core.sectionTitle': 'Crea turni del personale in pochi secondi',

  'lp.hero.langLine': 'Disponibile in 6 lingue — si adatta automaticamente al tuo team',
  'lp.hero.noCard': 'Nessuna carta richiesta',
  'lp.hero.trustBadge1': 'Conforme al GDPR',
  'lp.hero.trustBadge2': 'Nessun costo per dipendente',
  'lp.hero.trustBadge3': 'Configurazione in 15 minuti',

  'lp.pricing.annualSave': 'Fatturato 990€/anno — 2 mesi gratis',
  'lp.pricing.annualBadge': 'Risparmia 198€/anno',
  'lp.pricing.bestValue': 'Miglior valore',
  'lp.price6': 'Funziona in 6 lingue',
  'lp.price7': 'Pianificazione automatica IA',

  'lp.prob1.desc': 'Una formula sbagliata costa ore per la correzione e rischia di pagare male il team.',
  'lp.prob2.desc': 'Le violazioni contrattuali si accumulano silenziosamente fino a diventare un problema legale.',
  'lp.prob3.desc': 'I conflitti di ferie e le assenze dell\'ultimo minuto creano caos di copertura.',
  'lp.prob4.desc': 'Senza controlli automatizzati, sei a un audit di distanza da una multa costosa.',
  'lp.prob5.desc': 'Creare turni manualmente è un lavoro ripetitivo che dovrebbe richiedere secondi.',

  'lp.aud1.desc': 'Negozi, boutique e catene di moda',
  'lp.aud2.desc': 'Team di sala e cucina',
  'lp.aud3.desc': 'Cliniche, palestre e studi terapeutici',
  'lp.aud4.desc': 'Centri fitness e strutture per il tempo libero',
  'lp.aud5.desc': 'Franchising e operatori multi-sede',
  'lp.aud6.desc': 'Team sul campo e aziende di servizi',

  'lp.section5.title': 'Monitora ore contrattuali e conformità',
  'lp.section5.subtitle': 'Operota monitora automaticamente le ore contrattuali rispetto ai turni programmati, segnala violazioni di straordinari e genera report di conformità.',
  'lp.section5.card1.title': 'Pianificazione turni con IA',
  'lp.section5.card1.desc': 'Il nostro creatore di turni usa l\'IA per generare pianificazioni equilibrate che prevengono gli straordinari e rispettano la disponibilità.',
  'lp.section5.card2.title': 'Report di conformità',
  'lp.section5.card2.desc': 'Genera riepiloghi mensili con ore lavorate vs contrattuali, saldi ferie e violazioni — esportabili in Excel.',

  'lp.early.title': 'Sii il primo a condividere la tua esperienza',
  'lp.early.subtitle': 'Unisciti al nostro programma di accesso anticipato.',

  'footer.solutions': 'Soluzioni',

  'lp.faq.businesses.q': 'Quali tipi di attività usano Operota?',
  'lp.faq.businesses.a': 'Qualsiasi attività con lavoratori a turni — negozi, ristoranti, palestre, cliniche, imprese di pulizia, agenzie di sicurezza e altro.',
  'lp.faq.setup.q': 'Quanto tempo richiede la configurazione?',
  'lp.faq.setup.a': 'La maggior parte dei team è operativa in meno di 15 minuti. Aggiungi la tua sede, importa il personale e avvia la generazione automatica.',
  'lp.faq.selfService.q': 'I dipendenti possono vedere i propri turni?',
  'lp.faq.selfService.a': 'Il self-service per i dipendenti è nella nostra roadmap. Attualmente i turni possono essere esportati in PDF o Excel.',
  'lp.faq.perEmployee.q': 'C\'è un costo per dipendente?',
  'lp.faq.perEmployee.a': 'No. Operota ha un costo fisso di 99€ al mese indipendentemente dal numero di dipendenti o sedi.',
  'lp.faq.afterTrial.q': 'Cosa succede dopo la prova di 30 giorni?',
  'lp.faq.afterTrial.a': 'Passi al piano mensile a 99€/mese. Ti avvisiamo prima della scadenza e puoi cancellare in qualsiasi momento.',
  'lp.faq.languages.q': 'In quali lingue è disponibile?',
  'lp.faq.languages.a': 'Inglese, italiano, spagnolo, francese, tedesco e portoghese. Rileva automaticamente la lingua del browser.',

  'lp.pricing.title': 'Prezzi semplici e professionali',
  'lp.pricing.noFees': 'Nessun costo nascosto. Nessun addebito per dipendente.',
  'lp.price1': 'Dipendenti illimitati',
  'lp.price2': 'Supporto multi-sede',
  'lp.price3': 'Report di conformità completi',
  'lp.price4': 'Monitoraggio permessi e assenze',
  'lp.price5': 'Prova gratuita di 30 giorni',

  'lp.aud.title': 'Creato per attività a turni in crescita',
  'lp.aud.subtitle': 'Se gestisci dipendenti su turni o sedi diverse, Operota tiene il tuo team organizzato e conforme.',
  'lp.aud1': 'Negozi',
  'lp.aud2': 'Gruppi dell\'ospitalità',
  'lp.aud3': 'Cliniche',
  'lp.aud4': 'Palestre',
  'lp.aud5': 'Franchising',
  'lp.aud6': 'Aziende di servizi',

  'lp.faq.payroll.q': 'Gestisce le buste paga?',
  'lp.faq.payroll.a': 'No. Operota fornisce export mensili strutturati delle ore pronti per il tuo commercialista o sistema paghe, ma non calcola stipendi, tasse o detrazioni.',
  'lp.faq.contracts.q': 'Rispetta le ore contrattuali?',
  'lp.faq.contracts.a': 'Sì. Il sistema rispetta automaticamente le ore contrattuali settimanali di ogni dipendente nella generazione dei turni.',
  'lp.faq.excel.q': 'Posso importare da Excel?',
  'lp.faq.excel.a': 'Sì. Importa sedi e dipendenti da file Excel o CSV, oppure usa la nostra funzione di importazione IA.',
  'lp.faq.security.q': 'I miei dati sono al sicuro?',
  'lp.faq.security.a': 'I tuoi dati sono crittografati in transito e a riposo, ospitati interamente nell\'UE, con infrastruttura cloud enterprise e conformità GDPR rigorosa.',
  'lp.faq.stores.q': 'Posso gestire più sedi?',
  'lp.faq.stores.a': 'Assolutamente. Operota supporta sedi illimitate. Gestisci turni, personale e report da un unico pannello.',

  'settings.tabLeave': 'Politica ferie',
  'leave.globalPolicy': 'Politica ferie globale',
  'leave.globalPolicyDesc': 'Imposta le allocazioni predefinite di ferie pagate per tutti i dipendenti.',
  'leave.paidHolidayDays': 'Giorni di ferie pagate annuali',
  'leave.paidSickDays': 'Giorni di malattia pagati annuali',
  'leave.hoursPerDay': 'Ore di lavoro al giorno',
  'leave.savePolicy': 'Salva politica',
  'leave.policySaved': 'Politica ferie salvata',
  'leave.bulkActions': 'Azioni di massa',
  'leave.bulkActionsDesc': 'Applica modifiche della politica ai dipendenti o reimposta i contatori annuali.',
  'leave.applyToAll': 'Applica a tutti i dipendenti',
  'leave.confirmApply': 'Applicare la politica a tutti?',
  'leave.confirmApplyDesc': 'Questo aggiornerà le allocazioni di ferie e malattia per tutti i dipendenti senza override personalizzato.',
  'leave.appliedToAll': 'Politica applicata a tutti i dipendenti',
  'leave.resetCounters': 'Reimposta contatori',
  'leave.confirmReset': 'Reimpostare i contatori ferie?',
  'leave.confirmResetDesc': 'I saldi ferie sono calcolati dai registri. Filtra per anno per vedere i saldi attuali.',
  'leave.resetInfo': 'I saldi sono calcolati da registri con data. Filtra per anno.',
  'leave.overridePolicy': 'Override politica predefinita',
  'leave.paidHolH': 'Ferie pagate (h)',
  'leave.paidSickH': 'Malattia pagata (h)',
  'leave.unpaidH': 'Assenza non pagata (h)',
  'leave.holAllowance': 'Allocazione ferie',
  'leave.sickAllowance': 'Allocazione malattia',
  'leave.used': 'usato',
  'leave.remainHol': 'Ferie rest.',
  'leave.remainSick': 'Malattia rest.',
  'comp.contractCountedH': 'Ore conteggiate',
  'comp.contractCountedTooltip': 'Ore pianificate + ferie pagate. Per conformità contrattuale.',
  'comp.workingDays': 'giorni lavorativi',
  'comp.day': 'giorno',
  'comp.expectedH': 'Previsto',
  'comp.expectedTooltip': 'Calcolato da ore di contratto × giorni lavorativi nel periodo. Conteggio esatto.',
  'comp.scheduledTooltip': 'Ore di turno effettive nel periodo.',
  'comp.underhoursVsContract': 'Ore mancanti',
  'comp.underhoursTooltip': 'Ore conteggiate − Ore previste. Negativo = deficit.',
  'comp.complianceWeekly': 'Settimanale',
  'comp.complianceMonthly': 'Mensile',
  'comp.complianceReport': 'Report di conformità',
  'comp.period': 'Periodo',
  'comp.print': 'Stampa',
  'comp.savePDF': 'Salva come PDF',
  'comp.printOrSavePDF': 'Stampa / Salva come PDF',
  'comp.printPDFHint': 'Per salvare come PDF, seleziona "Salva come PDF" come destinazione nella finestra di stampa.',
  'comp.generatingPDF': 'Generazione PDF...',
  'comp.backToReports': 'Torna ai report',
  'comp.totalOvertime': 'Straordinario totale',
  'comp.totalUnderhours': 'Ore mancanti totali',
  'comp.totalUnpaidLeave': 'Permessi non retribuiti totali',
  'comp.generatedOn': 'Generato il',
  'comp.generatedBy': 'Generato da',
  'comp.organization': 'Organizzazione',
  'comp.reportPeriod': 'Periodo del report',
  'comp.hoursBreakdown': 'Dettaglio ore',
  'comp.compliance': 'Conformità',
  'comp.leaveBalanceYear': 'Saldo ferie (Anno)',
  'comp.leaveBalanceAnnual': 'Saldo ferie (Annuale)',
  'comp.scheduled': 'Programmato (lavorato)',
  'comp.paidHoliday': 'Ferie retribuite',
  'comp.paidSick': 'Malattia retribuita',
  'comp.unpaidLeave': 'Permesso non retribuito',
  'comp.contract': 'Contratto',
  'comp.expected': 'Previsto',
  'comp.expectedPeriod': 'Previsto per il periodo',
  'comp.counted': 'Conteggiato',
  'comp.variance': 'Scostamento',
  'comp.worked': 'Lavorato',
  'comp.holidayRemaining': 'Ferie rimanenti',
  'comp.sickRemaining': 'Malattia rimanente',
  'comp.days': 'giorni',
  'comp.notes': 'Note',
  'comp.statusOk': 'In regola',
  'comp.statusWarning': 'Variazione minore',
  'comp.statusCritical': 'Azione richiesta',
  'comp.allClear': 'Tutti i dipendenti in regola',
  'comp.varianceExplainer': 'Le ore contrattuali possono variare leggermente a causa degli orari di apertura, della disponibilità e della durata minima dei turni. Piccole differenze (±2h) sono normali.',
  'comp.toleranceOk': 'Entro la tolleranza',
  'comp.toleranceWarning': 'Revisione consigliata',
  'comp.toleranceAction': 'Azione richiesta',
};

const pt: Record<string, string> = {
  'header.signIn': 'Entrar', 'header.getStarted': 'Começar',
  'hero.badge': 'Para equipes retail multi-loja',
  'hero.title1': 'Planejamento IA de pessoal', 'hero.title2': 'Que realmente equilibra suas lojas',
  'hero.subtitle': 'Criado para equipes retail em crescimento que precisam de escalas conformes com contratos, equilibradas por IA, sem o caos.',
  'hero.cta': 'Teste gratuito de 30 dias', 'hero.signIn': 'Entrar',
  'hero.trust1': 'Teste gratuito de 30 dias', 'hero.trust2': 'Configuração em minutos', 'hero.trust3': 'Sem compromisso',
  'hero.euLine': 'Hospedado na UE. Em conformidade com o RGPD. Criado para contratos de trabalho europeus.',
  'trust.title': 'Criado para operações retail modernas',
  'trust.eu': 'Hospedagem UE', 'trust.gdpr': 'Conforme RGPD', 'trust.secure': 'Infraestrutura cloud segura', 'trust.encrypted': 'Criptografado em trânsito e em repouso',
  'trust.origin': 'Projetado e operado da Itália.',
  'screenshot.caption': 'Escala auto-equilibrada gerada em um clique.', 'screenshot.sub': 'Respeite contratos. Evite horas extras. Elimine lacunas de cobertura.',
  'features.title': 'Tudo para escalas mais inteligentes', 'features.subtitle': 'Ferramentas orientadas a resultados para gerentes retail ocupados.',
  'feature.scheduling.title': 'Planejamento inteligente', 'feature.scheduling.desc': 'Escalas geradas por IA que equilibram automaticamente as horas e previnem lacunas.',
  'feature.employees.title': 'Gestão de funcionários', 'feature.employees.desc': 'Acompanhe contratos, disponibilidade e ausências em um painel centralizado.',
  'feature.stores.title': 'Suporte multi-loja', 'feature.stores.desc': 'Gerencie todas as unidades a partir de um centro de controle inteligente.',
  'feature.absences.title': 'Controle de ausências', 'feature.absences.desc': 'Registre faltas e licenças. Sempre saberá quem está disponível.',
  'feature.holidays.title': 'Gestão de férias', 'feature.holidays.desc': 'Gerencie feriados e solicitações de férias automaticamente.',
  'feature.reports.title': 'Relatórios e exportações', 'feature.reports.desc': 'Gere relatórios e exporte para Excel.',
  'midCta.title': 'Pare de criar escalas manualmente.', 'midCta.subtitle': 'Junte-se às equipes retail que estão mudando para a gestão IA de escalas.', 'midCta.button': 'Teste gratuito',
  'stats.shifts': 'Turnos planejados', 'stats.teams': 'Equipes ativas', 'stats.uptime': 'Disponibilidade', 'stats.rating': 'Avaliação',
  'how.title': 'Como funciona', 'how.subtitle': 'Funcionando em minutos.',
  'how.step1.title': 'Adicione suas lojas', 'how.step1.desc': 'Configure suas unidades e horários.',
  'how.step2.title': 'Adicione funcionários', 'how.step2.desc': 'Insira sua equipe com contratos e disponibilidade.',
  'how.step3.title': 'Crie as escalas', 'how.step3.desc': 'Construa escalas com sugestões inteligentes.',
  'pricing.title': 'Preços simples e transparentes', 'pricing.subtitle': 'Teste gratuito de 30 dias. Sem cartão.',
  'pricing.perMonth': '/mês', 'pricing.afterTrial': 'Após o teste gratuito',
  'pricing.cta': 'Teste gratuito', 'pricing.included': 'Tudo incluído:',
  'pricing.item1': 'Lojas ilimitadas', 'pricing.item2': 'Funcionários ilimitados',
  'pricing.item3': 'Equilíbrio IA incluído', 'pricing.item4': 'Controle de ausências e férias',
  'pricing.item5': 'Planejamento conforme contratos', 'pricing.item6': 'Suporte prioritário',
  'pricing.badge': 'Ideal para equipes em crescimento', 'pricing.reassurance': 'Cancele a qualquer momento. Sem contratos de longo prazo.',
  'pricing.target': 'Para equipes retail em crescimento e negócios multi-loja.',
  'pricing.monthly': 'Mensal', 'pricing.annual': 'Anual', 'pricing.comingSoon': 'Em breve',
  'cta.title': 'Pronto para simplificar seu planejamento?', 'cta.subtitle': 'Junte-se aos gerentes que planejam de forma mais inteligente.',
  'cta.button': 'Iniciar teste gratuito',
  'footer.features': 'Recursos', 'footer.howItWorks': 'Como funciona', 'footer.signIn': 'Entrar',
  'footer.rights': '© {year} Operota. Todos os direitos reservados.',
  'footer.product': 'Produto', 'footer.legal': 'Legal',
  'footer.terms': 'Termos e Condições', 'footer.privacy': 'Política de Privacidade',
  'footer.cookies': 'Política de Cookies', 'footer.dpa': 'DPA', 'footer.refunds': 'Reembolsos e Cancelamento',
  'pain.title': 'Ainda gerenciando escalas do jeito difícil?',
  'pain.manual.title': 'Planejamento manual', 'pain.manual.desc': 'Fazer escalas no Excel ou papel consome horas toda semana.',
  'pain.gaps.title': 'Lacunas de cobertura', 'pain.gaps.desc': 'Faltas de última hora deixam lojas com pessoal insuficiente.',
  'pain.overtime.title': 'Erros de hora extra', 'pain.overtime.desc': 'Horas contratuais são excedidas sem aviso.',
  'pain.cta.text': 'Deixe o Operota cuidar disso', 'pain.cta.button': 'Iniciar teste gratuito',
  'compare.title': 'Por que Operota em vez de Excel?',
  'compare.excel.1': 'Planejamento manual', 'compare.excel.2': 'Sem rastreamento de contratos',
  'compare.excel.3': 'Sem visão multi-loja', 'compare.excel.4': 'Sem rastreamento de férias',
  'compare.operota.1': 'Equilíbrio automático com IA', 'compare.operota.2': 'Escalas que respeitam contratos',
  'compare.operota.3': 'Painel multi-loja', 'compare.operota.4': 'Gestão de ausências integrada',
  'testimonials.title': 'A confiança de equipes retail em crescimento',
  'testimonial.1.quote': 'Operota nos economiza 6 horas por semana. O equilíbrio IA é revolucionário.', 'testimonial.1.name': 'Marco Rossi', 'testimonial.1.role': 'Gerente Retail – Cadeia de moda',
  'testimonial.2.quote': 'Paramos de perder turnos depois da mudança. As lacunas de cobertura são coisa do passado.', 'testimonial.2.name': 'Emma Clarke', 'testimonial.2.role': 'Proprietária multi-loja – Grupo boutique',
  'testimonial.3.quote': 'Finalmente um software que respeita as horas contratuais e mantém nossas lojas em conformidade.', 'testimonial.3.name': 'Daniel Weber', 'testimonial.3.role': 'Líder de Operações – Cadeia retail',
  'faq.title': 'Perguntas Frequentes',
  'faq.cancel.q': 'Posso cancelar a qualquer momento?', 'faq.cancel.a': 'Sim, você pode cancelar sua assinatura a qualquer momento sem penalidades. Sem contratos de longo prazo ou taxas ocultas. Seus dados permanecem acessíveis até o final do período de faturamento.',
  'faq.trial.q': 'Existe um teste gratuito?', 'faq.trial.a': 'Sim. Cada nova conta recebe um teste gratuito completo de 30 dias com acesso a todos os recursos, incluindo planejamento IA. Sem cartão.',
  'faq.stores.q': 'Suporta múltiplas lojas?', 'faq.stores.a': 'Absolutamente. Operota suporta lojas ilimitadas dentro de uma organização. Gerencie escalas, pessoal e relatórios de todas as lojas em um único painel.',
  'faq.contracts.q': 'Respeita os contratos dos funcionários?', 'faq.contracts.a': 'Sim. Operota respeita automaticamente os limites de horas contratuais ao gerar escalas. A IA previne horas extras.',
  'faq.security.q': 'Meus dados estão seguros?', 'faq.security.a': 'Seus dados são criptografados em trânsito e em repouso, e hospedados inteiramente na UE. Infraestrutura cloud de nível empresarial com estrita conformidade com o RGPD.',
  'finalCta.title': 'Pare de gerenciar turnos em planilhas.',
  'finalCta.subtitle': 'Comece seu teste gratuito de 30 dias e assuma o controle do planejamento do seu pessoal.',
  'finalCta.button': 'Iniciar teste gratuito', 'finalCta.signIn': 'Entrar',

  'nav.dashboard': 'Painel', 'nav.stores': 'Lojas', 'nav.employees': 'Funcionários',
  'nav.schedule': 'Escalas', 'nav.holidays': 'Férias', 'nav.absences': 'Ausências',
  'nav.reports': 'Relatórios', 'nav.signOut': 'Sair', 'nav.settings': 'Configurações',

  'auth.back': 'Voltar', 'auth.title': 'Operota', 'auth.subtitle': 'Gerencie suas escalas facilmente',
  'auth.signIn': 'Entrar', 'auth.signUp': 'Cadastrar', 'auth.email': 'E-mail', 'auth.password': 'Senha',
  'auth.signingIn': 'Entrando...', 'auth.creatingAccount': 'Criando conta...',
  'auth.createAccount': 'Criar conta', 'auth.loading': 'Carregando...',
  'auth.checkEmail': 'Verifique seu e-mail para confirmar sua conta.',
  'auth.invalidCredentials': 'E-mail ou senha inválidos.',
  'auth.emailNotConfirmed': 'Verifique seu endereço de e-mail.',
  'auth.alreadyRegistered': 'Uma conta com este e-mail já existe.',
  'auth.unexpectedError': 'Ocorreu um erro inesperado.',
  'auth.invalidEmail': 'Insira um endereço de e-mail válido',
  'auth.passwordMin': 'A senha deve ter pelo menos 6 caracteres',

  'dash.title': 'Painel', 'dash.weekOf': 'Semana de {start} - {end}',
  'dash.today': 'Hoje',
  'dash.employeesWorking': 'funcionários trabalhando', 'dash.workingToday': 'trabalhando hoje',
  'dash.onShiftToday': 'em turno hoje',
  'dash.pendingRequests': 'solicitações pendentes', 'dash.requestsToReview': 'solicitações para revisar',
  'dash.staffingGaps': 'problemas de pessoal', 'dash.issuesToday': 'problemas hoje',
  'dash.noIssuesToday': 'Sem problemas hoje', 'dash.noIssues': 'Sem problemas',
  'dash.totalStores': 'Lojas', 'dash.totalEmployees': 'Funcionários',
  'dash.shiftsThisWeek': 'Turnos esta semana', 'dash.staffingAlerts': 'Alertas de pessoal',
  'dash.thisWeek': 'Esta semana', 'dash.upcomingIssues': 'Esta semana',
  'dash.actionRequired': 'Ação necessária', 'dash.insights': 'Informações',
  'dash.understaffed': '{count} dia(s) com falta de pessoal',
  'dash.missingStaff': '{count} pessoal em falta', 'dash.moreIssues': '...e mais {count}',
  'dash.viewSchedule': 'Ver escalas', 'dash.stores': 'Lojas', 'dash.storesDesc': 'Gerenciar unidades',
  'dash.pendingHolidayRequests': '{count} pedido(s) de férias pendente(s)',
  'dash.pendingHolidayDesc': 'Aprovar ou rejeitar pedidos', 'dash.reviewRequests': 'Revisar',
  'dash.nearOvertime': '{count} funcionário(s) perto de horas extras',
  'dash.employeeOverContract': 'funcionário acima do contrato',
  'dash.employeesOverContract': 'funcionários acima do contrato',
  'dash.approachingLimit': 'perto do limite', 'dash.more': 'mais',
  'dash.allClear': 'Tudo em ordem — sem problemas esta semana',
  'dash.weekGlance': 'Esta semana num relance',
  'dash.ofContractHours': 'de {hours}h contratuais',
  'dash.storesFullyCovered': 'Lojas totalmente cobertas',
  'dash.staffingGapsWeek': 'Faltas de pessoal esta semana',
  'dash.coverageOverview': 'Visão geral da cobertura',
  'dash.manageStores': 'Gerenciar lojas', 'dash.employees': 'Funcionários', 'dash.employeesDesc': 'Ver e gerenciar pessoal',
  'dash.manageEmployees': 'Gerenciar funcionários', 'dash.schedule': 'Escalas', 'dash.scheduleDesc': 'Criar e gerenciar escalas',
  'dash.openSchedule': 'Abrir escalas',

  'stores.title': 'Lojas', 'stores.subtitle': 'Gerencie unidades, pessoal e horários',
  'stores.add': 'Adicionar loja', 'stores.edit': 'Editar loja', 'stores.addNew': 'Nova loja',
  'stores.editDesc': 'Atualizar detalhes', 'stores.addDesc': 'Adicionar nova unidade',
  'stores.name': 'Nome', 'stores.address': 'Endereço', 'stores.staffingReq': 'Pessoal necessário por dia',
  'stores.timeBlockStaffing': 'Pessoal por bloco horário', 'stores.timeBlockDesc': 'Defina o pessoal necessário por período.', 'stores.addBlock': 'Adicionar', 'stores.noBlocks': 'Sem blocos — usa requisito diário.', 'stores.staff': 'pes.', 'stores.colTimeBlocks': 'Blocos',
  'stores.create': 'Criar loja', 'stores.update': 'Atualizar', 'stores.loading': 'Carregando lojas...',
  'stores.empty': 'Nenhuma loja. Adicione sua primeira unidade.', 'stores.colName': 'Nome',
  'stores.colAddress': 'Endereço', 'stores.colHours': 'Horários', 'stores.colStaffing': 'Pessoal',
  'stores.colActions': 'Ações', 'stores.notSet': 'Não definido',
  'stores.breakPolicy': 'Política de pausa', 'stores.breakPolicyDesc': 'Configure deduções automáticas de pausa para folha de pagamento.', 'stores.breakIfShiftExceeds': 'Aplicar pausa se turno exceder', 'stores.hours': 'horas', 'stores.breakDuration': 'Duração da pausa', 'stores.addSecondBreak': 'Adicionar segunda pausa para turnos longos', 'stores.storeClosesDuringBreak': 'Loja fecha durante a pausa', 'stores.colBreaks': 'Política de pausa', 'stores.breakSummary': '{duration}min pausa ≥{threshold}h',
  'stores.breakSummaryPerDay': 'Durações de pausa por dia configuradas', 'stores.breakPresetHint': 'Clique num preset para aplicar a todos os dias, ou personalize por dia abaixo.', 'stores.perDayBreakDuration': 'Duração da pausa por dia',
  'stores.breakExceedsLimit': 'A pausa excede 30% de um turno de {hours}h (máx {max}min)',
  'stores.perDayConfig': 'Configuração por dia',
  'stores.block': 'bloco',
  'stores.blocks': 'blocos',

  'emp.title': 'Funcionários', 'emp.subtitle': 'Gerenciar pessoal, disponibilidade e atribuições',
  'emp.hPerWeek': 'h/semana',
  'emp.add': 'Adicionar funcionário', 'emp.edit': 'Editar funcionário', 'emp.addNew': 'Novo funcionário',
  'emp.editDesc': 'Atualizar detalhes', 'emp.addDesc': 'Adicionar novo membro',
  'emp.name': 'Nome', 'emp.email': 'E-mail', 'emp.phone': 'Telefone', 'emp.contractHours': 'Horas/semana',
  'emp.primaryStore': 'Loja principal', 'emp.selectStore': 'Selecionar loja',
  'emp.dayAvailability': 'Disponibilidade', 'emp.canWorkAt': 'Pode trabalhar em',
  'emp.addBtn': 'Adicionar funcionário', 'emp.updateBtn': 'Atualizar',
  'emp.search': 'Buscar por nome, e-mail, telefone...', 'emp.nameAZ': 'Nome A–Z', 'emp.nameZA': 'Nome Z–A',
  'emp.hoursUp': 'Horas ↑', 'emp.hoursDown': 'Horas ↓', 'emp.byStore': 'Por loja', 'emp.allStores': 'Todas as lojas',
  'emp.loading': 'Carregando funcionários...', 'emp.empty': 'Nenhum funcionário. Adicione o primeiro membro.',
  'emp.colName': 'Nome', 'emp.colContact': 'Contato', 'emp.colHours': 'Horas',
  'emp.colStore': 'Loja', 'emp.colAvailability': 'Disponibilidade', 'emp.colActions': 'Ações',

  'sched.title': 'Escalas', 'sched.weekOf': 'Semana de {start} - {end}',
  'sched.allStores': 'Todas as lojas', 'sched.selectStore': 'Selecionar loja',
  'sched.autoGenerate': 'Gerar automaticamente', 'sched.export': 'Exportar', 'sched.clearAll': 'Limpar tudo',
  'sched.print': 'Imprimir', 'sched.printAll': 'Imprimir todas', 'sched.printStore': 'Imprimir {name}',
  'sched.prevWeek': 'Semana anterior', 'sched.nextWeek': 'Próxima semana', 'sched.today': 'Hoje',
  'sched.conflicts': 'Conflitos de escala', 'sched.andMore': '...e {count} mais',
  'sched.addStoresFirst': 'Adicione lojas para começar.', 'sched.clear': 'Limpar',
  'sched.holiday': 'Férias', 'sched.pendingHoliday': 'Férias pendentes', 'sched.pendingHolidays': '{count} pedidos de férias pendentes', 'sched.add': 'Adicionar',
  'sched.pendingTimeOff': 'Ausência pendente',
  'sched.conflictsDetected': '{count} conflitos férias-turno',
  'sched.includePending': 'Incluir pendentes',
  'sched.resolveConflicts': 'Corrigir automaticamente',
  'sched.approvedConflict': 'Férias aprovadas',
  'sched.pendingConflict': 'Pedido pendente',
  'sched.resolveTitle': 'Corrigir conflitos automaticamente',
  'sched.resolveDesc': 'As seguintes alterações serão feitas para resolver os conflitos:',
  'sched.willResolve': '{count} conflitos serão corrigidos:',
  'sched.cannotResolve': '{count} requerem ajuste manual:',
  'sched.noReplacement': 'Sem substituto disponível',
  'sched.removeShift': 'Remover turno',
  'sched.applyFixes': 'Aplicar {count} correções',
  'sched.resolveSuccess': '{count} conflitos resolvidos',
  'sched.resolvePartial': '{count} ainda requerem ajuste manual',
  'sched.editShift': 'Editar turno', 'sched.addShift': 'Adicionar turno',
  'sched.editShiftDesc': 'Atualizar detalhes', 'sched.addShiftDesc': 'Agendar novo turno',
  'sched.employee': 'Funcionário', 'sched.selectEmployee': 'Selecionar funcionário',
  'sched.store': 'Loja', 'sched.date': 'Data', 'sched.startTime': 'Início', 'sched.endTime': 'Fim',
  'sched.updateShift': 'Atualizar turno', 'sched.addShiftBtn': 'Adicionar turno',
  'sched.clearConfirm': 'Limpar todos os turnos desta semana?',
  'sched.clearStoreConfirm': 'Limpar todos os turnos de {name} esta semana?',
  'sched.strategyStandard': 'Padrão',
  'sched.strategyCoverage': 'Prioridade cobertura',
  'sched.strategyBalanced': 'Equilibrado',
  'sched.strategyFullCoverage': 'Cobertura total',
  'sched.balancedDesc': 'Otimiza equidade e conformidade contratual mantendo a cobertura.',
  'sched.coverageDesc': 'Prioriza a cobertura total. Pode permitir horas extras limitadas.',
  'sched.recommended': 'Recomendado',
  'sched.coverageWarning': 'Prioriza a cobertura total. Pode causar horas extras limitadas.',
  'sched.strategyLabel': 'Estratégia de agendamento',
  'sched.defaultStrategy': 'Estratégia padrão',
  'sched.defaultStrategyDesc': 'Definir a estratégia padrão para sua organização.',
  'sched.strategySaved': 'Estratégia padrão atualizada',
  'sched.shiftAdded': 'Turno adicionado com sucesso',
  'sched.shiftUpdated': 'Turno atualizado com sucesso',
  'sched.shiftRemoved': 'Turno removido com sucesso',
  'sched.shiftsSwapped': 'Turnos trocados com sucesso',
  'sched.shiftMoved': 'Turno movido com sucesso',
  'sched.errorSwap': 'Erro ao trocar turnos',
  'sched.errorMoveShift': 'Erro ao mover turno',
  'sched.cannotSwapStores': 'Não é possível trocar turnos entre lojas diferentes',
  'sched.swapHint': 'Soltar para trocar',
  'sched.alreadyScheduledSwap': 'O funcionário já está agendado nesse dia',
  'sched.availabilityOverrideTitle': 'Substituição de disponibilidade',
  'sched.availabilityOverrideDesc': '{name} só está disponível em {days}. Aprovar esta mudança de turno?',
  'sched.approve': 'Aprovar',
  'sched.noDaysAvailable': 'nenhum dia',
  'sched.errorAddShift': 'Erro ao adicionar turno',
  'sched.errorUpdateShift': 'Erro ao atualizar turno',
  'sched.errorRemoveShift': 'Erro ao remover turno',
  'sched.noShiftsToClear': 'Nenhum turno para limpar',
  'sched.clearedShifts': '{count} turno(s) limpo(s)',
  'sched.clickUndo': 'Clique em desfazer para restaurá-los.',
  'sched.undo': 'Desfazer',
  'sched.restoredShifts': '{count} turno(s) restaurado(s)',
  'sched.errorRestore': 'Erro ao restaurar turnos',
  'sched.errorClear': 'Erro ao limpar a escala',
  'sched.generatedShifts': '{count} turnos gerados com sucesso',
  'sched.errorGenerate': 'Erro ao gerar a escala',
  'sched.validationError': 'Erro de validação',
  'sched.cannotAssign': 'Não é possível atribuir turno',
  'sched.alreadyScheduled': '{name} já tem um turno nesta data.',
  'sched.onHoliday': '{name} está em férias aprovadas nesta data.',
  'sched.onLeave': '{name} está em {type} nesta data.',
  'sched.storeClosed': 'A loja está fechada neste dia.',
  'sched.shiftOutsideHours': 'O turno está fora do horário da loja',
  'sched.shiftOutsideHoursWarning': 'Este turno se estende além do horário da loja. O tempo extra será registrado como horas extras nos relatórios.',
  'sched.significantOvertimeTitle': 'Turno significativamente fora do horário da loja',
  'sched.significantOvertimeDesc': 'Este turno se estende mais de 60 minutos além do horário de funcionamento da loja. Tem certeza de que deseja salvá-lo?',
  'sched.saveAnyway': 'Salvar mesmo assim',
  'sched.shiftPaidBreak': '{paid} pago ({break} pausa)',
  'sched.breakDeducted': '{duration} pausa deduzida das horas pagas',
  'sched.bannerGenerated': 'Escala gerada — {employees} funcionários escalados, {hours}h contratuais respeitadas, {gaps}, turnos distribuídos de forma justa.',
  'sched.bannerNoGaps': 'sem falhas de cobertura',
  'sched.bannerGaps': '{count} falha(s) de cobertura',
  'sched.bannerNoIssues': 'Nenhum problema encontrado',
  'sched.bannerReviewGaps': '{count} falha(s) de cobertura para revisar →',
  'sched.exportSuccess': 'Escala exportada com sucesso',
  'sched.coverageDiag': 'Diagnóstico de cobertura',
  'sched.tbCoverageDiag': 'Problemas de cobertura por bloco',
  'sched.moreGaps': '...e mais {count} lacunas',
  'sched.coverageMet': 'Cobertura: {staffed} / {required} ✔',
  'sched.coverageShort': 'Cobertura: {staffed} / {required} ✘',
  'sched.coverageOver': 'Cobertura: {staffed} / {required} ⚠',
  'sched.missingStaff': 'Faltam {count} para {time}',
  'sched.requirements': 'Requisitos',
  'sched.shifts': 'Turnos',
  'sched.diagNoAvail': 'Sem disponibilidade ({count})',
  'sched.diagContractExh': 'Contrato esgotado ({count})',
  'sched.diagMaxDays': 'Dias máx. atingidos ({count})',
  'sched.diagNoAssigned': 'Nenhum funcionário atribuído a esta loja',
  'sched.diagNoEligible': 'Nenhum funcionário elegível',
  'sched.diagNoReq': 'Nenhum requisito de pessoal configurado',
  'sched.conflictOverHours': '{name} está escalado para {hours}h mas o contrato é de {contract}h',
  'sched.conflictUnavailable': '{name} não está disponível no {day}',
  'sched.conflictWrongStore': '{name} não está atribuído a {store}',
  'sched.exportEmployee': 'Funcionário',
  'sched.exportStaffing': 'Quadro',
  'sched.exportReqSched': 'Necessário/Escalado',
  'abs.typeSick': 'Doente',
  'abs.typeEmergency': 'Emergência',
  'sched.copyWeek': 'Copiar semana',
  'sched.copyWeekTitle': 'Copiar escala semanal',
  'sched.copyWeekDesc': 'Duplicar todos os turnos de uma semana para outra.',
  'sched.sourceWeek': 'Semana de origem',
  'sched.targetWeek': 'Semana de destino',
  'sched.copyWeekConfirm': 'Copiar escala',
  'sched.copyWeekSuccess': 'Escala copiada com sucesso.',
  'sched.copyWeekError': 'Erro ao copiar escala',
  'sched.copyWeekNoShifts': 'Nenhum turno encontrado na semana de origem.',
  'sched.copyWeekReplace': 'Esta semana já tem {count} turno(s). Substituir a escala existente?',
  'sched.copyWeekReplaceBtn': 'Substituir e copiar',
  'sched.copyWeekCopying': 'A copiar...',
  'sched.selectTargetWeek': 'Selecionar semana de destino',
  'sched.copyNextWeek': 'Semana seguinte',
  'sched.copyWeekAfterNext': 'Daqui a duas semanas',
  'sched.copySpecificWeek': 'Escolher semana',

  'hol.title': 'Férias', 'hol.subtitle': 'Acompanhamento de solicitações e saldos',
  'hol.request': 'Solicitar férias', 'hol.balances': 'Saldos de férias',
  'hol.allRequests': 'Todas as solicitações', 'hol.noRequests': 'Nenhuma solicitação',
  'hol.employee': 'Funcionário', 'hol.selectEmployee': 'Selecionar funcionário', 'hol.dateRange': 'Período',
  'hol.pickDates': 'Selecionar datas', 'hol.notes': 'Notas (opcional)', 'hol.notesPlaceholder': 'Motivo...',
  'hol.submit': 'Enviar solicitação', 'hol.businessDays': '{count} dias úteis',
  'hol.approved': 'Aprovado', 'hol.rejected': 'Rejeitado', 'hol.pending': 'Pendente', 'hol.delete': 'Excluir',
  'hol.days': '{count} dias', 'hol.used': 'Usados', 'hol.pendingLabel': 'Pendente', 'hol.total': 'Total',
  'hol.dialogTitle': 'Solicitar férias', 'hol.dialogDesc': 'Nova solicitação de férias',
  'hol.partialApprove': 'Aprovar parcialmente', 'hol.partialApproveTitle': 'Aprovação parcial',
  'hol.selectApprovedDates': 'Selecionar datas para aprovar', 'hol.partialSummary': '{approved} de {total} dias aprovados',
  'hol.partialApproveAction': 'Aprovar {count} dias', 'hol.partialApproved': 'Aprovado parcialmente',
  'hol.partialRejected': 'Não aprovado (parcial)', 'hol.partialRemainder': 'Restante da aprovação parcial',
  'hol.partialLabel': 'Parcial',

  'abs.title': 'Ausências', 'abs.subtitle': 'Controle de faltas e licenças de emergência',
  'abs.record': 'Registrar ausência', 'abs.summary': 'Resumo por funcionário',
  'abs.allRecords': 'Todos os registros', 'abs.all': 'Todos', 'abs.sickDays': 'Faltas por doença',
  'abs.emergencyLeave': 'Licenças de emergência', 'abs.sick': 'Doença', 'abs.emergency': 'Emergência',
  'abs.noRecords': 'Nenhum registro', 'abs.employee': 'Funcionário', 'abs.selectEmployee': 'Selecionar funcionário',
  'abs.type': 'Tipo', 'abs.sickDay': 'Falta por doença', 'abs.dates': 'Datas', 'abs.pickDates': 'Selecionar datas',
  'abs.reason': 'Motivo', 'abs.reasonPlaceholder': 'ex. Gripe, emergência familiar...',
  'abs.notes': 'Notas (opcional)', 'abs.notesPlaceholder': 'Detalhes adicionais...',
  'abs.recordBtn': 'Registrar ausência', 'abs.businessDays': '{count} dias úteis',
  'abs.dialogTitle': 'Registrar ausência', 'abs.dialogDesc': 'Registrar falta ou licença',
  'abs.totalLabel': 'Total',

  'rep.title': 'Relatórios', 'rep.weekOf': 'Semana de {label}', 'rep.excel': 'Excel', 'rep.pdfPrint': 'PDF / Imprimir',
  'rep.thisWeek': 'Esta semana', 'rep.allStores': 'Todas as lojas', 'rep.warnings': 'Avisos',
  'rep.hoursVsContract': 'Horas vs Contrato', 'rep.storeCoverage': 'Cobertura', 'rep.holidayBalance': 'Saldo férias',
  'rep.year': 'Ano:', 'rep.loading': 'Carregando…',
  'rep.employeeOverContract': 'Funcionário acima do contrato',
  'rep.employeesOverContract': 'Funcionários acima do contrato',
  'rep.totalOvertimeRisk': 'Risco total de horas extras',
  'rep.employeeHours': 'Horas dos funcionários',
  'rep.colEmployee': 'Funcionário', 'rep.colStore': 'Loja', 'rep.colContract': 'Contrato',
  'rep.colScheduled': 'Agendado', 'rep.colExpected': 'Esperado', 'rep.colDifference': 'Diferença',
  'rep.noEmployees': 'Nenhum funcionário a exibir.',
  'rep.noStores': 'Nenhuma loja a exibir.',
  'rep.understaffedDay': '{count} dia com falta de pessoal esta semana.',
  'rep.understaffedDays': '{count} dias com falta de pessoal esta semana.',
  'rep.needMore': 'Faltam {count}', 'rep.extra': '+{count} extra', 'rep.ok': 'OK',
  'rep.holidayBalances': 'Saldos de férias',
  'rep.colAllowance': 'Direito', 'rep.colApproved': 'Aprovado', 'rep.colPending': 'Pendente',
  'rep.colRemaining': 'Restante', 'rep.colUsage': 'Uso',
  'rep.lowOrOverusedSingular': 'Funcionário com saldo de férias baixo ou excedido',
  'rep.lowOrOverused': 'Funcionários com saldo de férias baixo ou excedido',
  'rep.allClear': 'Tudo em ordem', 'rep.noIssues': 'Nenhum problema de agendamento esta semana.',
  'rep.issueSingular': 'Problema para revisar',
  'rep.issuesToReview': 'Problemas para revisar',
  'rep.urgent': 'urgente(s)',
  'rep.holidayConflict': 'Conflito de férias', 'rep.absenceConflict': 'Conflito de ausência',
  'rep.overHours': 'Horas excedentes', 'rep.unavailable': 'Indisponível', 'rep.understaffed': 'Falta de pessoal',
  'rep.fix': 'Corrigir',
  'rep.tabOverview': 'Visão geral',
  'rep.tabHoursPayroll': 'Horas e Folha',
  'rep.tabCoverage': 'Cobertura',
  'rep.tabLeave': 'Licenças',
  'rep.tabCompliance': 'Conformidade',
  'rep.overviewScheduledH': 'Horas agendadas',
  'rep.overviewOvertimeH': 'Horas extras',
  'rep.overviewCoverageGaps': 'Falhas de cobertura',
  'rep.overviewNearLimit': 'Perto do limite',
  'rep.overviewExceeding': 'Excedendo contrato',
  'rep.overviewRiskLevel': 'Nível de risco',
  'rep.overviewNoIssues': 'Sem problemas ativos esta semana.',
  'rep.viewCompliance': 'Ver conformidade',
  'rep.viewCoverage': 'Ver cobertura',
  'rep.exportSummary': 'Exportar resumo',
  'rep.exportExcel': 'Exportar Excel',
  'rep.exportPDF': 'Exportar PDF',
  'rep.exportedExcel': 'Exportado para Excel',
  'rep.exportedExcelDesc': 'Verifique sua pasta de Downloads.',
  'rep.exportedCSV': 'Exportado para CSV',
  'rep.weekly': 'Semanal',
  'rep.monthly': 'Mensal',
  'rep.totalScheduledH': 'Total horas agendadas',
  'rep.chartTitle': 'Horas agendadas vs esperadas por funcionário',
  'rep.chartOnTrack': 'Conforme',
  'rep.chartOverContract': 'Acima do contrato',
  'rep.chartUnderhours': 'Horas insuficientes',
  'rep.chartExpected': 'Esperado',
  'rep.issuesToReviewTitle': 'Problemas para revisar',
  'rep.allClearPeriod': 'Tudo em ordem — sem problemas neste período.',
  'rep.employeesOverContractLabel': 'Funcionários acima do contrato',
  'rep.totalOvertimeRiskLabel': 'Risco total de horas extras',
  'rep.employeeHoursTitle': 'Horas dos funcionários',
  'rep.colOvertime': 'Horas extras',
  'rep.colUnderhours': 'Horas faltantes',
  'rep.colStatus': 'Status',
  'rep.payrollBreakdown': 'Detalhamento folha — {period}',
  'rep.colScheduledH': 'Agendado (h)',
  'rep.colPaidHolidayH': 'Férias pagas (h)',
  'rep.colPaidSickH': 'Doença paga (h)',
  'rep.colCountedHours': 'Horas contadas',
  'rep.colSickDays': 'Dias de doença',
  'rep.totals': 'Totais',
  'rep.totalScheduled': 'Total agendado',
  'rep.totalActual': 'Total real',
  'rep.lowOrOverusedLabel': 'Funcionários com saldo de férias baixo ou excedido',
  'rep.holidayAllowanceTitle': 'Uso de férias por funcionário',
  'rep.sickDaySummary': 'Resumo dias de doença',
  'rep.colSickDaysUsed': 'Dias de doença usados',
  'rep.colAnnualAllowance': 'Subsídio anual',
  'rep.colInstances': 'Ocorrências',
  'rep.colPaid': 'Pago',
  'rep.colUnpaid': 'Não pago',
  'rep.totalEmployees': 'Total funcionários',
  'rep.compliant': 'Conforme',
  'rep.compWarnings': 'Avisos',
  'rep.actionRequired': 'Ação necessária',
  'rep.complianceDisclaimer': 'As horas contratuais podem variar ligeiramente devido aos horários de funcionamento e durações mínimas de turno. Pequenas diferenças (±2h) são normais.',
  'rep.hExp': '{h}h esp',
  'rep.hCounted': '{h}h contado',
  'rep.hoursBreakdown': 'Detalhamento de horas',
  'rep.scheduledWorked': 'Agendado (trabalhado)',
  'rep.paidHoliday': 'Férias pagas',
  'rep.paidSick': 'Doença paga',
  'rep.unpaidLeave': 'Licença não paga',
  'rep.complianceDetail': 'Detalhe de conformidade',
  'rep.contract': 'Contrato',
  'rep.expected': 'Esperado',
  'rep.counted': 'Contado',
  'rep.variance': 'Variação',
  'rep.maxStreak': 'Dias consecutivos máx',
  'rep.leaveBalanceYear': 'Saldo de licenças (Ano)',
  'rep.holiday': 'Férias',
  'rep.sick': 'Doença',
  'rep.remainingOf': '{remaining} restante(s) de {total}d',
  'rep.riskLog': 'Registro de riscos',
  'rep.highRisk': 'Risco alto',
  'rep.mediumRisk': 'Risco médio',
  'rep.lowRisk': 'Risco baixo',
  'rep.colRiskType': 'Tipo de risco',
  'rep.colDateWeek': 'Data/Semana',
  'rep.colSeverity': 'Gravidade',
  'rep.colDescription': 'Descrição',
  'rep.filterAll': 'Todos',
  'rep.filterHigh': 'Alto',
  'rep.filterMedium': 'Médio',
  'rep.filterLow': 'Baixo',
  'rep.statusCompliant': 'Conforme',
  'rep.statusWarning': 'Aviso',
  'rep.statusActionRequired': 'Ação necessária',
  'rep.statusOnTrack': 'Conforme',
  'rep.statusAtRisk': 'Em risco',
  'rep.riskHigh': 'Alto',
  'rep.riskMedium': 'Médio',
  'rep.riskLow': 'Baixo',
  'rep.gap': 'FALTA',
  'rep.tooltipScheduled': 'Agendado: {h}h',
  'rep.tooltipExpected': 'Esperado: {h}h',
  'rep.tooltipVariance': 'Variação: {h}h',
  'rep.tooltipStatus': 'Status: {status}',

  'pva.title': 'Horas agendadas vs reais',
  'pva.totalScheduled': 'Total agendado',
  'pva.totalPlanned': 'Total planeado',
  'pva.totalActual': 'Total real',
  'pva.totalVariance': 'Variação total',
  'pva.confirmRate': '{confirmed}/{total} turnos confirmados',
  'pva.employeeBreakdown': 'Detalhe por funcionário',
  'pva.scheduled': 'Agendado',
  'pva.planned': 'Planeado',
  'pva.actual': 'Real',
  'pva.variance': 'Variação',
  'pva.breakVar': 'Pausa ±',
  'pva.confirmed': 'Confirmado',
  'pva.lateArrivals': 'Atraso',
  'pva.earlyLeaves': 'Saída antecipada',
  'pva.totals': 'Totais',
  'pva.noConfirmedShifts': 'Sem turnos confirmados — confirme turnos para ver dados reais',
  'pva.lateArrivalsCount': '{count} atraso(s)',
  'pva.earlyLeavesCount': '{count} saída(s) antecipada(s)',
  'pva.totalOvertimeInsight': '{hours}h de horas extras',
  'pva.totalUnderhoursInsight': '{hours}h abaixo do agendado',
  'pva.breakOvertime': '{mins}m de pausa extra',
  'pva.breakScheduledVsActual': 'Pausa: Agendado / Real',
  'review.actualBreakStart': 'Início da pausa',
  'review.actualBreakEnd': 'Fim da pausa',

  // ── Warning Explanations ──
  'warn.hoursOverTitle': '{hours} horas acima do contrato',
  'warn.hoursOverDesc': 'Este funcionário está agendado além das horas semanais acordadas.',
  'warn.hoursOverOpt1': 'Remover um turno',
  'warn.hoursOverOpt2': 'Reatribuir a outro funcionário',
  'warn.hoursOverOpt3': 'Aumentar horas de contrato',
  'warn.unfillableTitle': '{hours} horas não podem ser agendadas esta semana',
  'warn.unfillableWhy': 'Por quê:',
  'warn.unfillableR1': 'Todas as lojas estão completas ou fechadas',
  'warn.unfillableR2': 'A disponibilidade restante não corresponde aos turnos abertos',
  'warn.unfillableR3': 'A duração mínima do turno impede atribuições parciais',
  'warn.unfillableOpt1': 'Reduzir horas de contrato esta semana',
  'warn.unfillableOpt2': 'Permitir turnos mais curtos',
  'warn.unfillableOpt3': 'Aumentar necessidades de pessoal',
  'warn.nearContractTitle': '{hours} horas abaixo do contrato',
  'warn.nearContractDesc': 'Isso acontece porque todos os turnos restantes são completos e o sistema evita criar turnos curtos demais.',
  'warn.nearContractOpt1': 'Aceitar semana abaixo do contrato',
  'warn.nearContractOpt2': 'Permitir turnos mais curtos',
  'warn.nearContractOpt3': 'Ajustar horas de contrato',
  'warn.underTitle': '{hours} horas abaixo do contrato',
  'warn.underDesc': 'Este funcionário tem menos horas agendadas do que seu contrato exige. Mais turnos podem ser adicionados.',
  'warn.underOpt1': 'Adicionar turnos manualmente',
  'warn.underOpt2': 'Executar geração automática',
  'warn.underOpt3': 'Ajustar horas de contrato',
  'warn.options': 'Opções:',
  'warn.holidayDesc': 'Este funcionário está em férias aprovadas nesta data e não pode ser agendado.',
  'warn.holidayOpt1': 'Remover o turno',
  'warn.holidayOpt2': 'Atribuir outro funcionário',
  'warn.absenceDesc': 'Este funcionário está em licença aprovada nesta data e não deve ser agendado.',
  'warn.absenceOpt1': 'Remover o turno',
  'warn.absenceOpt2': 'Atribuir outro funcionário',
  'warn.unavailableDesc': 'Este turno está fora dos dias de disponibilidade do funcionário.',
  'warn.unavailableOpt1': 'Alterar data do turno',
  'warn.unavailableOpt2': 'Atribuir outro funcionário',
  'warn.unavailableOpt3': 'Atualizar disponibilidade',
  'warn.understaffedDesc': 'Esta loja não tem pessoal suficiente agendado para este dia.',
  'warn.understaffedOpt1': 'Adicionar outro funcionário',
  'warn.understaffedOpt2': 'Ajustar requisitos de pessoal',
  'warn.understaffedOpt3': 'Verificar disponibilidade dos funcionários',
  'warn.weeklyHours': 'Resumo semanal de horas',
  'warn.maxDaysTitle': 'Máximo de dias de trabalho atingido',
  'warn.maxDaysDesc': 'Este funcionário atingiu o máximo de {days} dias de trabalho esta semana. Turnos adicionais não podem ser atribuídos mesmo que horas contratuais restem.',
  'warn.maxDaysOpt1': 'Aumentar o máximo de dias de trabalho',
  'warn.maxDaysOpt2': 'Aceitar semana abaixo do contrato',
  'warn.maxDaysOpt3': 'Ajustar horas contratuais',
  'emp.maxWorkingDays': 'Dias de trabalho máx./semana',

  'notfound.title': '404', 'notfound.message': 'Página não encontrada', 'notfound.link': 'Voltar ao início',
  'onb.title': 'Crie sua organização',
  'onb.subtitle': 'Dê um nome à sua empresa ou equipa para começar.',
  'onb.label': 'Nome da organização',
  'onb.placeholder': 'Minha Empresa',
  'onb.creating': 'Criando...',
  'onb.create': 'Criar Organização',
  'onb.signOut': 'Sair',
  'onb.invalidName': 'Nome inválido',
  'onb.nameLength': 'O nome da organização deve ter entre 1 e 100 caracteres.',
  'setup.title': 'Vamos configurar o seu negócio',
  'setup.subtitle': 'Adicione a sua primeira loja e funcionários para gerar a sua primeira escala equilibrada.',
  'setup.progress': 'Passo {count} de {total} concluído',
  'setup.step1': 'Adicione a sua primeira loja',
  'setup.step2': 'Adicione o seu primeiro funcionário',
  'setup.step3': 'Gere a sua primeira escala',
  'setup.step3Hint': 'Requer pelo menos 1 loja e 1 funcionário',
  'setup.addAnother': 'Adicionar outro',
  'setup.storeAdded': 'Loja adicionada!',
  'setup.employeeAdded': 'Funcionário adicionado!',
  'setup.storeCount1': 'loja adicionada',
  'setup.storeCountN': 'lojas adicionadas',
  'setup.empCount1': 'funcionário adicionado',
  'setup.empCountN': 'funcionários adicionados',
  'setup.generate': 'Gerar primeira escala',
  'setup.generating': 'Gerando...',
  'setup.success': 'A sua primeira escala equilibrada está pronta.',
  'setup.genError': 'Erro ao gerar escala',
  'setup.noShiftsError': 'Nenhum turno gerado. Verifique requisitos de pessoal e disponibilidade.',
  'setup.skip': 'Pular por agora',
  'setup.emptySchedule': 'Precisa de pelo menos 1 loja e 1 funcionário para criar uma escala.',
  'settings.title': 'Configurações',
  'settings.subtitle': 'Preferências e configuração da empresa',
  'settings.balancedDesc': 'Maximiza a cobertura minimizando horas extras. Recomendado para a maioria das equipes.',
  'hours.label': 'Horário de funcionamento',
  'hours.quickSet': 'Ajuste rápido',
  'hours.to': 'até',
  'hours.lunchBreak': 'Pausa para almoço',
  'hours.applyWeekdays': 'Aplicar Seg–Sex',
  'hours.applyAll': 'Aplicar todos os dias',
  'hours.open': 'Aberto',
  'hours.closed': 'Fechado',
  'ai.getSuggestions': 'Sugestões IA',
  'ai.autoFix': 'Correção auto',
  'sched.fix': 'Corrigir',
  'sched.reduceHours': 'Reduzir horas',
  'sched.reassignShift': 'Reatribuir turno',
  'sched.confirm': 'Confirmar',
  'sched.edit': 'Editar',
  'sched.remove': 'Remover',
  'sched.understaffed': '⚠ Falta pessoal',
  'sched.missingCount': 'Faltam {count} funcionários',
  'sched.staffed': 'coberto',
  'sched.staff': 'pessoal',
  'sched.statusReady': 'Escala pronta',
  'sched.statusIssue1': '1 problema para verificar',
  'sched.statusIssues': '{count} problemas para verificar',
  'sched.issueOverHours1': '{name} excede as horas semanais',
  'sched.issueOverHoursN': '{count} funcionários excedem as horas',
  'sched.issueShiftTooLong1': '{count} turno muito longo',
  'sched.issueShiftTooLongN': '{count} turnos muito longos',
  'sched.issueInvalidTimes1': '{count} turno com horários inválidos',
  'sched.issueInvalidTimesN': '{count} turnos com horários inválidos',
  'sched.issueOutsideHours1': '{count} turno fora do horário da loja',
  'sched.issueOutsideHoursN': '{count} turnos fora do horário da loja',
  'sched.issueClosedDay1': '{count} turno em dia fechado',
  'sched.issueClosedDayN': '{count} turnos em dias fechados',
  'sched.issueUnavailable1': '{name} agendado em dia indisponível',
  'sched.issueUnavailableN': '{count} turnos em dias indisponíveis',
  'sched.issueWrongStore1': '{name} na loja errada',
  'sched.issueWrongStoreN': '{count} funcionários na loja errada',
  'sched.issueOverlap1': '{count} turno sobreposto',
  'sched.issueOverlapN': '{count} turnos sobrepostos',
  'sched.issueShiftTooShort1': '{count} turno muito curto',
  'sched.issueShiftTooShortN': '{count} turnos muito curtos',
  'sched.issueUnderstaffed1': '{store} precisa de mais pessoal no {day}',
  'sched.issueUnderstaffedN': '{count} dias com falta de pessoal',
  'ai.fixSchedule': 'Corrigir com IA',
  'ai.fixScheduleAI': 'Corrigir escala com IA',
  'sched.issuesThisWeek': 'Problemas esta semana',
  'ai.analyzing': 'Analisando...',
  'ai.suggestions': 'Sugestões IA',
  'ai.fixesFound': '{count} correções encontradas',
  'ai.addresses': 'Resolve:',
  'ai.apply': 'Aplicar',
  'ai.preview': 'Pré-visualizar',
  'ai.hidePreview': 'Ocultar',
  'ai.before': 'Antes',
  'ai.after': 'Depois',
  'ai.keepsCoverage': 'Cobertura mantida',
  'ai.withinContract': 'Dentro do contrato',
  'ai.shiftRemoved': 'Turno removido',
  'ai.noShift': 'Sem turno',
  'ai.refresh': 'Atualizar',
  'ai.swap': 'Trocar',
  'ai.reassign': 'Reatribuir',
  'ai.addShift': 'Adicionar turno',
  'ai.remove': 'Remover',
  'ai.adjust': 'Ajustar',
  'ai.applied': 'Correção aplicada',
  'ai.changes': 'Alterações',
  'ai.failedApply': 'Falha ao aplicar',
  'ai.failedFetch': 'Falha ao obter sugestões',
  'ai.aiError': 'Erro IA',
  'ai.importTitle': 'Importação de dados IA',
  'ai.stageInput': 'Cole uma descrição do seu negócio. As lojas serão analisadas primeiro, depois os funcionários.',
  'ai.stageParsingStores': 'Analisando seu texto para informações de lojas...',
  'ai.stagePreviewStores': 'Revise as lojas abaixo. Após confirmação, os funcionários serão analisados.',
  'ai.stageSavingStores': 'Salvando lojas...',
  'ai.stageCounting': 'Detectando funcionários no seu texto...',
  'ai.stageParsingEmps': 'Analisando funcionários em lotes...',
  'ai.stageValidating': 'Validando dados dos funcionários...',
  'ai.stagePreviewEmps': 'Revise os funcionários abaixo. Clique em "Confirmar" para salvar.',
  'ai.stageImporting': 'Importando funcionários...',
  'ai.stageComplete': 'Todos os dados importados com sucesso!',
  'ai.parseStores': 'Analisar lojas',
  'ai.startOver': 'Recomeçar',
  'ai.confirmStores': 'Confirmar lojas e analisar funcionários',
  'ai.importAnyway': 'Importar mesmo assim',
  'ai.confirmImport': 'Confirmar importação',
  'ai.done': 'Concluído',
  'ai.tip': 'Dica: inclua nomes de lojas, horários, pessoal, nomes de funcionários, horas contratuais, atribuições e disponibilidade.',
  'ai.storesSaved': 'lojas salvas',
  'ai.employees': 'funcionários',
  'ai.totalWeekly': 'total semanal',
  'ai.importedSuccess': '{stores} lojas e {employees} funcionários importados com sucesso.',
  'summary.ofContract': 'do contrato',
  'summary.over': '+{hours}h acima',
  'summary.cantFill': '{hours}h não preenchíveis',
  'summary.belowMin': '{hours}h abaixo do mínimo',
  'summary.remaining': '{hours}h restantes',
  'dash.importData': 'Importar dados',
  'dash.importTitle': 'Importe os dados do seu negócio',
  'dash.importDescription': 'Escolha como configurar suas lojas e funcionários.',
  'dash.aiImportTitle': 'Importação IA inteligente',
  'dash.aiImportDesc': 'Cole uma descrição do seu negócio e deixe a IA criar automaticamente lojas, funcionários e contratos.',
  'dash.startAiImport': 'Iniciar importação IA',
  'dash.excelImportTitle': 'Enviar modelo Excel',
  'dash.excelImportDesc': 'Envie um arquivo Excel estruturado com suas lojas e funcionários para uma importação rápida e validada.',
  'dash.uploadFile': 'Enviar arquivo',
  'dash.importHint': 'Não sabe qual escolher? A importação IA é ideal para uma configuração rápida.',

  'day.sun': 'Domingo', 'day.mon': 'Segunda', 'day.tue': 'Terça', 'day.wed': 'Quarta',
  'day.thu': 'Quinta', 'day.fri': 'Sexta', 'day.sat': 'Sábado',
  'day.sunShort': 'Dom', 'day.monShort': 'Seg', 'day.tueShort': 'Ter', 'day.wedShort': 'Qua',
  'day.thuShort': 'Qui', 'day.friShort': 'Sex', 'day.satShort': 'Sáb',

  'settings.tabOrg': 'Organização', 'settings.tabTeam': 'Equipe', 'settings.tabRules': 'Regras de agendamento', 'settings.tabDanger': 'Zona de perigo',
  'settings.orgName': 'Nome da organização', 'settings.orgNameDesc': 'O nome exibido para os membros da equipe.', 'settings.save': 'Salvar', 'settings.saved': 'Configurações salvas',
  'settings.teamTitle': 'Membros da equipe', 'settings.teamDesc': 'Gerencie sua equipe e envie convites.', 'settings.invite': 'Convidar membro',
  'settings.colName': 'Nome', 'settings.colEmail': 'Email', 'settings.colRole': 'Função', 'settings.colStatus': 'Status', 'settings.colActions': 'Ações',
  'settings.roleOwner': 'Proprietário', 'settings.roleManager': 'Gerente', 'settings.active': 'Ativo', 'settings.pending': 'Pendente',
  'settings.remove': 'Remover', 'settings.resend': 'Reenviar', 'settings.removeConfirm': 'Remover este membro?', 'settings.removed': 'Membro removido',
  'settings.inviteSent': 'Convite enviado', 'settings.inviteEmail': 'Endereço de email', 'settings.inviteRole': 'Função', 'settings.inviteBtn': 'Enviar convite',
  'settings.inviteTitle': 'Convidar membro da equipe', 'settings.inviteDesc': 'Envie um link de convite para participar da sua organização.',
  'settings.rulesTitle': 'Regras de agendamento', 'settings.rulesDesc': 'Configure como os horários são gerados.',
  'settings.dangerTitle': 'Zona de perigo', 'settings.dangerDesc': 'Ações irreversíveis. Prossiga com cuidado.',
  'settings.deleteOrg': 'Excluir organização', 'settings.deleteOrgDesc': 'Excluir permanentemente esta organização e todos os dados associados.',
  'settings.deleteOrgConfirm': 'Tem certeza? Esta ação não pode ser desfeita.', 'settings.clearSchedules': 'Limpar todos os horários',
  'settings.clearSchedulesDesc': 'Remover todos os dados de turnos. Os dados de funcionários e lojas serão mantidos.',
  'settings.clearConfirm': 'Tem certeza? Todos os dados de horários serão excluídos.',
  'settings.deleted': 'Organização excluída', 'settings.cleared': 'Horários limpos', 'settings.cancel': 'Cancelar',
  'settings.you': '(você)', 'settings.cannotRemoveSelf': 'Você não pode se remover',

  'common.loading': 'Carregando...',
  'common.cancel': 'Cancelar',
  'auth.forgotPassword': 'Esqueceu a senha?',
  'auth.or': 'ou',
  'auth.googleSignIn': 'Continuar com Google',
  'auth.appleSignIn': 'Continuar com Apple',
  'forgot.title': 'Esqueceu a senha',
  'forgot.subtitle': 'Insira seu email para receber um link de redefinição.',
  'forgot.button': 'Enviar link',
  'forgot.sent': 'Se uma conta existir, um link de redefinição foi enviado.',
  'forgot.backToLogin': 'Voltar ao login',
  'reset.title': 'Redefinir senha',
  'reset.subtitle': 'Insira sua nova senha.',
  'reset.newPassword': 'Nova senha',
  'reset.confirmPassword': 'Confirmar senha',
  'reset.button': 'Atualizar',
  'reset.passwordMin': 'A senha deve ter pelo menos 8 caracteres',
  'reset.mismatch': 'As senhas não coincidem',
  'reset.failed': 'Falha ao atualizar. Tente novamente.',
  'reset.success': 'Senha atualizada. Redirecionando...',
  'reset.invalidLink': 'Este link é inválido ou expirou.',
  'reset.backToLogin': 'Voltar ao login',
  'settings.tabAccount': 'Conta',
  'settings.tabBilling': 'Faturamento',
  'billing.currentPlan': 'Plano atual',
  'billing.currentPlanDesc': 'Detalhes da sua assinatura',
  'billing.refresh': 'Atualizar',
  'billing.noSubscription': 'Sem assinatura ativa',
  'billing.paymentMethod': 'Método de pagamento',
  'billing.invoices': 'Faturas',
  'billing.billingDetails': 'Detalhes de cobrança',
  'billing.editDetails': 'Editar detalhes',
  'billing.activating': 'Ativando sua assinatura…',
  'billing.activatingDesc': 'Isso geralmente leva apenas alguns segundos.',
  'billing.subEnded': 'Sua assinatura terminou',
  'billing.choosePlan': 'Escolha seu plano para começar',
  'billing.pastDue': 'Pagamento em atraso',
  'billing.canceled': 'Cancelado',
  'billing.expired': 'Expirado',
  'billing.monthly': 'Mensal',
  'billing.annual': 'Anual',
  'billing.save17': 'Economize ~17%',
  'billing.trialDesc': 'Teste gratuito de 30 dias incluído. Cancele a qualquer momento.',
  'billing.redirecting': 'Redirecionando…',
  'billing.reactivate': 'Reativar assinatura',
  'billing.startTrial': 'Iniciar teste gratuito',
  'billing.checkStatus': 'Já completou o pagamento? Verificar status',
  'billing.accountSettings': 'Configurações da conta',
  'billing.signOut': 'Sair',
  'billing.title': 'Cobrança e Pagamentos',
  'billing.subtitle': 'Gerencie sua assinatura, método de pagamento e faturas',
  'billing.freeTrial': 'Teste gratuito',
  'billing.trialEnds': 'Fim do teste',
  'billing.billingCycle': 'Ciclo de cobrança',
  'billing.cancelNotice': 'Sua assinatura terminará em {date}. Você pode retomá-la pelo portal de cobrança.',
  'billing.noSub': 'Nenhuma assinatura ativa encontrada.',
  'billing.update': 'Atualizar',
  'billing.noPayment': 'Nenhum método de pagamento registrado',
  'billing.addCard': 'Adicionar cartão',
  'billing.noInvoicesYet': 'Nenhuma fatura ainda',
  'billing.detailsDesc': 'Atualize o nome da empresa, email de cobrança, número de IVA e endereço pelo portal.',
  'billing.companyName': 'Nome da empresa',
  'billing.billingEmail': 'Email de cobrança',
  'billing.vatNumber': 'Número de IVA',
  'billing.address': 'Endereço',
  'billing.editBilling': 'Editar detalhes de cobrança',
  'billing.noDetailsFound': 'Nenhum detalhe de cobrança encontrado',
  'billing.setUp': 'Configurar',
  'billing.expires': 'Expira',
  'billing.status_active': 'Ativo',
  'billing.status_trialing': 'Teste',
  'billing.status_past_due': 'Vencido',
  'billing.status_canceled': 'Cancelado',
  'billing.status_incomplete': 'Incompleto',
  'billing.status_unpaid': 'Não pago',
  'billing.interval_month': 'mês',
  'billing.interval_year': 'ano',
  'billing.cancelsAtPeriodEnd': 'Cancela no fim do período',
  'billing.subActivated': 'Assinatura ativada!',
  'billing.syncSlow': 'A sincronização está demorando mais do que o esperado. Por favor, atualize.',
  'billing.subFound': 'Assinatura encontrada!',
  'billing.noSubYet': 'Nenhuma assinatura ativa encontrada ainda.',
  'account.title': 'Informações da conta',
  'account.subtitle': 'Gerencie seu email e senha.',
  'account.changeEmail': 'Alterar email',
  'account.changeEmailDesc': 'Um email de verificação será enviado ao novo endereço.',
  'account.newEmail': 'Novo email',
  'account.confirmEmail': 'Confirmar email',
  'account.currentPassword': 'Senha atual',
  'account.emailMismatch': 'Os endereços de email não coincidem',
  'account.emailVerificationSent': 'Um link de verificação foi enviado ao seu novo email.',
  'account.changePassword': 'Alterar senha',
  'account.changePasswordDesc': 'Você deve verificar sua senha atual.',
  'account.newPassword': 'Nova senha',
  'account.confirmNewPassword': 'Confirmar nova senha',
  'account.savePassword': 'Atualizar',
  'account.passwordMin': 'A senha deve ter pelo menos 8 caracteres',
  'account.mismatch': 'As senhas não coincidem',
  'account.wrongPassword': 'Senha atual incorreta.',
  'account.updateFailed': 'Falha ao atualizar. Tente novamente.',
  'account.passwordChanged': 'Senha atualizada',
  'account.deleteTitle': 'Excluir conta',
  'account.deleteDesc': 'Excluir permanentemente sua conta e todos os dados.',
  'account.deleteButton': 'Excluir minha conta',
  'account.deleteConfirmTitle': 'Tem certeza absoluta?',
  'account.deleteConfirmDesc': 'Esta ação excluirá permanentemente sua conta, organização e todos os dados.',
  'account.deletePasswordLabel': 'Digite sua senha para confirmar',
  'account.deleteConfirmButton': 'Excluir tudo',
  'account.deleteCancelButton': 'Cancelar',
  'account.deleteSuccess': 'Conta excluída.',

  // ── Excel Import ──
  'import.title': 'Importar dados',
  'import.subtitle': 'Carregue um arquivo Excel ou CSV com suas lojas e funcionários.',
  'import.downloadTemplate': 'Baixar modelo Excel',
  'import.dropzone': 'Arraste e solte seu arquivo aqui, ou clique para navegar',
  'import.dropzoneHint': 'Aceita .xlsx ou .csv · Máx 5MB',
  'import.parsing': 'Analisando arquivo...',
  'import.storesDetected': 'Lojas detectadas',
  'import.employeesDetected': 'Funcionários detectados',
  'import.hours': 'Horários',
  'import.staff': 'Pessoal (DS/FS)',
  'import.days': 'Disponibilidade',
  'import.totalWeekly': 'total semanal',
  'import.importing': 'Importando dados...',
  'import.success': 'Importação bem-sucedida!',
  'import.successMessage': 'Importação bem-sucedida. Seus dados estão prontos.',
  'import.failed': 'Falha na importação',
  'import.confirmImport': 'Confirmar importação',
  'import.cancel': 'Cancelar',
  'import.tryAgain': 'Tentar novamente',
  'import.done': 'Concluído',
  'import.uploadData': 'Importar dados',
  'import.uploadHint': 'Ou importe de um arquivo Excel/CSV',

  // ── Compliance Reports ──
  'comp.complianceSummary': 'Conformidade',
  'comp.payrollExport': 'Exportação folha',
  'comp.legalRisk': 'Risco legal',
  'comp.totalEmployees': 'Total funcionários',
  'comp.compliant': 'Conformes',
  'comp.warnings': 'Avisos',
  'comp.critical': 'Ação necessária',
  'comp.employee': 'Funcionário',
  'comp.store': 'Loja',
  'comp.contractH': 'Contrato (h/s)',
  'comp.scheduledH': 'Agendado (h)',
  'comp.regularH': 'Horas regulares',
  'comp.overtimeH': 'Horas extras',
  'comp.underhours': 'Horas em falta',
  'comp.holidays': 'Férias',
  'comp.sickDays': 'Dias de doença',
  'comp.maxStreak': 'Série máx.',
  'comp.status': 'Estado',
  'comp.totalH': 'Total horas',
  'comp.totals': 'Totais',
  'comp.allEmployees': 'Todos os funcionários',
  'comp.payrollDesc': 'Resumo mensal estruturado de horas para processamento de folha de pagamento. Sem cálculos salariais.',
  'comp.exportedCSV': 'Exportado em CSV',
  'comp.exportedExcel': 'Exportado em Excel',
  'comp.exportedExcelDesc': 'Verifique a pasta de Downloads para abrir o arquivo',
  'comp.exportedCSVDesc': 'Verifique a pasta de Downloads para abrir o arquivo',
  'excel.employee': 'Funcionário',
  'excel.store': 'Loja',
  'excel.contractH': 'Contrato (h)',
  'excel.scheduledH': 'Agendado (h)',
  'excel.differenceH': 'Diferença (h)',
  'excel.status': 'Estado',
  'excel.hoursVsContract': 'Horas vs Contrato',
  'excel.metric': 'Métrica',
  'excel.required': 'Necessário',
  'excel.scheduled': 'Agendado',
  'excel.difference': 'Diferença',
  'excel.allowance': 'Subsídio',
  'excel.approved': 'Aprovado',
  'excel.pending': 'Pendente',
  'excel.remaining': 'Restante',
  'excel.leaveBalance': 'Saldo de férias',
  'comp.highRisk': 'Risco alto',
  'comp.mediumRisk': 'Risco médio',
  'comp.lowRisk': 'Risco baixo',
  'comp.allSeverities': 'Todas as severidades',
  'comp.exportRisk': 'Exportar relatório de risco',
  'comp.riskType': 'Tipo de risco',
  'comp.dateWeek': 'Data / Semana',
  'comp.severity': 'Severidade',
  'comp.description': 'Descrição',
  'comp.noRisks': 'Nenhum risco detectado',
  'comp.noRisksDesc': 'Todos os horários estão em conformidade com a legislação trabalhista este mês.',
  'comp.riskType48h': 'Horas semanais > 48',
  'comp.riskTypeNoRest': 'Sem dia de descanso',
  'comp.riskType6Consec': '6+ dias consecutivos',
  'comp.riskTypeContract': 'Violação de horas contratuais',
  'comp.riskTypeMaxDays': 'Excede máx dias/semana',
  'comp.riskTypeAvail': 'Agendado fora da disponibilidade',
  'comp.riskDesc48h': '{hours}h agendadas (máx 48h)',
  'comp.riskDescNoRest': '{days} dias trabalhados sem descanso',
  'comp.riskDesc6Consec': '{days} dias trabalhados na semana',
  'comp.riskDescContract': '{hours}h vs {contract}h contrato (+{pct}%)',
  'comp.riskDescMaxDays': '{days} dias trabalhados (máx {max})',
  'comp.riskDescAvail': 'Agendado em dia indisponível',
  'comp.severityHigh': 'Alto',
  'comp.severityMedium': 'Médio',
  'comp.severityLow': 'Baixo',

  // ── Landing Page (restructured) ──
  'lp.hero.title': 'Planejamento e conformidade para equipes por turnos',
  'lp.hero.subtitle': 'Crie escalas, acompanhe horas contratuais, gerencie licenças remuneradas e previna problemas de conformidade — tudo em um sistema.',
  'lp.hero.demo': 'Agendar uma demo',
  'lp.hero.trust': 'Para varejo, hospitalidade, clínicas, academias, franquias e empresas de serviços.',

  'lp.prob.title': 'Gestão de turnos não deveria ser tão complicada',
  'lp.prob1': 'Planilhas manuais causam erros',
  'lp.prob2': 'Horas extras passam despercebidas',
  'lp.prob3': 'O controle de licenças fica confuso',
  'lp.prob4': 'Os riscos de conformidade aumentam',
  'lp.prob5': 'Gestores perdem horas toda semana',
  'lp.prob.closing': 'Pequenos erros de planejamento custam dinheiro real.',

  'lp.core.title': 'Tudo que você precisa para gerenciar equipes por turnos',
  'lp.core1.title': 'Criador de escalas inteligente',
  'lp.core1.desc': 'Gere automaticamente escalas equilibradas respeitando horas contratuais e disponibilidade.',
  'lp.core2.title': 'Controle de contratos e horas',
  'lp.core2.desc': 'Acompanhe horas previstas vs reais mensal e semanalmente.',
  'lp.core3.title': 'Controle de licenças e faltas',
  'lp.core3.desc': 'Aplique automaticamente limites remunerados e detecte abusos.',
  'lp.core4.title': 'Suite de relatórios de conformidade',
  'lp.core4.desc': 'Gere resumos de conformidade mensal instantaneamente.',
  'lp.core5.title': 'Funciona em 6 idiomas',
  'lp.core5.desc': 'Operota detecta automaticamente o seu idioma e adapta toda a plataforma. Inglês, italiano, espanhol, francês, alemão e português — sem configuração.',
  'lp.core6.title': 'Suporte multi-unidade',
  'lp.core6.desc': 'Gerencie várias unidades a partir de uma única conta. Cada unidade tem suas próprias escalas, regras de pessoal e relatórios de conformidade.',
  'lp.core.subtitle': 'O motor de planejamento IA da Operota cria escalas equilibradas que respeitam horas contratuais, disponibilidade dos funcionários e cobertura da equipe automaticamente. Sem planilhas. Sem adivinhação.',
  'lp.core.sectionTitle': 'Crie escalas de pessoal em segundos',

  'lp.hero.langLine': 'Disponível em 6 idiomas — adapta-se automaticamente à sua equipe',
  'lp.hero.noCard': 'Sem cartão necessário',
  'lp.hero.trustBadge1': 'Conforme com RGPD',
  'lp.hero.trustBadge2': 'Sem cobranças por funcionário',
  'lp.hero.trustBadge3': 'Configuração em 15 minutos',

  'lp.pricing.annualSave': 'Faturado 990€/ano — 2 meses grátis',
  'lp.pricing.annualBadge': 'Economize 198€/ano',
  'lp.pricing.bestValue': 'Melhor valor',
  'lp.price6': 'Funciona em 6 idiomas',
  'lp.price7': 'Agendamento automático com IA',

  'lp.prob1.desc': 'Uma fórmula errada custa horas para corrigir e arrisca pagar sua equipe incorretamente.',
  'lp.prob2.desc': 'Violações contratuais se acumulam silenciosamente até se tornarem um problema jurídico.',
  'lp.prob3.desc': 'Conflitos de licenças e ausências de última hora criam caos de cobertura.',
  'lp.prob4.desc': 'Sem verificações automatizadas, você está a uma auditoria de uma multa custosa.',
  'lp.prob5.desc': 'Criar escalas manualmente é trabalho repetitivo que deveria levar segundos.',

  'lp.aud1.desc': 'Lojas, boutiques e redes de moda',
  'lp.aud2.desc': 'Equipes de salão e cozinha',
  'lp.aud3.desc': 'Clínicas, academias e consultórios de terapia',
  'lp.aud4.desc': 'Centros de fitness e instalações de lazer',
  'lp.aud5.desc': 'Franquias e operadores multi-unidade',
  'lp.aud6.desc': 'Equipes de campo e empresas de serviços',

  'lp.section5.title': 'Acompanhe horas contratuais e conformidade',
  'lp.section5.subtitle': 'Operota monitora automaticamente as horas contratuais contra os turnos agendados, sinaliza violações de horas extras e gera relatórios de conformidade.',
  'lp.section5.card1.title': 'Agendamento de turnos com IA',
  'lp.section5.card1.desc': 'Nosso criador de escalas usa IA para gerar escalas equilibradas que previnem horas extras e respeitam a disponibilidade.',
  'lp.section5.card2.title': 'Relatórios de conformidade',
  'lp.section5.card2.desc': 'Gere resumos mensais com horas trabalhadas vs contratuais, saldos de licenças e violações — exportáveis para Excel.',

  'lp.early.title': 'Seja o primeiro a compartilhar sua experiência',
  'lp.early.subtitle': 'Junte-se ao nosso programa de acesso antecipado.',

  'footer.solutions': 'Soluções',

  'lp.faq.businesses.q': 'Que tipos de negócios usam Operota?',
  'lp.faq.businesses.a': 'Qualquer negócio com trabalhadores por turnos — lojas, restaurantes, academias, clínicas, empresas de limpeza, segurança e mais.',
  'lp.faq.setup.q': 'Quanto tempo leva a configuração?',
  'lp.faq.setup.a': 'A maioria das equipes está operacional em menos de 15 minutos. Adicione sua unidade, importe seu pessoal e gere automaticamente.',
  'lp.faq.selfService.q': 'Os funcionários podem ver suas próprias escalas?',
  'lp.faq.selfService.a': 'O autoatendimento para funcionários está em nosso roteiro. Atualmente as escalas podem ser exportadas em PDF ou Excel.',
  'lp.faq.perEmployee.q': 'Há uma taxa por funcionário?',
  'lp.faq.perEmployee.a': 'Não. Operota tem uma taxa fixa de 99€ por mês independentemente do número de funcionários ou unidades.',
  'lp.faq.afterTrial.q': 'O que acontece após o teste de 30 dias?',
  'lp.faq.afterTrial.a': 'Você passa para o plano mensal a 99€/mês. Avisamos antes do fim do teste e você pode cancelar a qualquer momento.',
  'lp.faq.languages.q': 'Em quais idiomas está disponível?',
  'lp.faq.languages.a': 'Inglês, italiano, espanhol, francês, alemão e português. Detecta automaticamente o idioma do seu navegador.',

  'lp.pricing.title': 'Preços simples e profissionais',
  'lp.pricing.noFees': 'Sem taxas ocultas. Sem cobranças por funcionário.',
  'lp.price1': 'Funcionários ilimitados',
  'lp.price2': 'Suporte multi-unidade',
  'lp.price3': 'Relatórios de conformidade completos',
  'lp.price4': 'Controle de licenças e ausências',
  'lp.price5': 'Teste gratuito de 30 dias',

  'lp.aud.title': 'Criado para negócios por turnos em crescimento',
  'lp.aud.subtitle': 'Se você gerencia funcionários por turnos ou em várias unidades, Operota mantém sua equipe organizada e em conformidade.',
  'lp.aud1': 'Lojas',
  'lp.aud2': 'Grupos de hospitalidade',
  'lp.aud3': 'Clínicas',
  'lp.aud4': 'Academias',
  'lp.aud5': 'Franquias',
  'lp.aud6': 'Empresas de serviços',

  'lp.faq.payroll.q': 'Gerencia a folha de pagamento?',
  'lp.faq.payroll.a': 'Não. Operota fornece exportações mensais estruturadas de horas prontas para seu contador ou sistema de folha, mas não calcula salários, impostos ou deduções.',
  'lp.faq.contracts.q': 'Respeita as horas contratuais?',
  'lp.faq.contracts.a': 'Sim. O sistema respeita automaticamente as horas contratuais semanais de cada funcionário ao gerar as escalas.',
  'lp.faq.excel.q': 'Posso importar do Excel?',
  'lp.faq.excel.a': 'Sim. Importe unidades e funcionários de arquivos Excel ou CSV, ou use nossa função de importação IA.',
  'lp.faq.security.q': 'Meus dados estão seguros?',
  'lp.faq.security.a': 'Seus dados são criptografados em trânsito e em repouso, hospedados na UE, com infraestrutura cloud empresarial e conformidade rigorosa com o RGPD.',
  'lp.faq.stores.q': 'Posso gerenciar múltiplas unidades?',
  'lp.faq.stores.a': 'Com certeza. Operota suporta unidades ilimitadas. Gerencie escalas, pessoal e relatórios em um único painel.',

  'settings.tabLeave': 'Política de licenças',
  'leave.globalPolicy': 'Política global de licenças',
  'leave.globalPolicyDesc': 'Definir as alocações padrão de licenças pagas para todos os funcionários.',
  'leave.paidHolidayDays': 'Dias de férias pagas anuais',
  'leave.paidSickDays': 'Dias de doença pagos anuais',
  'leave.hoursPerDay': 'Horas de trabalho por dia',
  'leave.savePolicy': 'Salvar política',
  'leave.policySaved': 'Política de licenças salva',
  'leave.bulkActions': 'Ações em massa',
  'leave.bulkActionsDesc': 'Aplicar alterações de política aos funcionários ou redefinir contadores anuais.',
  'leave.applyToAll': 'Aplicar a todos os funcionários',
  'leave.confirmApply': 'Aplicar política a todos?',
  'leave.confirmApplyDesc': 'Isso atualizará as alocações de férias e doença para todos os funcionários sem substituição personalizada.',
  'leave.appliedToAll': 'Política aplicada a todos os funcionários',
  'leave.resetCounters': 'Redefinir contadores',
  'leave.confirmReset': 'Redefinir contadores de licenças?',
  'leave.confirmResetDesc': 'Os saldos de licenças são calculados a partir dos registros. Filtre por ano para ver os saldos atuais.',
  'leave.resetInfo': 'Os saldos são calculados a partir de registros com data. Filtre por ano.',
  'leave.overridePolicy': 'Substituir política padrão',
  'leave.paidHolH': 'Férias pagas (h)',
  'leave.paidSickH': 'Doença paga (h)',
  'leave.unpaidH': 'Licença não paga (h)',
  'leave.holAllowance': 'Alocação férias',
  'leave.sickAllowance': 'Alocação doença',
  'leave.used': 'usado',
  'leave.remainHol': 'Férias rest.',
  'leave.remainSick': 'Doença rest.',
  'comp.contractCountedH': 'Horas contadas',
  'comp.contractCountedTooltip': 'Horas agendadas + licenças pagas. Para conformidade contratual.',
  'comp.workingDays': 'dias úteis',
  'comp.day': 'dia',
  'comp.expectedH': 'Esperado',
  'comp.expectedTooltip': 'Calculado a partir de horas de contrato × dias úteis no período. Contagem exata.',
  'comp.scheduledTooltip': 'Horas de turno reais no período.',
  'comp.underhoursVsContract': 'Horas em falta',
  'comp.underhoursTooltip': 'Horas contadas − Horas esperadas. Negativo = déficit.',
  'comp.complianceWeekly': 'Semanal',
  'comp.complianceMonthly': 'Mensal',
  'comp.complianceReport': 'Relatório de conformidade',
  'comp.period': 'Período',
  'comp.print': 'Imprimir',
  'comp.savePDF': 'Guardar como PDF',
  'comp.printOrSavePDF': 'Imprimir / Guardar como PDF',
  'comp.printPDFHint': 'Para guardar como PDF, selecione "Guardar como PDF" como destino na janela de impressão.',
  'comp.generatingPDF': 'A gerar PDF...',
  'comp.backToReports': 'Voltar aos relatórios',
  'comp.totalOvertime': 'Horas extra totais',
  'comp.totalUnderhours': 'Horas em falta totais',
  'comp.totalUnpaidLeave': 'Licença não remunerada total',
  'comp.generatedOn': 'Gerado em',
  'comp.generatedBy': 'Gerado por',
  'comp.organization': 'Organização',
  'comp.reportPeriod': 'Período do relatório',
  'comp.hoursBreakdown': 'Detalhe das horas',
  'comp.compliance': 'Conformidade',
  'comp.leaveBalanceYear': 'Saldo férias (Ano)',
  'comp.leaveBalanceAnnual': 'Saldo férias (Anual)',
  'comp.scheduled': 'Programado (trabalhado)',
  'comp.paidHoliday': 'Férias pagas',
  'comp.paidSick': 'Doença paga',
  'comp.unpaidLeave': 'Licença não remunerada',
  'comp.contract': 'Contrato',
  'comp.expected': 'Esperado',
  'comp.expectedPeriod': 'Esperado para o período',
  'comp.counted': 'Contado',
  'comp.variance': 'Variação',
  'comp.worked': 'Trabalhado',
  'comp.holidayRemaining': 'Férias restantes',
  'comp.sickRemaining': 'Doença restante',
  'comp.days': 'dias',
  'comp.notes': 'Notas',
  'comp.statusOk': 'No caminho certo',
  'comp.statusWarning': 'Variação menor',
  'comp.statusCritical': 'Ação necessária',
  'comp.allClear': 'Todos os funcionários conformes',
  'comp.varianceExplainer': 'As horas contratuais podem variar ligeiramente devido aos horários de abertura, disponibilidade e duração mínima dos turnos. Pequenas diferenças (±2h) são normais.',
  'comp.toleranceOk': 'Dentro da tolerância',
  'comp.toleranceWarning': 'Revisão recomendada',
  'comp.toleranceAction': 'Ação necessária',
};

import { legalTranslations } from './legal-translations';

const base: Record<Language, Record<string, string>> = { en, fr, es, de, it, pt };

// Merge legal translations into base
const translations: Record<Language, Record<string, string>> = Object.fromEntries(
  Object.entries(base).map(([lang, dict]) => [
    lang,
    { ...dict, ...(legalTranslations[lang] || {}) },
  ])
) as Record<Language, Record<string, string>>;

export function t(lang: Language, key: string, vars?: Record<string, string>): string {
  let text = translations[lang]?.[key] ?? translations.en[key] ?? key;
  if (vars) {
    Object.entries(vars).forEach(([k, v]) => {
      text = text.replace(`{${k}}`, v);
    });
  }
  return text;
}

const DAY_KEYS = ['day.sun', 'day.mon', 'day.tue', 'day.wed', 'day.thu', 'day.fri', 'day.sat'] as const;
const DAY_SHORT_KEYS = ['day.sunShort', 'day.monShort', 'day.tueShort', 'day.wedShort', 'day.thuShort', 'day.friShort', 'day.satShort'] as const;

export function dayName(lang: Language, dayIndex: number): string {
  return t(lang, DAY_KEYS[dayIndex]);
}

export function dayShort(lang: Language, dayIndex: number): string {
  return t(lang, DAY_SHORT_KEYS[dayIndex]);
}
